import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiThrow } from '../utils/api';
import { FIELD_LABEL_CLASS, FORM_INPUT_FIELD, ROW_EDIT_CONTROL, ROW_EDIT_MONEY_CONTROL } from '../constants/inputs';
// Phase 11 polish: lock-guard responses (HTTP 423) carry { error, code: 'version_locked' }.
// At sites where the body is read BEFORE the ok check, we throw an Error via
// throwApiError() and copy data.code onto it inline so friendlyErrorMessage()
// can take the structured branch instead of falling back to substring sniffing.
// Where the body has NOT been consumed yet we use the shared apiThrow() helper
// from utils/api.js (same effect, also tolerates non-JSON 423 bodies).
import { notify as notifyDataBus } from '../utils/dataBus';
// 🔻 `formatCurrency` was imported here until 2026-09-20 and this page's ONLY
// caller of it was the local `CurrencyInput`, which has moved to
// `components/ui/CurrencyInput.js` and imports it there. Every other money
// string on this page goes through `useVatMode`'s `formatMoney`.
// 🔀 THE canonical local-calendar-date formatter. A private `toDateLocal` arrow
// inside the Gantt drag handler was a fifth hand-rolled copy of these three
// lines (declared LEGACY_DEBT group `a9681237`); deleted 2026-09-08.
// 🚫 Never `.toISOString()` followed by a `.slice(0, 10)` here — that is the UTC
// drift `server/tests/regressions/no-utc-date-drift.test.js` bans, and every
// week key this page writes is a LOCAL Monday.
// 🪤 The two halves are written APART on purpose: that scanner is comment-BLIND
// and line-based, so naming the idiom verbatim fails CI at this very line —
// the trap root `CLAUDE.md` records under *Operational gotchas*.
import { toLocalISO } from '../utils/dateFormat';
import { toPayloadAmount, toPayloadNumber, isMalformedAmount } from '../utils/moneyPayload';
import { roundMoney } from '../utils/money';
import { moneyOrAbsent } from '../utils/moneyOrAbsent';
import { useDepartment } from '../contexts/DepartmentContext';
// `useToastOrNoop`, NOT the throwing `useToast`: `App.js` always mounts
// `ToastProvider`, so in the running app this IS the real toast — but the 29
// existing suites for this page render it bare, and the throwing hook would
// convert every one into a provider-wrapping rewrite. This is the exact
// harness-compatibility case `ToastContext.js` documents (the
// `DepartmentOrders`/`MyOrders` precedent).
import { useToastOrNoop } from '../contexts/ToastContext';
// THE money input, shared with `pages/MarketplaceCreateListing.js` since
// 2026-09-20; it was defined in BOTH files before that. See the marker further
// down this file where the local copy used to live.
import CurrencyInput from '../components/ui/CurrencyInput';
import NumberInput from '../components/ui/NumberInput';
import { useVatMode } from '../hooks/useVatMode';
import { scrollUnderPinnedChrome } from '../hooks/useOpenAtTop';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import { usePageTitle } from '../contexts/PageHeaderContext';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import CollapsibleSection from '../components/CollapsibleSection';
import InfoTip from '../components/InfoTip';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

import {
  ALLOCATION_OPTIONS, DEFAULT_ALLOCATION_OPTIONS,
  allocationForDayCount, dayCountForAllocation,
  COMPUTER_ALLOWANCE_NAME, COMPUTER_ALLOWANCE_LABEL,
} from '../constants/budget';
import { BTN, BTN_XS } from '../constants/buttons';
import CrewGanttView from '../components/budget/CrewGanttView';
import CrewDayGrid, {
  daysForPreset, dateForWeekDay, UnpricedPremiumWarning, BelowDefaultPremiumWarning,
  PREMIUM_DEFAULT_MULTIPLIER_LABELS,
} from '../components/budget/CrewDayGrid';
import { runPositions } from '../utils/consecutiveDays';
// 🔑 THE ONE ANSWER TO "which days does this week hold". Shared with the Crew
// Gantt's per-day toggles since 2026-08-22 — read the header of that module
// before changing anything that resolves or posts a day set.
import {
  generateWeeks,
  EMPTY_DAY_BASELINE,
  buildWeekDayBaseline,
  resolveWeekDays,
  sameDaySet,
  dayPayloadHasChanges,
  buildDayPayload,
  resolveWorkedDateSet,
  dateChangeWeekAllocations,
  weekOverridesForEdit,
  weeksSaveBody,
  draftWeekOverridesOrNull,
  weekSlotForDate,
  withDraftDefaultAllocation,
} from '../utils/crewDayResolution';
import UserAutocomplete, { UserStatusIndicator } from '../components/UserAutocomplete';
import Dropdown from '../components/ui/Dropdown';
import Toolbar, { TOOLBAR_GROUP } from '../components/ui/Toolbar';
import SearchInput from '../components/ui/SearchInput';
import Tabs from '../components/ui/Tabs';
import ConfirmDialog from '../components/ConfirmDialog';
import { UNSAVED_PROMPT_BUTTONS } from '../constants/unsavedChangesPrompt';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';
import SpendAdjustmentMarker from '../components/SpendAdjustmentMarker';
import { buildBudgetManagePdf, buildManageSectionCsv } from '../utils/budgetManagePdf';
import BudgetManageActionsMenu from '../components/budget/BudgetManageActionsMenu';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import useColumnWidths from '../hooks/useColumnWidths';
import useMeasuredWidth from '../hooks/useMeasuredWidth';
import useLinkedScrollLeft from '../hooks/useLinkedScrollLeft';
import { saveBarSpacerHeight } from '../utils/saveBarSpacer';
import useStickyToolbar from '../hooks/useStickyToolbar';
import CrewMembersTableFrame from '../components/budget/CrewMembersTableFrame';
import ColumnResizeHandle from '../components/ui/ColumnResizeHandle';
import { useCrewCostPreview } from '../hooks/useCrewCostPreview';
import { emailInUseHandoffMessage, isEmailInUse } from '../constants/emailInUse';
import useFitColumnsToScreen from '../hooks/useFitColumnsToScreen';
import { fitColumnWidths } from '../utils/fitColumnWidths';
import OverrideMarker from '../components/ui/OverrideMarker';
import BudgetTableScroller from '../components/budget/BudgetTableScroller';
import useExpandRowEditor from '../hooks/useExpandRowEditor';
import {
  editableRowClass, rowPanelFadeClass, stopRowToggle, stopRowToggleOnControl, RowReorderButtons, RowMenuCell, ROW_MENU_COL_WIDTH, RowEditActions, RowEditPrompt,
  RowEditBar, RowDeleteButton, RowExpansion, ROW_CELL_PAD, ROW_CELL_PAD_EDIT,
} from '../components/budget/ExpandRowParts';
import EditableList, { useEditableList } from '../components/budget/EditableList';
import { ExpandChevron, RowToggleChevron } from '../components/TableRowExpansion';
import BudgetTabsNav, { BUDGET_TABS_BAND, BUDGET_TABS_ROW } from '../components/budget/BudgetTabsNav';
import { rowsToCsv, downloadTextFile, CSV_EXT, CSV_MIME, CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import { PAGE_SIDE_GUTTER } from '../constants/pageLayout';
import { AGREEMENT_CHANGED_INPUT_CLASS } from '../components/timesheets/AgreementSummary';
import RenameSyncNote from '../components/gags/RenameSyncNote';

/**
 * 🕐 THE GANTT STRIP'S COSTING DEBOUNCE — 250ms, 2026-08-23, and it is
 * DELIBERATELY NOT the panel's 400.
 *
 * `CREW_COST_PREVIEW_DEBOUNCE_MS` is 400 for a reason that is specific to
 * TYPED CURRENCY: a user enters `1250` as one number, so a longer wait costs a
 * request rather than a redraw. **A drag is not typing.** It produces discrete
 * whole-day steps several times a second and then stops, and at 400ms the
 * figures only arrive after the user has already let go — which is the
 * complaint this change exists to answer, arriving through the delay instead
 * of through the formula.
 *
 * 250 is `useSearchRequest`'s own `useDebouncedValue` default: the house
 * answer for a value that changes as fast as a user can move. It still
 * suppresses every intermediate frame of a continuous drag (steps land far
 * closer together than 250ms), and it lands within a quarter-second of a
 * pause.
 *
 * 🪤 A DEBOUNCE IS NOT A STALE-RESPONSE GUARD — `useCrewCostPreview` carries
 * `useLatestRequest` for that, and lowering this number widens exactly the
 * overlap window that guard exists to close. Do not treat 250 as safe because
 * it is short.
 *
 * ⚠️ Whether it FEELS right under a real pointer is the owner's browser
 * check; jsdom can drive the handlers but cannot observe timing.
 */
export const GANTT_COST_PREVIEW_DEBOUNCE_MS = 250;

/**
 * Phase 11 polish: throw an Error that preserves `data.code` for sites where
 * the response body has already been consumed via `await res.json()`. This is
 * the inline counterpart to apiThrow() — same effect (Error carries .code and
 * .status) without re-reading the body.
 *
 * Used at every BudgetCostCodes call-site that hits a lock-guarded endpoint
 * (crew_members*, crew_lines*, cost-code-budgets, versions/:id/lock|unlock)
 * so that a 423 `code: 'version_locked'` response surfaces the friendly toast
 * "This budget version is locked. Unlock or clone it to make changes." via
 * friendlyErrorMessage's structured-code branch instead of substring sniffing.
 */
function throwApiError(res, data, fallback) {
  const err = new Error(data?.error || fallback);
  if (data?.code) err.code = data.code;
  err.status = res.status;
  err.body = data;
  throw err;
}

/**
 * THE coercion for a NULLABLE RATE FIELD on its way from a form control into a
 * request body — the 6th/7th-day premium rates and their per-member overrides.
 *
 * ═════ 🚨 `null` AND `0` ARE DIFFERENT ANSWERS WITH DIFFERENT COSTS ═════
 *   ''  / null → `null` → UNSTATED. The server applies the 1.5×/2× default.
 *                It does NOT cost zero.
 *   '0' / 0    → `0`    → 🔻 **REFUSED AT SAVE since 2026-08-23** (owner:
 *                "there should be no free days"). A day someone worked is
 *                always worth something. See `premiumMoneyMessage` below.
 *
 * ⚠️ **THIS DOCBLOCK DESCRIBED THE PRE-DEFAULT WORLD UNTIL 2026-08-23 AND WAS
 * THE FOURTH SITE OF THE SAME EXPIRED CLAIM.** It said a blank rate left the
 * day "in the standard pool at `weekly_rate / 5`" — true until the resolve-time
 * default landed, and false the moment it did. The other three were fixed as
 * F3; this one survived because it sits on a COERCION helper rather than on
 * anything a buyer reads, so no screen contradicted it. Flagged by the agent
 * that fixed the others, deliberately left out of scope there, fixed here.
 *
 * 🔑 **THE FUNCTION ITSELF DID NOT CHANGE AND MUST NOT — the read side still
 * distinguishes the two.** A `0` already stored (planted before the rule, or by
 * an operator) still costs zero: the refusal is on the WRITE path only, so no
 * existing row moved a penny. `nullableRate` therefore still maps `'0' → 0`
 * faithfully; what changed is that the save no longer offers the value.
 * `server/helpers/crewCost.js#resolvePremiumRates` still returns `null` (not 0)
 * for unset, and the server tests still assert both directions against each
 * other.
 *
 * 🪤 SO EVERY TRUTHINESS FORM IS A MONEY BUG, AND EACH FAILS SILENTLY:
 * `parseFloat(x) || null`, `x || null`, `x ? parseFloat(x) : null` and
 * `if (!x)` all read a typed `0` as "nobody priced this", which silently PAYS
 * for a day the buyer said was free. This codebase already carries a LIVE
 * production defect of exactly that shape — `parseFloat(default_allocation)
 * || 1.0` reads an explicit "Off" as a full week — which is why this is ONE
 * named function rather than an expression repeated at four call sites.
 *
 * 🔑 `Number.isFinite` is the right predicate, never `!Number.isNaN`:
 * `Number.isNaN(Infinity)` is false, so the looser form would post `Infinity`
 * as a rate. A value that will not parse is treated as absent rather than
 * posted as NaN — the server refuses it either way (400), but a form should
 * not send a number it knows is not one.
 *
 * 🚨 **`Number.isFinite` WAS NECESSARY BUT NOT SUFFICIENT HERE, AND THIS IS THE
 * CORRECTION (2026-09-21).** It was being handed `parseFloat`'s output, and
 * `parseFloat` is PREFIX-LENIENT: `parseFloat('1,000')` is `1`, which is
 * finite, so this function answered `1` for a thousandfold under-price and the
 * guard read as correct the whole time. **`Number.isFinite(parseFloat(x))` and
 * `Number.isFinite(Number(x))` are DIFFERENT PREDICATES and only the second is
 * the house rule.** It now delegates to `utils/moneyPayload.js`, which is built
 * on `Number` — nothing about `0`, blank or `Infinity` changed.
 *
 * ⚠️ **AND WHAT THIS FUNCTION ALONE STILL CANNOT DO, stated rather than
 * implied.** These columns are NULLABLE, so `null` means UNSTATED and the
 * server applies the 1.5×/2× default — it is a legitimate value, not a
 * refusal. Mapping malformed to `null` therefore stops the wrong NUMBER being
 * stored but does NOT make the server's gate fire. **The refusal for these
 * fields is `crewMoneyFormError` below**, which runs ahead of every save; this
 * is the second line, not the gate.
 *
 * ⚠️ NOT for `weekly_rate` / `computer_rental`: those columns are NOT NULL
 * with a zero default, so they have no unpriced state and their `|| 0` is
 * correct. Do not "make them consistent".
 */
function nullableRate(value) {
  return toPayloadAmount(value, null);
}

/**
 * THE message a refused 6th/7th-day premium rate shows. One string, because it
 * is asserted in a test and shown at five save sites.
 *
 * 🔑 It has to name the remedy or it reads as "the field is broken". Blank is a
 * real, common answer meaning "use the default", so a user who typed 0 intending
 * "no uplift" is told that blank is what they wanted — and that a genuinely
 * reduced rate is a number ABOVE zero, which the amber triangle then flags for
 * confirmation rather than refusing.
 */
export const PREMIUM_RATE_ZERO_MESSAGE =
  'A 6th or 7th day rate cannot be £0. Leave it blank to use the default premium ' +
  '(1.5× / 2× the daily rate), or enter a rate above zero.';

/**
 * THE message a negative money entry shows. Owner, 2026-08-23: *"negative
 * rates not allowed."* Names the FIELD, because these forms carry up to eleven
 * money inputs and "a value is negative" leaves the user hunting.
 *
 * 🚨 IT APPLIES TO CAPS AS WELL AS RATES, and the cap half is the one that
 * would otherwise be missed: a negative cap is FINITE, so the server's
 * `cappedAmount` computes `Math.min(200, -5)` = **-5** and the allowance pays
 * the production. Flagged in the adversarial review and left open; closed here
 * and, independently, on the server.
 */
export const negativeMoneyMessage = (label) => `${label} cannot be negative.`;

/**
 * WHAT A MONEY FIELD WE CANNOT READ SAYS TO THE USER (2026-09-21).
 *
 * 🔑 IT NAMES THE COMMA CASE EXPLICITLY, because that is the input this refusal
 * exists for and it is the one a user will swear is valid — they typed a
 * thousand and they are right that it is a number. A bare "must be a number"
 * reads as "the field is broken" against text that looks like money.
 *
 * ⚖️ It NEVER fires on a blank field (blank is a real answer on every one of
 * these columns) and NEVER on a typed `0` (a real, ruled, legitimate value).
 * Only on text that `Number()` cannot read at all.
 */
export const malformedMoneyMessage = (label) =>
  `${label} must be a plain number — for example 1000, not 1,000.`;

/**
 * WHAT A BLANK 6th/7th-DAY RATE MEANS, IN WORDS — one string, every site.
 *
 * 🔻 IT REPLACES THE WORD "Unpriced", WHICH BECAME A LIE. Until the 1.5×/2×
 * resolve-time default landed (2026-08-23) a blank premium rate really did
 * leave the day in the standard pool at `weekly_rate / 5`, and "Unpriced" was
 * an accurate reading of `null`. Since the default, a blank field costs 1.5× or
 * 2× the DAILY rate — so the rate card was telling a buyer a day was unpriced
 * while the budget charged the most expensive figure on the screen.
 *
 * 🚨 THE IDENTICAL CLAIM WAS FOUND AND FIXED AT THE MEMBER OVERRIDE INPUT by
 * the defaults commit, which called it *"the interface asserting the opposite
 * of the money"* — and MISSED THE GRADE SCREEN, which is where the policy is
 * actually set and where every grade on production reads "Unpriced" today. This
 * is that fix, applied to the eight sites it missed, and this helper exists so
 * there is no ninth wording: the member input's own inline copy now calls it.
 *
 * 🔑 IT NAMES THE MULTIPLIER, NEVER A FIGURE. The £ amount depends on the
 * member's own effective weekly rate, which a GRADE row does not have and which
 * a client file may not derive in any case — "the server computes the money;
 * this directory reports it", and a display-only site is still a money claim.
 * The multipliers come from `PREMIUM_DEFAULT_MULTIPLIER_LABELS`, which
 * `regressions/premium-rate-defaults.test.js` §5 holds against the server
 * constants, so this sentence cannot describe an uplift the money stopped using.
 *
 * 🔀 STAGE 3 (owner 2026-10-28): the uplift is the PRODUCTION's — `rates` is the row's
 * server-published `agreement_rates` (`sixth_day_multiplier` / `seventh_day_multiplier`). Absent
 * (an older payload, a row not loaded yet) it falls back to the Pact mirror above, the one value
 * a production starts at.
 */
export const premiumMultiplierLabel = (position, rates) => {
  const key = position === 7 ? 'seventh_day_multiplier' : 'sixth_day_multiplier';
  return rates && Number.isFinite(rates[key]) ? `${rates[key]}×` : PREMIUM_DEFAULT_MULTIPLIER_LABELS[position];
};
export const premiumDefaultLabel = (position, rates) =>
  `${premiumMultiplierLabel(position, rates)} daily (default)`;

/**
 * The SAME claim, short — `1.5× (default)` — for the Crew Grades TABLE CELL
 * only (2026-10-24, S2 review: the long form wrapped the 6th/7th columns to
 * three lines). Every input placeholder keeps `premiumDefaultLabel`; this is
 * a display string for a column already headed "6th Day (£)".
 */
export const premiumDefaultShortLabel = (position, rates) =>
  `${premiumMultiplierLabel(position, rates)} (default)`;

/* ═══════════════════════════════════════════════════════════════════════════
   🔀 2026-10-25 OWNER RULINGS — THE £ FIGURE THE BOX COMES TO (display only)
   ═══════════════════════════════════════════════════════════════════════════
   *"the inputs are mostly 1x 1.5x ect NOT actual money values so we DO need to
   see the resulting vlaue"*, and for the 6th/7th boxes the figure the default
   comes to, *"in the box"*. 🚨 THIS REVERSES "IT NAMES THE MULTIPLIER, NEVER A
   FIGURE" above, BY RULING — for PLACEHOLDERS and LABELS only: no request body
   reads any of these, and the server still prices every day itself.
   🔑 ONE definition of each input: the multipliers below are DERIVED from
   `PREMIUM_DEFAULT_MULTIPLIER_LABELS` (which the server's
   `premium-rate-defaults.test.js` §5 holds against `SIXTH_DAY_DEFAULT_MULTIPLIER`
   / `SEVENTH_DAY_DEFAULT_MULTIPLIER`), the divisor is the owner's 5 (NEVER 7 —
   `crewCost.js#dailyRate`), every product goes through `utils/money#roundMoney`
   (never `toFixed`), and the HOURLY is always the SERVER's resolved figure
   (`overtime_rates`), never weekly ÷ 5 ÷ 11 here. Pinned by
   `BudgetCostCodes.boxFigures.test.js`. */
export const PREMIUM_DEFAULT_MULTIPLIERS = Object.freeze({
  6: parseFloat(PREMIUM_DEFAULT_MULTIPLIER_LABELS[6]),
  7: parseFloat(PREMIUM_DEFAULT_MULTIPLIER_LABELS[7]),
});
const PREMIUM_DAYS_PER_WEEK = 5; // the owner's divisor — 5, NEVER 7

const TYPED_BLANK = Symbol('blank');
/** A typed box, through the shared payload parser: `{ blank }`, `{ value }` or `{ garbage }`. */
export function readTypedNumber(raw) {
  const n = toPayloadNumber(raw, TYPED_BLANK);
  if (n === TYPED_BLANK) return { blank: true };
  if (n == null) return { garbage: true };
  return { value: n };
}

/**
 * The weekly rate a MEMBER's default 6th/7th uplift is taken from, live: the
 * typed weekly override if valid, else the grade's weekly rate (the server's
 * `resolveEffectiveRate` order); garbage → `null` (no figure).
 */
export function memberPremiumWeekly(form, member) {
  const typed = readTypedNumber(form ? form.weekly_rate_override : undefined);
  if (typed.garbage) return null;
  if (typed.value != null) return typed.value;
  return member ? member.grade_weekly_rate : null;
}

/**
 * The 6th/7th-day box's placeholder (and the full sentence for its `title`):
 * what applies if it is left BLANK. A member whose GRADE states a rate shows
 * that figure, "(grade)"; otherwise the default uplift on `weekly` (a typed
 * or resolved weekly rate) — `short` is the in-row cell's form. No usable
 * weekly → the multiplier-only label, never £NaN or £0.00.
 */
export function premiumPlaceholder(position, { weekly, gradeRate = null, short = false, formatMoney, rates = null }) {
  if (gradeRate != null) {
    const text = `${formatMoney(gradeRate)} (grade)`;
    return { placeholder: text, title: text };
  }
  const w = readTypedNumber(weekly);
  if (w.value == null || w.value <= 0) {
    return { placeholder: short ? premiumDefaultShortLabel(position, rates) : premiumDefaultLabel(position, rates), title: undefined };
  }
  // 🔀 Stage 3: the PRODUCTION's multiplier (`rates` = the row's `agreement_rates`), else Pact.
  const label = premiumMultiplierLabel(position, rates);
  const key = position === 7 ? 'seventh_day_multiplier' : 'sixth_day_multiplier';
  const multiplier = rates && Number.isFinite(rates[key]) ? rates[key] : PREMIUM_DEFAULT_MULTIPLIERS[position];
  const daily = roundMoney(w.value / PREMIUM_DAYS_PER_WEEK);
  const figure = roundMoney((w.value / PREMIUM_DAYS_PER_WEEK) * multiplier);
  return {
    // Measured at 1280: the ruled long form "1.5× daily = £750.00 (default)" CLIPPED in
    // the member panel's box, so the panel form leads with the figure; the full
    // sentence rides on the title.
    placeholder: short ? `${formatMoney(figure)} (${label})` : `${formatMoney(figure)} (default ${label})`,
    title: `Default: ${label} daily (${formatMoney(daily)}) = ${formatMoney(figure)}`,
  };
}

/**
 * THE pre-save money check for this page's crew forms — grades and members.
 * Returns a message to show, or `null` when everything is acceptable.
 *
 * ═══ 🔴 A PREMIUM RATE OF ZERO IS REFUSED — owner ruling 2026-08-23 ═══
 * "£0.00 should not be a option. 6th and 7th days are either blank = no cap or
 * must have a value" / "as you said there should be no free days." A day someone
 * worked is always worth something, so `0` is a data-entry mistake wearing the
 * clothes of a value. A rate BELOW the 1.5×/2× default is legitimate (reduced
 * hours) and passes — that is what `BelowDefaultPremiumWarning` is for.
 *
 * 🔄 THIS REVERSES BEHAVIOUR BUILT AND PINNED ON 2026-08-23, when a typed `0`
 * meant "priced at zero" and beat the default. `nullableRate` above still maps
 * `'0' → 0` faithfully; what changed is that the SAVE no longer offers it. The
 * server refuses independently (`helpers/crewCost.js`
 * #normalisePremiumRateForStorage) — this is the instant feedback, never the
 * gate, exactly like `utils/poNumber.js`'s client mirror.
 *
 * 🪤 THE `premium` FLAG IS PER FIELD AND MUST STAY THAT WAY. On the very same
 * forms, an ALLOWANCE CAP of `0` is entirely valid and means the allowance is
 * worth nothing (owner, same day: "only a 0 entered effectively makes the cap
 * 0"), and a zero WEEKLY rate override is a question the owner has not ruled on.
 * Same digit, three fields, three rules — a blanket "no zero money" check here
 * would be a money bug on two of them.
 *
 * 🪤 A value `Number()` cannot read is REFUSED here (`malformedMoneyMessage`,
 * since 2026-09-21) — it is no longer passed over to the server's 400.
 */
export function crewMoneyFormError(entries) {
  for (const entry of entries) {
    const { label, value, premium } = entry;
    if (value === '' || value == null) continue;
    // 🚨 THE NEGATIVE TEST STAYS ON `parseFloat`, AND DELIBERATELY SO — it runs
    // BEFORE the malformed test below and that ORDER IS THE CONTRACT. `'-5abc'`
    // has been reported as *negative* since 2026-08-23 (pinned by §1.2 of
    // `BudgetCostCodes.noNegativeMoney.test.js`), because `parseFloat` is the
    // only reader that can see the sign through a stray character: `Number`
    // answers `NaN` and a bare `'-5abc' < 0` is FALSE. Both orderings REFUSE
    // the save, so no money moves either way — but reordering them silently
    // rewords a pinned refusal, which is a contract change, not tidying.
    const signed = parseFloat(value);
    if (Number.isFinite(signed) && signed < 0) return negativeMoneyMessage(label);
    // 🚨 NOW REFUSE WHAT WE CANNOT READ AT ALL (2026-09-21). This used to be
    // `if (!Number.isFinite(parsed)) continue;` — it SKIPPED an unreadable
    // value, treating "I cannot parse this" as "no complaint". Worse, it never
    // reached the skip for the input that matters: `parseFloat('1,000')` is
    // `1`, which is finite, non-negative and non-zero, so a thousandfold
    // under-price passed every test here and was SENT.
    // 🔑 `Number.isFinite(parseFloat(x))` and `Number.isFinite(Number(x))` are
    // DIFFERENT PREDICATES, and only the second one is the house rule. This is
    // the first site in the repo where `Number.isFinite` was necessary but NOT
    // SUFFICIENT — not because the predicate is wrong, but because of what it
    // was being handed. A guard can look correct, read correctly in review,
    // have tests, and still be wrong about exactly one input class.
    if (isMalformedAmount(value)) return malformedMoneyMessage(label);
    // Non-null by construction: blank skipped, malformed returned.
    const parsed = toPayloadAmount(value, null);
    // 🔴 NEGATIVE — EVERY field on the list, rates AND caps (owner, 2026-08-23:
    // "negative rates not allowed"). Checked BEFORE the zero rule so a typed
    // `-0` … is actually `-0`, which `=== 0` is true for, so the order does not
    // decide that case; it is first because a negative is the coarser error and
    // reads better reported as such.
    // 🪤 The `parseFloat` above is load-bearing, not tidiness: a bare
    // `value < 0` coerces, and `Number('-5abc')` is NaN, so `'-5abc' < 0` is
    // FALSE — the bare form waves a negative through the moment a stray
    // character arrives, and a form control hands you strings.
    if (parsed < 0) return negativeMoneyMessage(label);
    // `parsed === 0`, never `!parsed` — blank was already skipped above, but a
    // falsy test here would also swallow any future non-numeric sentinel.
    if (premium && parsed === 0) return PREMIUM_RATE_ZERO_MESSAGE;
  }
  return null;
}

/**
 * THE money fields of a CREW GRADE form, enumerated from the columns
 * `POST`/`PUT /api/budget/crew-grades` write rather than from the inputs that
 * happen to be on screen — the enumerate-the-writers rule.
 * `crew_grades` money columns: weekly_rate, computer_rental, sixth_day_rate,
 * seventh_day_rate. All four are here; only the last two are `premium`.
 *
 * 🔻 `label` IS WHAT A USER READS IN THE ERROR, SO IT MUST NAME THE CONTROL,
 * NOT THE COLUMN. `computer_rental` said the literal 'Computer Rental' until
 * 2026-08-23 while both grade forms label that input `{COMPUTER_ALLOWANCE_LABEL}
 * (£/wk)` — so typing -5 into **Computer Allowance** was answered with *"Computer
 * Rental cannot be negative."* The constant was already imported two lines above
 * the entry list and unused: a literal re-inlined beside its own extraction. The
 * docblock's "enumerated from the columns rather than from the inputs that happen
 * to be on screen" is right about WHICH FIELDS to enumerate and does not license
 * naming them after columns in a sentence a buyer reads.
 */
export const gradeMoneyEntries = (form) => [
  { label: 'Weekly Rate', value: form.weekly_rate },
  { label: COMPUTER_ALLOWANCE_LABEL, value: form.computer_rental },
  { label: '6th Day Rate', value: form.sixth_day_rate, premium: true },
  { label: '7th Day Rate', value: form.seventh_day_rate, premium: true },
];

/**
 * THE money fields of a CREW MEMBER form, enumerated the same way from the
 * eleven money columns `POST`/`PUT /api/budget/crew-members` write:
 * computer_rental, computer_rental_cap, weekly_rate_override, the two premium
 * overrides, and the three allowance amount/cap pairs.
 * 🚨 Only the two PREMIUM overrides are `premium`. A cap of `0` is valid and
 * means the allowance is worth nothing; a zero weekly override is unruled.
 * ⚠️ The ADD-member form renders no allowance inputs today, so those keys are
 * simply `undefined` there and are skipped — listing them anyway is what keeps
 * ONE enumeration for both forms and stops the edit form growing a second.
 *
 * 🔻 THE COMPUTER SLOT'S TWO LABELS ARE DERIVED, NOT TYPED. They said 'Computer
 * Rental' / 'Computer Rental Cap' until 2026-08-23; both member forms label
 * those controls `{COMPUTER_ALLOWANCE_LABEL}` and `Allowance Cap`. Owner, three
 * times, the last in capitals: *"COMPUTER RENTAL IS JUST ONE TYPE OF ALLOWANCE
 * it should NOT be treated differently from other allowances."* Naming the
 * message after the column is precisely treating it differently.
 * ⚠️ `COMPUTER_ALLOWANCE_NAME` — still literally "Computer Rental" — is NOT
 * wrong and must not be swept away with this: it is the allowance's NAME, what
 * the locked name box shows where the other three slots show a typed name. What
 * was wrong is a control labelled *Allowance* whose error said *Rental*.
 * 🪤 THE CAP IS `${COMPUTER_ALLOWANCE_LABEL} Cap`, NOT THE BARE SCREEN LABEL,
 * AND THAT IS DELIBERATE. Four controls on the edit form read "Allowance Cap" —
 * the computer slot and the three named ones — so the bare label would identify
 * none of them, and this message exists precisely because these forms carry up
 * to eleven money inputs. The qualifier is the SIBLING CONTROL'S label (the
 * amount input beside it reads "Computer Allowance"), which is the same device
 * the three numbered slots already use.
 */
export const memberMoneyEntries = (form) => [
  { label: COMPUTER_ALLOWANCE_LABEL, value: form.computer_rental },
  { label: `${COMPUTER_ALLOWANCE_LABEL} Cap`, value: form.computer_rental_cap },
  { label: 'Rate Override', value: form.weekly_rate_override },
  { label: '6th Day Rate', value: form.sixth_day_rate_override, premium: true },
  { label: '7th Day Rate', value: form.seventh_day_rate_override, premium: true },
  { label: 'Allowance Amount', value: form.allowance_amount },
  { label: 'Allowance Cap', value: form.allowance_cap },
  { label: 'Allowance 2 Amount', value: form.allowance_amount_2 },
  { label: 'Allowance 2 Cap', value: form.allowance_cap_2 },
  { label: 'Allowance 3 Amount', value: form.allowance_amount_3 },
  { label: 'Allowance 3 Cap', value: form.allowance_cap_3 },
];

/* ═══════════════════════════════════════════════════════════════════════════
   OVERTIME RATES — timesheets S2 (owner rulings, 2026-10-24)
   ═══════════════════════════════════════════════════════════════════════════
   The rates timesheet overtime HOURS will be priced with (stage S3). Nothing on
   this page prices anything with them.

   🔑 THE SERVER RESOLVES, THIS PAGE REPORTS (crew-gantt contract item 6). Every
   crew grade / member row arrives with `overtime_rates` — per field
   `{ value, source, inherited_value, inherited_source }` from
   `server/helpers/crewCost.js#resolveOvertimeRates`. Nothing here divides a
   weekly rate by 5 or by 11: the derived hourly is the SERVER's figure, shown.

   🚨 A FOURTH ZERO RULE, NOT THE PREMIUM ONE: blank = UNPRICED (a real state,
   never shown as £0); `0` is ALLOWED on the four multipliers and the night
   penalty ("agreed unpaid"); negative is refused; an hourly override of `0` is
   refused by the server (`hourly_rate_zero_refused`, NOT YET RULED) and its
   message is shown. So these entries carry no `premium` flag.
   🪤 A BLANK READS "Not set", NEVER "Unpriced", on screen: that word is banned
   from the grades tab and the member panel (premiumRateInputs §5.5/§5.6)
   because a blank PREMIUM is priced at the default — the same word beside two
   different meanings on one form would mislead. The server's `source` value
   stays 'unpriced'.
   ─────────────────────────────────────────────────────────────────────────── */
export const OVERTIME_RATE_FIELDS = Object.freeze([
  { key: 'hourly_rate', label: 'Hourly rate (£)', short: 'Hourly', kind: 'money' },
  { key: 'camera_ot_multiplier', label: 'Camera O/T (×)', short: 'Camera O/T', kind: 'multiplier' },
  // "Pre/post", not "Pre-call / post-wrap" (S2 fix round F5a): the long label
  // wrapped to two lines and pushed its input off the shared baseline at 1280px.
  { key: 'non_camera_ot_multiplier', label: 'Pre/post O/T (×)', short: 'Pre/post', kind: 'multiplier' },
  { key: 'travel_multiplier', label: 'Travel (×)', short: 'Travel', kind: 'multiplier' },
  { key: 'meal_penalty_multiplier', label: 'Meal penalty (×)', short: 'Meal', kind: 'multiplier' },
  { key: 'night_penalty', label: 'Night penalty (£)', short: 'Night', kind: 'money' },
  // 🔀 THE OT CAP (owner, 2026-10-25, ruling A): a per-hour CEILING on Camera
  // O/T and Pre/post O/T ONLY (`OT_CAPPED_FIELDS`) — `server/helpers/crewCost.js
  // #cappedOvertimeHourlyRate`. Money; blank = NO CAP (grade) / follow the
  // grade (member, `ot_cap_override`). A 0 is REFUSED by the server
  // (`ot_cap_zero_refused`, unruled) and its message is shown — no client rule.
  { key: 'ot_cap', label: 'OT cap (£/hr)', short: 'OT cap', kind: 'money' },
]);

/** The two lines the OT cap bites (owner ruling A). Travel and meal: NEVER. */
export const OT_CAPPED_FIELDS = Object.freeze(['camera_ot_multiplier', 'non_camera_ot_multiplier']);

/** The form/payload key for a field: the grade's own name, or the member's `_override`. */
const overtimeKey = (field, mode) => (mode === 'member' ? `${field.key}_override` : field.key);

/**
 * 🪞 MIRROR of `server/helpers/crewCost.js#OVERTIME_MULTIPLIER_STARTING_VALUES`
 * (the S2 backfill / new-grade values) — the ONE client copy, held in step by
 * `BudgetCostCodes.boxFigures.test.js`, which reads the server file as TEXT.
 * 🔀 2026-10-25 OWNER RULING (*"if the value is removed it should default to
 * the default and not NOT SET"*; *"emplty box is default, a user typing in 0
 * or 1 or more is allowed"*): a CLEARED GRADE multiplier showed this as its
 * placeholder and SENT it on save.
 * 🔀 2026-10-28 (agreement Stage 2, "blank = follow the level above"): the
 * default IS the PRODUCTION's figure now, so a cleared grade multiplier SENDS
 * `null` and the server resolves it to the production (`source: 'production'`).
 * This mirror is only the DISPLAY fallback for a payload carrying neither
 * `fallback_source: 'production'` nor `agreement_rates` (an older server, a row
 * not loaded) — it is never sent. A typed `0` is still sent as `0`.
 */
export const OVERTIME_MULTIPLIER_DEFAULTS = Object.freeze({
  camera_ot_multiplier: 2,
  non_camera_ot_multiplier: 1.5,
  travel_multiplier: 1,
  meal_penalty_multiplier: 1,
});

/**
 * Hydrate the overtime keys of an edit form from a row. 🪤 `??`, never `||` — a
 * stored 0 is "agreed unpaid" and must not hydrate as blank. 🔑 A key the ROW
 * does not carry is left OUT of the form, so `overtimeRatePayload` omits it and
 * the server keeps the stored value — an older payload shape can never clear it.
 */
export function overtimeRateFormFrom(row, mode) {
  const out = {};
  for (const field of OVERTIME_RATE_FIELDS) {
    const key = overtimeKey(field, mode);
    if (row && key in row) out[key] = row[key] ?? '';
  }
  return out;
}

/**
 * The overtime keys of a POST/PUT body: blank → `null` (stays blank), money
 * through `toPayloadAmount`, a multiplier through `toPayloadNumber` (NOT money
 * — it must never round: a time-and-a-third is 1.3333). A key the form does not
 * hold is OMITTED, never sent as null. 🔀 2026-10-28: a cleared GRADE multiplier
 * sends `null` too — it FOLLOWS the production (was: sent the Pact default).
 */
export function overtimeRatePayload(form, mode) {
  const out = {};
  for (const field of OVERTIME_RATE_FIELDS) {
    const key = overtimeKey(field, mode);
    if (!form || form[key] === undefined) continue;
    out[key] = field.kind === 'money' ? toPayloadAmount(form[key], null) : toPayloadNumber(form[key], null);
  }
  return out;
}

/**
 * THE LEVEL ABOVE a multiplier box (agreement Stage 2) — what a BLANK box follows, and what the
 * yellow compares against. GRADE: the production's figure (the server's `fallback_value` when it
 * says `fallback_source: 'production'`, else the row's `agreement_rates`, else the Pact mirror);
 * MEMBER: the server's `inherited_value` (the grade's, or the production's through it).
 * → `{ value, source }`, `source` = 'production' | 'grade' | 'default'. Reported, never resolved here.
 */
export function overtimeLevelAbove(field, rate, mode, agreementRates) {
  if (mode === 'member') {
    return rate ? { value: rate.inherited_value, source: rate.inherited_source === 'production' ? 'production' : 'grade' } : { value: null, source: 'grade' };
  }
  if (rate && rate.fallback_source === 'production' && rate.fallback_value != null) return { value: rate.fallback_value, source: 'production' };
  if (agreementRates && Number.isFinite(agreementRates[field.key])) return { value: agreementRates[field.key], source: 'production' };
  return { value: OVERTIME_MULTIPLIER_DEFAULTS[field.key], source: 'default' };
}

/**
 * The production's hourly divisor for the derivation COPY ("weekly ÷ 5 ÷ N") — the row's
 * `agreement_rates.hourly_divisor`; 11 (Pact) only when the payload carries none. Copy, never
 * arithmetic: the derived hourly itself is always the SERVER's figure.
 */
export const hourlyDivisorOf = (agreementRates) => (
  agreementRates && Number.isFinite(agreementRates.hourly_divisor) ? agreementRates.hourly_divisor : 11
);

/**
 * THE YELLOW (owner 2026-10-28: "yellow = differs from the level above") for a MULTIPLIER box.
 * A box the form holds: its typed number ≠ the level above (the one comparison made here, on
 * unsaved input — `AgreementSummary`'s rule); a key the form does not hold: the server's `differs`.
 * Blank, garbage or an unresolved level above → never yellow.
 */
export function overtimeMultiplierDiffers(field, form, rate, mode, agreementRates) {
  if (field.kind !== 'multiplier') return false;
  const key = overtimeKey(field, mode);
  if (!form || form[key] === undefined) return !!(rate && rate.differs);
  const typed = readTypedNumber(form[key]);
  if (typed.value == null) return false;
  const above = overtimeLevelAbove(field, rate, mode, agreementRates).value;
  return above != null && typed.value !== above;
}

/** The overtime entries for `crewMoneyFormError` — negative / malformed only; zero is allowed. */
export const overtimeRateMoneyEntries = (form, mode) => OVERTIME_RATE_FIELDS.map((field) => ({
  label: field.label.replace(/ \((£|×|£\/hr)\)$/, ''),
  value: form ? form[overtimeKey(field, mode)] : undefined,
}));

/**
 * One RESOLVED rate as text. `null` → "not set" (never £0.00 — blank is a
 * different answer from zero). A derived hourly says so.
 */
export function formatOvertimeRate(field, rate, formatMoney) {
  // An absent OT cap is NO CAP — nothing is missing (the server's `'none'`).
  if (field.key === 'ot_cap' && (!rate || rate.value == null)) return 'No cap';
  if (!rate || rate.value == null) return 'not set';
  if (field.kind === 'multiplier') return `×${rate.value}`;
  const money = formatMoney(rate.value);
  return field.key === 'hourly_rate' ? `${money}/hr` : money;
}

/**
 * The "Not set" shade (S2 fix round F8). `text-gray-400` on white measured
 * 2.54:1 and fails; `text-gray-500` with the file's house pair
 * (`dark:text-gray-400`) replaces it.
 */
export const OVERTIME_NOT_SET_CLASS = 'italic text-gray-500 dark:text-gray-400';

/* 🔻 The grade table's one-line "Overtime" summary (`OvertimeRatesInline`,
   `OVERTIME_INLINE_ORDER_TITLE` and their two helpers) is DELETED 2026-10-25 —
   owner: *"not very useful… hard to work out what it means"*. The column is
   now Hourly (£) (`GradeHourlyCell` below); the multipliers are edited, and
   read, in the expanded details. Pre-deletion SHA: 1a053711. */

export const OVERTIME_HOURLY_FIELD = OVERTIME_RATE_FIELDS.find((f) => f.key === 'hourly_rate');

/** A details-panel field cell: the box pinned to the bottom, so boxes in one row share a top edge. */
export const PANEL_FIELD_CELL = 'flex flex-col justify-end min-w-0';
/** A details-panel field label: always ONE line (the full text rides on `title`). */
export const PANEL_FIELD_LABEL_ONE_LINE = 'whitespace-nowrap overflow-hidden text-ellipsis';
/** A member allowance £/week or cap label: one line — the text (`PANEL_FIELD_LABEL_ONE_LINE`) ellipses, the pen beside it never does (F2). */
export const MEMBER_ALLOWANCE_LABEL = `${FIELD_LABEL_CLASS} flex items-center gap-1 min-w-0`;

/**
 * The pen's sentence for an OVERRIDDEN hourly rate, or `null` when the
 * server's resolved source says it is not one (grade: `'grade'`; member:
 * `'member'`, or `'grade'` inherited). The "worked out from the weekly rate"
 * clause is the SERVER's derived figure (`fallback_value` when
 * `fallback_source === 'derived'`) and is OMITTED when the server sent none —
 * never weekly ÷ 5 ÷ 11 here.
 */
export function hourlyOverrideTitle(rate, formatMoney, mode = 'grade') {
  if (!rate || rate.value == null) return null;
  const overridden = mode === 'member' ? (rate.source === 'member' || rate.source === 'grade') : rate.source === 'grade';
  if (!overridden) return null;
  const where = mode === 'member' ? (rate.source === 'member' ? ' (set on this crew member)' : ' (set on the grade)') : '';
  const derived = rate.fallback_source === 'derived' && rate.fallback_value != null
    ? ` Worked out from the weekly rate it would be ${formatMoney(rate.fallback_value)}/hr — it won't change if the weekly rate changes.`
    : '';
  return `Hourly rate overridden${where}: ${formatMoney(rate.value)}/hr.${derived}`;
}

/**
 * THE grade table's Hourly (£) cell at rest: the server-resolved hourly in the
 * Weekly Rate cell's own text style (a derived figure is not muted), the pen
 * when it is the grade's override, "—" when unpriced.
 */
export function GradeHourlyCell({ rate, formatMoney, testId }) {
  if (!rate || rate.value == null) return <span data-testid={testId}>—</span>;
  const title = hourlyOverrideTitle(rate, formatMoney, 'grade');
  return (
    <span className="inline-flex items-center justify-end gap-1 max-w-full" data-testid={testId}>
      {title && <OverrideMarker title={title} testId={testId ? `${testId}-override` : undefined} />}
      <span className="truncate">{formatMoney(rate.value)}</span>
    </span>
  );
}

/**
 * THE LIVE £ FIGURE A MULTIPLIER BOX COMES TO, for its LABEL (owner ruling
 * 2026-10-25: *"Camera O/T ×2 = £90.90/hr"*, live as they type).
 *   multiplier = the box's typed value if valid; BLANK → grade: the default
 *                (it saves as the default), member: the SERVER's inherited
 *                value; a key the row does not carry → the resolved value;
 *                GARBAGE → `null` (the plain label, never £NaN).
 *   hourly     = the hourly box's typed override if valid; BLANK → the
 *                SERVER's rung below (grade: `fallback_value`, the derived
 *                hourly; member: `inherited_value`); never weekly ÷ 5 ÷ 11.
 * Returns `{ multiplier, perHour }` (`perHour` null when no hourly is priced —
 * the label then shows the multiplier alone, no £) or `null`.
 */
export function overtimeMultiplierFigure(field, form, resolved, mode, agreementRates) {
  const read = (f) => {
    const key = overtimeKey(f, mode);
    const rate = resolved ? resolved[f.key] : null;
    if (!form || form[key] === undefined) return rate ? rate.value : null;
    const typed = readTypedNumber(form[key]);
    if (typed.garbage) return undefined;
    // A typed £ figure is read AS IT WILL BE STORED (`toPayloadAmount` → 2dp
    // `roundMoney`), so the label prices what the server will (review F3).
    if (typed.value != null) return f.kind === 'money' ? roundMoney(typed.value) : typed.value;
    // 🔀 2026-10-28: a blank GRADE multiplier follows the PRODUCTION — the server's figure.
    if (mode !== 'member' && f.kind === 'multiplier') return overtimeLevelAbove(f, rate, mode, agreementRates).value;
    if (!rate) return null;
    return mode === 'member' ? rate.inherited_value : rate.fallback_value;
  };
  const multiplier = read(field);
  if (multiplier == null) return null;
  const hourly = read(OVERTIME_HOURLY_FIELD);
  if (hourly == null) return { multiplier, perHour: null, cap: null };
  // 🔑 THE OT CAP (ruling A): Camera + Pre/post O/T ONLY, and it can only LOWER
  // — the server's `Math.min(hourly × multiplier, cap)`, the PRODUCT compared
  // unrounded as the server compares it, its inputs at their STORED 2dp. The cap is read like
  // the hourly: typed if valid; BLANK → grade: no cap, member: the SERVER's
  // inherited (grade) cap; GARBAGE → no cap shown (Save refuses it anyway).
  // `cap` is set only when it BITES, so an uncapped label is byte-identical.
  let cap = null;
  if (OT_CAPPED_FIELDS.includes(field.key)) {
    const capKey = mode === 'member' ? 'ot_cap_override' : 'ot_cap';
    const capRate = resolved ? resolved.ot_cap : null;
    let capValue;
    if (!form || form[capKey] === undefined) capValue = capRate ? capRate.value : null;
    else {
      const typed = readTypedNumber(form[capKey]);
      if (typed.garbage) capValue = null;
      else if (typed.value != null) capValue = roundMoney(typed.value); // as STORED (F3)
      else capValue = mode === 'member' && capRate ? capRate.inherited_value : null;
    }
    // 🪤 `> 0`: a typed 0 is refused by the server and a negative is refused
    // too, so neither may render "capped at £0.00/hr" / "-£5.00/hr" (F3).
    if (capValue != null && Number.isFinite(capValue) && capValue > 0 && capValue < hourly * multiplier) cap = capValue;
  }
  return { multiplier, perHour: roundMoney(hourly * multiplier), cap };
}

/**
 * The multiplier box's label: "Camera O/T ×2 = £90.90/hr", else the plain
 * label. When the OT cap BITES, `capped` carries the figure that applies:
 * the row renders `×2 = ~~£90.90~~ £75.00 cap` (`OvertimeCapFigure`), and
 * `figure` is the same sentence as plain text for the label's `title`.
 */
export function overtimeMultiplierLabel(field, figure, formatMoney) {
  if (!figure) return { name: field.label, figure: null, capped: null };
  const name = field.label.replace(/ \(×\)$/, '');
  if (figure.cap != null && figure.perHour != null) {
    return {
      name,
      figure: `×${figure.multiplier} = ${formatMoney(figure.perHour)} capped at ${formatMoney(figure.cap)}/hr`,
      capped: { multiplier: figure.multiplier, raw: formatMoney(figure.perHour), cap: formatMoney(figure.cap) },
    };
  }
  const money = figure.perHour == null ? '' : ` = ${formatMoney(figure.perHour)}/hr`;
  return { name, figure: `×${figure.multiplier}${money}`, capped: null };
}

/**
 * 🎨 THE CAPPED FIGURE'S COLOURS (owner mock, 2026-10-25): the raw figure
 * struck through and MUTED, the capped figure + the word "cap" bold amber.
 * 🔑 The WORD "cap" is what keeps it from being colour-only; the strike is
 * `line-through` + a grey, NEVER `opacity-*` (spoken for twice on this page).
 * `text-amber-700 dark:text-amber-400` is the measured pair of
 * `components/budget/CrewDayGrid.js#UNPRICED_PREMIUM_CLASS` — RE-MEASURED on
 * THIS panel's own backgrounds (a contrast figure is a fact about a PAIR):
 * figures in the browser receipt of the allowances UI build.
 */
export const OT_CAP_RAW_CLASS = 'line-through text-gray-500 dark:text-gray-400';
export const OT_CAP_APPLIED_CLASS = 'font-semibold text-amber-700 dark:text-amber-400';

export function OvertimeCapFigure({ capped, testId }) {
  return (
    <span data-testid={testId}>
      {`×${capped.multiplier} = `}
      <s className={OT_CAP_RAW_CLASS} data-testid={testId ? `${testId}-raw` : undefined}>{capped.raw}</s>
      {' '}
      <span className={OT_CAP_APPLIED_CLASS} data-testid={testId ? `${testId}-cap` : undefined}>{`${capped.cap} cap`}</span>
    </span>
  );
}

/**
 * What applies if this box is left BLANK — the server's answer, the rung
 * BELOW the one being edited (see `OvertimeRateFields`). Shared by the panel's
 * inputs and the grade row's in-cell Hourly input.
 */
export function overtimeRatePlaceholder(field, resolved, mode, formatMoney, agreementRates) {
  const rate = resolved ? resolved[field.key] : null;
  // 🔀 2026-10-28: a cleared GRADE multiplier FOLLOWS THE PRODUCTION — say so, with its figure
  // ("(default)" only when the payload names no production figure at all).
  if (mode !== 'member' && field.kind === 'multiplier') {
    const above = overtimeLevelAbove(field, rate, mode, agreementRates);
    return `×${above.value} (${above.source})`;
  }
  // A blank MEMBER multiplier follows the grade, or the production through it.
  if (mode === 'member' && field.kind === 'multiplier' && rate && rate.inherited_value != null && rate.inherited_source === 'production') {
    return `×${rate.inherited_value} (production)`;
  }
  // The OT cap: a blank GRADE box is "No cap"; a blank MEMBER box follows the
  // grade — its cap, or "No cap (grade)". Never "Not set": no cap is an answer.
  if (field.key === 'ot_cap') {
    if (mode !== 'member') return 'No cap';
    const inherited = rate ? rate.inherited_value : null;
    return inherited == null ? 'No cap (grade)' : `${formatMoney(inherited)} (grade)`;
  }
  if (!rate) return field.key === 'hourly_rate' && mode !== 'member' ? `Weekly ÷ 5 ÷ ${hourlyDivisorOf(agreementRates)}` : 'Not set';
  const value = mode === 'member' ? rate.inherited_value : rate.fallback_value;
  const source = mode === 'member' ? rate.inherited_source : rate.fallback_source;
  if (value == null) return 'Not set';
  const text = field.kind === 'multiplier' ? `×${value}` : formatMoney(value);
  if (source === 'derived') return `${text} (derived)`;
  return `${text} (grade)`;
}

/**
 * The read-only summary of a row's resolved overtime rates as a list — the
 * phone card's block and the read-only grade panel (the desktop grade TABLE
 * shows only Hourly (£), `GradeHourlyCell`, since the one-line summary was
 * deleted 2026-10-25). Text only: `opacity-*`
 * is spoken for twice on this page.
 */
export function OvertimeRatesSummary({ rates, formatMoney, testId, agreementRates }) {
  if (!rates) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-gray-600 dark:text-gray-300" data-testid={testId}>
      {OVERTIME_RATE_FIELDS.map((field) => {
        const rate = rates[field.key];
        const unpriced = !rate || rate.value == null;
        return (
          <React.Fragment key={field.key}>
            <dt className="text-gray-500 dark:text-gray-400">{field.short}</dt>
            <dd
              className={unpriced ? OVERTIME_NOT_SET_CLASS : 'text-gray-800 dark:text-gray-200'}
              data-testid={testId ? `${testId}-${field.key}` : undefined}
            >
              {formatOvertimeRate(field, rate, formatMoney)}
              {rate && rate.source === 'derived' && (
                <span className="ml-1 text-gray-500 dark:text-gray-400">{`(weekly ÷ 5 ÷ ${hourlyDivisorOf(agreementRates)})`}</span>
              )}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}

/**
 * The six overtime inputs, for a GRADE (its own values) or a MEMBER (overrides).
 * The PLACEHOLDER is the server's answer to "what applies if this is blank" —
 * the rung BELOW the one being edited:
 *   member → `inherited_*` (the grade's value, else the derived hourly);
 *   grade  → `fallback_*` (the derived hourly, else nothing). 🔻 S2 fix round
 *            F4: grade mode read `inherited_*` too, which for a grade IS its
 *            own stored value — so a CLEARED box showed the old value it was
 *            about to lose. A grade never shows "(grade)".
 * Blank otherwise reads "Not set". `resolved` is the row's `overtime_rates`
 * (absent on a brand-new row, whose hourly hints at the derivation instead).
 * `omit` (2026-10-25): field keys the caller edits ELSEWHERE — the grade
 * row's own Hourly (£) cell — so the box is never repeated under it.
 * 🔀 `agreementRates` (2026-10-28, agreement Stage 2): the row's server-published
 * production figures — what a blank GRADE multiplier follows on a row the server
 * has not resolved yet, and the divisor in the derivation copy. A multiplier box
 * whose figure differs from the level above takes THE agreement yellow
 * (`AGREEMENT_CHANGED_INPUT_CLASS`, the Settings → Productions class, appended).
 */
export function OvertimeRateFields({ mode, form, onChange, resolved, formatMoney, inputClassName, testIdPrefix, omit, agreementRates }) {
  const placeholderFor = (field) => overtimeRatePlaceholder(field, resolved, mode, formatMoney, agreementRates);
  const fields = OVERTIME_RATE_FIELDS.filter((field) => !(omit && omit.includes(field.key)));
  // Tailwind needs LITERAL classes: six boxes (the grade row, Hourly is a
  // column) or seven (the OT cap joined the line, 2026-10-25) on one line.
  const lgCols = fields.length > 6 ? 'lg:grid-cols-7' : 'lg:grid-cols-6';
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 ${lgCols} gap-3`} data-testid={testIdPrefix}>
      {fields.map((field) => {
        const key = overtimeKey(field, mode);
        const testId = testIdPrefix ? `${testIdPrefix}-${field.key}` : undefined;
        // A multiplier's label carries the live £ it comes to (owner ruling
        // 2026-10-25), in its OWN span so the field name stays readable; the
        // money boxes carry theirs only in the placeholder. ONE line always
        // (nowrap + ellipsis, the full text on `title`), and the cell is a
        // bottom-pinned column so every box in the row shares one top edge
        // even if a label ever does wrap (owner: *"if some lables wrap the
        // input boxes dont align horizontaly"*).
        const label = field.kind === 'multiplier'
          ? overtimeMultiplierLabel(field, overtimeMultiplierFigure(field, form, resolved, mode, agreementRates), formatMoney)
          : { name: field.label, figure: null };
        const labelText = label.figure ? `${label.name} ${label.figure}` : label.name;
        // The hourly OVERRIDE pen (owner 2026-10-25) — the same component and
        // sentence as the grade table's Hourly cell; decided by the server's
        // resolved `source`, so a derived hourly never carries it.
        const penTitle = field.key === 'hourly_rate'
          ? hourlyOverrideTitle(resolved ? resolved.hourly_rate : null, formatMoney, mode)
          : null;
        return (
          <div key={field.key} className={PANEL_FIELD_CELL}>
            <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title={labelText}>
              {label.name}
              {/* 📏 A CAPPED figure takes its OWN line (measured 2026-10-25:
                  "Camera O/T ×2 = £90.90 £75.00 cap" needs 212px against a
                  147-173px box at 1280/1440, so one line ellipsed away the
                  very "£75.00 cap" it exists to show). The cell is
                  `PANEL_FIELD_CELL` (bottom-pinned), so the boxes stay level. */}
              {label.capped
                ? <span className="block"><OvertimeCapFigure capped={label.capped} testId={testId ? `${testId}-figure` : undefined} /></span>
                : label.figure && <span data-testid={testId ? `${testId}-figure` : undefined}>{` ${label.figure}`}</span>}
              {penTitle && <OverrideMarker title={penTitle} className="ml-1 align-middle" testId={testId ? `${testId}-override` : undefined} />}
            </label>
            {field.kind === 'money' ? (
              <CurrencyInput
                value={form[key] ?? ''}
                onChange={(v) => onChange(key, v)}
                placeholder={placeholderFor(field)}
                className={inputClassName}
                testId={testId}
              />
            ) : (
              // 🚨 `ui/NumberInput`, NEVER a bare `type="number"` (input audit HIGH 2, 2026-10-28): a bare
              // box handed `''` up for text the browser cannot read, `''` is "follow the level above",
              // and O/T pay silently reverted. It emits `MALFORMED_NUMBER` instead, which
              // `overtimeRateMoneyEntries` → `crewMoneyFormError` refuses BEFORE `overtimeRatePayload`
              // (which would map it to `null` = blank) — so keep that guard in front of every save.
              <NumberInput
                min="0"
                // Owner 2026-10-24: multipliers cap at ×10. A MIRROR of the server's
                // OVERTIME_MULTIPLIER_MAX for native feedback — convenience, never the gate.
                max="10"
                step="any"
                value={form[key] ?? ''}
                onChange={(v) => onChange(key, v)}
                placeholder={placeholderFor(field)}
                // 🔀 2026-10-28: yellow = differs from the level above (the production for a
                // grade, the grade — or the production through it — for a member).
                className={overtimeMultiplierDiffers(field, form, resolved ? resolved[field.key] : null, mode, agreementRates)
                  ? `${inputClassName} ${AGREEMENT_CHANGED_INPUT_CLASS}`
                  : inputClassName}
                data-changed={overtimeMultiplierDiffers(field, form, resolved ? resolved[field.key] : null, mode, agreementRates) ? 'true' : 'false'}
                testId={testId}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
/* ═══ END OVERTIME RATES (S2) ═══ */

/* ═══════════════════════════════════════════════════════════════════════════
   GRADE ALLOWANCES (owner, 2026-10-25: *"if any are filled out then they
   affect all crew of that grade. Computer is there by default but with 0
   value"*; RULED (A): members FOLLOW the grade unless overridden).
   ═══════════════════════════════════════════════════════════════════════════
   A grade carries the SAME four slots a member does, in the SAME column names.
   🔑 THE SLOT MAPPING — the UI's "Allowance 2 / 3 / 4" (Computer is the first)
   is the server's `allowances.allowance_1 / _2 / _3`, i.e. the columns
   `allowance_*`, `allowance_*_2`, `allowance_*_3` —
   `server/helpers/crewCost.js#ALLOWANCE_SLOTS`. One table, read by every
   allowance surface here, so the mapping has ONE definition.
   🔑 THE SERVER RESOLVES WHICH SLOT A MEMBER FOLLOWS (`allowances.<slot>
   .source`, `inherited_*`); this page REPORTS it and never decides it. */
export const ALLOWANCE_SLOTS = Object.freeze([
  Object.freeze({ key: 'computer', label: 'Computer', nameField: null, amountField: 'computer_rental', capField: 'computer_rental_cap' }),
  Object.freeze({ key: 'allowance_1', label: 'Allowance 2', nameField: 'allowance_name', amountField: 'allowance_amount', capField: 'allowance_cap' }),
  Object.freeze({ key: 'allowance_2', label: 'Allowance 3', nameField: 'allowance_name_2', amountField: 'allowance_amount_2', capField: 'allowance_cap_2' }),
  Object.freeze({ key: 'allowance_3', label: 'Allowance 4', nameField: 'allowance_name_3', amountField: 'allowance_amount_3', capField: 'allowance_cap_3' }),
]);

/** The grade's allowance keys the PANEL edits. `computer_rental` is NOT one —
 *  it was always in the grade body (blank → 0, *"Computer is there by default
 *  but with 0 value"*) and keeps its own line there, byte-identical. */
const GRADE_ALLOWANCE_FORM_KEYS = Object.freeze(ALLOWANCE_SLOTS.flatMap((s) => (
  [s.nameField, s.amountField, s.capField].filter((k) => k && k !== 'computer_rental')
)));

/**
 * Hydrate the grade's allowance keys. 🪤 `??`, never `||` — a stored 0 is a
 * real amount / a real cap of zero. 🔑 A key the ROW does not carry is left OUT
 * (the `overtimeRateFormFrom` rule), so `gradeAllowancePayload` omits it and the
 * server keeps the stored value — an older payload shape can never clear it.
 */
export function gradeAllowanceFormFrom(row) {
  const out = {};
  for (const key of GRADE_ALLOWANCE_FORM_KEYS) {
    if (row && key in row) out[key] = row[key] ?? '';
  }
  return out;
}

/**
 * The grade allowance keys of a PUT body. Names: trimmed text, blank → null.
 * Amounts and caps: `toPayloadAmount(v, null)` — blank → null (an EMPTY slot /
 * NO cap), a typed 0 → 0. A key the form does not hold is OMITTED.
 * 🚨 A MALFORMED cap would ALSO come out null here — i.e. "no cap", which on a
 * grade uncaps every follower (review F5). That is why the Save path runs
 * `gradeAllowanceMoneyEntries` through `crewMoneyFormError` FIRST and refuses.
 */
export function gradeAllowancePayload(form) {
  const out = {};
  for (const key of GRADE_ALLOWANCE_FORM_KEYS) {
    if (!form || form[key] === undefined) continue;
    if (key.startsWith('allowance_name')) {
      const t = String(form[key] ?? '').trim();
      out[key] = t || null;
    } else {
      out[key] = toPayloadAmount(form[key], null);
    }
  }
  return out;
}

/** The pre-save money entries of the grade's allowance slots (caps INCLUDED —
 *  a malformed cap must BLOCK, never be sent as blank). Zero is allowed. */
export const gradeAllowanceMoneyEntries = (form) => ALLOWANCE_SLOTS.flatMap((s) => [
  ...(s.amountField === 'computer_rental' ? [] : [{ label: `${s.label} £/week`, value: form ? form[s.amountField] : undefined }]),
  { label: `${s.label} cap`, value: form ? form[s.capField] : undefined },
]);

/** The grade table's column header (owner mock, 2026-10-25). */
export const GRADE_ALLOWANCES_TOTAL_LABEL = 'Total allowances (£/wk)';

export const GRADE_ALLOWANCES_HEADING =
  'Allowances — apply to every crew member on this grade unless overridden on the member';

/**
 * THE grade's allowance list (owner mock, approved 2026-10-25): under the
 * Overtime rates line, a divider and more space, the heading, then ONE set of
 * column labels (Name · £ / week · Cap (£)) over four rows — Computer (name
 * fixed, always present) then Allowance 2 / 3 / 4.
 * 📐 Name ~200px left-aligned; £/week and Cap are the SHARED money input width
 * (`constants/inputs.js#MONEY_INPUT_WIDTH`, 8.25rem — the two grid tracks
 * below ARE that width; Tailwind needs literals, so keep them in step),
 * right-aligned. `minmax(0, …)` lets a phone card shrink the tracks rather
 * than overflow.
 * 🎨 Every text class carries its `dark:` pair — the mocks went black-on-dark
 * twice (owner caught it); the name/label shades are the house
 * `FIELD_LABEL_CLASS` / input classes, measured in the browser receipt.
 */
export function GradeAllowanceFields({ form, onChange, fieldClassName, nameOptions, testIdPrefix }) {
  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);
  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-testid={testIdPrefix}>
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3" data-testid={tid('heading')}>{GRADE_ALLOWANCES_HEADING}</div>
      {/* 📱 Below `md` (the phone card) the NAME takes its own full-width row
          above its two money boxes — measured at 390: three tracks gave each
          box 97px and clipped "Allowance 4"; two give the money 146px. */}
      <div className="grid grid-cols-2 md:grid-cols-[minmax(0,12.5rem)_minmax(0,8.25rem)_minmax(0,8.25rem)] gap-x-3 gap-y-2 items-center">
        <div className={`${FIELD_LABEL_CLASS} col-span-2 md:col-span-1`}>Name</div>
        <div className={`${FIELD_LABEL_CLASS} text-right`}>£ / week</div>
        <div className={`${FIELD_LABEL_CLASS} text-right`}>Cap (£)</div>
        {ALLOWANCE_SLOTS.map((slot) => (
          <React.Fragment key={slot.key}>
            <div className="col-span-2 md:col-span-1 min-w-0">
            {slot.nameField ? (
              <AllowanceNameInput
                value={form[slot.nameField] ?? ''}
                onChange={(v) => onChange(slot.nameField, v)}
                options={nameOptions}
                placeholder={slot.label}
                className={fieldClassName}
                testId={tid(`${slot.key}-name`)}
                ariaLabel={`${slot.label} name`}
              />
            ) : (
              // Boxed like the other names (owner 2026-10-25: "should be in a
              // box like the other allowances"), but READ-ONLY — the name is
              // fixed and is never sent (the slot has no name column).
              <input
                type="text"
                readOnly
                value={slot.label}
                className={`${fieldClassName} cursor-default`}
                title="Built-in allowance — name is fixed"
                aria-label={`${slot.label} allowance name (fixed)`}
                data-testid={tid(`${slot.key}-name`)}
              />
            )}
            </div>
            <CurrencyInput
              value={form[slot.amountField] ?? ''}
              onChange={(v) => onChange(slot.amountField, v)}
              placeholder={slot.amountField === 'computer_rental' ? '£0.00' : 'None'}
              className={`${fieldClassName} text-right`}
              testId={tid(`${slot.key}-amount`)}
            />
            <CurrencyInput
              value={form[slot.capField] ?? ''}
              onChange={(v) => onChange(slot.capField, v)}
              placeholder="No cap"
              className={`${fieldClassName} text-right`}
              testId={tid(`${slot.key}-cap`)}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** The member panel's one-line explanation of the per-slot rule (owner Q3
 *  accepted: to change only the cap you override the whole slot). */
export const MEMBER_ALLOWANCE_FOLLOW_HELP =
  'Leave £/week blank to follow the grade — its name, amount and cap. Type an amount (even 0) to make the slot this member\'s own; the name and cap are then this member\'s too, so to change only the cap, set the amount as well.';

/**
 * THE MEMBER PANEL's hints for one allowance slot — placeholders, the two
 * labels and the pen — from the SERVER's resolved slot (`member.allowances`,
 * `crewCost.js#publishCrewAllowances`), never a re-derivation.
 *   FOLLOWING (the form's £/week box is blank — live, as they type): the boxes
 *     show the grade's slot as their placeholders — its amount ("£40.00", or
 *     "None"), its cap ("£500.00" / "No cap"), its name ("Box (grade)") — and
 *     the two LABELS say "(grade)": "Box (grade)" / "Cap (grade)".
 *     📏 WHY THE LABEL AND NOT THE BOX carries "(grade)" (measured in the
 *     real build, 2026-10-25): a member's £/week and cap boxes are 97px with
 *     87px of text room at 1440, and "£40.00 (grade)" needs 100px, "£500.00
 *     (grade)" 108px — the brief's "£40.00 (grade)" placeholder CLIPPED in
 *     every slot. The label has the room; the name box (wider) keeps it.
 *   OWN (an amount is typed, 0 included): the member's own blanks ("No cap"
 *     — a NULL own cap does NOT inherit) and the plain labels.
 * 🖊️ The pen (`OverrideMarker`) marks a slot the SAVED row overrides
 *   (`source === 'member'`), its title naming the grade's value.
 * A payload with no `allowances` (an older server) falls back to the plain
 *   placeholders and labels this form always had.
 */
const MEMBER_ALLOWANCE_NAME_EXAMPLES = Object.freeze({ allowance_1: 'e.g. Box', allowance_2: 'e.g. Phone', allowance_3: 'e.g. Travel' });
export function memberAllowanceHints(slot, allowances, form, formatMoney) {
  const entry = allowances ? allowances[slot.key] : null;
  const typed = form ? form[slot.amountField] : undefined;
  const following = typed == null || String(typed).trim() === '';
  const example = slot.nameField ? MEMBER_ALLOWANCE_NAME_EXAMPLES[slot.key] : null;
  const ownName = slot.nameField && form ? String(form[slot.nameField] || '').trim() : '';
  const inheritedName = entry && entry.inherited_name ? entry.inherited_name : '';
  const prefix = (ownName || (following ? inheritedName : '')).slice(0, 4);
  const ownAmountLabel = slot.nameField ? (prefix ? `${prefix} Allowance` : 'Allowance') : COMPUTER_ALLOWANCE_LABEL;
  const plain = { name: example, amount: '£/wk', cap: 'No cap', amountLabel: ownAmountLabel, capLabel: 'Allowance Cap' };
  if (!entry) return { ...plain, penTitle: null };
  const gradeAmount = entry.inherited_amount;
  const penTitle = entry.source === 'member'
    ? `Set on this crew member: ${formatMoney(entry.amount ?? 0)}/wk. The grade's: ${gradeAmount == null ? 'none' : `${formatMoney(gradeAmount)}/wk`}.`
    : null;
  if (!following) return { ...plain, penTitle };
  return {
    name: slot.nameField ? (inheritedName ? `${inheritedName} (grade)` : example) : null,
    amount: gradeAmount == null ? 'None' : formatMoney(gradeAmount),
    cap: entry.inherited_cap == null ? 'No cap' : formatMoney(entry.inherited_cap),
    amountLabel: slot.nameField ? `${prefix || 'Allowance'} (grade)` : 'Computer (grade)',
    capLabel: 'Cap (grade)',
    penTitle,
  };
}

/**
 * The read-only grade's allowances — the SERVER's resolved slots
 * (`allowances`), for a viewer who cannot edit. Empty named slots are omitted;
 * Computer always shows (it is always there, £0 by default).
 */
export function GradeAllowancesSummary({ allowances, formatMoney, testId }) {
  if (!allowances) return null;
  const rows = ALLOWANCE_SLOTS.map((slot) => ({ slot, a: allowances[slot.key] || {} }))
    .filter(({ slot, a }) => slot.key === 'computer' || a.amount != null || a.name != null);
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-gray-600 dark:text-gray-300" data-testid={testId}>
      {rows.map(({ slot, a }) => (
        <React.Fragment key={slot.key}>
          <dt className="text-gray-500 dark:text-gray-400">{slot.nameField ? (a.name || slot.label) : slot.label}</dt>
          <dd className="text-gray-800 dark:text-gray-200" data-testid={testId ? `${testId}-${slot.key}` : undefined}>
            {`${formatMoney(a.amount ?? 0)}/wk`}
            {a.cap != null && <span className="ml-1 text-gray-500 dark:text-gray-400">{`(cap ${formatMoney(a.cap)})`}</span>}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
/* ═══ END GRADE ALLOWANCES ═══ */

/* ────────────────────────────────────────────────
   CurrencyInput: shows formatted £ on blur, raw number on focus.

   🔻 DEFINED HERE UNTIL 2026-09-20 (job-list C10). It was the SECOND of two
   copies — `pages/MarketplaceCreateListing.js` had its own — so it moved to
   `components/ui/CurrencyInput.js`, the same move `ui/SearchInput.js` made out
   of `components/gags/GagSearchInput.js`. **This page's ~30 money inputs render
   BYTE-IDENTICALLY**: every prop the shared component added (`id`, `name`,
   `prefix`) is opt-in and defaults to this file's old behaviour, which is
   asserted rather than assumed in `components/ui/CurrencyInput.test.js`.
   🪤 The `£`-glyph affordance is `prefix`, it is OFF here, and turning it on
   for this page would put a second symbol beside a value that already carries
   one — read THE PREFIX CONTRACT in the component before touching it.
   ──────────────────────────────────────────────── */

/* ────────────────────────────────────────────────
   Manual spend adjustment (2026-08-15, owner-designed money feature)

   "Its not an absolut overide its an adjustment thats added to the overall
   cost to date" — a SIGNED DELTA per (cost_code, selected version), added to
   the computed spend wherever this page shows Spent to Date. The server
   (`PATCH /api/budget/cost-code-budgets/spend-adjustment`) refuses
   independently (canManageBudget in the version's department, 423 locked);
   everything here is display + convenience, never a gate.

   Exported for the leaf-component test harness (the file's own precedent:
   GradeAccessRightsPicker / canManageBudgetVersions — the page itself is
   deliberately never mounted in this suite).
   ──────────────────────────────────────────────── */

// The ruled warning, verbatim basis from the owner: "We need a popup to say
// that this manulay entered figure is not assigned to any purchases and is
// not reconcilable... continue?"
export const SPEND_ADJUSTMENT_WARNING =
  'This manually entered figure is not assigned to any purchases and is not ' +
  'reconcilable against them. It will be added to the computed spend to date ' +
  'for this cost code. Continue?';

/**
 * The Spent to Date cell content: adjusted figure + the shared amber marker
 * (tooltip "Computed £X + manual adjustment £Y" — no who/date, ruled) + a
 * small note button that OPENS the one-line reason (never shown inline —
 * ruled). Rendered for every viewer; the adjustment is data, not a secret.
 */
export function SpendToDateCell({ computed, adjustment, note, formatMoney }) {
  const adj = Number(adjustment) || 0;
  const total = (Number(computed) || 0) + adj;
  return (
    <span className="inline-flex items-center justify-end gap-0.5">
      <span>{formatMoney(total)}</span>
      <SpendAdjustmentMarker computed={computed} adjustment={adj} />
      {adj !== 0 && note ? (
        /* 🔀 2026-10-24: this cell sits in an expand-to-edit ROW, and React
           bubbles a click through the portalled panel to that row too — so
           the note trigger and its panel stop propagation here, or opening
           the note would also toggle the row. */
        <span className="inline-flex" onClick={(e) => e.stopPropagation()} data-testid="spend-adjustment-note-stop">
        <Dropdown
          portal
          align="right"
          haspopup="dialog"
          ariaLabel="Adjustment note"
          title="View adjustment note"
          triggerTestId="spend-adjustment-note-button"
          // Ghost icon trigger. Hover steps by OPACITY, not colour — the
          // dark `!important` override table makes dark hover colours on
          // gray-400 inert (the SortableHeader lesson).
          triggerClassName="inline-flex items-center text-gray-400 opacity-75 hover:opacity-100 focus-visible:opacity-100 p-0.5"
          panelClassName="w-64 p-3"
          label={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          }
        >
          <div
            className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words"
            data-testid="spend-adjustment-note-text"
          >
            {note}
          </div>
        </Dropdown>
        </span>
      ) : null}
    </span>
  );
}

/**
 * The Adjustment column AT REST: read-only text, NO input (owner, staging
 * 2026-08-15: "Adjusetment is wokring but its always editable. it should
 * require the edit button to be clicked"). 0/absent renders the page's
 * em-dash absent-value treatment. Editing happens in the row's expanded
 * panel (`renderCostCodePanel`; it was the Edit button's edit branch until
 * 2026-10-24) — the one input, bound to editCostCodeForm.spend_adjustment.
 *
 * ⚠️ CONTRACT CHANGE: this replaced `SpendAdjustmentEditor`, the
 * always-active self-committing input (commit-on-blur → own ConfirmDialog →
 * PATCH) that shipped 2026-08-14 and drew the owner's correction. The ruled
 * warning dialog did NOT go away — it moved to the ROW SAVE path (see
 * handleSaveEditCostCode), firing BEFORE the PATCH exactly as before.
 */
export function AdjustmentValue({ amount, formatMoney }) {
  const adj = Number(amount) || 0;
  if (adj === 0) {
    return <span className="text-gray-400 dark:text-gray-500" data-testid="adjustment-rest-empty">—</span>;
  }
  return <span data-testid="adjustment-rest-value">{formatMoney(adj)}</span>;
}

/**
 * Is this allowance slot CONFIGURED? — the existence test the cost summary
 * uses to decide whether to render a row. **Named OR funded.**
 *
 * ═══ 🚨 MEASURED AGAINST REAL DATA, AND THE FIRST VERSION WAS WRONG ══════
 * This was `name is set || amount != null`, on the reasoning that a money
 * field must never double as its own presence marker. That reasoning is
 * right; the implementation was useless, because **the amount columns are
 * never NULL.** Counted on the development database, 2026-08-23:
 *
 *     allowance_amount_2   0 NULL   145 zero   150 rows
 *     computer_rental      0 NULL   142 zero   150 rows
 *
 * An unconfigured slot stores `0`, exactly like a deliberate £0 — so
 * `amount != null` is TRUE for every member and all three allowance rows
 * rendered for everybody. Caught in the browser: a real member showed
 * "Box £294.00" beside two rows reading "Allowance £0.00".
 *
 * 🔑 THE NAME IS THE ONLY THING THAT CARRIES INTENT, so it is the primary
 * marker — but it cannot be the only one, because the amount input accepts a
 * figure with the name left blank, and keying on the name alone would hide an
 * allowance that is actually costing money. Hence NAMED **OR** FUNDED:
 *
 *     named + £0        shows   <- the £0-allowance case that must not vanish
 *     unnamed + £250    shows   <- real money, no label
 *     unnamed + £0      hidden  <- nothing to say, and indistinguishable
 *                                  from a slot nobody touched
 *
 * ⚠️ THE LAST ROW IS A COLLAPSED STATE, NOT A RESOLVED ONE. "Deliberately
 * zero" and "never configured" have the same representation here, which is
 * the same shape as ledger S2-F ("deliberately no days" and "no data yet" are
 * both simply no rows). Nothing is lost by hiding it — an unnamed £0 allowance
 * has no content — but do not read this function as evidence that the two
 * states can be told apart. They cannot, and fixing that is a schema change.
 *
 * 🪤 `!== 0` ON A `Number`, NOT TRUTHINESS. `amount` arrives as a pg NUMERIC
 * string on some paths, and `'0.00'` is truthy while `Number('0.00')` is 0.
 */
export function hasAllowanceSlot(name, amount) {
  if (typeof name === 'string' && name.trim() !== '') return true;
  if (amount == null || amount === '') return false;
  const n = Number(amount);
  return Number.isFinite(n) && n !== 0;
}

/**
 * WHICH RUNG stated a below-default premium rate — the member's own rate
 * override, or the grade (owner ruling, 2026-08-24: the triangle must name
 * whichever rung set the figure). READ, NEVER DERIVED: `*_day_rate_is_override`
 * is published by the server (`helpers/crewMemberCostFields.js`) from the same
 * resolution that computed `*_below_default`; comparing an override against a
 * grade rate here would be a second implementation of a resolution that
 * already exists. `=== true` so an absent field (stale payload) falls back to
 * the grade wording, never to a guess. ONE definition for BOTH render sites —
 * the day grid's chips and the cost summary's 6th/7th figures.
 */
export function premiumOverridePositionsFor(m) {
  const s = new Set();
  if (m.sixth_day_rate_is_override === true) s.add(6);
  if (m.seventh_day_rate_is_override === true) s.add(7);
  return s;
}

/**
 * CREW MEMBER COST SUMMARY — the strip under the notes in the level-2 details
 * panel. Owner: *"add sub totals and totals and info under the notes for
 * wages, all box, etc as we have free space after the last allowance as the
 * list doesn't fill the whole space."*
 *
 * ═══ 🔀 ONE HORIZONTAL LINE OF LABELLED FIGURES, 2026-08-23 ═══════════════
 * Owner, on the stacked version: *"all of this 'Cost summary / Worked days:
 * 72 | Rate: £1,800.00 / Labour £43,400.00 / 4 × 6th day · 3 × 7th day
 * £20,000.00 / Member total £43,400.00' should be in a single line next to
 * 'Allowance Name / e.g. Travel / Allowance / £0.00 / Allowance Cap' and
 * under the notes section. maybe 'allowances total £xxx' 'normal days xx
 * £xxx' '6th days xx £xxx' '7th days xx £xxx' 'total ££' something like
 * that"*, and then: *"notes can be made one row shorter to give two rows for
 * the calculations figures if required."*
 *
 * 🚨 NOTHING NUMERIC MAY WRAP OR TRUNCATE. Every figure is one
 * `whitespace-nowrap` unit carrying its own label, count and money, so the
 * flex row can only break BETWEEN figures — a count separated from its cost
 * across a line break invites reading the wrong pair, and a clipped money
 * figure is a wrong money figure.
 *
 * ═══ 📏 MEASURED IN CHROMIUM, 2026-08-23 — NOT A TEST CLAIM ══════════════
 * jsdom lays nothing out, so the tests can only assert the CLASS that makes
 * the property true. These are real numbers, taken against the compiled
 * production build served by the app's own Express, over a COPY of the dev
 * database (150 crew members), in both themes.
 *
 *   ONE LINE                            two rows
 *   ─────────────────────────────────   ─────────────────────────────────
 *   the ordinary member: 5 figures,     the FULLEST member: 8 figures
 *   829px of a 1025px content box       (3 named allowances + a laptop +
 *   at 1440 with the sidebar OPEN.      an unpriced 6th AND 7th day) needs
 *   Panel 37px tall.                    1302px. It fits on one line at
 *                                       1697px (1920, sidebar collapsed),
 *                                       and takes TWO rows at 1025/1217.
 *                                       Panel 61px tall.
 *
 * 🔑 SO THE SECOND ROW IS THE DEGRADATION, NOT THE DESIGN. Owner, granting
 * it: *"notes can be made one row shorter to give two rows for the
 * calculations figures IF REQUIRED"* — and the wrap costs 24px, once, on the
 * minority of members who carry every component at once.
 *
 * 🪤 THE NOTES FIELD WAS NOT SHRUNK, AND SHRINKING IT WOULD HAVE BOUGHT
 * NOTHING — MEASURED, NOT ASSUMED. The textarea is `md:h-auto … flex-1
 * min-h-[5rem]` inside a `lg:row-span-4` grid cell, so the allowance stack
 * beside it sets its height: it renders **213px at `rows={1}`, `rows={2}`
 * AND `rows={6}`** (measured by setting the attribute live). The `rows`
 * attribute is inert here. And this strip sits BELOW that grid at full
 * width, so a shorter notes box frees VERTICAL space the strip does not
 * need and no HORIZONTAL space at all — the only axis that decides the wrap.
 *
 * 📱 Below `md` it degrades to ONE FIGURE PER LINE (8 lines at 375px, panel
 * 197px) with no horizontal page scroll. Probed at 375 with £1,234,567.89
 * forced into every slot, the widest nowrap unit is 232px against a 254px
 * content box — so a seven-figure member still cannot make a figure escape
 * its panel. That probe is the one worth re-running if a label is ever
 * lengthened.
 *
 * 🔀 WHAT CHANGED, AND WHAT SURVIVED. The breakdown went from
 * labour / premium / total to allowances · computer · normal · 6th · 7th ·
 * total. KEPT: the basis + rate + `(override)` header chip (the owner listed
 * a replacement shape, not a demand that every fact be deleted, and the rate
 * is the one figure on this panel derivable from nothing else on it); the
 * measured allowance-presence rule; the `—`-for-absent rule. DROPPED from
 * the visible line: the per-allowance rows and the combined premium row —
 * the per-allowance costs survive as the `title`/`sr-only` breakdown on the
 * Allowances figure, and the premium is now shown as its two halves, which
 * is what the owner asked for.
 *
 * ═══ 🚨 EVERY FIGURE HERE IS A PUBLISHED FIELD, PRINTED ═══════════════════
 * Contract item 6: the server computes the money, this directory reports it.
 * There is no arithmetic in this component at all — no addition, no division,
 * no multiplication, not even for the total.
 *
 * 🪤 THIS IS A DISPLAY-ONLY SITE AND THAT IS EXACTLY WHY IT IS DANGEROUS. The
 * recorded defect (2026-08-21) is a sweep that unified the three sites which
 * MULTIPLIED a rate and skipped the one that only PRINTED it, leaving a
 * tooltip showing three server-honest numbers beside one wrong one. A number
 * that is merely rendered is still a money claim.
 *
 * 🔴 `budget_cost / total_weeks` IS NOT A RATE and must never appear here.
 * `budget_cost` is day-derived and `total_weeks` week-derived, and on some
 * surfaces `budget_cost` also carries rental — `effective_weekly_rate` is
 * published precisely so that nothing divides.
 *
 * 🔑 `member_total` IS PUBLISHED, NOT SUMMED HERE. It was added to
 * `GET /api/budget/crew-members` for this block. Summing the rows on screen
 * would be correct today and would drift the first time a fifth component
 * appears, which is the same shape as the defect above.
 *
 * ⚠️ jsdom cannot see any of this render; what the tests assert is that the
 * figures equal the PUBLISHED fields and not any client-side re-derivation of
 * them (`CrewMemberCostSummary.test.js` uses a fixture where the two differ).
 */
export function CrewMemberCostSummary({
  member: m, formatMoney, showRate = true, provisional = false,
}) {
  if (!m) return null;

  /* 🪤 `v == null` FIRST, AND IT IS NOT BELT-AND-BRACES — `Number(null)` is
     `0` and `Number(undefined)` is `NaN`, so a `Number.isFinite` test ALONE
     prints "£0.00" for a field the server did not send. That is the falsy
     hazard wearing its other face: here the danger is not that a real 0 is
     read as absent, but that an ABSENT value is read as a real 0 — which on a
     money surface is a claim that something costs nothing. Caught by §9. */
  const money = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    return Number.isFinite(n) ? formatMoney(n) : '—';
  };

  /* 🔀 GRADE ALLOWANCES (2026-10-25): the name and amount are the SERVER's
     RESOLVED slot (`m.allowances.<slot>`), NEVER the raw member column — a
     member FOLLOWING the grade stores NULL there, so the raw read would hide
     the grade's "Box £40" this member is costed for. The cost is the
     published `allowance_cost*` as before. A payload with no `allowances` (an
     older server) shows a slot on its COST alone, unnamed. */
  const resolvedSlot = (key) => (m.allowances && m.allowances[key]) || {};
  const allowances = [
    { name: resolvedSlot('allowance_1').name, amount: resolvedSlot('allowance_1').amount, cost: m.allowance_cost },
    { name: resolvedSlot('allowance_2').name, amount: resolvedSlot('allowance_2').amount, cost: m.allowance_cost_2 },
    { name: resolvedSlot('allowance_3').name, amount: resolvedSlot('allowance_3').amount, cost: m.allowance_cost_3 },
  ].filter((a) => hasAllowanceSlot(a.name, a.amount) || hasAllowanceSlot(null, a.cost));

  // 🪤 `Number.isFinite`, never `||` — a genuinely ZERO weekly rate is falsy
  // and `||` would fall through and print money for a deliberately unpaid
  // member. The standing hazard: a falsy value that is also a legitimate
  // answer can never double as the absence marker.
  const rateText = money(m.effective_weekly_rate);

  /* 🔑 THE BASIS IS PUBLISHED, SO SAY WHICH ONE — AND IT IS THREE VALUES, NOT
     TWO (2026-08-27, the fractions-to-days migration's step 3). `labour_basis`
     is `'days'` when the member holds real `crew_member_days` rows, `'weeks'`
     when they fell back to the week fraction (`total_weeks`), and now
     `'presumed'` for a member costed day-by-day from a DERIVED presumption —
     dates only, no stored rows, no fraction. A panel that printed "Weeks" for
     either day-costed shape would assert a week-fraction basis for someone
     costed day-by-day, which is exactly the N3 shape (three numbers on one
     line contradicting each other's arithmetic).

     🔑 'presumed' reads as 'days', NOT as 'weeks' — the coordinator's steer,
     and the right call: the cost genuinely derives from a day set, just an
     UNSTORED one. Folding it into the 'weeks' branch would be the bug this
     item exists to fix, wearing different clothes. Folding it into the
     UNLABELLED 'days' branch would erase the one thing worth saying about
     it — that the day set is a live derivation, not something a human (or a
     backfill) actually recorded — so the label says which. No new visual
     channel: same text-only device that already tells 'days' from 'weeks'
     apart, plus a `title` on the figure (an existing channel on every
     `Figure` here) rather than colour, opacity or a glyph. */
  const onDayBasis = m.labour_basis === 'days' || m.labour_basis === 'presumed';
  const isPresumedBasis = m.labour_basis === 'presumed';
  const basisValue = onDayBasis ? m.worked_days : m.total_weeks;
  const basisLabel = isPresumedBasis ? 'Presumed days' : onDayBasis ? 'Worked days' : 'Weeks';
  const basisTitle = isPresumedBasis
    ? 'Derived from this member\'s dates - nobody has recorded which days were '
      + 'worked, so this figure is computed fresh each time rather than read from stored rows.'
    : undefined;

  // The premium POSITION counts. Visibility of the two premium figures is
  // gated on these being > 0 — the reasoning is at the figures themselves.
  const sixth = Number(m.sixth_day_count) || 0;
  const seventh = Number(m.seventh_day_count) || 0;

  /* 🪤 NEVER truthiness and never a bare `!== 0`: `Number(undefined)` is
     `NaN`, and `NaN !== 0` is TRUE — so the obvious presence test SHOWS a
     figure the server never sent. A pg NUMERIC arrives as the STRING '0.00',
     which is truthy, so truthiness fails in the other direction. */
  const nonZero = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n !== 0;
  };

  /* 🪤 COMPUTER RENTAL HAS NO NAME FIELD, so its amount is the only presence
     marker it has — and that column is never NULL either (measured: 0 NULLs
     in 150 rows, 142 of them zero). `0` is the only available encoding of "no
     laptop", and showing "Computer rental £0.00" for 95% of the crew would
     bury the one figure that matters.

     🔑 IT IS SHOWN WHENEVER EITHER THE RATE OR THE COST IS NON-ZERO, and the
     second half is what keeps the LINE ADDING UP: these figures are supposed
     to sum to `member_total`, and `member_total` carries `computer_cost`
     whatever the rate column says. A cost of zero contributes nothing, so
     hiding it costs the reader nothing either. */
  // The RESOLVED Computer amount (a follower's raw column is NULL), 2026-10-25.
  const showComputer = nonZero(resolvedSlot('computer').amount) || nonZero(m.computer_cost);

  /* ═══ R2 — THE BREAKDOWN NOW NAMES THE LAPTOP TOO (2026-08-23) ═══════════
     The visible figure absorbed computer rental, so the detail must too, or
     the headline stops being explained by the thing that explains it. The
     laptop is listed FIRST because that is the order the edit form presents
     it in (Computer Rental is drawn as the first allowance row, name locked)
     — the grouping the owner is reading from.

     🪤 IT IS INCLUDED ON THE SAME MEASURED PRESENCE RULE THE VISIBLE FIGURE
     USED, NOT UNCONDITIONALLY. `computer_rental` is never NULL (measured: 0
     NULLs in 150 rows, 142 of them zero), so `0` is the only encoding of "no
     laptop" and listing "Computer rental £0.00" for 95% of the crew would
     bury the entries that matter. `showComputer` is that rule and is reused
     rather than re-expressed.

     `hasAllowanceSlot` is the MEASURED presence rule for the three named
     ones and is doing real work: keying on the amount alone showed three
     empty rows for every member. */
  const allowanceDetailParts = [
    ...(showComputer ? [`Computer rental ${money(m.computer_cost)}`] : []),
    ...allowances.map((a) => `${(typeof a.name === 'string' && a.name.trim()) || 'Allowance'} ${money(a.cost)}`),
  ];
  const allowanceDetail = allowanceDetailParts.length ? allowanceDetailParts.join(' · ') : null;
  const allowanceTitle = allowanceDetail
    ? `Allowances, including computer rental. ${allowanceDetail}`
    : 'Allowances, including computer rental. None configured.';

  /* 🪤 UNPRICED IS NOT UNPAID, AND THIS IS THE WHOLE REASON THE COUNT AND THE
     CHARGE COUNT ARE TWO PUBLISHED FIELDS. `sixth_days_charged` is lower than
     `sixth_day_count` exactly when the RESOLVED rate is NULL, in which case
     those days stayed in the standard pool and were paid the STANDARD rate —
     they are inside `standard_labour`, not free. So "6th days 4 £0.00" is a
     real and meaningful state and the triangle is what says which of the two
     zero stories it is. `unpricedPremiumTitle` already carries that sentence
     and is reused rather than re-worded (`UnpricedPremiumWarning` renders it
     as a `title` AND an `sr-only` span from ONE string).

     🔻 READ "RESOLVED", NOT "STATED" — the two stopped being the same thing on
     2026-08-23. Since the 1.5×/2× default, an unstated rate normally RESOLVES
     to the uplift and IS charged, so this gap no longer opens merely because
     nobody typed a figure. It now needs `resolvePremiumRates` to return null,
     and it returns null only when nobody stated a rate AND the member has no
     usable weekly rate to take a default from — `sixthIsDefault` is
     `sixthStated == null && sixthDefaultRate != null` precisely so that a
     member with no rate is reported as genuinely unpriced rather than
     defaulted. So this state is now RARER and means something narrower; it is
     not dead, and the triangle still exists for it.

     🪤 A native `title` on a CHILD wins over its parent's, so a figure that
     carries the warning must NOT also set its own `title` — it would be
     shadowed on exactly the pixels a user aims at. */
  const unpriced = (count, charged) => Number(count) > 0 && Number(charged) < Number(count);
  const sixthUnpriced = unpriced(m.sixth_day_count, m.sixth_days_charged);
  const seventhUnpriced = unpriced(m.seventh_day_count, m.seventh_days_charged);

  /* 🔻 THE SECOND WARNING ON THE SAME CHANNEL (owner, 2026-08-23): *"maybe
     change the amber warning to where the 6th day rate is less than 1.5x and
     where 7th day is less than 2x daily rate as a warning that it might not
     be set correctly"*.

     🔑 THE UNPRICED WARNING IS NOT REPLACED. It became nearly unreachable —
     it now needs the member's WEEKLY rate to be absent, because any positive
     one yields a default — but that residual case is real and is exactly when
     a warning is most warranted, so both states live on one triangle with two
     sentences. The two are mutually exclusive per position by construction on
     the server, so a figure never carries both.

     🔑 READ, NEVER DERIVED. `*_below_default` is a server field
     (`helpers/crewCost.js`), computed from the STATED rate — which is what
     makes "a defaulted member never warns" unreachable rather than merely
     unlikely. Do not compare `sixth_day_rate` against a multiple of
     `effective_weekly_rate` here. */
  const sixthBelowDefault = Number(m.sixth_day_count) > 0 && m.sixth_day_rate_below_default === true;
  const seventhBelowDefault = Number(m.seventh_day_count) > 0 && m.seventh_day_rate_below_default === true;

  /**
   * ONE labelled figure. `whitespace-nowrap` is load-bearing: it is what
   * guarantees the row can only break BETWEEN figures — never inside one.
   *
   * 🔀 FILL-WIDTH, 2026-08-23 — `className` is new and additive, defaulting to
   * `''` so `crew-cost-summary-basis`'s and the heading's own hand-written
   * spans (which do not go through this component) are unaffected. The four
   * RECONCILING figures (Allowances / Normal / 6th / 7th) pass `flex-1
   * min-w-fit`; the context figures (basis/rate) and the Total do not — see
   * the container's own docblock for why the split falls there.
   */
  const Figure = ({
    label, count, value, testId, strong = false, title, warn = null, className = '',
  }) => (
    <span
      className={`inline-flex items-baseline gap-1.5 whitespace-nowrap${className ? ` ${className}` : ''}`}
      data-testid={testId}
      title={title}
    >
      <span className={`text-xs ${strong ? 'font-semibold text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>{label}</span>
      {/* 🪤 `!= null`, so a real count of 0 still renders. */}
      {count != null && (
        <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{count}</span>
      )}
      <span className={`tabular-nums ${strong ? 'text-sm font-semibold text-gray-800 dark:text-gray-100' : 'text-sm text-gray-700 dark:text-gray-300'}`}>{value}</span>
      {warn}
    </span>
  );

  return (
    <div
      className="mt-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 px-3 py-2"
      data-testid="crew-member-cost-summary"
      data-provisional={provisional ? 'true' : undefined}
      /* Announced, not shouted: the figures change under the reader's hands
         while they type, and `polite` waits for a pause instead of interrupting
         the field they are in. */
      aria-live="polite"
    >
      {/* ═══ 🔀 FILL THE WIDTH, 2026-08-23 ═══════════════════════════════════
          Owner: *"make cost summary fill the width available."* Measured in
          Chromium against the compiled production build: an ORDINARY member
          (Allowances + Normal only — no premium days) used ~675px of a
          ~1025px content box at 1440 with the sidebar open, bunched left with
          real dead space beside it; a WEEKS-BASIS member is sparser still.

          🚨 THE OBVIOUS FIX (`justify-between`) WAS TRIED FIRST AND REJECTED.
          Spreading N figures edge-to-edge makes the GAP a function of how
          FEW figures are present — exactly backwards, since the sparse
          members are the COMMON case (the premium figures are gated on
          `> 0`, and most members carry no premium days at all this week).
          Two figures flung to either end of a 1025px row reads as MORE
          broken than the bunching it replaces, and it is a bigger visual
          jump between members than the bunching ever was.

          ✅ CHOSEN: EQUAL-GROWTH FLEX WITH RESERVED SLOTS FOR THE HIDDEN
          PREMIUM FIGURES. Allowances, Normal/Labour and the two premium
          figures are the four terms of the sum this strip exists to show
          adding up; each carries `flex-1 min-w-fit` (grow to share leftover
          space equally; `min-w-fit` is the explicit floor — content is
          already protected by the flex automatic-minimum-size rule, this
          just states it rather than relying on it implicitly). A HIDDEN 6th
          or 7th figure is NOT simply omitted — it is replaced by an empty
          `flex-1` placeholder (`aria-hidden`, no text), so its column's
          WIDTH survives even though its content does not.
          🔑 RESERVE, NOT COLLAPSE — the decision the brief asked to be made
          and named. Reserving is what stops the gap between Allowances and
          Normal growing just because two OTHER figures are absent this
          week: those two also each hold a `flex-1` share, so the visible
          pair sit in their own bounded quarter-columns rather than
          stretching to fill the whole row between them. The cost is
          accepted, not hidden: a sparse member's row still shows two empty
          quarters of blank space on the right — the trade the brief itself
          named ("reserving keeps alignment; collapsing avoids a visible
          hole"). Collapsing was rejected because it reintroduces exactly the
          `justify-between` defect one level down: two REAL figures with no
          reserved neighbours would still expand to fill 50/50 of the whole
          remaining row, flinging them apart on every member missing a
          premium day — which is most of them.
          🔑 THE HEADING AND THE BASIS/RATE CONTEXT DO NOT GROW. They are not
          terms of the sum, so stretching them would not "fill the width" in
          any sense that helps — it would just widen the space between a
          label and a number nobody is trying to add together.
          🔑 THE TOTAL DOES NOT GROW EITHER — it is not a fifth equal cell.
          It keeps `strong` styling AND gains a `border-l` divider + `pl-4`,
          reading as an "=" before the answer, and it renders LAST so the
          four grow items — which by definition consume 100% of whatever
          space is left on their line — push it to the row's true right
          edge for free, with no `ml-auto` needed.
          `flex-wrap` + `gap-y-1` is UNCHANGED and is what makes the owner's
          "two rows if required" a graceful degradation for the FULLEST
          member (three named allowances + computer rental + unpriced 6th
          AND 7th days) rather than a second layout: each item still wraps
          to a new line independently when its own line runs out of room,
          and `whitespace-nowrap` on every figure is what guarantees a wrap
          can only fall BETWEEN figures, never inside one.
          ⚠️ jsdom has no layout, so WHETHER it reads as filling the width,
          whether the reserved columns actually line up, and whether the
          fullest member still degrades to two rows are BROWSER checks — the
          tests here can only assert the classes that make those properties
          true. See the report for the real Chromium numbers. */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {/* ═══ THE PROVISIONAL MARKER — CHANNEL: A WORD, PLUS ITALIC ════════
            The owner asked for values to "lock in when save is pressed", so
            the pre-save state has to read as pending.

            🚫 THE CHANNEL WAS THE CONSTRAINT, NOT THE WORDING. In this
            directory `opacity-*` is spoken for TWICE (the crew-table dirty
            guard; worked-vs-not inside the Gantt bar), and greyscale/SATURATION
            was taken by the inactive-row treatment on 2026-08-22. `border-
            dashed` means "OFF". Colour alone would need a 3:1 sole-carrier
            measurement and would collide with the amber the unpriced-premium
            triangle already owns on this very line.

            ✅ SO IT TAKES A CHANNEL NOTHING HERE USES: **TEXT** — the literal
            word `unsaved` appended to the heading — with italic as a secondary,
            non-load-bearing cue. Text needs no contrast argument (it is not a
            non-text mark), is immune to `index.css`'s `html.dark !important`
            table, cannot multiply with the per-day opacity, and is the only
            channel that says WHAT is provisional rather than merely that
            something is.

            ♿ It is real text in the DOM, so a screen reader gets it with the
            heading and no `sr-only`/`title` pair is needed. `aria-live="polite"`
            on the container announces the change without stealing focus.

            🪤 It marks the HEADING, not each figure. Marking every figure would
            put seven copies of one fact on a line whose whole design goal is to
            fit on one row — and the figures move TOGETHER or not at all, so one
            marker is the honest cardinality. */}
        <h5 className="shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
          Cost summary
          {provisional && (
            <span
              className="ml-1.5 normal-case italic font-normal tracking-normal text-gray-500 dark:text-gray-400"
              data-testid="crew-cost-summary-provisional"
            >
              unsaved
            </span>
          )}
        </h5>
        {/* ═══ R1 — THE RATE FIGURE IS EDIT-BRANCH-ONLY (2026-08-23) ═══════
            Owner: *"we dont need 'Rate: £1,800.00' in the summary as the rate
            is already in that section."*

            🔻 HIS PREMISE IS TRUE OF ONE BRANCH AND FALSE OF THE OTHER, WHICH
            IS WHY THIS IS A PROP AND NOT A DELETION. In the EDIT branch the
            section does carry the rate twice over — the Grade `<select>` prints
            each grade's `weekly_rate`, and there is a `Rate Override (£/wk)`
            control — so the figure really is a duplicate and it goes.
            **In the READ-ONLY branch the section shows Grade / Location /
            Allocation / Notes and NO RATE AT ALL**, so deleting it there would
            remove the only place a non-editing viewer can see what the member
            is charged at. He asked to remove a duplicate; that is not one.

            ⚠️ SO THE TWO BRANCHES NOW DIFFER, DELIBERATELY, AND THAT IS
            RECORDED RATHER THAN TIDIED. If the owner rules "remove it
            everywhere", this prop goes and the block with it — one line.

            🔑 AND THE THING HE MAY NOT KNOW, WORTH KEEPING BESIDE THE CODE:
            neither site in the edit branch shows `effective_weekly_rate`. The
            grade select carries the GRADE's rate and the override input carries
            a DRAFT the user is still typing; the resolved figure — the one the
            member is actually charged at, override applied — is published for
            exactly this reason and appeared only here. Removing it from the
            edit branch removes the only display of the RESOLVED number, not a
            copy of one. */}
        <span
          className="shrink-0 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap"
          data-testid="crew-cost-summary-basis"
          title={basisTitle}
        >
          {basisLabel}: <span className="tabular-nums text-gray-700 dark:text-gray-300">{basisValue ?? '—'}</span>
          {showRate && (
            <>
              <span className="mx-1.5 text-gray-300 dark:text-gray-600">|</span>
              Rate: <span className="tabular-nums text-gray-700 dark:text-gray-300">{rateText}</span>
              {/* `rate_is_override` is published so the client never has to work
                  out whether the member row beat the grade. 🪤 It is the SERVER'S
                  answer, resolved with `Number.isFinite`, so a deliberate £0
                  override reads as an override here — unlike the edit form's own
                  `(Overridden)` suffix, which is a separate expression. */}
              {m.rate_is_override ? <span className="ml-1 text-gray-500 dark:text-gray-400">(override)</span> : null}
            </>
          )}
        </span>

        {/* ═══ R2 — ALLOWANCES ABSORBS COMPUTER RENTAL (2026-08-23) ═════════
            Owner: *"combine 'Allowances £0.00 … Computer rental £305.00' into
            'Allowances'"*. The edit form already presents Computer Rental AS
            an allowance (first row, name locked), and he has confirmed that is
            how he reads it — so this RESOLVES the open wording question, and
            resolves it the OTHER WAY to yesterday's build.

            🔑 THIS FIGURE IS EXACTLY THE PUBLISHED `allowance_total` —
            `computer_cost + a1 + a2 + a3` — AND NOTHING IS SUMMED HERE. The
            field this page spent yesterday carefully avoiding is the CORRECT
            one at this site, because the GROUPING changed. `named_allowances_
            total` was right while a separate Computer rental figure sat beside
            it; with that figure gone, using it would understate the line and
            the row would stop summing to `member_total`.

            🚨 AND THIS IS THE TRAP: **THE PAGE FOOTER MUST NOT FOLLOW.**
            `crewGrandTotal` counts `totalComputer` as its own term, so
            `allowance_total` there would double-count every laptop — the exact
            defect caught and measured yesterday (£3,755 against £3,455). The
            footer's arithmetic is UNTOUCHED by this change and must stay that
            way. One field, two groupings, and only this panel moves. The same
            sentence is written at the footer's `totalAllowance`.

            🔻 The separate `Computer rental` Figure that stood here is DELETED,
            not hidden — its money is inside this one now, and rendering both
            would be the double-count on this panel instead of the footer. The
            laptop survives BY NAME in the title/sr-only breakdown below, so
            the detail still explains the headline. */}
        <Figure
          className="flex-1 min-w-fit"
          label="Allowances"
          value={money(m.allowance_total)}
          testId="crew-cost-summary-allowances"
          title={allowanceTitle}
        />
        <span className="sr-only">{allowanceTitle}</span>

        {/* 🪤 `standard_day_count` is `null` — never 0 — on the week-fraction
            basis, because there are no days to count. Rendering a count there
            would claim "zero standard days worked". The LABEL changes with the
            basis for the same reason the header chip's does: a panel that
            showed a day count for a week-costed member would contradict the
            figure beside it. */}
        <Figure
          className="flex-1 min-w-fit"
          label={onDayBasis ? 'Normal days' : 'Labour'}
          count={onDayBasis ? (m.standard_day_count ?? '—') : null}
          value={money(m.standard_labour)}
          testId="crew-cost-summary-normal"
        />

        {/* 🔻 ALWAYS RENDERED, EVEN AT ZERO — OWNER RULING, 2026-08-23:
            *"6th and 7th days should show 0 days and £0 if no 6th or 7 days
            exist for that person."*

            🔄 THIS REVERSES A DELIBERATE DECISION AND THE OLD ARGUMENT IS KEPT
            SO NOBODY RE-DERIVES IT: *"a count of zero premium days is a real
            answer and is simply not news … showing '6th days 0 £0.00 · 7th days
            0 £0.00' on every member — which is every member on production today
            — would be two dead figures pushing the line onto a second row for no
            information."* It was a reasonable call about NOISE and the owner has
            made the opposite one: a figure that is present at zero tells a buyer
            the premium was CONSIDERED and came to nothing, where an absent one
            cannot be told from a figure that has not loaded. His screen, his
            call — `git revert` is the undo.

            🔑 IT ALSO DELETED A MECHANISM, WHICH IS THE PART WORTH NOTICING.
            The fill-width layout reserved an empty `flex-1` column for a HIDDEN
            premium figure, so the two real figures either side did not stretch
            into the gap. With both figures always present there is no gap and
            no hidden case, so `crew-cost-summary-sixth-reserved` /
            `-seventh-reserved` and their §17/§18 cases are GONE rather than left
            "in case". A mechanism kept past its case is how dead complexity
            accumulates — and worse here, a reserved column that can never
            render is a lie about the layout for the next person reading it.

            🟢 NO EXTRA NULL GUARD IS NEEDED AND NONE WAS ADDED — CHECKED, NOT
            ASSUMED. `sixth` / `seventh` are already `Number(x) || 0` at the top
            of this component, and the server sends `0` rather than `null` on the
            WEEK-FRACTION basis too (there are no dates to position, so "no 6th
            days" is the honest answer; `crewMemberCostFields.js` states it at
            the field, and `standard_day_count` is the one that legitimately goes
            null). Adding a `?? 0` here would read as defence against a state
            that cannot occur and would send the next reader looking for it.

            🔻 THE UNPRICED WARNING STAYS COUNT-GATED, and that is not an
            oversight: `sixthUnpriced` is *"this member has 6th days AND the rate
            is NULL"*, so a member with no 6th day has nothing unpriced to warn
            about. Warning at zero would put an amber flag on every member on
            production for a premium none of them is owed.

            🔀 MERGED 2026-08-23 with the premium-rate DEFAULTS branch. The
            always-render ruling above wins; the BELOW-DEFAULT warning from that
            branch is grafted onto both `warn` props. It is count-gated for the
            same reason the unpriced one is — a member with no 6th day has no
            6th-day rate to be below anything. */}
        <Figure
          className="flex-1 min-w-fit"
          label="6th days"
          count={sixth}
          value={money(m.sixth_day_labour)}
          testId="crew-cost-summary-sixth"
          warn={sixthUnpriced
            ? <UnpricedPremiumWarning positions={[6]} />
            : (sixthBelowDefault
              ? <BelowDefaultPremiumWarning positions={[6]} overridePositions={premiumOverridePositionsFor(m)} rates={m.agreement_rates} />
              : null)}
        />
        <Figure
          className="flex-1 min-w-fit"
          label="7th days"
          count={seventh}
          value={money(m.seventh_day_labour)}
          testId="crew-cost-summary-seventh"
          warn={seventhUnpriced
            ? <UnpricedPremiumWarning positions={[7]} />
            : (seventhBelowDefault
              ? <BelowDefaultPremiumWarning positions={[7]} overridePositions={premiumOverridePositionsFor(m)} rates={m.agreement_rates} />
              : null)}
        />

        {/* 🔀 FILL-WIDTH: the total does NOT carry `flex-1` — it is not a
            fifth equal cell. The four terms above it consume 100% of
            whatever space is left on their line by definition, which pushes
            this to the row's true right edge with no `ml-auto` needed. The
            `border-l` + `pl-4` reads as an "=" ahead of the answer — the
            channel that keeps it visually distinct from an equal column. */}
        <Figure
          className="shrink-0 border-l border-gray-300 dark:border-gray-600 pl-4"
          strong
          label="Member total"
          value={money(m.member_total)}
          testId="crew-cost-summary-total"
        />
      </div>
    </div>
  );
}

/**
 * Does saving this row require the manual-spend-adjustment ConfirmDialog?
 * True only when the drafted adjustment PARSES and DIFFERS from the saved
 * one. Blank/unparseable drafts are treated as unchanged (the save reverts
 * them, sends no adjustment, warns about nothing). 0 is a legitimate VALUE
 * (clears the adjustment), so the tests are Number.isFinite / ===, never
 * falsy — the '0'-vs-0 house rule.
 */
export function shouldConfirmAdjustmentSave(savedAmount, draft) {
  const current = Number(savedAmount) || 0;
  if (typeof draft !== 'string' && typeof draft !== 'number') return false;
  const text = String(draft);
  if (text.trim() === '') return false;
  // 🚨 `toPayloadAmount`, never `parseFloat` (2026-09-21). The docblock above
  // already said an UNPARSEABLE draft warns about nothing — but `parseFloat`
  // disagreed about what "unparseable" means: `parseFloat('1,000')` is `1`, so
  // a £1,000 draft read as a £1 CHANGE, opened the warning dialog, and invited
  // the user to confirm a thousandfold under-price. It is now unparseable, so
  // no dialog opens and `performRowSave` refuses it by name.
  const parsed = toPayloadAmount(text, null);
  if (parsed === null) return false;
  return parsed !== current;
}

/**
 * BUDGET_COST_CODES_COLUMNS — the desktop cost-codes table's column model.
 *
 * Owner: "can you make the columns on .../budget/manage-budgets more even and
 * adjustable like the other tables? seems like everything gets more cramped
 * to the right". Wired the same way the order tables and the Gag Breakdown
 * are: `hooks/useColumnWidths.js` + `components/ui/ColumnResizeHandle.js`,
 * one width MAP keyed by column key (never an index array — see
 * `useColumnWidths.js`'s own warning), its own `localStorage` key.
 *
 * 🔑 **EVERY COLUMN CARRIES A `min`, INCLUDING THE ONE WITH NO LABEL
 * (`reorder`).** `useColumnWidths.sanitise`/`clamp` read
 * `Number.isFinite(c.min) ? c.min : 0`, so a column with no `min` gets a
 * floor of ZERO, silently — the documented trap. `reorder` has no header
 * text to measure, so it takes its own shipped width (40) as its floor,
 * the same device `ORDER_TABLE_COLUMNS`'s `expand` column uses.
 *
 * `min` for every OTHER column is measured in headless Chromium against the
 * compiled production stylesheet (Inter, `font-semibold text-sm`, the
 * classes this table's headers actually render): label width + `px-3`
 * (24px), rounded up to the next 10. `width` (the default / reset target) is
 * chosen separately, to distribute the row evenly instead of cramping every
 * column to its header-only floor: Name/Description (the two TEXT columns)
 * get the most room, the three money columns (Budget/Adjustment/Spent to
 * Date) share one consistent width, and Actions is sized to its two BTN_XS
 * buttons ("Edit"/"Delete", measured ~95px + padding).
 */
// 🔀 `fitFloor: true` (2026-09-28, owner ruling: *"none text, or known text
// width columns first"*): Code, Budget, Adjustment, Spent to Date and
// Actions are all NON-TEXT or KNOWN-WIDTH (a short code, money figures, a
// button group) and pin at their `min` when fitting. Name and Description
// are free text and share whatever room `Fit columns to screen` leaves
// over. `reorder` is already `fixed: true`, which the fit function treats
// the same way — see `utils/fitColumnWidths.js`.
export const BUDGET_COST_CODES_COLUMNS = Object.freeze([
  // 📏 `min` 60 → 70 (owner ruling 2026-10-29, Q2 = B: codes rendered "1…" /
  // "J…" after Fit at 1280/1366). Unlike its neighbours this floor is set by
  // the DATA, not the header ("Code" + px-3 = 56.94px): measured in Chromium on
  // the real build, the cell is px-3 (24) + chevron 10.34 + gap 4 + the code in
  // `font-mono` 14px (7.70px a character here) — "101"/"J01" (every starter
  // code, every job code to J99) need 61.44px, "1001" (the add form's own
  // example) 69.14px → 70. A 5-character code ("12001", 76.84px) still ellipses;
  // `font-mono` resolves per OS, so re-measure rather than reuse these figures.
  { key: 'code', label: 'Code', width: 100, min: 70, align: 'left', fitFloor: true },
  { key: 'name', label: 'Name', width: 180, min: 70, align: 'left' },
  { key: 'description', label: 'Description', width: 180, min: 110, align: 'left' },
  // 💷 2026-10-25 (owner: "all money inputs should be the same width"): the three
  // money columns share ONE width, floored at the in-row money box
  // (`MONEY_INPUT_WIDTH`, 132px + the edited cell's px-1) — measured, the Budget
  // box was 72px at the old 80 floor. A stored width below 140 resets to it.
  { key: 'budget', label: 'Budget', width: 140, min: 140, align: 'center', fitFloor: true },
  // Renders only for budget managers with a version selected — see
  // `visibleBudgetCostCodesColumns` below. Owner's original wording was
  // "Overide cost to date"; the semantics are a signed added delta, so the
  // header says Adjustment (flagged for him to rename, unchanged by this).
  { key: 'adjustment', label: 'Adjustment', width: 140, min: 140, align: 'right', fitFloor: true },
  // 🔻 NARROWED 220/260/160 → 180/180/130 (owner ruling, 2026-09-23): on a
  // laptop screen the Actions column (Edit/Save/Cancel/Delete) was being
  // pushed off. A user's own DRAGGED width always wins over this default
  // (`useColumnWidths.js#sanitise` reads the stored value first) — see that
  // hook's docblock — so this cut cannot move anyone's saved layout.
  { key: 'spent', label: 'Spent to Date', width: 140, min: 140, align: 'right', fitFloor: true },
  // Icon-only reorder handle. No header text to measure a floor from, so it
  // takes its own shipped width — the `ORDER_TABLE_COLUMNS.expand` device.
  // `fixed: true` (2026-09-28, `Fit columns to screen`): a pure icon/control
  // column the fit never resizes, matching `expand` on every other table.
  { key: 'reorder', label: '', width: 50, min: 40, align: 'center', fixed: true },
  // ⋯ row menu (Delete) — owner ruling A 2026-10-25; a NEW key, so no stored width.
  { key: 'row_menu', label: '', width: 32, min: 32, align: 'center', fixed: true },
  // 🔻 `actions` (Edit/Delete, 130) DELETED 2026-10-24: the row expands on
  // click and Delete lives in its panel. A stored width for it is ignored.
]);

export const DEFAULT_BUDGET_COST_CODES_COLUMN_WIDTHS = Object.fromEntries(
  BUDGET_COST_CODES_COLUMNS.map((c) => [c.key, c.width])
);

/**
 * `align` ('left' | 'center' | 'right') → the Tailwind class that renders it.
 * THE ONE PLACE a `BUDGET_COST_CODES_COLUMNS` alignment becomes a class —
 * the header row and the desktop totals row both call this rather than each
 * carrying its own copy of the ternary.
 *
 * 🚨 **Exists because the two had already drifted.** Before this helper, the
 * totals row hard-coded `text-right` on the Budget cell while
 * `BUDGET_COST_CODES_COLUMNS`'s `budget` entry (and the header + every data
 * row built from it) has always been `align: 'center'` — confirmed back to
 * before the 2026-08-16 column-widths commit (`187e4165`), so this is not a
 * regression that landed WITH the resizable columns, only exposed by the
 * owner's alignment sweep once the columns could be seen moving. A totals
 * figure sat flush against the right cell edge under a centred header and
 * centred data column on every screen. Deriving both from
 * `BUDGET_COST_CODES_COLUMNS` means a future column's alignment can never
 * diverge between the header and its own total again.
 */
export function budgetCostCodesAlignClass(align) {
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
}

/** `{ key: align }` for every column, incl. ones the current view has
 * filtered out of `costCodesColumns` — the totals row looks columns up by
 * KEY, not by position, so this must never be built from the filtered list. */
export const BUDGET_COST_CODES_ALIGN_BY_KEY = Object.fromEntries(
  BUDGET_COST_CODES_COLUMNS.map((c) => [c.key, c.align])
);

/** Same ceiling the Gag Breakdown uses (`MAX_GAG_COLUMN_WIDTH`) — one shared,
 * already-reasoned-about number rather than a fresh guess. */
export const BUDGET_COST_CODES_MAX_COLUMN_WIDTH = 600;

/**
 * Which columns actually render, given the two flags that gate them.
 *
 * 🚨 **THE ONE FUNCTION THE `<colgroup>`, THE HEADER ROW AND THE BODY CELLS
 * ALL READ — never a second, independently-maintained boolean expression.**
 * That is what keeps the map and the table from desyncing: `adjustment`
 * renders only for budget managers with a version selected, `reorder` only
 * while editable, and every consumer (colgroup width sum, header cells,
 * the body's `hasAdjustmentColumn` / `hasReorderColumn` and the expanded
 * panel's `colSpan`, derived from THIS array's contents rather than
 * re-testing `showAdjustmentColumn`/`canEdit` a second time) agrees by
 * construction — a hidden column can never silently mis-align the row to
 * its right, the documented th/td desync trap.
 */
export function visibleBudgetCostCodesColumns({ showAdjustmentColumn, canEdit }) {
  return BUDGET_COST_CODES_COLUMNS.filter((c) => {
    if (c.key === 'adjustment') return !!showAdjustmentColumn;
    if (c.key === 'reorder' || c.key === 'row_menu') return !!canEdit;
    return true;
  });
}

/**
 * The widest of several scrollport measurements, or `null` when none has been
 * measured. The Cost Codes tab's three tables (Cost codes, Job codes, the
 * combined Total) share one width map; a FOLDED section measures 0, so the
 * answer is whichever is on screen (2026-10-29).
 */
export function widestMeasured(widths) {
  const measured = widths.filter((w) => Number.isFinite(w));
  return measured.length ? Math.max(...measured) : null;
}

/**
 * One list's Budget / Adjustment / Spent sums — the per-section SUBTOTALS of
 * the Cost Codes tab (2026-10-29). Term for term the expressions the combined
 * Total has always used (`totalBudget`, and the two `costCodes.reduce`s at the
 * Total row), applied to one section's codes, so the two subtotals sum to that
 * Total by construction. Layout only — no figure is computed differently.
 */
/** The two code sections' SHARED header pieces (2026-10-29) — one card, one
 *  title-with-count, one `+ Add …` button class, so neither section can drift
 *  into reading as the other's footer. A nested card: a 1px keyline, no second
 *  shadow inside the tab's own `shadow-md` card. Secondary, not primary: two
 *  creates sit on screen together now, and the one-filled-control rule
 *  (`components/ui/CLAUDE.md`) allows one. 44px tap target below `md`. */
export const CODE_SECTION_CARD = '!shadow-none border border-gray-200 dark:border-gray-700';
export const CODE_SECTION_ADD_BUTTON = `${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center whitespace-nowrap`;
export function codeSectionTitle(label, count) {
  return (
    <>
      {label}
      <span className="ml-2 align-middle rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600 dark:text-gray-300" data-code-section-count="">{count}</span>
    </>
  );
}

export function codeListTotals(list, budgetAmounts, adjustments) {
  return {
    budget: list.reduce((sum, cc) => sum + (parseFloat(budgetAmounts[cc.id] || '0') || 0), 0),
    adjustment: list.reduce((sum, cc) => sum + ((adjustments[cc.id] && adjustments[cc.id].amount) || 0), 0),
    computed: list.reduce((sum, cc) => sum + (cc.expenditure_to_date || 0), 0),
  };
}

/**
 * GRADE_TABLE_COLUMNS — the desktop Crew Grades table's column model
 * (2026-10-25, owner: Access Rights 2nd; *"make the colums fit the page, i'm
 * sure breakdown does this too?"*). The table is FITTED TO ITS SCROLLPORT BY
 * DEFAULT through the shared `utils/fitColumnWidths.js` (the Breakdown's and
 * Cost Codes' Fit arithmetic): spare width spreads in proportion to `width`,
 * never below `min`. The two left-aligned TEXT columns (Grade Name, Access
 * Rights) are `fixed` — they never grow, which left "an unusual gap between
 * Grade Name and Access Rights" (owner, looking at a proportional mock); the
 * spare width goes to the five right-aligned money columns. Reorder `fixed`.
 * No stored widths and no drag handles — the fit is a pure function of the
 * measured width, so nothing persists.
 * 🔀 The "Overtime" summary column is GONE (owner: *"not very useful… hard to
 * work out what it means"*): Hourly (£) took its place; the multipliers live
 * in the expanded details only.
 * 🚨 The ORDER here is the `<colgroup>`, header and body-cell order; the body
 * cells are hand-ordered JSX, so `gradesExpandRow` G7.1 pins this list as a
 * LITERAL against the rendered `<col>`s and the header.
 */
// 📐 The floors sum to 1100 (180×2 + 140×5 + 40) so the table fits a 1440
// window's 1132px scrollport with room (review I2: at 200 it was 1140 and
// overflowed 8px). The 40px came off the TEXT columns, not the money ones:
// the money floor is the input's width, and the input's width is placeholder room.
const GRADE_TEXT_COLUMN_WIDTH = 180;
const GRADE_MONEY_COLUMN_WIDTH = 140;
// The floor fits `MONEY_INPUT_WIDTH` (132px) + the edited cell's `px-1`.
const GRADE_MONEY_COLUMN_MIN = 140;
export const GRADE_TABLE_COLUMNS = Object.freeze([
  // One width for the two text columns too (owner: same kind, same width).
  { key: 'name', width: GRADE_TEXT_COLUMN_WIDTH, min: GRADE_TEXT_COLUMN_WIDTH, fixed: true },
  { key: 'access', width: GRADE_TEXT_COLUMN_WIDTH, min: GRADE_TEXT_COLUMN_WIDTH, fixed: true },
  // 💷 ONE width for the five money columns (owner: *"all money inputs should
  // be the same width"*), so they grow EQUALLY and their in-row inputs match.
  { key: 'weekly_rate', width: GRADE_MONEY_COLUMN_WIDTH, min: GRADE_MONEY_COLUMN_MIN },
  // 🔀 2026-10-25 (owner mock): "Total allowances (£/wk)" — the SERVER's
  // `allowances_total_weekly`, read-only; still a money column, equal width.
  { key: 'allowances_total', width: GRADE_MONEY_COLUMN_WIDTH, min: GRADE_MONEY_COLUMN_MIN },
  { key: 'sixth_day_rate', width: GRADE_MONEY_COLUMN_WIDTH, min: GRADE_MONEY_COLUMN_MIN },
  { key: 'seventh_day_rate', width: GRADE_MONEY_COLUMN_WIDTH, min: GRADE_MONEY_COLUMN_MIN },
  { key: 'hourly_rate', width: GRADE_MONEY_COLUMN_WIDTH, min: GRADE_MONEY_COLUMN_MIN },
  { key: 'reorder', width: 40, min: 40, fixed: true },
  { key: 'row_menu', width: 32, min: 32, fixed: true },
]);

/**
 * The grade table's rendered widths: `fitColumnWidths` over the visible
 * columns for `available`, or the declared widths before the first
 * measurement (`null`) — never a guessed width.
 */
export function gradeTableWidths(available, { canEdit }) {
  const columns = GRADE_TABLE_COLUMNS.filter((c) => (c.key !== 'reorder' && c.key !== 'row_menu') || canEdit);
  const fitted = fitColumnWidths(available, columns, BUDGET_COST_CODES_MAX_COLUMN_WIDTH);
  return { columns, widths: fitted || Object.fromEntries(columns.map((c) => [c.key, c.width])) };
}

/* ────────────────────────────────────────────────
   GradeAccessRightsPicker: the crew-grade default-role dropdown.

   SECURITY 2026-08-02 — this field caused a real privilege escalation on
   production. A buyer created crew grades ("Prep Supervisor", "Supervisor") and
   set the then-unexplained "Default Access Rights" field to "Admin", because the
   dropdown listed every role by bare display name with nothing to say what any
   of them meant. Hiring a colleague against one of those grades silently granted
   that placeholder user the real global admin role.

   Two changes, and there are three call sites (the add form, the mobile edit
   card and the desktop edit row) — which is exactly why this is one component
   rather than three copies of a filter that only two of them might get right.

   1. ADMIN-LEVEL ROLES ARE FILTERED OUT — for EVERY caller, global admins
      included. This is not a permission question, it is a dead option:
      POST /api/budget/crew-members refuses an admin-level stored default role
      UNCONDITIONALLY (409 `grade_default_role_admin_level`), so a grade carrying
      one can never be hired against by anybody. Offering it can therefore only
      produce a confusing 409 later, or — before that server gate shipped — an
      unintended grant. Removing it costs nothing and removes the trap.

   2. THE PREDICATE IS NOT RE-DERIVED HERE. `requires_global_admin_to_assign`
      is computed server-side in GET /api/roles from requiresGlobalAdminToAssign
      in server/helpers/roleAuthority.js — the same function the write paths
      enforce. CLAUDE.md forbids re-inlining `system_role === 'admin' && name
      !== 'buyer'`: that expression ignores `production_id`, and four hand-rolled
      copies of it were themselves a live privilege escalation. If the flag is
      ever missing from a response the option simply stays listed, and the
      unconditional server gate is what still holds the line — the filter is
      usability, never the boundary.
   ──────────────────────────────────────────────── */
const GRADE_ACCESS_RIGHTS_LABEL = 'Access Rights When Hired';
const GRADE_ACCESS_RIGHTS_HELP =
  'Sets the access level a person receives when they are hired against this grade. ' +
  'Leave it as None and hiring changes nothing about what they can see or do.';

/* 🔀 R1 (owner, Pete, 2026-10-19): the member's "Allocation" field is the
   DEFAULT for weeks a date change adds; it no longer re-lays weeks already on
   the Gantt (server/helpers/crewDays.js#addedDayCountForDefault). The label
   says so and the ⓘ says how — his ruling, in the user's words. */
export const DEFAULT_ALLOCATION_LABEL = 'Default allocation';
export const DEFAULT_ALLOCATION_HELP =
  'Days per week for weeks you add by dragging or changing the dates. ' +
  'Weeks already on the Gantt keep their days.';

export function DefaultAllocationLabel() {
  return (
    <span className="inline-flex items-center gap-1">
      {DEFAULT_ALLOCATION_LABEL}
      <InfoTip title={DEFAULT_ALLOCATION_HELP} />
    </span>
  );
}

export function assignableGradeRoles(roleDefinitions) {
  return (roleDefinitions || []).filter(r => !r.requires_global_admin_to_assign);
}

/**
 * May this caller manage budget VERSIONS on the department this page is showing?
 *
 * Exported only so it can be tested without mounting the whole page (same reason
 * as `assignableGradeRoles` above). It is a UI convenience — the boundary is
 * `requireDepartmentCapability(req, res, deptId, 'canManageBudget')` on every
 * write route — but it must ASK THE SAME QUESTION, which means asking it about
 * one department.
 *
 * `capabilities` is `useDepartment().capabilities`: the per-department set that
 * `GET /api/roles/my-assignments` attaches to the active assignment row,
 * resolved server-side by `departmentAuthority`. It re-derives on every
 * department switch. Never substitute the flat `canManageBudget` / `isBuyer`
 * from the same response — those are any-department flags (CLAUDE.md, "Authority
 * model"), and a supervisor in several departments would see version controls in
 * all of them and be refused in most.
 *
 * `isAdmin` is a separate disjunct because a global admin holds no department
 * assignment, so their `capabilities` is `{}`.
 */
export function canManageBudgetVersions({ isAdmin, capabilities }) {
  return !!isAdmin || !!(capabilities && capabilities.canManageBudget);
}

/**
 * May this caller INVITE from the crew list — `Send Invite` and `Add & Invite`?
 *
 * Ruling 24 (owner, 2026-09-23): *only managers invite* — a site admin, or a
 * holder of `canAssignDepartmentRoles` IN THE ACTIVE DEPARTMENT. The same
 * question `POST /api/invitations` asks (`mayGrantDepartmentRole`), so the
 * buttons are never offered to someone the server would refuse. Budget
 * authority is NOT invite authority: a `canManageBudget` holder without the
 * role capability edits crew but does not see the invite buttons.
 * 🚫 Never `isBuyer` — the unscoped flag (clientAuthorityManifest.test.js).
 * Measured on prod before the ruling: this hides the buttons from nobody today.
 */
export function canSendCrewInvites({ isAdmin, capabilities }) {
  return !!isAdmin || !!(capabilities && capabilities.canAssignDepartmentRoles === true);
}

export function GradeAccessRightsLabel() {
  return (
    <span className="inline-flex items-center gap-1">
      {GRADE_ACCESS_RIGHTS_LABEL}
      <InfoTip title={GRADE_ACCESS_RIGHTS_HELP} />
    </span>
  );
}

/**
 * @param {boolean} [loadFailed] `GET /api/roles` was refused or never landed,
 *   so `roleDefinitions` is empty because nobody could ask — not because there
 *   are no assignable roles.
 *
 * 🚨 WHEN IT IS TRUE THE PICKER RENDERS NO CONTROL AT ALL, 2026-08-22, AND THE
 * MISSING CONTROL IS THE POINT. A controlled `<select value={default_role_id}>`
 * whose value has no matching `<option>` falls back to `selectedIndex = 0` —
 * the literal `-- None --` — so a grade that DOES confer access rights reads as
 * conferring none, and the only thing the reader can then pick is `-- None --`,
 * which saves as `null` over a real `crew_grades.default_role_id`. That column
 * becomes a hired crew member's UNSCOPED `user_roles` role (CLAUDE.md, Role
 * Checking, paths 1–3), so it is not a cosmetic field: silently blanking it
 * loses an authority decision somebody made, invisibly, off a dropped request.
 */
export function GradeAccessRightsPicker({ value, onChange, roleDefinitions, className, showLabel = true, loadFailed = false, ariaLabel, title }) {
  if (loadFailed) {
    return (
      <>
        {showLabel && (
          <label className={FIELD_LABEL_CLASS}>
            <GradeAccessRightsLabel />
          </label>
        )}
        <p
          className="text-xs text-amber-700 dark:text-amber-300"
          role="status"
          data-testid="grade-access-rights-load-failed"
        >
          Access-rights roles couldn’t be loaded — this grade’s setting is unchanged
        </p>
      </>
    );
  }
  return <GradeAccessRightsSelect value={value} onChange={onChange} roleDefinitions={roleDefinitions} className={className} showLabel={showLabel} ariaLabel={ariaLabel} title={title} />;
}

function GradeAccessRightsSelect({ value, onChange, roleDefinitions, className, showLabel, ariaLabel, title }) {
  return (
    <>
      {showLabel && (
        <label className={FIELD_LABEL_CLASS}>
          <GradeAccessRightsLabel />
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        aria-label={ariaLabel}
        title={title}
      >
        <option value="">-- None --</option>
        {assignableGradeRoles(roleDefinitions).map(r => (
          <option key={r.id} value={r.id}>{r.display_name}</option>
        ))}
      </select>
      {showLabel && (
        <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          Applies when someone is hired against this grade.
        </p>
      )}
    </>
  );
}

/* ────────────────────────────────────────────────
   AllowanceNameInput: text input with case-insensitive autocomplete
   suggestions from existing allowance names in this department. Free text
   still allowed — selecting a suggestion just fills the value.
   ──────────────────────────────────────────────── */
function AllowanceNameInput({ value, onChange, options, placeholder, className, testId, ariaLabel }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    const opts = Array.isArray(options) ? options : [];
    if (!q) return opts.slice(0, 20);
    return opts.filter(o => o.toLowerCase().includes(q)).slice(0, 20);
  }, [value, options]);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setShowSuggestions(true); }}
        onFocus={() => setShowSuggestions(true)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        aria-label={ariaLabel}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions && filtered.length > 0}
      />
      {showSuggestions && filtered.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-20 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto"
        >
          {filtered.map((name) => (
            <li
              key={name}
              role="option"
              aria-selected={(value || '').toLowerCase() === name.toLowerCase()}
              className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer truncate"
              onMouseDown={(e) => { e.preventDefault(); onChange(name); setShowSuggestions(false); }}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────
   Phase 11 / Task 20: inline lock icons used in the version selector + modal
   headers. `locked` toggles between a closed padlock and an open one.
   ──────────────────────────────────────────────── */
function LockIcon({ locked, className = 'h-4 w-4' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      {locked ? (
        // Closed padlock — body + symmetric U-shape shackle resting on top.
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 118 0v3M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
      ) : (
        // Open padlock — same body, shackle tilted up and to the right so
        // the right foot visibly lifts OFF the body, creating an obvious
        // "popped open" silhouette that reads correctly even at 16px.
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 017-3M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
      )}
    </svg>
  );
}

/* ────────────────────────────────────────────────
   Phase 11 / Task 19: amber banner shown at the top of the Crew Members and
   Cost Codes sections when the selected budget version is locked. Lock icon
   is an inline SVG (no emoji per project style). Renders nothing when the
   version is unlocked so callers can drop it in unconditionally.
   ──────────────────────────────────────────────── */
function LockedVersionBanner({ show }) {
  if (!show) return null;
  return (
    <div
      className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 rounded-lg px-3 py-2 text-sm"
      role="status"
    >
      <LockIcon locked className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <span>
        This version is locked. Unlock from the version selector to edit, or clone it to make changes.
      </span>
    </div>
  );
}

/* 🔀 `generateWeeks`, `EMPTY_DAY_BASELINE`, `buildWeekDayBaseline`,
   `resolveWeekDays` and `sameDaySet` USED TO BE DEFINED HERE. They MOVED to
   `client/src/utils/crewDayResolution.js` on 2026-08-22, unchanged, when the
   Crew Gantt became a SECOND editor of `crew_member_days` — see that file's
   header. Nothing was left behind: a private copy here would be the second
   answer to "which days does this week hold", which is the defect itself. */
function formatWeekLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

/* ────────────────────────────────────────────────
   BudgetCostCodes Component
   ════════════════════════════════════════════════ */
function BudgetCostCodes() {
  const { activeDepartment, isAdmin, capabilities } = useDepartment();
  // 🔻 VAT display removed from the UI 2026-08-08 — `formatMoney` no longer
  // uplifts and there is no longer a mode to label. See `hooks/useVatMode.js`.
  const { formatMoney } = useVatMode();
  const toast = useToastOrNoop();
  const departmentId = activeDepartment?.department_id;

  // --- Versions state ---
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  const [showNewVersionInput, setShowNewVersionInput] = useState(false);
  const [settingCurrent, setSettingCurrent] = useState(false);
  const [cloningVersion, setCloningVersion] = useState(false);
  const [renamingVersion, setRenamingVersion] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [deletingVersion, setDeletingVersion] = useState(false);
  // Phase 11 / Task 20: lock/unlock UI state — both flows open a confirmation
  // modal first (cloning is the recommended path, so the unlock dialog calls
  // that out explicitly).
  const [lockingVersion, setLockingVersion] = useState(false);
  const [unlockingVersion, setUnlockingVersion] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  // "Make Baseline" — anchors Variance Analysis on this version.
  const [settingBaseline, setSettingBaseline] = useState(false);

  // --- Cost codes state ---
  const [costCodes, setCostCodes] = useState([]);
  const [costCodesLoading, setCostCodesLoading] = useState(true);
  // 🔀 JOB COST CODES (2026-10-28): `costCodes` holds BOTH kinds (a job code is
  // a cost code with `is_job = 1`); the tab renders them in two sections on one
  // column model. Totals still read the whole `costCodes` list.
  const standardCodes = useMemo(() => costCodes.filter((cc) => !cc.is_job), [costCodes]);
  const jobCodes = useMemo(() => costCodes.filter((cc) => !!cc.is_job), [costCodes]);
  const [showAddCostCode, setShowAddCostCode] = useState(false);
  const [costCodeForm, setCostCodeForm] = useState({ code: '', name: '', description: '', budget_amount: '' });
  const [savingCostCode, setSavingCostCode] = useState(false);
  /* 🔀 EXPAND-TO-EDIT since 2026-10-24 — the Crew Grades change, applied to
     this table (owner: "check the other tabs on /budget/ they might need
     updating too"). Was `editingCostCodeId` + an Edit button swapping the
     row's cells for inputs. `costCodeFormFrom` is the old `handleEditCostCode`
     body; the save path (`performRowSave`, the spend-adjustment confirm and
     its audited PATCH) is unchanged. */
  const costCodeEditor = useExpandRowEditor({
    formFrom: (cc) => costCodeFormFrom(cc),
    onSave: (id) => handleSaveEditCostCode(id),
    nameOf: (cc) => cc.code,
  });
  const editCostCodeForm = costCodeEditor.form;
  const setEditCostCodeForm = costCodeEditor.setForm;

  // --- Payment types state ---
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypesLoading, setPaymentTypesLoading] = useState(true);
  const [savingPaymentType, setSavingPaymentType] = useState(false);
  /* 🔀 EXPAND-TO-EDIT since 2026-10-24 (the Crew Grades change, applied here).
     Was `editingPaymentTypeId` + an Edit button swapping cells for inputs. */
  const paymentTypeEditor = useExpandRowEditor({
    formFrom: (pt) => ({ name: pt.name, description: pt.description || '' }),
    onSave: (id) => handleSaveEditPaymentType(id),
    nameOf: (pt) => pt.name,
  });
  const editPaymentTypeForm = paymentTypeEditor.form;
  /* 🔀 2026-10-25 (owner): Payment Types is an ALWAYS-EDITABLE list with one
     Save / Cancel (`components/budget/EditableList.js`) for an editor; the
     expand-to-edit rows above stay for the phone cards and read-only viewers. */
  const ptList = useEditableList(paymentTypes, (pt) => ({ name: pt.name, description: pt.description || '' }), { name: '', description: '' });

  // --- Budgets state ---
  const [budgetAmounts, setBudgetAmounts] = useState({}); // { cost_code_id: amount_string }
  // Manual spend adjustments for the SELECTED version (2026-08-15):
  // { cost_code_id: { amount: number, note: string|null } }. Version data —
  // rides the same fetch as budgetAmounts and resets with it.
  const [adjustments, setAdjustments] = useState({});
  // The ruled warning dialog, now raised from the ROW SAVE path (edit-mode
  // rework, owner feedback 2026-08-15): { id, value } while a save is held
  // awaiting Continue; null otherwise. The reason draft feeds the dialog's
  // prompt slot.
  const [pendingAdjustmentSave, setPendingAdjustmentSave] = useState(null);
  const [adjustmentNoteDraft, setAdjustmentNoteDraft] = useState('');

  // --- Role definitions (for grade default role dropdown) ---
  const [roleDefinitions, setRoleDefinitions] = useState([]);

  // --- Crew Grades state ---
  const [grades, setGrades] = useState([]);
  // The department's production figures (agreement Stages 2+3) — every grade row carries the same
  // server-published `agreement_rates`; the NEW-grade form has no row of its own, so it reads any.
  const departmentAgreementRates = useMemo(
    () => ((grades || []).find((gr) => gr && gr.agreement_rates) || {}).agreement_rates || null,
    [grades],
  );
  const [gradesLoading, setGradesLoading] = useState(true);
  const [showAddGrade, setShowAddGrade] = useState(false);
  const [gradeForm, setGradeForm] = useState({ name: '', weekly_rate: '', computer_rental: '', default_role_id: '', sixth_day_rate: '', seventh_day_rate: '' });
  const [savingGrade, setSavingGrade] = useState(false);
  /* 🔀 EXPAND-TO-EDIT since 2026-10-24 (owner: *"clicking it should expand the
     row without having to click the edit button"*). Was an `editingGradeId` +
     Edit button that swapped the row's cells for inputs in place. The form,
     its open/close fade, single-expand and the three-way dirty prompt are
     `hooks/useExpandRowEditor.js`; `gradeFormFrom` is the old `handleEditGrade`
     body, moved verbatim, and the save below is unchanged. */
  const gradeEditor = useExpandRowEditor({
    formFrom: (g) => gradeFormFrom(g),
    onSave: (id) => handleSaveEditGrade(id),
    nameOf: (g) => g.name,
  });
  const editGradeForm = gradeEditor.form;
  const setEditGradeForm = gradeEditor.setForm;

  // --- Crew Members state ---
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  // Sub-departments for the active department — options for the per-crew picker.
  const [subDepartments, setSubDepartments] = useState([]);
  // 🚨 "WE COULD NOT ASK" vs "THERE ARE NONE" — 2026-08-22. Both leave the
  // array empty, and a controlled `<select>` whose value has no matching
  // `<option>` then falls back to `selectedIndex = 0`, so a set value reads as
  // unset and the only correction on offer writes the emptiness back. See the
  // two fetches below and `GradeAccessRightsPicker` above.
  const [subDepartmentsFailed, setSubDepartmentsFailed] = useState(false);
  const [roleDefinitionsFailed, setRoleDefinitionsFailed] = useState(false);
  // Crew member id whose sub-department is currently saving (for inline spinner).
  const [savingSubDeptMemberId, setSavingSubDeptMemberId] = useState(null);
  // Live filter for the crew members list (powers both Table and Gantt views).
  const [crewSearch, setCrewSearch] = useState('');
  // Existing allowance names from other crew members — powers the
  // AllowanceNameInput autocomplete on each of the 3 allowance slots.
  const [allowanceNameOptions, setAllowanceNameOptions] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({
    crew_grade_id: '', name: '', user_id: null, location: '', start_date: '', end_date: '',
    default_allocation: 1.0, computer_rental: '', computer_rental_cap: '', weekly_rate_override: '', sixth_day_rate_override: '', seventh_day_rate_override: '', notes: '',
    user_email: '', user_phone: '',
  });
  const [savingMember, setSavingMember] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [editMemberForm, setEditMemberForm] = useState({});
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [closingMemberId, setClosingMemberId] = useState(null);
  const [openingMemberId, setOpeningMemberId] = useState(null);
  /* 🔀 R8 FIX ROUND (2026-10-19) — bumped by every OPEN (`handleExpandMember`'s
     seed). The collapse below runs its clear 250ms LATER, so an open that
     lands inside that window (the Gantt's exit prompt: Discard / Save and
     continue on A, then the click that opens B) had its freshly seeded form
     and `expandedMemberId` wiped by A's timer — B's strip came up owned by an
     EMPTY form, its drag was never costed and its Save sent `crew_grade_id:
     null`. The timer now stands down if anything opened since it was set. */
  const expandGenerationRef = useRef(0);
  const collapseMember = useCallback((clearForm = true) => {
    if (!expandedMemberId) return;
    const closingId = expandedMemberId;
    const generation = expandGenerationRef.current;
    setClosingMemberId(closingId);
    setTimeout(() => {
      if (expandGenerationRef.current !== generation) {
        setClosingMemberId((cur) => (cur === closingId ? null : cur));
        return;
      }
      setExpandedMemberId(null);
      setClosingMemberId(null);
      if (clearForm) {
        setEditMemberForm({});
        setOriginalMemberForm(null);
        setOriginalWeekData(null);
        setWeekData([]);
        setWeekOverrides({});
        setWeekDayOverlay({});
        setWeekDayBaseline(EMPTY_DAY_BASELINE);
        setExpandedWeek(null);
      }
    }, 250); // matches CSS transition duration
  }, [expandedMemberId]);
  const [crewViewMode, setCrewViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem('sfx_crew_view_mode');
      return stored === 'gantt' ? 'gantt' : 'table';
    } catch { return 'table'; }
  });
  useEffect(() => {
    try { localStorage.setItem('sfx_crew_view_mode', crewViewMode); } catch {}
  }, [crewViewMode]);

  /**
   * 🧪 PROTOTYPE — the single-member Gantt inside the crew Full Details panel.
   *
   * Owner, 2026-08-22, on the *Working Days* day grid: it *"now seems a little
   * redundant as it only shows x number of days per week and not which days"*,
   * then *"No i want the gantt chart JUST for that one person on the full
   * details section. Please build it as a test."* and, clarifying:
   * *"just copy the whole gantt chart but only show the one user."*
   *
   * 🔑 IT IS AN EXHIBIT, NOT A REPLACEMENT. The day grid stays; this is a
   * toggle beside it so the two can be compared. `SINGLE_MEMBER_GANTT_PROTOTYPE`
   * gates every part of it, so pulling it is ONE deletion followed by whatever
   * the linter then calls dead. Grep stem: `singleMemberGantt` /
   * `single-member-gantt`.
   *
   * 🚫 NO `localStorage` KEY, deliberately — `npm run check-bloat` already
   * warns about orphaned ones, and a preference that outlives the experiment is
   * exactly what a prototype must not leave behind. The view resets to the day
   * grid on every panel open, which is also the honest default while the grid
   * is still the incumbent.
   *
   * 🚫 NO NEW SAVE PATH. The embedded chart is handed the SAME handlers as the
   * page-level one, so there is one day-edit state and one save order
   * (dates → `/weeks` → `/days`). See `detailsGanttMembers`.
   */
  const SINGLE_MEMBER_GANTT_PROTOTYPE = true;
  const [singleMemberGanttView, setSingleMemberGanttView] = useState('grid');
  // Reset to the incumbent whenever the panel moves to another member, so the
  // prototype never becomes a sticky preference by the back door.
  useEffect(() => { setSingleMemberGanttView('grid'); }, [expandedMemberId]);

  // --- Page-level tabs (desktop-only trial) ---
  const SECTION_TABS = useMemo(() => [
    { id: 'budget-cost-codes', label: 'Cost Codes & Budget' },
    { id: 'budget-payment-types', label: 'Payment Types' },
    { id: 'budget-crew-grades', label: 'Crew Grades' },
    { id: 'budget-crew-members', label: 'Labour Flow' },
  ], []);
  const TAB_IDS = useMemo(() => SECTION_TABS.map(t => t.id), [SECTION_TABS]);
  // Per-tab short slug appended to the PDF and export filenames. 🔻 The
  // default-ticked set (Cost Codes + Crew Members + the old Ad-hoc tab) went with the
  // band's `Download / Print` button (2026-10-05): the `Actions ▾` print
  // group pre-ticks the CURRENT tab only (owner's design).
  const TAB_FILENAME_SUFFIX = useMemo(() => ({
    'budget-cost-codes': '-cost-codes',
    'budget-payment-types': '-payment-types',
    'budget-crew-grades': '-crew-grades',
    'budget-crew-members': '-crew-members',
  }), []);
  // The tabs as the `Actions ▾` print group needs them (label + filename slug).
  const PRINT_TABS = useMemo(
    () => SECTION_TABS.map(t => ({ ...t, filenameSuffix: TAB_FILENAME_SUFFIX[t.id] || '' })),
    [SECTION_TABS, TAB_FILENAME_SUFFIX]
  );
  // Below `md` the tabs and tables are hidden: the `Actions ▾` there is print-only.
  const isPhoneViewport = useIsMobileOrderViewport();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const hash = window.location.hash.replace(/^#/, '');
      const validIds = ['budget-cost-codes', 'budget-payment-types', 'budget-crew-grades', 'budget-crew-members'];
      if (validIds.includes(hash)) return hash;
      const stored = localStorage.getItem('sfx_budget_manage_active_tab');
      if (validIds.includes(stored)) return stored;
    } catch {}
    return 'budget-cost-codes';
  });
  useEffect(() => {
    try {
      localStorage.setItem('sfx_budget_manage_active_tab', activeTab);
      if (window.location.hash.replace(/^#/, '') !== activeTab) {
        // `window.history.state`, never `null`: the data router keeps its
        // entry index there, and a nulled entry cannot have Back blocked (R14).
        window.history.replaceState(window.history.state, '', `#${activeTab}`);
      }
    } catch {}
  }, [activeTab]);
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace(/^#/, '');
      if (TAB_IDS.includes(hash)) setActiveTab(hash);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [TAB_IDS]);

  // --- Dirty guard for expand-to-edit ---
  const [originalMemberForm, setOriginalMemberForm] = useState(null);
  const [originalWeekData, setOriginalWeekData] = useState(null);

  // --- Week overrides for expanded member ---
  const [weekData, setWeekData] = useState([]); // server data [{week_start, allocation}]
  const [weekOverrides, setWeekOverrides] = useState({}); // { week_start: allocation }
  const [weekLoading, setWeekLoading] = useState(false);
  // 🔻 `handleSaveWeeks` + `savingWeeks` DELETED 2026-10-20 (no caller; the week
  // grid saves through the member's Save Changes). Pre-deletion SHA ac4d757d.

  // ─── The crew DAY GRID (6th/7th-day premium wage feature, Task 6) ───────
  // `weekDayOverlay` holds ONLY the weeks the user has touched in this edit
  // session — it is deliberately NOT a full copy of the member's day set.
  // The baseline is derived (see `dayGrid` below) from the member's stored
  // `crew_member_days` rows, or from the week allocation when there are none,
  // so a week the user has not touched has no entry here and cannot drift.
  const [weekDayOverlay, setWeekDayOverlay] = useState({}); // { week_start: number[] }
  const [weekDayBaselineStored, setWeekDayBaseline] = useState(EMPTY_DAY_BASELINE);
  // Only ONE week may be expanded at a time. That single value is what keeps
  // the tab-stop count a constant +7 rather than +7 per week — see the header
  // block in `components/budget/CrewDayGrid.js`.
  const [expandedWeek, setExpandedWeek] = useState(null);
  /**
   * The DAY GRID's current engagement range — the dates as the FORM has them,
   * which is what `resolveWeekDays` needs to tell a day the user has just
   * brought into range from a day that is genuinely not worked. ONE definition,
   * so the render, the tick handler and the save payload cannot disagree about
   * which days are new and re-open the split `resolveWeekDays` exists to close.
   *
   * 🔻 IT REPLACED `weekFallbackAlloc` (2026-08-23), which answered "what
   * allocation does an unknown week fall back to" by reading the STORED
   * member's `default_allocation` through `parseFloat(x) || 1.0`. Two things
   * were wrong with it and only one was the falsy collapse: the owner ruled
   * that a newly-in-range day is ALWAYS Mon–Fri, so the allocation was the
   * wrong input to the question. See `utils/crewDayResolution.js`'s
   * `DEFAULT_WORKED_DAY_INDICES`. Nothing else read it, so it is deleted rather
   * than left as a live helper nobody calls.
   */
  const weekDayCurrentRange = useMemo(
    () => (editMemberForm.start_date && editMemberForm.end_date
      ? { start: editMemberForm.start_date, end: editMemberForm.end_date }
      : null),
    [editMemberForm.start_date, editMemberForm.end_date]
  );

  /* ─── THE CREW GANTT's DAY TOGGLES — a SECOND editor of `crew_member_days`,
   *     2026-08-22. Owner: an expand chevron beside the person's name on the
   *     Gantt opens their bar into clickable days; "clicks only on week view".
   *
   * 🚨 SEPARATE STATE FROM THE DAY GRID'S, DELIBERATELY, AND SHARED LOGIC WITH
   * IT ABSOLUTELY. The grid's `weekDayOverlay` / `weekDayBaseline` belong to the
   * expanded TABLE row and are torn down by `collapseMember`; the Gantt's belong
   * to a Gantt row and survive independently. What must NOT be duplicated is the
   * RULE — which days a week holds, whether anything changed, and what gets
   * posted — and none of it is: every one of those questions is answered by
   * `utils/crewDayResolution.js`, the same functions `buildDayPayloadForSave`
   * and `isMemberDirty` call. Two states, one resolver.
   *
   * 🔑 BATCHED, NOT IMMEDIATE (my call, flagged to the owner). A click updates
   * local state and marks the row dirty; Save commits, Cancel discards. Every
   * click moves money, there is no undo, and the rest of this app is a
   * dirty-edit model — a PUT per click would also make the freeze below fire on
   * the first stray click rather than on a decision.
   * ─── */
  const [ganttDayMemberId, setGanttDayMemberId] = useState(null);
  const [ganttDayOverlay, setGanttDayOverlay] = useState({});     // { week_start: number[] }
  const [ganttDayWeekOverrides, setGanttDayWeekOverrides] = useState({}); // { week_start: allocation }
  const [ganttDayBaselineStored, setGanttDayBaseline] = useState(EMPTY_DAY_BASELINE);
  const [ganttDaySaving, setGanttDaySaving] = useState(false);
  // The Ctrl+Z stack — one entry per day toggle, each recording the PREVIOUS
  // value of BOTH maps above for the affected week. Declared here with the
  // rest of the strip's state; the rules that govern it are at
  // `handleGanttDayUndo`. Per-strip and never persisted.
  const [ganttDayUndoStack, setGanttDayUndoStack] = useState([]);

  /* ═══ THE PENDING DATE CHANGE — owner ruling, 2026-08-22 ══════════════════
   *
   * Verbatim: *"once the gantt is expanded we should be able to drag and see
   * the price and bar update etc but it doesnt 'Lock in' until saved."*
   *
   * 🔻 A BAR DRAG USED TO WRITE IMMEDIATELY AND NOW DOES NOT. `handleGanttUpdate`
   * called `applyGanttUpdate`, which fired `PUT /crew-members/:id` plus a
   * `PUT /weeks` the moment the pointer was released — with no drag threshold
   * anywhere in `CrewGanttView` (see `canDragMember` there), so a 16px slip at
   * month zoom was a persisted change to somebody's employment dates. The drag
   * is now an OVERLAY on the member, exactly like the day toggles: it moves the
   * bar and the previewed price, marks the row dirty, and commits only on Save.
   * `null` means "the dates are as stored".
   *
   * 🔑 IT CARRIES THE PREVIEW FIGURES TOO, AND THEY ARE CAPTURED, NOT
   * RE-DERIVED. `budget_cost` / `total_weeks` are lifted straight off the last
   * `dragPreview` frame — the value `CrewGanttView`'s drag tooltip already
   * computed for this member through `previewWeeklyRate` — so there is exactly
   * ONE preview arithmetic in the app and this is a longer-lived reading of it,
   * not a second opinion. Without them the bar would stay where the user
   * dropped it while the price snapped back to the stored figure: two numbers
   * on one row contradicting each other, which is the `weekly_rate_override`
   * tooltip defect wearing new clothes.
   * ⚠️ THE DECLARED LIMIT IS UNCHANGED AND NOW LASTS LONGER: the preview is
   * `weeks × rate` while day-model labour is `days / 5 × rate`, so for a member
   * holding any hand-recorded day the previewed figure is approximate until the
   * save returns the server's. It was visible only during a drag before; it is
   * now visible until Save. Declared in `previewWeeklyRate`'s docblock and in
   * §4 of `server/tests/regressions/crew-cost-path-parity-manifest.test.js`.
   */
  const [ganttDayDates, setGanttDayDates] = useState(null);

  /* ═══ LEVEL 2 — THE GANTT'S **SECOND** EXPAND (2026-08-22, PROTOTYPE) ═════
   *
   * Owner: *"When we click full details expand again smoothly to show the user
   * details that are normally on the 'Full Details' expanded table view."* So:
   * Gantt row → expand → day strip (level 1) → expand again → full member
   * details (level 2). This is OPTION B; option A (the whole Gantt inside the
   * table's details panel) is on the same page and is NOT touched — the owner
   * is comparing them.
   *
   * 🔑 THIS HOLDS **ONLY WHICH MEMBER'S PANEL IS VISIBLE**. The panel's own
   * state is `expandedMemberId` / `editMemberForm` — the crew table's state,
   * reused unchanged — so there is no second form state that could drift from
   * the first. `handleGanttDetailsToggle` sets both; only this one is cleared
   * when level 2 collapses back to level 1, which is what makes that collapse
   * non-destructive (brief, trap 2).
   *
   * 🔒 LEVEL 2 REQUIRES LEVEL 1 AND THE STRIP STAYS OPEN BEHIND IT. Ruled, not
   * incidental: the strip is where the day set and the pending drag live, and
   * the panel's Save commits all three halves in one ordered sequence. A level
   * 2 that REPLACED the strip would hide the day set the same Save posts.
   */
  const [ganttDetailsMemberId, setGanttDetailsMemberId] = useState(null);

  /**
   * 🚨 THE MEMBER WHOSE **FORM** THE GANTT OWNS - which is NOT the same thing
   * as the member whose panel is VISIBLE, and conflating the two was a real
   * hole that §4.4 of `ganttSecondExpand.test.js` caught.
   *
   * Collapsing level 2 back to level 1 deliberately KEEPS the edits (trap 2),
   * so `expandedMemberId`, `editMemberForm` and `isMemberDirty` all survive it.
   * Everything that asks "is there unsaved work here" - the exit guard, the one
   * Save button, `beforeunload`, and the choice of body step 1 sends - therefore
   * has to key on OWNERSHIP. Keyed on visibility instead, hiding the panel
   * silently disarmed the guard and the chevron threw the edit away without a
   * word: the panel is a curtain, not a lifetime.
   *
   * Set when level 2 opens; cleared ONLY by `closeGanttDayStrip`, which is also
   * what clears the form.
   */
  const [ganttFormMemberId, setGanttFormMemberId] = useState(null);

  /* 🔀 2026-10-19 — "DEFAULT ALLOCATION" (owner, Pete): *"…a drag now defaults
   * to 3 days"*. The days a date change ADDS are laid at the default the SAME
   * save writes, so each editor's baseline (seeded once from the STORED member)
   * takes its form's DRAFT default: the Gantt strip only while level 2 owns the
   * form (level 1 sends the stored default), the crew table's day grid while a
   * row is open for edit. `toPayloadNumber` is the PUT body's own reading; an
   * unusable value (the server refuses it) leaves the stored default in force.
   * Read by every resolver call through `ganttDayBaseline` / `weekDayBaseline`,
   * so the picture, the dirty test and the `/days` body cannot disagree. */
  const draftDefaultAllocation = toPayloadNumber(editMemberForm && editMemberForm.default_allocation, null);
  const ganttDraftDefaultAllocation = ganttFormMemberId != null && ganttFormMemberId === ganttDayMemberId
    && draftDefaultAllocation !== null ? draftDefaultAllocation : undefined;
  const tableDraftDefaultAllocation = expandedMemberId != null && draftDefaultAllocation !== null
    ? draftDefaultAllocation : undefined;
  const ganttDayBaseline = useMemo(
    () => withDraftDefaultAllocation(ganttDayBaselineStored, ganttDraftDefaultAllocation),
    [ganttDayBaselineStored, ganttDraftDefaultAllocation]
  );
  const weekDayBaseline = useMemo(
    () => withDraftDefaultAllocation(weekDayBaselineStored, tableDraftDefaultAllocation),
    [weekDayBaselineStored, tableDraftDefaultAllocation]
  );

  /* 🔀 2026-10-19 — OWNER RULING A (Pete): the crew TABLE's date change
   * behaves exactly as a Gantt drag. Its `/weeks` body and its cost preview's
   * `week_overrides` are `weekOverridesForEdit` over the STORED member, the
   * form's dates and draft default, and the weeks the day grid touched — the
   * Gantt strip's own rule. It used to post `weekOverrides` AS LOADED, so an
   * end move stored no partial week here and one from the Gantt (probe R9) —
   * an allocation disagreeing with the day set. `weekOverrides` stays the grid's
   * dirty map; only the TOUCHED weeks' values travel. Pinned by
   * `BudgetCostCodes.tableDatesMatchGantt.test.js`. */
  const tableStoredMember = useMemo(
    () => (expandedMemberId == null ? null : members.find((x) => x.id === expandedMemberId) || null),
    [members, expandedMemberId]
  );
  const tableWeekOverrides = useMemo(() => {
    if (!tableStoredMember) return null;
    const touched = {};
    for (const ws of Object.keys(weekDayOverlay)) {
      if (weekOverrides[ws] !== undefined) touched[ws] = weekOverrides[ws];
    }
    return weekOverridesForEdit({
      member: tableStoredMember,
      startDate: editMemberForm.start_date,
      endDate: editMemberForm.end_date,
      draftDefault: tableDraftDefaultAllocation,
      touched,
    });
  }, [tableStoredMember, weekDayOverlay, weekOverrides, editMemberForm.start_date, editMemberForm.end_date,
    tableDraftDefaultAllocation]);

  /**
   * `isMemberDirty` is declared ~350 lines BELOW `guardGanttDayEdit`, and the
   * guard has to see it (brief, trap 3: the guard must understand unsaved FORM
   * edits, and **there must not be a second guard**). A ref read at CALL time
   * costs nothing and avoids reordering two large blocks of state; the guard
   * runs from a click, never from render, so there is no staleness window.
   */
  const ganttDetailsFormDirtyRef = useRef(false);

  /**
   * The member whose level-2 panel is FADING OUT. 250ms, matching
   * `collapseMember`'s own window and `ROW_EXPAND_TRANSITION`'s 200ms duration.
   *
   * 🔑 IT EXISTS SO THE PANEL CAN BOTH ANIMATE SHUT **AND** LEAVE THE DOM.
   * Keeping the form mounted at `height: 0` was the cheaper way to animate the
   * close and it is WRONG: `overflow-hidden` hides a control visually and does
   * nothing to the tab order, so a keyboard user would tab into a form nobody
   * can see. Unmounting immediately loses the animation. This does both, in
   * that order.
   */
  const [ganttDetailsClosingId, setGanttDetailsClosingId] = useState(null);

  /** THE ONE WAY LEVEL 2 CLOSES. Both closers call it, so "what happens when
   *  the panel goes away" has exactly one answer. */
  const hideGanttDetails = useCallback((memberId) => {
    // 🚫 IT DOES **NOT** CLEAR THE GUARD'S REF. Hiding the panel keeps the
    // edits, so the work is still unsaved and every exit must still ask. See
    // `ganttFormMemberId`.
    setGanttDetailsMemberId(null);
    if (memberId == null) return;
    setGanttDetailsClosingId(memberId);
    setTimeout(
      () => setGanttDetailsClosingId((cur) => (cur === memberId ? null : cur)),
      250
    );
  }, []);

  /* ═══ TRAP 1 — THE DATES ARE NOW EDITABLE IN **TWO PLACES AT ONCE** ═══════
   *
   * At level 2 the bar's drag and the form's `Start Date` / `End Date` inputs
   * are on screen together and address the same two columns. Two controls over
   * one value that do not track each other is not a polish problem: whichever
   * one the Save happens to read wins, and the other silently discards what the
   * user typed or dragged.
   *
   * 🔑 THE BINDING IS TWO ONE-WAY MIRRORS, NOT A SHARED SOURCE, AND THAT IS
   * DELIBERATE. `ganttDayDates` (the pending drag) and `editMemberForm` (the
   * form) have different lifetimes — the drag overlay is dropped by Cancel and
   * by `closeGanttDayStrip`, the form is seeded once by `handleExpandMember` —
   * so collapsing one into the other would change the behaviour of a shipped
   * exit path to buy tidiness. Instead: this writes drag → form, and
   * `handleMemberDateFieldChange` writes form → drag. Each is a no-op when
   * level 2 is closed, so **nothing in the table view moves at all.**
   *
   * 🪤 IT COMPARES BEFORE IT WRITES. `onMemberDragPreview` fires on every
   * pointer frame; an unconditional `setEditMemberForm` would allocate a new
   * form object per frame, re-run `isMemberDirty` and re-render the panel — and
   * on a drag that lands where it started it would leave the form referentially
   * different but deeply equal, which `isMemberDirty` (a deep compare) happily
   * reports as clean but which costs a full panel re-render per frame.
   */
  /* 🔀 R8 FIX ROUND — keyed on OWNERSHIP (`ganttFormMemberId`), not on
     visibility (contract item 13). It read `ganttDetailsMemberId`, so with
     Details HIDDEN a drag (or its Ctrl+Z) never reached the form: the form kept
     the old dates, Save stayed armed after an undo, the Unsaved chip and the
     collapse prompt reported an edit that was not there, and Save wrote an echo
     PUT. Since R8 every page-chart expand owns the form, so "hidden" is common. */
  const syncGanttDetailsDates = useCallback((memberId, startDate, endDate) => {
    if (ganttFormMemberId == null || ganttFormMemberId !== memberId) return;
    const s2 = startDate || '';
    const e2 = endDate || '';
    setEditMemberForm((prev) => (
      prev.start_date === s2 && prev.end_date === e2
        ? prev
        : { ...prev, start_date: s2, end_date: e2 }
    ));
  }, [ganttFormMemberId]);

  const ganttDayMember = useMemo(
    () => members.find((m) => m.id === ganttDayMemberId) || null,
    [members, ganttDayMemberId]
  );

  /** The live `members` array, readable from a stale closure. Needed by a
   *  batched-drag re-resolution that runs a tick or more after the render
   *  that captured the member object — see `handleGanttDetailsToggle`
   *  below. (Also fed the now-deleted `handleGanttMemberClick`'s exit-prompt
   *  `proceed`, removed 2026-08-24 with the `Full Details` button; this ref
   *  outlives it because this caller remains.) */
  const membersRef = useRef(members);
  useEffect(() => { membersRef.current = members; }, [members]);

  /**
   * The dates the open strip is working against: the pending drag if there is
   * one, otherwise the member's stored dates.
   *
   * 🚨 EVERY DERIVED THING BELOW READS THIS, NOT `ganttDayMember` DIRECTLY, AND
   * THAT IS THE WHOLE SAFETY ARGUMENT FOR BATCHING THE DRAG. `ganttDayWeeks`,
   * the resolved worked set, the dirty test and the save payload must all be
   * cut from ONE date range or they disagree about which weeks exist — the
   * shape `utils/crewDayResolution.js#resolveWeekDays` records as having cost a
   * 4.2-week engagement 48% of its labour. A week brought into range by a drag
   * sits in neither the overlay nor the baseline and resolves through the
   * allocation fallback, identically for the render and for the payload.
   */
  const ganttDayEffectiveDates = useMemo(() => {
    if (!ganttDayMember) return { start_date: null, end_date: null };
    if (ganttDayDates) {
      return { start_date: ganttDayDates.start_date, end_date: ganttDayDates.end_date };
    }
    return {
      start_date: ganttDayMember.start_date ? ganttDayMember.start_date.slice(0, 10) : null,
      end_date: ganttDayMember.end_date ? ganttDayMember.end_date.slice(0, 10) : null,
    };
  }, [ganttDayMember, ganttDayDates]);

  /** Has the drag actually MOVED anything? A drag that lands back where it
   *  started must not arm Save or the exit prompt. */
  const ganttDayDatesDirty = useMemo(() => {
    if (!ganttDayMember || !ganttDayDates) return false;
    const storedStart = ganttDayMember.start_date ? ganttDayMember.start_date.slice(0, 10) : null;
    const storedEnd = ganttDayMember.end_date ? ganttDayMember.end_date.slice(0, 10) : null;
    return ganttDayDates.start_date !== storedStart || ganttDayDates.end_date !== storedEnd;
  }, [ganttDayMember, ganttDayDates]);

  const ganttDayWeeks = useMemo(
    () => (ganttDayMember
      ? generateWeeks(ganttDayEffectiveDates.start_date, ganttDayEffectiveDates.end_date)
      : []),
    [ganttDayMember, ganttDayEffectiveDates]
  );

  /**
   * The GANTT STRIP's current engagement range — the EFFECTIVE dates, so an
   * unsaved bar drag is inside it. ONE definition for this surface, exactly as
   * `weekDayCurrentRange` is the one definition for the day grid.
   *
   * 🚨 IT IS THE **EFFECTIVE** RANGE, NOT THE STORED ONE, AND THAT IS THE WHOLE
   * FIX FOR THE DRAG DEFECT. `resolveWeekDays` calls a day "new" when this
   * range contains it and the baseline's stored range did not — so reading the
   * stored dates here would make the two ranges identical and nothing would
   * ever be new.
   *
   * 🔻 IT REPLACED `ganttDayFallbackAlloc` (2026-08-23) — the strip's own copy
   * of the deleted `parseFloat(default_allocation) || 1.0` allocation lookup.
   * See `weekDayCurrentRange` above and `DEFAULT_WORKED_DAY_INDICES`.
   */
  const ganttDayCurrentRange = useMemo(
    () => (ganttDayEffectiveDates.start_date && ganttDayEffectiveDates.end_date
      ? { start: ganttDayEffectiveDates.start_date, end: ganttDayEffectiveDates.end_date }
      : null),
    [ganttDayEffectiveDates]
  );

  // `ganttDayWorkedDates` — the worked dates the Gantt draws — is defined below
  // `ganttStripCostDates` (2026-10-06), because it now follows the LIVE drag
  // frame and that state is declared further down.

  // 🔑 THE SAME PREDICATE THE SAVE USES. `buildDayPayload` returns null when
  // `dayPayloadHasChanges` is false, so a dirty flag derived any other way
  // could offer a Save that posts nothing — or worse, withhold one that would.
  const ganttDayDaysDirty = useMemo(
    () => (ganttDayMember
      ? dayPayloadHasChanges(ganttDayWeeks, ganttDayOverlay, ganttDayBaseline, ganttDayCurrentRange)
      : false),
    [ganttDayMember, ganttDayWeeks, ganttDayOverlay, ganttDayBaseline, ganttDayCurrentRange]
  );

  /**
   * 🚨 DIRTY IS NOW **EITHER HALF**, AND THE DATE HALF IS THE ONE THAT WOULD
   * HAVE BEEN MISSED. Every exit on this surface — the chevron, the row, Full
   * Details, the view toggle, the section tabs, the version select, Deactivate,
   * `beforeunload` — asks exactly this one flag. Leaving it as days-only after
   * the drag became batched would mean a dragged-but-unsaved bar could be
   * navigated away from in silence, which is precisely the defect the exit
   * prompt was built to close, arriving through the door the prompt does not
   * watch. One flag, both halves, so a new exit cannot cover one and not the
   * other.
   */
  const ganttDayDirty = ganttDayDaysDirty || ganttDayDatesDirty;

  /**
   * The weeks whose day set has actually CHANGED — the list the exit prompt
   * names, so the dialog says what is unsaved rather than that something is.
   *
   * 🔑 It is `dayPayloadHasChanges` run one week at a time, NOT a second
   * opinion about what "changed" means. A separate expression here could name
   * a week the save would not send, or stay silent about one it would, and a
   * prompt that misdescribes the work is worse than the silent refusal it
   * replaces — the user discards on the strength of what it says.
   */
  const ganttDayChangedWeeks = useMemo(
    () => (ganttDayMember
      ? ganttDayWeeks.filter((ws) => dayPayloadHasChanges(
        [ws], ganttDayOverlay, ganttDayBaseline, ganttDayCurrentRange
      ))
      : []),
    [ganttDayMember, ganttDayWeeks, ganttDayOverlay, ganttDayBaseline, ganttDayCurrentRange]
  );

  /**
   * The REPLACE-ALL week-override array for the OPEN STRIP, or null for "no
   * draft". Both preview bodies post it, and the level-2 body needs the same
   * answer the level-1 body gets, so it is written once.
   *
   * 🔑 THE RULES, EACH OF WHICH IS A MEASURED DEFECT AND NOT A PREFERENCE:
   *  • the strip's overlay is a PATCH over the member's STORED set, not the
   *    whole set — the crew table's `weekOverrides` is the whole set, which is
   *    why the two surfaces assemble differently and always did;
   *  • the OUTER gate is `ganttDayDirty || datesMoved` (since the R8 review,
   *    F3: a moved costed range is a draft even mid-gesture, before
   *    `ganttDayDates` exists). Without a gate, `allocationForDayCount(5)` on a
   *    member with no stored override is a BRAND-NEW row carrying the value
   *    they already had by default, so the maps alone still report a draft
   *    after the strip has gone clean;
   *  • 🪤 COMPARE THE RESULT, NEVER "HAS A KEY BEEN TOUCHED". The overlay keeps
   *    an entry for every week the user has clicked in, so a toggle UNDONE
   *    leaves a key behind whose value is back to the stored one — which holds
   *    the preview open and leaves stale provisional figures on screen with
   *    nothing left to justify them. Measured, not reasoned: §6.1 of
   *    `BudgetCostCodes.dayTogglePreview.test.js` reds on exactly that. The
   *    same shape as `dayPayloadHasChanges`, which is why the DAY half of this
   *    pair never had the bug.
   *
   * ⚠️ `weeks` is the caller's, because the two callers cut it from different
   * date ranges: level 2 from the effective dates, level 1 from the LIVE drag
   * frame. An allocation ticked into a week a later drag removed must not be
   * costed into an engagement that no longer contains it — the same filter
   * `handleGanttDaySave` applies.
   */
  /* 🔀 R8 FIX ROUND (review F3) — `dates` is the range being costed, and when
     it differs from the STORED dates the base is NOT the stored overrides: it
     is `dateChangeWeekOverrides(member, start, end, draftDefault)`, exactly the
     array STEP 2 of `handleGanttDaySave` starts its `/weeks` merge from. Before
     this a toggle-free date drag previewed NO `week_overrides` (rental and the
     three allowances at the stored allocation) while the Save's `/weeks` wrote
     the moved weeks' partial allocations — shown ≠ saved, in money. The gate
     widens with it: a moved range is a draft even mid-gesture, before
     `ganttDayDates` (and so `ganttDayDirty`) exists. */
  /* 🔀 2026-10-19 (ruling A): the merge is `weekOverridesForEdit`, the SAME
     rule the strip's Save and the crew table's save + preview call. */
  const buildGanttStripWeekOverrides = useCallback((dates) => {
    if (!ganttDayMember) return null;
    const storedStart = ganttDayMember.start_date ? ganttDayMember.start_date.slice(0, 10) : null;
    const storedEnd = ganttDayMember.end_date ? ganttDayMember.end_date.slice(0, 10) : null;
    const datesMoved = dates.start_date !== storedStart || dates.end_date !== storedEnd;
    if (!ganttDayDirty && !datesMoved) return null;
    return draftWeekOverridesOrNull(ganttDayMember, weekOverridesForEdit({
      member: ganttDayMember,
      startDate: dates.start_date,
      endDate: dates.end_date,
      draftDefault: ganttDraftDefaultAllocation,
      touched: ganttDayWeekOverrides,
    }));
  }, [ganttDayMember, ganttDayDirty, ganttDayWeekOverrides, ganttDraftDefaultAllocation]);

  /* ═══════ THE UNSAVED-DAYS EXIT PROMPT — 2026-08-22 ═══════════════════════
   *
   * 🔑 WHY THIS REPLACED A SILENT REFUSAL. The strip shipped blocking the
   * chevron and the name while dirty, on the day grid's idiom, and setting
   * `showDirtyWarning`. But every renderer of that flag is a TABLE-view
   * control (the Save/Cancel buttons at the top of the crew card and at the
   * bottom of the expanded panel) and **none of them is mounted in Gantt
   * view** — so the pulse landed on nothing and the refusal really was
   * silent. Owner, having used it: *"having to click save is NOT obvious. If
   * the buyer/supervisor tries to click on another control or name or
   * anything outside of editing the days we should have a pop up."*
   *
   * 🚨 THE ENUMERATION IS THE FEATURE, NOT THE DIALOG. An exit nobody listed
   * is a route by which somebody's work disappears without a word, so the
   * exits are enumerated from the STATE the strip depends on rather than from
   * the controls that look relevant — the same "enumerate the writers, not
   * the readers" rule root `CLAUDE.md` applies to authority gates. The strip
   * needs four things to stay alive and reachable, and every writer of each
   * is guarded or declared at its site:
   *   • `crewViewMode === 'gantt'`     → the Table/Gantt view toggle
   *   • the Labour Flow section open   → its `CollapsibleSection` onToggle
   *   • `ganttDayMember` still in `filteredMembers`
   *                                    → the crew search box, the version
   *                                      selector, Deactivate member
   *   • that member's dates unchanged  → a bar drag on their own bar
   *   • the document still loaded      → `beforeunload`
   * The full list, INCLUDING what is deliberately not prompted and why, is in
   * `BudgetCostCodes.ganttDayExitGuard.test.js`'s header.
   *
   * 🔴 THREE OUTCOMES, NOT TWO, and that is the point of using
   * `ConfirmDialog`'s `altLabel` slot. `Keep editing` · `Discard changes` ·
   * `Save and continue`. A two-button dialog here forces a choice between
   * losing the work and being stuck on a control that still will not do what
   * was asked of it, which is the silent refusal with an extra click. Same
   * three outcomes, same left-to-right order and the same
   * `initialFocus="cancel"` as `components/OrderInlineEditor.js`'s
   * collapse prompt — so an immediate Enter can neither discard an edit nor
   * fire an unreviewed save.
   */
  const [ganttDayExit, setGanttDayExit] = useState(null); // { what, proceed }

  /**
   * THE ONE GATE every exit goes through. Clean → run it now. Dirty → hold
   * the action and ask.
   *
   * ⚠️ `proceed` is held in state and called LATER, so it must not close over
   * anything it needs to be fresh. Every caller passes a closure over values
   * that are either arguments or setters, both of which are stable.
   */
  /* 🔀 2026-08-22 — IT NOW ASKS ABOUT THE **FORM** TOO, AND THAT IS A SECOND
   *    DISJUNCT ON THE ONE GATE RATHER THAN A SECOND GATE. Level 2 renders the
   *    crew member's whole edit form inside the Gantt, so an exit can now
   *    discard a typed rate override or an allowance as easily as a ticked day.
   *    `ganttDayDirty` cannot see that (it is days + pending dates), and the
   *    brief forbids a second guard for the good reason that two guards drift.
   *    ⚠️ The ref is only ever true while level 2 is OPEN, so the table view's
   *    own dirty form is unaffected — it has its own `kind: 'table'` prompt in
   *    `handleExpandMember` (2026-10-06), which is a different surface with a different
   *    remedy.
   * 🚨 TWO DIRTY SURFACES AT ONCE — NO SAVE PATH MAY DROP EITHER (R8 review F5,
   *    2026-10-19). The crew TABLE's form (unowned by the Gantt) and an UNOWNED
   *    strip (the embedded chart's ticks) can both be dirty when an R8 expand
   *    of another member is clicked. One "Save and continue" cannot land both
   *    safely: the table Save writes the form only, and running the strip's
   *    level-1 Save after it would replay the member from a stale closure (its
   *    narrow body and `/weeks` base predate the form save). So the prompt
   *    becomes `kind: 'both'`: it NAMES both, Save is DISABLED, the only ways
   *    on are Keep editing or "Discard both" — the user saves each surface with
   *    its own Save. Chosen over "save both" as the smaller change with no new
   *    write sequence to get wrong. A NEW dual-surface exit must do the same. */
  const guardGanttDayEdit = useCallback((what, proceed) => {
    if (!ganttDayDirty && !ganttDetailsFormDirtyRef.current) { proceed(); return; }
    setGanttDayExit({ what, proceed });
  }, [ganttDayDirty]);

  // --- Job codes (owner ruling 2026-10-28) ---
  // 🔀 The "Ad-hoc Budget Lines" tab, its retired crew-lines API state and
  // its editors were DELETED with the job cost codes (pre-deletion SHA
  // 4ae12235): every ad-hoc line is a JOB CODE now — a `cost_codes` row with
  // `is_job = 1` — so job codes live in `costCodes` above and render in their
  // own "Job codes" section of the Cost Codes & Budget tab.
  const [showAddJobCode, setShowAddJobCode] = useState(false);
  // The New job code form sits at the top of the Job codes section (2026-10-29;
  // ABOVE the one table until then, ~2,900px from its button on a phone — the
  // 2026-10-28 phone audit). Opening it still brings it into view and focuses
  // its first field; the button also unfolds a folded section (`expandWhen`).
  const addJobCodeFormRef = useRef(null);
  useEffect(() => {
    if (!showAddJobCode) return;
    const form = addJobCodeFormRef.current;
    if (!form) return;
    scrollUnderPinnedChrome(form);
    const first = form.querySelector('input, select, textarea');
    if (first) first.focus({ preventScroll: true });
  }, [showAddJobCode]);
  const [jobCodeForm, setJobCodeForm] = useState({ code: '', name: '', gag_id: '', budget_amount: '' });
  const [savingJobCode, setSavingJobCode] = useState(false);
  // The department's live gags, for the job code's gag link (add form + label).
  const [jobGags, setJobGags] = useState([]);
  // `{ [jobCodeId]: [order, …] }` — orders for the job's gag still on another
  // (or no) code, from GET /api/budget/job-codes/:id/relink-candidates.
  const [relinkCandidates, setRelinkCandidates] = useState({});
  // The job code whose "link them?" list is open in the ConfirmDialog.
  const [relinkTarget, setRelinkTarget] = useState(null);
  const [relinking, setRelinking] = useState(false);

  // --- Messages ---
  // 🔻 `successMessage` state (with its own 3s auto-clear effect) was DELETED
  // 2026-09-23 when every `setSuccessMessage` caller on this page moved to the
  // shared toast (`contexts/ToastContext.js`) — see `performRowSave`'s
  // docblock above `toast.success` for why the in-flow banner cost a page
  // jump in the first place. `error` stays: it now carries ONLY the
  // pre-submit validation guards (`crewMoneyFormError`, `malformedMoneyMessage`,
  // "Select a budget version first") that must stay visible in the form until
  // the user fixes the field — never an async request outcome.
  const [error, setError] = useState(null);

  // Override the Layout page header with the budget being managed
  // (e.g. "Managing Aurora v1.0"). Updates when the selected version changes,
  // including after creating a new version or marking a different one as current.
  const selectedVersionForTitle = versions.find(v => v.id === selectedVersionId);
  usePageTitle(selectedVersionForTitle ? `Managing ${selectedVersionForTitle.version_name}` : null);

  // --- Fetch versions ---
  const fetchVersions = useCallback(async () => {
    if (!departmentId) return;
    try {
      setVersionsLoading(true);
      const res = await apiGet(`/api/budget/versions?department_id=${departmentId}`);
      if (!res.ok) throw new Error('Failed to fetch budget versions');
      const data = await res.json();
      setVersions(data);
      // Auto-select current version, or first
      if (data.length > 0) {
        const current = data.find(v => v.is_current);
        setSelectedVersionId(prev => {
          // Keep selection if still valid
          if (prev && data.find(v => v.id === prev)) return prev;
          return current ? current.id : data[0].id;
        });
      } else {
        setSelectedVersionId(null);
      }
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setVersionsLoading(false);
    }
  }, [departmentId]);

  // --- Fetch cost codes ---
  const fetchCostCodes = useCallback(async () => {
    if (!departmentId) return;
    try {
      setCostCodesLoading(true);
      const res = await apiGet(`/api/budget/cost-codes?department_id=${departmentId}&include_spend=1`);
      if (!res.ok) throw new Error('Failed to fetch cost codes');
      const data = await res.json();
      setCostCodes(data);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setCostCodesLoading(false);
    }
  }, [departmentId]);

  // --- Fetch payment types ---
  const fetchPaymentTypes = useCallback(async () => {
    if (!departmentId) return;
    try {
      setPaymentTypesLoading(true);
      const res = await apiGet(`/api/budget/payment-types?department_id=${departmentId}`);
      if (!res.ok) throw new Error('Failed to fetch payment types');
      const data = await res.json();
      setPaymentTypes(data);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setPaymentTypesLoading(false);
    }
  }, [departmentId]);

  // --- Fetch budgets for selected version ---
  const fetchBudgets = useCallback(async () => {
    if (!selectedVersionId) {
      setBudgetAmounts({});
      setAdjustments({});
      return;
    }
    try {
      const res = await apiGet(`/api/budget/cost-code-budgets?version_id=${selectedVersionId}`);
      if (!res.ok) throw new Error('Failed to fetch budgets');
      const data = await res.json();
      const amountsMap = {};
      const adjMap = {};
      for (const b of data) {
        amountsMap[b.cost_code_id] = String(b.budget_amount || 0);
        // Number(), not falsy tests — 0 is the legitimate "no adjustment".
        adjMap[b.cost_code_id] = {
          amount: Number(b.spend_adjustment) || 0,
          note: b.spend_adjustment_note || null,
        };
      }
      setBudgetAmounts(amountsMap);
      setAdjustments(adjMap);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  }, [selectedVersionId]);

  // Manual spend adjustment PATCH — the ruled ConfirmDialog has already run
  // (warning + optional reason; raised from the row-save path since the
  // 2026-08-15 edit-mode rework) before this is called. The server refuses
  // independently (403 / 423 version_locked); apiThrow keeps the structured
  // code for friendlyErrorMessage's toast.
  const handleSaveSpendAdjustment = async (costCodeId, value, noteText) => {
    try {
      setError(null);
      const res = await apiPatch('/api/budget/cost-code-budgets/spend-adjustment', {
        version_id: selectedVersionId,
        cost_code_id: costCodeId,
        spend_adjustment: value,
        spend_adjustment_note: noteText || null,
      });
      if (!res.ok) await apiThrow(res);
      const data = await res.json();
      setAdjustments(prev => ({
        ...prev,
        [costCodeId]: {
          amount: Number(data.spend_adjustment) || 0,
          note: data.spend_adjustment_note || null,
        },
      }));
      notifyDataBus('budget-changed', { departmentId, reason: 'spend-adjustment' });
    } catch (err) {
      // 🔻 FIX ROUND A4 (2026-09-23) — NO `toast.error` HERE. This function's
      // only caller, `performRowSave`, awaits it from inside its own `try`
      // and already toasts the same error when this re-throw lands in ITS
      // catch — toasting here too announced one refused PATCH twice. Just
      // re-throw: the caller announces it, and the re-throw itself is still
      // what keeps the ConfirmDialog open for a retry.
      throw err;
    }
  };

  // Displayed Spent to Date = computed order spend + the selected version's
  // manual adjustment (owner-ruled). 0/absent adjustment changes nothing.
  const spentWithAdjustment = (cc) =>
    (cc.expenditure_to_date || 0) + ((adjustments[cc.id] && adjustments[cc.id].amount) || 0);

  /* ─── Fetch role definitions (for grade default role dropdown) ─── */
  const fetchRoleDefinitions = useCallback(async () => {
    try {
      const prodId = activeDepartment?.production_id;
      const res = await apiGet(`/api/roles${prodId ? `?production_id=${prodId}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch role definitions');
      const data = await res.json();
      setRoleDefinitions(data);
      setRoleDefinitionsFailed(false);
    } catch (err) {
      // 🔻 "Non-critical: just log, don't block the page" — TRUE OF THE PAGE AND
      // FALSE OF THE PICKER, 2026-08-22. Not blocking the page is right; leaving
      // the empty list to speak for itself is not. `GradeAccessRightsPicker`
      // reads an empty `roleDefinitions` as "this grade grants no access
      // rights" and offers `-- None --` as the only choice, which saves `null`
      // over a real `crew_grades.default_role_id`. The flag is what lets the
      // picker say "we could not ask" instead.
      console.error('Failed to fetch role definitions:', err);
      setRoleDefinitionsFailed(true);
    }
  }, []);

  /* ─── Crew Fetch helpers ─── */
  const fetchGrades = useCallback(async () => {
    if (!departmentId) return;
    try {
      setGradesLoading(true);
      const res = await apiGet(`/api/budget/crew-grades?department_id=${departmentId}`);
      if (!res.ok) throw new Error('Failed to fetch crew grades');
      const data = await res.json();
      setGrades(data);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setGradesLoading(false);
    }
  }, [departmentId]);

  const fetchMembers = useCallback(async (silent = false) => {
    if (!departmentId) return;
    try {
      if (!silent) setMembersLoading(true);
      // Phase 11 / Task 18: scope crew reads to the selected version. When no
      // version is picked yet, omit version_id and let the server fall back to
      // the dept's active version (preserves first-load semantics).
      const url = selectedVersionId
        ? `/api/budget/crew-members?department_id=${departmentId}&version_id=${selectedVersionId}`
        : `/api/budget/crew-members?department_id=${departmentId}`;
      const res = await apiGet(url);
      if (!res.ok) throw new Error('Failed to fetch crew members');
      const data = await res.json();
      setMembers(data);
      // Returned as well as stored: `handleSaveEditMember` must re-seed the
      // day grid's baseline from the row the server JUST answered with, and
      // `members` state is not readable inside the same tick.
      return data;
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      if (!silent) setMembersLoading(false);
    }
    return null;
  }, [departmentId, selectedVersionId]);

  const fetchSubDepartments = useCallback(async () => {
    if (!departmentId) return;
    try {
      const res = await apiGet(`/api/sub-departments?department_id=${departmentId}`);
      // 🔻 THIS `return` CARRIED THE COMMENT `// silent — picker just shows
      // none`, WHICH DESCRIBED THE BUG RATHER THAN A DECISION — 2026-08-22.
      // The picker does not "show none": it is a controlled `<select>` whose
      // value falls back to `selectedIndex = 0`, so a crew member who IS in a
      // sub-department reads `— No sub-dept —`, and the only correction on
      // offer writes that emptiness back through `handleSetCrewSubDept`.
      // A non-ok response now takes the same path as a rejection.
      if (!res.ok) throw new Error('Failed to fetch sub-departments');
      const data = await res.json();
      if (Array.isArray(data)) setSubDepartments(data);
      setSubDepartmentsFailed(false);
    } catch {
      setSubDepartmentsFailed(true);
    }
  }, [departmentId]);

  // Assign/clear a crew member's sub-department. This is a membership edit on the
  // linked user (department_assignments.sub_department_id) via the shared,
  // buyer-permitted endpoint — NOT a crew_members/budget write — so it saves
  // immediately, independent of the crew Save button and version-lock state.
  const handleSetCrewSubDept = async (member, rawVal) => {
    if (!member?.user_id) return;
    const sub_department_id = rawVal ? parseInt(rawVal, 10) : null;
    try {
      setSavingSubDeptMemberId(member.id);
      const res = await apiPut(`/api/departments/${member.department_id}/members/${member.user_id}`, { sub_department_id });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to update sub-department'); }
      await fetchMembers(true);
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingSubDeptMemberId(null);
    }
  };

  const fetchAllowanceNames = useCallback(async () => {
    if (!departmentId) return;
    try {
      const res = await apiGet(`/api/budget/allowance-names?department_id=${departmentId}`);
      if (!res.ok) return; // silent — autocomplete is opportunistic
      const data = await res.json();
      if (Array.isArray(data)) setAllowanceNameOptions(data);
    } catch {
      /* silent — empty suggestions are fine */
    }
  }, [departmentId]);

  // The department's gags, for a job code's gag link. Silent on failure: the
  // link is optional and the job code list still renders without names.
  const fetchJobGags = useCallback(async () => {
    if (!departmentId) return;
    try {
      const res = await apiGet(`/api/gags?department_id=${departmentId}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setJobGags(data);
    } catch {
      /* silent — the gag link is optional */
    }
  }, [departmentId]);

  // Load role definitions once on mount
  useEffect(() => {
    fetchRoleDefinitions();
  }, [fetchRoleDefinitions]);

  // Load versions, cost codes, payment types, and crew data on department change
  useEffect(() => {
    if (departmentId) {
      fetchVersions();
      fetchCostCodes();
      fetchPaymentTypes();
      fetchGrades();
      fetchMembers();
      fetchSubDepartments();
      fetchAllowanceNames();
      fetchJobGags();
    }
  }, [departmentId, fetchVersions, fetchCostCodes, fetchPaymentTypes, fetchGrades, fetchMembers, fetchSubDepartments, fetchAllowanceNames, fetchJobGags]);

  // Load budgets when selected version changes
  useEffect(() => {
    fetchBudgets();
  }, [selectedVersionId, fetchBudgets]);

  // Phase 11 polish (post-Task 18): the version-switch useEffect that used to live
  // here was redundant — fetchMembers is a useCallback with
  // selectedVersionId in their dep arrays, so their identity changes on every
  // version switch, which already re-fires the outer dept-change effect above.
  // Keeping it caused a double GET on every version switch. Removed.

  // Fetch week data when expanding a member
  const fetchWeekData = useCallback(async (memberId, silent = false, captureOriginal = false) => {
    try {
      if (!silent) setWeekLoading(true);
      // Phase 11 / Task 18: week endpoint is per-member so version is already
      // implicit (crew_members.version_id resolves it server-side), but we still
      // forward the current selection as a defensive hint for clarity.
      const url = selectedVersionId
        ? `/api/budget/crew-members/${memberId}/weeks?version_id=${selectedVersionId}`
        : `/api/budget/crew-members/${memberId}/weeks`;
      const res = await apiGet(url);
      if (!res.ok) throw new Error('Failed to fetch week data');
      const data = await res.json();
      setWeekData(data);
      // Initialise overrides from existing data
      const overridesMap = {};
      for (const w of data) {
        overridesMap[w.week_start] = w.allocation;
      }
      setWeekOverrides(overridesMap);
      // Capture original for dirty tracking
      if (captureOriginal) {
        setOriginalWeekData(overridesMap);
      }
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      if (!silent) setWeekLoading(false);
    }
  }, [selectedVersionId]);

  // isDirty: track whether expand-to-edit form has unsaved changes
  const isMemberDirty = useMemo(() => {
    if (!originalMemberForm) return false;
    const formChanged = JSON.stringify(editMemberForm) !== JSON.stringify(originalMemberForm);
    const weeksChanged = originalWeekData !== null && JSON.stringify(weekOverrides) !== JSON.stringify(originalWeekData);
    // 🔑 THE DAY GRID IS PART OF THE DIRTY STATE. Swapping Saturday for
    // Monday on a 5-day week leaves the day COUNT — and therefore the
    // allocation — untouched, so `weeksChanged` above cannot see it, and the
    // collapse guard would let an unsaved schedule change fall on the floor.
    // Compared against what the week SHOWED before the edit — the same
    // resolver the grid and the save use — so a week an unsaved date change
    // brought into range is not reported dirty merely for existing.
    // 🔀 2026-08-22: this WAS a hand-rolled `.some(...)` here and an
    // all-but-identical one inside `buildDayPayloadForSave`. Two expressions
    // of one rule — "has any week's day set actually changed?" — is the shape
    // that costs money in this feature, so they are now ONE shared function.
    const daysChanged = dayPayloadHasChanges(
      Object.keys(weekDayOverlay), weekDayOverlay, weekDayBaseline, weekDayCurrentRange
    );
    return formChanged || weeksChanged || daysChanged;
  }, [editMemberForm, originalMemberForm, weekOverrides, originalWeekData, weekDayOverlay, weekDayBaseline, weekDayCurrentRange]);

  /* The ONE writer of the guard's form-dirty ref. Gated on level 2 being open,
   * because a dirty form in the TABLE view must keep its own remedy (the amber
   * pulse) rather than start raising the Gantt strip's three-outcome dialog. */
  useEffect(() => {
    ganttDetailsFormDirtyRef.current = ganttFormMemberId != null && isMemberDirty;
  }, [ganttFormMemberId, isMemberDirty]);

  /**
   * 🚨 ONE SAVE BUTTON NEEDS ONE DIRTY SIGNAL, AND `ganttDayDirty` IS NOT IT.
   *
   * `ganttDayDirty` is days + pending dates. At level 2 the same Save also
   * commits the FORM, so a typed rate override left the button DISABLED over an
   * unsaved edit - a control that refuses silently, which is the exact defect
   * the exit prompt was built to remove, arriving through the button instead of
   * the door. Measured: §5.5 of `ganttSecondExpand.test.js` was red on it.
   *
   * 🔑 IT IS A SECOND FLAG, NOT A WIDENING OF `ganttDayDirty`. That one still
   * means what it has always meant and is still what decides whether the strip
   * has a day payload to post, which weeks the exit prompt NAMES, and whether
   * `handleGanttDaySave` runs steps 2 and 3 at all. Widening it in place would
   * make a typed allowance look like a changed day set.
   */
  const ganttDetailsFormDirty = ganttFormMemberId != null
    && ganttFormMemberId === ganttDayMemberId
    && isMemberDirty;
  const ganttSurfaceDirty = ganttDayDirty || ganttDetailsFormDirty;

  /* 🔀 THE CREW TABLE ASKS, IT DOES NOT REFUSE (owner, 2026-10-06). A dirty
   * form used to refuse the click and pulse Save/Cancel amber for 2s, which
   * Pete could not see. It now raises the page's ONE three-way exit dialog
   * (`ganttDayExit`, kind 'table'); Discard or a save that LANDED re-runs the
   * held click with `force`, through the ref so it is the latest handler.
   * `ask: false` keeps the old silent refusal for the Gantt details toggle,
   * whose own guard (`guardGanttDayEdit`) already asked. Pinned by
   * `BudgetCostCodes.crewTableDirtyPrompt.test.js`; the pulse is deleted
   * (pre-deletion SHA 6ac2fa5a). */
  const handleExpandMember = (member, { force = false, ask = true } = {}) => {
    const holdForPrompt = (what) => {
      if (!ask) return;
      setGanttDayExit({
        kind: 'table',
        memberName: editMemberForm.name || 'This crew member',
        what,
        proceed: () => handleExpandMemberRef.current(member, { force: true }),
      });
    };
    if (expandedMemberId === member.id) {
      if (isMemberDirty && !force) {
        holdForPrompt(`close ${member.name}`);
        return;
      }
      collapseMember();
    } else {
      if (expandedMemberId && isMemberDirty && !force) {
        holdForPrompt(`open ${member.name}`);
        return;
      }
      // Populate edit form from member data
      const formData = {
        crew_grade_id: member.crew_grade_id,
        name: member.name,
        user_id: member.user_id || null,
        location: member.location || '',
        start_date: member.start_date ? member.start_date.slice(0, 10) : '',
        end_date: member.end_date ? member.end_date.slice(0, 10) : '',
        default_allocation: member.default_allocation ?? 1.0,
        computer_rental: member.computer_rental ?? '',
        computer_rental_cap: member.computer_rental_cap ?? '',
        weekly_rate_override: member.weekly_rate_override ?? '',
        // 🪤 `??`, never `||` — a stored 0 is a PRICED premium and `|| ''`
        // would hydrate it as blank, so an unrelated save would silently
        // revert the member to unpriced. Same rule as the grade form.
        sixth_day_rate_override: member.sixth_day_rate_override ?? '',
        seventh_day_rate_override: member.seventh_day_rate_override ?? '',
        // S2 overtime-rate overrides — hydrated only for keys the row carries.
        ...overtimeRateFormFrom(member, 'member'),
        notes: member.notes || '',
        user_email: member.user_email || '',
        user_phone: member.user_phone || '',
        allowance_name: member.allowance_name || '',
        allowance_amount: member.allowance_amount ?? '',
        allowance_cap: member.allowance_cap ?? '',
        allowance_name_2: member.allowance_name_2 || '',
        allowance_amount_2: member.allowance_amount_2 ?? '',
        allowance_cap_2: member.allowance_cap_2 ?? '',
        allowance_name_3: member.allowance_name_3 || '',
        allowance_amount_3: member.allowance_amount_3 ?? '',
        allowance_cap_3: member.allowance_cap_3 ?? '',
      };
      setEditMemberForm(formData);
      setOriginalMemberForm(formData);
      // The day grid starts from what the member ALREADY has; the overlay is
      // empty until the user touches a week. Seeded from the member's own
      // dates, not from `editMemberForm`, because the form has only just been
      // populated from them and the two cannot differ yet.
      setWeekDayOverlay({});
      setExpandedWeek(null);
      setWeekDayBaseline(buildWeekDayBaseline(
        member,
        generateWeeks(
          member.start_date ? member.start_date.slice(0, 10) : null,
          member.end_date ? member.end_date.slice(0, 10) : null
        )
      ));
      // 🚨 THE PREVIOUS MEMBER'S WEEK STATE DIES HERE, SYNCHRONOUSLY (review
      // C8, 2026-10-06). It used to survive until `fetchWeekData` resolved, so
      // a Save before it landed — or after it FAILED — PUT the previous
      // member's (possibly unsaved) allocations onto this one. `null`
      // `originalWeekData` = "weeks not loaded", which the save reads.
      setWeekOverrides({});
      setOriginalWeekData(null);
      setWeekData([]);
      expandGenerationRef.current += 1;   // a pending collapse timer must not undo this open
      setOpeningMemberId(member.id);
      setExpandedMemberId(member.id);
      setTimeout(() => setOpeningMemberId(null), 50); // trigger fade-in on next frame
      fetchWeekData(member.id, false, true);
    }
  };
  const handleExpandMemberRef = useRef(handleExpandMember);
  handleExpandMemberRef.current = handleExpandMember;

  // 🔻 `handleGanttMemberClick` WAS HERE AND IS DELETED (2026-08-24). It was
  // the click handler for the Gantt's member-name label, back when the name
  // navigated to the table view (guarding the open day strip, re-resolving
  // the member through `membersRef` for the batched-drag reason recorded at
  // that ref's declaration, then scrolling the row into focus). The name
  // stopped being a navigation on 2026-08-22 (it is a plain `<span>` now —
  // see `CrewGanttView.js`), and its last remaining caller, the `Full
  // Details` button, was removed the same day this comment was: "remove the
  // 'Full Details' link from the expanded gantt as the user gets to that
  // page from the table view." With both callers gone this was exactly the
  // dead scaffolding the 2026-08-23 code review flagged five instances of.
  // `guardGanttDayEdit` — which this handler called into — is NOT deleted:
  // it has five other live call sites in this file.

  // --- Version actions ---
  const handleCreateVersion = async () => {
    const trimmed = newVersionName.trim();
    if (!trimmed) return;
    try {
      setCreatingVersion(true);
      setError(null);
      const res = await apiPost('/api/budget/versions', {
        department_id: departmentId,
        version_name: trimmed,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create version');
      setNewVersionName('');
      setShowNewVersionInput(false);
      toast.success(`Version "${trimmed}" created`);
      await fetchVersions();
      setSelectedVersionId(data.id);
    } catch (err) {
      toast.error(err);
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleSetCurrent = async () => {
    if (!selectedVersionId) return;
    try {
      setSettingCurrent(true);
      setError(null);
      const res = await apiPost(`/api/budget/versions/${selectedVersionId}/set-current`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to set current version');
      toast.success('Version set as current');
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setSettingCurrent(false);
    }
  };

  const handleCloneVersion = async () => {
    if (!selectedVersionId) return;
    try {
      setCloningVersion(true);
      setError(null);
      const res = await apiPost(`/api/budget/versions/${selectedVersionId}/clone`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to clone version');
      toast.success('Version cloned');
      await fetchVersions();
      setSelectedVersionId(data.id);
    } catch (err) {
      toast.error(err);
    } finally {
      setCloningVersion(false);
    }
  };

  const handleRenameVersion = async () => {
    if (!selectedVersionId || !renameValue.trim()) return;
    try {
      setSavingRename(true);
      setError(null);
      const res = await apiPut(`/api/budget/versions/${selectedVersionId}`, { version_name: renameValue.trim() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to rename version');
      toast.success('Version renamed');
      setRenamingVersion(false);
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingRename(false);
    }
  };

  // Phase 11 / Task 20: lock / unlock handlers. Both call the new server
  // endpoints, refresh the versions list (so the is_locked flag flips in the
  // selector), and close the confirm modal.
  const handleLockVersion = async () => {
    if (!selectedVersionId) return;
    try {
      setLockingVersion(true);
      setError(null);
      const res = await apiPost(`/api/budget/versions/${selectedVersionId}/lock`);
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to lock version');
      toast.success('Version locked');
      setShowLockConfirm(false);
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setLockingVersion(false);
    }
  };

  const handleUnlockVersion = async () => {
    if (!selectedVersionId) return;
    try {
      setUnlockingVersion(true);
      setError(null);
      const res = await apiPost(`/api/budget/versions/${selectedVersionId}/unlock`);
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to unlock version');
      toast.success('Version unlocked');
      setShowUnlockConfirm(false);
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setUnlockingVersion(false);
    }
  };

  // "Make Baseline" — flips is_baseline on the server (atomic clear-others
  // + set-target). Triggers a version-list refresh so the (Baseline) badge
  // moves to the new anchor. The Variance Analysis on /budget/overview
  // re-derives its anchor via pdfCompareDefaultTargetId, which prefers
  // any is_baseline=1 row; live updates flow via useBudgetInvalidation.
  const handleSetBaseline = async () => {
    if (!selectedVersionId || isSelectedBaseline) return;
    const versionName = selectedVersion?.version_name || 'this version';
    if (!window.confirm(
      `Make "${versionName}" the baseline for Variance Analysis? `
      + `This changes which version the Cost Report compares Current against. `
      + `It does not modify any budget data.`
    )) return;
    try {
      setSettingBaseline(true);
      setError(null);
      const res = await apiPost(`/api/budget/versions/${selectedVersionId}/set-baseline`);
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to set baseline');
      toast.success(`Baseline set to ${versionName}`);
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setSettingBaseline(false);
    }
  };

  const handleDeleteVersion = async () => {
    if (!selectedVersionId || isSelectedCurrent) return;
    if (!window.confirm(`Delete budget version "${selectedVersion?.version_name}"? This cannot be undone.`)) return;
    try {
      setDeletingVersion(true);
      setError(null);
      const res = await apiDelete(`/api/budget/versions/${selectedVersionId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete version');
      toast.success('Version deleted');
      // Switch to current version after deleting
      const current = versions.find(v => v.is_current);
      if (current) setSelectedVersionId(current.id);
      await fetchVersions();
    } catch (err) {
      toast.error(err);
    } finally {
      setDeletingVersion(false);
    }
  };

  // --- Cost code CRUD ---
  const handleAddCostCode = async (e) => {
    e.preventDefault();
    if (!costCodeForm.code.trim() || !costCodeForm.name.trim()) return;
    // 🔴 MONEY — same shape as the five `crewMoneyFormError` sites: instant
    // feedback ahead of the request, never the gate, and the form is left
    // exactly as the user typed it. Without this a `'1,000'` budget would post
    // `null` and `String(budgetVal)` would then write "null" into the local
    // budget map, so the refusal has to come BEFORE the request, not after.
    if (isMalformedAmount(costCodeForm.budget_amount)) {
      setError(malformedMoneyMessage('Budget amount'));
      return;
    }
    try {
      setSavingCostCode(true);
      setError(null);
      const res = await apiPost('/api/budget/cost-codes', {
        department_id: departmentId,
        code: costCodeForm.code.trim(),
        name: costCodeForm.name.trim(),
        description: costCodeForm.description.trim(),
        sort_order: costCodes.length > 0 ? Math.max(...costCodes.map(c => c.sort_order ?? 0)) + 1 : 0,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add cost code');
      setCostCodes(prev => [...prev, data].sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code)));
      if (costCodeForm.budget_amount && selectedVersionId) {
        // 🚨 PAYLOAD — `toPayloadAmount`, never `parseFloat(x) || 0`. Blank is
        // already excluded by the `if` above, a typed `0` still sends `0`, and
        // an unreadable amount now sends `null` for the server to refuse by
        // name instead of arriving as a plausible number.
        const budgetVal = toPayloadAmount(costCodeForm.budget_amount, 0);
        // Phase 11 polish: surface lock-guard 423 with structured code so the
        // friendly "version is locked" toast fires instead of silently swallowing.
        const budgetRes = await apiPut('/api/budget/cost-code-budgets', {
          version_id: selectedVersionId,
          budgets: [{ cost_code_id: data.id, budget_amount: budgetVal }],
        });
        if (!budgetRes.ok) await apiThrow(budgetRes);
        setBudgetAmounts(prev => ({ ...prev, [data.id]: String(budgetVal) }));
      }
      setCostCodeForm({ code: '', name: '', description: '', budget_amount: '' });
      setShowAddCostCode(false);
      toast.success(`Cost code "${data.code}" added`);
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingCostCode(false);
    }
  };

  function costCodeFormFrom(cc) {
    return {
      code: cc.code,
      name: cc.name,
      description: cc.description || '',
      budget_amount: budgetAmounts[cc.id] || '0',
      // The adjustment edits through the row's Edit flow (owner, 2026-08-15).
      // String, like budget_amount — it feeds a CurrencyInput.
      spend_adjustment: String((adjustments[cc.id] && adjustments[cc.id].amount) || 0),
    };
  }

  // The actual row save. `adjustmentPayload` is null (adjustment unchanged —
  // nothing sent for it) or { value, note } already confirmed through the
  // ruled ConfirmDialog. Returns true on success so the dialog knows whether
  // to close; a failure leaves the row in edit mode with the failure
  // toasted, exactly like every other failed row save.
  const performRowSave = async (id, adjustmentPayload) => {
    // 🔴 MONEY — the edit twin of the check in `handleAddCostCode`. Both money
    // drafts on this row are checked: the budget amount, and the spend
    // adjustment, whose value was read with a bare `parseFloat` at the Row Save
    // click and so could reach the audited PATCH as a wrong number.
    if (isMalformedAmount(editCostCodeForm.budget_amount)) {
      setError(malformedMoneyMessage('Budget amount'));
      return false;
    }
    if (showAdjustmentColumn && isMalformedAmount(editCostCodeForm.spend_adjustment)) {
      setError(malformedMoneyMessage('Spend adjustment'));
      return false;
    }
    try {
      setSavingCostCode(true);
      setError(null);
      const res = await apiPut(`/api/budget/cost-codes/${id}`, {
        code: editCostCodeForm.code.trim(),
        name: editCostCodeForm.name.trim(),
        description: editCostCodeForm.description.trim(),
        sort_order: costCodes.find(c => c.id === id)?.sort_order ?? 0,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update cost code');
      // MERGE the PUT response over the existing row — never replace it.
      // PUT /cost-codes/:id answers with a bare cost_codes row (SELECT *) and
      // NEVER computes expenditure_to_date (only GET ?include_spend=1 does).
      // Replacing the row dropped the computed spend from state, so the Spent
      // cell fell to `|| 0` and showed ONLY the adjustment until a full
      // reload — the owner's 2026-08-15 staging report ("when i turn the
      // adjust price to 0 the spend to date goes to 0 as well"). The spread
      // keeps exactly the keys the response lacks; every column the PUT can
      // change is present in `data` and overwrites. Editing a row cannot
      // change its computed spend, so the kept value is still correct.
      const { sync_notice: _syncNotice, ...savedRow } = data; // the notice is a message, not a column
      setCostCodes(prev => prev.map(cc => cc.id === id ? { ...cc, ...savedRow } : cc).sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code)));
      // Also save budget amount if a version is selected
      if (selectedVersionId) {
        // 🚨 PAYLOAD — see the `handleAddCostCode` twin. Malformed is refused
        // at the top of this function, so this cannot post `null`.
        const budgetVal = toPayloadAmount(editCostCodeForm.budget_amount, 0);
        // Phase 11 polish: surface lock-guard 423 via apiThrow so friendlyErrorMessage
        // can take the structured `code: 'version_locked'` branch.
        const budgetRes = await apiPut('/api/budget/cost-code-budgets', {
          version_id: selectedVersionId,
          budgets: [{ cost_code_id: id, budget_amount: budgetVal }],
        });
        if (!budgetRes.ok) await apiThrow(budgetRes);
        setBudgetAmounts(prev => ({ ...prev, [id]: String(budgetVal) }));
      }
      // The adjustment stays its own audited PATCH — the contract — merely
      // triggered from the row-save path now. Throws on failure (handled below).
      if (adjustmentPayload) {
        await handleSaveSpendAdjustment(id, adjustmentPayload.value, adjustmentPayload.note);
      }
      // The landed save collapses the expanded row (was `setEditingCostCodeId(null)`).
      costCodeEditor.collapse();
      // 🔑 THE SHARED TOAST, NEVER `setSuccessMessage` — THIS LINE WAS THE PAGE
      // JUMP (owner, staging 2026-09-23: "when cliking save after editing a cost
      // code the page jumps by a row"). `successMessage` renders an IN-FLOW
      // banner inside the header band, above every section, so a row Save
      // pushed the whole page down and its own 3s auto-clear pulled it back up.
      // MEASURED in Chromium on the real build, row CC06 at 1440x900: Edit 0px,
      // Cancel 0px, Save +54px (band 45 → 99px), then -54px ~3s later; at 1280
      // the band already wraps, so the same banner cost only +5px — the jump's
      // size depended on the viewer's width. Focus (lands on <body>, no
      // scroll), row identity (the <tr> is reused, key `cc.id`) and row order
      // (no refetch; the merge re-sorts by sort_order/code) were measured too
      // and move nothing. The toast host is a `fixed` portal
      // (`contexts/ToastContext.js`), so announcing here cannot move a pixel.
      // 🔻 CORRECTED 2026-09-23 — every OTHER `setSuccessMessage`/async-failure
      // `setError` caller on this page has SINCE moved to this same shared
      // toast (owner: "Yes, all of them"); `successMessage` state is deleted.
      // `error` survives for the pre-submit validation guards alone (money
      // format, "select a version first") — see the `error` declaration above.
      // Pinned by `BudgetCostCodes.saveNoJump.test.js` §1.
      // M-8: a linked job code's rename that could NOT reach its gag (the gag is in the bin) says so.
      toast.success(data.sync_notice ? `Cost code "${data.code}" updated. ${data.sync_notice}` : `Cost code "${data.code}" updated`);
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    } finally {
      setSavingCostCode(false);
    }
  };

  // Row Save click. If the adjustment draft CHANGED, the ruled warning
  // dialog fires HERE — before any request at all (the whole save waits on
  // Continue; Cancel of the dialog aborts the entire save, keeps the row in
  // edit mode, and sends NOTHING). Unchanged adjustment → save as always,
  // no dialog. The row's own Cancel button reverts the adjustment draft with
  // every other field (it just drops editCostCodeForm).
  const handleSaveEditCostCode = async (id) => {
    // 🔑 THE MALFORMED CHECK IS DELIBERATELY *NOT* HERE, and that is worth a
    // line. An unreadable adjustment must not reach the warning dialog — the
    // user would be asked to confirm a £1 change they typed as £1,000 — but the
    // refusal belongs in `shouldConfirmAdjustmentSave` (which now answers false
    // for it, so no dialog opens) and in `performRowSave` (which shows the
    // message). An early `return;` here instead would read as the dialog's own
    // gate and break §3.5 of `BudgetCostCodes.spendAdjustment.test.js`, which
    // pins the ORDER of this function: gate → pend → return → performRowSave.
    const saved = (adjustments[id] && adjustments[id].amount) || 0;
    if (showAdjustmentColumn && shouldConfirmAdjustmentSave(saved, editCostCodeForm.spend_adjustment)) {
      setAdjustmentNoteDraft((adjustments[id] && adjustments[id].note) || '');
      // `toPayloadAmount`, never `parseFloat` — this value goes to the audited
      // spend-adjustment PATCH. Malformed is already refused above.
      setPendingAdjustmentSave({ id, value: toPayloadAmount(editCostCodeForm.spend_adjustment, null) });
      return; // the ConfirmDialog's Continue resumes the save
    }
    // Resolves performRowSave's true/false — the unsaved-row prompt reads it.
    return performRowSave(id, null);
  };

  const handleAdjustmentConfirmCancel = () => {
    // Abort the WHOLE save: no request was made, the row stays in edit mode
    // with the typed values intact so the user can fix or Cancel the row.
    setPendingAdjustmentSave(null);
  };

  const handleAdjustmentConfirmContinue = async () => {
    if (!pendingAdjustmentSave) return;
    const ok = await performRowSave(pendingAdjustmentSave.id, {
      value: pendingAdjustmentSave.value,
      note: adjustmentNoteDraft.trim(),
    });
    // Keep the dialog (and the typed reason) on failure for a retry — the
    // toast explains; success closes everything.
    if (ok) setPendingAdjustmentSave(null);
  };

  const handleDeleteCostCode = async (cc) => {
    if (!window.confirm(`Deactivate cost code "${cc.code} — ${cc.name}"? It will no longer appear in lists.`)) return;
    try {
      setError(null);
      const res = await apiDelete(`/api/budget/cost-codes/${cc.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate cost code');
      setCostCodes(prev => prev.filter(c => c.id !== cc.id));
      // Delete lives in the expanded panel now; the panel goes with its row.
      if (costCodeEditor.expandedId === cc.id) costCodeEditor.reset();

      toast.success(`Cost code "${cc.code}" deactivated`);
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
    } catch (err) {
      toast.error(err);
    }
  };

  /* ═══════════════════════════════════════════════
     JOB COST CODES (owner ruling 2026-10-28)
     ═══════════════════════════════════════════════
     The rule → `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28. */
  const handleAddJobCode = async (e) => {
    e.preventDefault();
    if (!jobCodeForm.name.trim()) return;
    if (isMalformedAmount(jobCodeForm.budget_amount)) {
      setError(malformedMoneyMessage('Budget amount'));
      return;
    }
    try {
      setSavingJobCode(true);
      setError(null);
      const res = await apiPost('/api/budget/cost-codes', {
        department_id: departmentId,
        is_job: true,
        // Blank → the server numbers it (J01, J02 …).
        code: jobCodeForm.code.trim(),
        name: jobCodeForm.name.trim(),
        gag_id: jobCodeForm.gag_id === '' ? null : Number(jobCodeForm.gag_id),
        sort_order: costCodes.length > 0 ? Math.max(...costCodes.map(c => c.sort_order ?? 0)) + 1 : 0,
      });
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to add job code');
      setCostCodes(prev => [...prev, data].sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code)));
      if (jobCodeForm.budget_amount && selectedVersionId) {
        const budgetVal = toPayloadAmount(jobCodeForm.budget_amount, 0);
        const budgetRes = await apiPut('/api/budget/cost-code-budgets', {
          version_id: selectedVersionId,
          budgets: [{ cost_code_id: data.id, budget_amount: budgetVal }],
        });
        if (!budgetRes.ok) await apiThrow(budgetRes);
        setBudgetAmounts(prev => ({ ...prev, [data.id]: String(budgetVal) }));
      }
      setJobCodeForm({ code: '', name: '', gag_id: '', budget_amount: '' });
      setShowAddJobCode(false);
      toast.success(`Job code "${data.code}" added`);
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingJobCode(false);
    }
  };

  // 🔑 "N orders found for this job — link them to J01?" — offered only to a
  // caller holding BOTH capabilities the server's relink routes require in
  // this department (`canManageBudget` AND `canManageDepartmentOrders`, the
  // gate on changing a line's cost code). Orders placed before a job code
  // existed are NEVER re-coded automatically (owner ruling 3): the buyer sees
  // the list and confirms.
  const canRelinkJobOrders = !!isAdmin || !!(capabilities && capabilities.canManageBudget && capabilities.canManageDepartmentOrders);
  // Only the job codes that HAVE a gag can have orders "for this job".
  const relinkKey = jobCodes.filter((cc) => cc.gag_id != null).map((cc) => `${cc.id}:${cc.gag_id}`).join(',');
  const fetchRelinkCandidates = useCallback(async (codeIds) => {
    const results = await Promise.all(codeIds.map(async (id) => {
      try {
        const res = await apiGet(`/api/budget/job-codes/${id}/relink-candidates`);
        if (!res.ok) return [id, []];
        const data = await res.json();
        return [id, Array.isArray(data && data.orders) ? data.orders : []];
      } catch {
        return [id, []];
      }
    }));
    setRelinkCandidates((prev) => ({ ...prev, ...Object.fromEntries(results) }));
  }, []);
  useEffect(() => {
    if (!canRelinkJobOrders || !relinkKey) { setRelinkCandidates({}); return; }
    fetchRelinkCandidates(relinkKey.split(',').map((pair) => Number(pair.split(':')[0])));
  }, [canRelinkJobOrders, relinkKey, fetchRelinkCandidates]);

  const handleConfirmRelink = async () => {
    if (!relinkTarget) return;
    const orders = relinkCandidates[relinkTarget.id] || [];
    if (orders.length === 0) { setRelinkTarget(null); return; }
    try {
      setRelinking(true);
      const res = await apiPost(`/api/budget/job-codes/${relinkTarget.id}/relink`, {
        order_ids: orders.map((o) => o.id),
      });
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to link the orders to this job code');
      toast.success(`${data.orders} order${data.orders === 1 ? '' : 's'} linked to ${relinkTarget.code}`);
      setRelinkTarget(null);
      // The spend moved between codes: re-read it, and what is still unlinked.
      fetchCostCodes();
      fetchRelinkCandidates([relinkTarget.id]);
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
    } catch (err) {
      toast.error(err);
    } finally {
      setRelinking(false);
    }
  };

  /** The gag a job code is linked to, from the department's gag list. */
  const jobGagFor = (cc) => (cc.gag_id == null ? null : jobGags.find((g) => Number(g.id) === Number(cc.gag_id)) || null);
  /** `· gag Sc 42` / `· gag` / `· no gag` beside the job name. */
  const jobGagLabel = (cc) => {
    if (cc.gag_id == null) return '· no gag';
    const gag = jobGagFor(cc);
    return gag && gag.scenes_display ? `· gag Sc ${gag.scenes_display}` : '· gag';
  };
  /** The "N orders found for this job — link them to J01?" prompt, or null. */
  const renderRelinkPrompt = (cc) => {
    const orders = relinkCandidates[cc.id] || [];
    if (!canRelinkJobOrders || orders.length === 0) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setRelinkTarget(cc); }}
        title={`Orders for ${cc.name} that use another cost code. You see the list and confirm before any move.`}
        className="block mt-1 text-xs text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-200 text-left whitespace-normal"
        data-testid={`job-relink-${cc.id}`}
      >
        {orders.length} order{orders.length === 1 ? '' : 's'} found for this job — link {orders.length === 1 ? 'it' : 'them'} to {cc.code}?
      </button>
    );
  };

  /**
   * THE cost code's expanded details. 🔀 2026-10-25: on the DESKTOP row
   * (`'row'`) every field is edited in its own cell, the Breakdown's model,
   * so this holds only Delete (Save / Cancel are the bar above). The PHONE
   * card (`'card'`) keeps the full form: code, name, description,
   * budget (with a version selected) and the manual SPEND ADJUSTMENT, then
   * Save Changes / Cancel / Delete. 🔑 Save is `handleSaveEditCostCode`
   * UNCHANGED: a changed adjustment still raises the ruled warning before any
   * request and still goes out as its own audited PATCH. `variant` `'row'` /
   * `'card'` as on Crew Grades (the split is CSS; both are in the DOM).
   * READ-ONLY viewer: expands to the full description and the adjustment note.
   */
  const renderCostCodePanel = (cc, variant) => {
    const fieldCls = variant === 'card'
      ? 'w-full h-11 border border-gray-300 dark:border-gray-600 rounded px-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base'
      : inputCls;
    const set = (key) => (v) => costCodeEditor.setField(key, v);
    const note = adjustments[cc.id]?.note;
    if (!canEdit) {
      return (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm" data-testid={`cc-${variant === 'row' ? 'details' : 'panel'}-${variant}-${cc.id}`}>
          <dt className="text-gray-500 dark:text-gray-400">Description</dt>
          <dd className="text-gray-800 dark:text-gray-200 break-words">{cc.description || '—'}</dd>
          {note ? (
            <>
              <dt className="text-gray-500 dark:text-gray-400">Adjustment note</dt>
              <dd className="text-gray-800 dark:text-gray-200 break-words">{note}</dd>
            </>
          ) : null}
        </dl>
      );
    }
    // 🔀 2026-10-25: on the DESKTOP row every field is edited in its own cell
    // (the Breakdown's model), so nothing is repeated here — only Delete.
    // Delete is in the row's ⋯ menu (owner ruling A, 2026-10-25): no panel.
    if (variant === 'row') return null;
    return (
      <div className="space-y-4" data-testid={`cc-panel-${variant}-${cc.id}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className={FIELD_LABEL_CLASS}>Code</label>
            <input type="text" value={editCostCodeForm.code ?? ''} onChange={(e) => costCodeEditor.setField('code', e.target.value)} className={fieldCls} data-testid={`edit-cc-code-${variant}`} />
          </div>
          <div className="lg:col-span-2">
            <label className={FIELD_LABEL_CLASS}>Name</label>
            <input type="text" value={editCostCodeForm.name ?? ''} onChange={(e) => costCodeEditor.setField('name', e.target.value)} className={fieldCls} data-testid={`edit-cc-name-${variant}`} />
            {cc.is_job && cc.gag_id != null && (
              <RenameSyncNote where="Breakdown" testId={`cc-name-sync-note-${variant}-${cc.id}`}
                changed={String(editCostCodeForm.name ?? '').trim() !== String(cc.name || '').trim()} />
            )}
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={FIELD_LABEL_CLASS}>Description</label>
            <input type="text" value={editCostCodeForm.description ?? ''} onChange={(e) => costCodeEditor.setField('description', e.target.value)} className={fieldCls} data-testid={`edit-cc-description-${variant}`} />
          </div>
          {selectedVersionId && (
            <div>
              <label className={FIELD_LABEL_CLASS}>Budget (£)</label>
              <CurrencyInput
                value={editCostCodeForm.budget_amount}
                onChange={set('budget_amount')}
                placeholder="£0.00"
                className={`${fieldCls} text-right`}
                testId={`edit-cc-budget-${variant}`}
              />
            </div>
          )}
          {showAdjustmentColumn && (
            <div>
              <label className={FIELD_LABEL_CLASS}>Adjustment (£)</label>
              <CurrencyInput
                allowNegative
                value={editCostCodeForm.spend_adjustment}
                onChange={set('spend_adjustment')}
                placeholder="£0.00"
                className={`${fieldCls} text-right`}
                testId={`edit-cc-adjustment-${variant}`}
              />
            </div>
          )}
        </div>
        <RowEditActions
          onSave={() => handleSaveEditCostCode(cc.id)}
          onCancel={costCodeEditor.collapse}
          onDelete={() => handleDeleteCostCode(cc)}
          deleteLabel={cc.is_job ? 'Delete job code' : 'Delete cost code'}
          saving={savingCostCode}
          dirty={costCodeEditor.isDirty}
          testIdPrefix={`cc-panel-${variant}-${cc.id}`}
        />
      </div>
    );
  };

  // --- Payment type CRUD ---
  /** Resolves `true` only when the PUT landed (the editor collapses on it). */
  const handleSaveEditPaymentType = async (id) => {
    try {
      setSavingPaymentType(true);
      setError(null);
      const res = await apiPut(`/api/budget/payment-types/${id}`, {
        name: editPaymentTypeForm.name.trim(),
        description: editPaymentTypeForm.description.trim(),
        sort_order: paymentTypes.find(p => p.id === id)?.sort_order ?? 0,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update payment type');
      setPaymentTypes(prev => prev.map(pt => pt.id === id ? data : pt).sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)));
      toast.success(`Payment type "${data.name}" updated`);
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    } finally {
      setSavingPaymentType(false);
    }
  };

  const handleDeletePaymentType = async (pt) => {
    if (!window.confirm(`Deactivate payment type "${pt.name}"? It will no longer appear in lists.`)) return;
    try {
      setError(null);
      const res = await apiDelete(`/api/budget/payment-types/${pt.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate payment type');
      setPaymentTypes(prev => prev.filter(p => p.id !== pt.id));
      if (paymentTypeEditor.expandedId === pt.id) paymentTypeEditor.reset();
      toast.success(`Payment type "${pt.name}" deactivated`);
    } catch (err) {
      toast.error(err);
    }
  };

  /** THE payment type's expanded details. 🔀 2026-10-25: the DESKTOP row
   *  (`'row'`) edits name + description in its own cells (the Breakdown's
   *  model), so this holds only Delete; the PHONE card keeps name,
   *  description and Save Changes / Cancel / Delete. READ-ONLY viewer: the
   *  full description. */
  const renderPaymentTypePanel = (pt, variant) => {
    const fieldCls = variant === 'card'
      ? 'w-full h-11 border border-gray-300 dark:border-gray-600 rounded px-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base'
      : inputCls;
    if (!canEdit) {
      return (
        <div className="text-sm text-gray-800 dark:text-gray-200 break-words" data-testid={`pt-${variant === 'row' ? 'details' : 'panel'}-${variant}-${pt.id}`}>
          <span className="text-gray-500 dark:text-gray-400">Description: </span>{pt.description || '—'}
        </div>
      );
    }
    // Delete is in the row's ⋯ menu (owner ruling A, 2026-10-25): no panel.
    if (variant === 'row') return null;
    return (
      <div className="space-y-4" data-testid={`pt-panel-${variant}-${pt.id}`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={FIELD_LABEL_CLASS}>Name</label>
            <input type="text" value={editPaymentTypeForm.name ?? ''} onChange={(e) => paymentTypeEditor.setField('name', e.target.value)} className={fieldCls} data-testid={`edit-pt-name-${variant}`} />
          </div>
          <div className="sm:col-span-2">
            <label className={FIELD_LABEL_CLASS}>Description</label>
            <input type="text" value={editPaymentTypeForm.description ?? ''} onChange={(e) => paymentTypeEditor.setField('description', e.target.value)} className={fieldCls} data-testid={`edit-pt-description-${variant}`} />
          </div>
        </div>
        <RowEditActions
          onSave={paymentTypeEditor.saveAndCollapse}
          onCancel={paymentTypeEditor.collapse}
          onDelete={() => handleDeletePaymentType(pt)}
          deleteLabel="Delete payment type"
          saving={savingPaymentType}
          dirty={paymentTypeEditor.isDirty}
          testIdPrefix={`pt-panel-${variant}-${pt.id}`}
        />
      </div>
    );
  };

  const handleReorderPaymentType = async (idx, direction) => {
    const newList = [...paymentTypes];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[idx], newList[swapIdx]] = [newList[swapIdx], newList[idx]];
    const reordered = newList.map((pt, i) => ({ ...pt, sort_order: i }));
    setPaymentTypes(reordered);
    // Save each updated sort_order
    for (const pt of reordered) {
      await apiPut(`/api/budget/payment-types/${pt.id}`, { name: pt.name, description: pt.description || '', sort_order: pt.sort_order });
    }
  };

  /* ═══════════════════════════════════════════════
     CREW GRADES CRUD
     ═══════════════════════════════════════════════ */
  const handleAddGrade = async (e) => {
    e.preventDefault();
    if (!gradeForm.name.trim()) return;
    // 🔴 MONEY 2026-08-23 — instant feedback, never the gate; the server refuses
    // independently. Ahead of the request so a refusal costs no round trip and
    // leaves the form exactly as the user left it.
    const moneyError = crewMoneyFormError(gradeMoneyEntries(gradeForm));
    if (moneyError) {
      setError(moneyError);
      return;
    }
    try {
      setSavingGrade(true);
      setError(null);
      const res = await apiPost('/api/budget/crew-grades', {
        department_id: departmentId,
        name: gradeForm.name.trim(),
        // 🚨 PAYLOAD — `toPayloadAmount(x, 0)`, never `parseFloat(x) || 0`.
        // These two columns default to zero (nullable, DEFAULT 0), so BLANK still
        // means `0` and that has not changed; what changed is that an amount we
        // cannot read now sends `null` for the server to refuse by name rather
        // than `parseFloat('1,000') === 1`. A typed `0` is untouched.
        weekly_rate: toPayloadAmount(gradeForm.weekly_rate, 0),
        computer_rental: toPayloadAmount(gradeForm.computer_rental, 0),
        default_role_id: gradeForm.default_role_id ? parseInt(gradeForm.default_role_id, 10) : null,
        // 🚨 MONEY — `nullableRate`, NEVER the `|| 0` used by the two lines
        // above. Those columns DEFAULT 0 (nullable, but blank sends 0) and have no
        // unpriced state to lose; these are nullable with no DEFAULT, and the
        // difference between `null` and `0` is what a 6th day costs:
        //   null → UNSTATED, the server applies the 1.5×/2× default
        //   0    → refused at save since 2026-08-23 (no free days); a value
        //          already STORED still costs zero, the read side is untouched
        // See helpers/crewCost.js#resolvePremiumRates and `nullableRate` above.
        sixth_day_rate: nullableRate(gradeForm.sixth_day_rate),
        seventh_day_rate: nullableRate(gradeForm.seventh_day_rate),
        sort_order: grades.length > 0 ? Math.max(...grades.map(g => g.sort_order ?? 0)) + 1 : 0,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add crew grade');
      setGrades(prev => [...prev, data].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)));
      setGradeForm({ name: '', weekly_rate: '', computer_rental: '', default_role_id: '', sixth_day_rate: '', seventh_day_rate: '' });
      setShowAddGrade(false);

      toast.success(`Grade "${data.name}" added`);
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingGrade(false);
    }
  };

  function gradeFormFrom(g) {
    return {
      name: g.name,
      weekly_rate: g.weekly_rate ?? '',
      computer_rental: g.computer_rental ?? '',
      default_role_id: g.default_role_id ?? '',
      // 🪤 `??`, never `||`. A stored 0 is a PRICED premium ("this day is
      // unpaid") and `g.sixth_day_rate || ''` would render it as blank —
      // so opening the form and saving an unrelated field would silently
      // revert the grade to unpriced, moving money nobody touched.
      sixth_day_rate: g.sixth_day_rate ?? '',
      seventh_day_rate: g.seventh_day_rate ?? '',
      // S2 overtime rates — hydrated only for keys the row carries.
      ...overtimeRateFormFrom(g, 'grade'),
      // Grade allowances (2026-10-25) — the same rule: only keys the row carries.
      ...gradeAllowanceFormFrom(g),
    };
  }

  /** Resolves `true` only when the PUT landed (the editor collapses on it). */
  const handleSaveEditGrade = async (id) => {
    // See handleAddGrade for why this runs ahead of the request.
    // 🚨 The allowance CAPS are on this list (review F5): a malformed cap would
    // otherwise go out as null = "no cap" and uncap every member following it.
    const moneyError = crewMoneyFormError([
      ...gradeMoneyEntries(editGradeForm),
      ...overtimeRateMoneyEntries(editGradeForm, 'grade'),
      ...gradeAllowanceMoneyEntries(editGradeForm),
    ]);
    if (moneyError) {
      setError(moneyError);
      return false;
    }
    try {
      setSavingGrade(true);
      setError(null);
      const res = await apiPut(`/api/budget/crew-grades/${id}`, {
        name: editGradeForm.name.trim(),
        // See the POST twin above for why these are `toPayloadAmount(x, 0)`.
        weekly_rate: toPayloadAmount(editGradeForm.weekly_rate, 0),
        computer_rental: toPayloadAmount(editGradeForm.computer_rental, 0),
        default_role_id: editGradeForm.default_role_id ? parseInt(editGradeForm.default_role_id, 10) : null,
        // See the POST twin above for why this is `nullableRate` and the two
        // lines before it are `|| 0`.
        sixth_day_rate: nullableRate(editGradeForm.sixth_day_rate),
        seventh_day_rate: nullableRate(editGradeForm.seventh_day_rate),
        // S2 overtime rates: blank → null (UNPRICED, stays blank), 0 allowed.
        ...overtimeRatePayload(editGradeForm, 'grade'),
        // Grade allowances (2026-10-25): the Computer cap + the three named
        // slots. A key the row did not carry is omitted (server keeps it).
        ...gradeAllowancePayload(editGradeForm),
        sort_order: grades.find(g => g.id === id)?.sort_order ?? 0,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update crew grade');
      setGrades(prev => prev.map(g => g.id === id ? data : g).sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)));

      toast.success(`Grade "${data.name}" updated`);
      // Refresh members since grade rate may have changed
      fetchMembers();
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    } finally {
      setSavingGrade(false);
    }
  };

  const handleDeleteGrade = async (g) => {
    if (!window.confirm(`Deactivate grade "${g.name}"? It will no longer appear in lists.`)) return;
    try {
      setError(null);
      const res = await apiDelete(`/api/budget/crew-grades/${g.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate grade');
      setGrades(prev => prev.filter(x => x.id !== g.id));
      // Delete lives in the expanded panel now; the panel goes with its row.
      if (gradeEditor.expandedId === g.id) gradeEditor.reset();

      toast.success(`Grade "${g.name}" deactivated`);
    } catch (err) {
      toast.error(err);
    }
  };

  /**
   * THE grade's expanded details. Rendered twice per open grade — `variant`
   * `'row'` under the desktop row, `'card'` inside the phone card (the split is
   * CSS) — so every testid carries the variant.
   * 🔀 2026-10-25, THE BREAKDOWN'S MODEL: on the DESKTOP row, name, rates,
   * premiums and access are edited IN THE ROW'S OWN CELLS, so `'row'` holds
   * only what has no column — the six S2 overtime inputs — and Delete (Save /
   * Cancel are the "Editing this row" bar above it). A field the row edits is
   * never repeated here. The PHONE card has no cells to edit in, so `'card'`
   * keeps every field and its own Save Changes / Cancel / Delete.
   * 🔑 READ-ONLY VIEWER: the row still expands (Crew Members does the same),
   * to the resolved overtime list — every other figure is already on the row.
   */
  const renderGradePanel = (g, variant) => {
    const fieldCls = variant === 'card'
      ? 'w-full h-11 border border-gray-300 dark:border-gray-600 rounded px-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base'
      : inputCls;
    const set = (key) => (v) => gradeEditor.setField(key, v);
    if (!canEdit) {
      return (
        <div className="space-y-2" data-testid={`grade-${variant === 'row' ? 'details' : 'panel'}-${variant}-${g.id}`}>
          <div className={FIELD_LABEL_CLASS}>Overtime rates</div>
          <OvertimeRatesSummary rates={g.overtime_rates} agreementRates={g.agreement_rates} formatMoney={formatMoney} testId={`grade-panel-overtime-${variant}-${g.id}`} />
          <div className={`${FIELD_LABEL_CLASS} pt-2`}>Allowances</div>
          <GradeAllowancesSummary allowances={g.allowances} formatMoney={formatMoney} testId={`grade-panel-allowances-${variant}-${g.id}`} />
        </div>
      );
    }
    // The allowance list (2026-10-25) — ONE component for the row and the card.
    const allowanceFields = (
      <GradeAllowanceFields
        form={editGradeForm}
        onChange={(key, v) => gradeEditor.setField(key, v)}
        fieldClassName={fieldCls}
        nameOptions={allowanceNameOptions}
        testIdPrefix={`edit-grade-allowance-${variant}`}
      />
    );
    if (variant === 'row') {
      return (
        <div className="space-y-3" data-testid={`grade-details-row-${g.id}`}>
          {roleDefinitionsFailed && (
            <GradeAccessRightsPicker loadFailed value={editGradeForm.default_role_id} onChange={set('default_role_id')} roleDefinitions={roleDefinitions} />
          )}
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Overtime rates</div>
            <OvertimeRateFields
              mode="grade"
              form={editGradeForm}
              onChange={(key, v) => gradeEditor.setField(key, v)}
              resolved={g.overtime_rates} agreementRates={g.agreement_rates}
              formatMoney={formatMoney}
              inputClassName={`${fieldCls} text-right`}
              testIdPrefix="edit-grade-overtime-row"
              // Hourly (£) is a ROW column now — edited in its cell above.
              omit={['hourly_rate']}
            />
          </div>
          {allowanceFields}
        </div>
      );
    }
    return (
      <div className="space-y-4" data-testid={`grade-panel-${variant}-${g.id}`}>
        {/* `items-end`: a label that wraps at this width must not push its
            input off the line; bottom-aligning keeps the row of inputs level. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className={FIELD_LABEL_CLASS}>Grade Name</label>
            <input
              type="text"
              value={editGradeForm.name ?? ''}
              onChange={(e) => gradeEditor.setField('name', e.target.value)}
              className={fieldCls}
              data-testid={`edit-grade-name-${variant}`}
            />
          </div>
          <div className={PANEL_FIELD_CELL}>
            <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="Weekly Rate (£)">Weekly Rate (£)</label>
            <CurrencyInput
              value={editGradeForm.weekly_rate}
              onChange={set('weekly_rate')}
              className={`${fieldCls} text-right`}
              testId={`edit-grade-weekly-rate-${variant}`}
            />
          </div>
          {/* 🔀 2026-10-25: the Computer amount moved into the allowance list
              below (its first, name-fixed row) — one editor per field. */}
          <div className={PANEL_FIELD_CELL} title={premiumPlaceholder(6, { weekly: editGradeForm.weekly_rate, formatMoney, rates: g.agreement_rates }).title}>
            <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="6th Day Rate (£)">6th Day Rate (£)</label>
            <CurrencyInput
              value={editGradeForm.sixth_day_rate}
              onChange={set('sixth_day_rate')}
              placeholder={premiumPlaceholder(6, { weekly: editGradeForm.weekly_rate, formatMoney, rates: g.agreement_rates }).placeholder}
              className={`${fieldCls} text-right`}
              testId={`edit-grade-sixth-day-rate-${variant}`}
            />
          </div>
          <div className={PANEL_FIELD_CELL} title={premiumPlaceholder(7, { weekly: editGradeForm.weekly_rate, formatMoney, rates: g.agreement_rates }).title}>
            <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="7th Day Rate (£)">7th Day Rate (£)</label>
            <CurrencyInput
              value={editGradeForm.seventh_day_rate}
              onChange={set('seventh_day_rate')}
              placeholder={premiumPlaceholder(7, { weekly: editGradeForm.weekly_rate, formatMoney, rates: g.agreement_rates }).placeholder}
              className={`${fieldCls} text-right`}
              testId={`edit-grade-seventh-day-rate-${variant}`}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <GradeAccessRightsPicker
              value={editGradeForm.default_role_id}
              onChange={set('default_role_id')}
              roleDefinitions={roleDefinitions}
              loadFailed={roleDefinitionsFailed}
              className={fieldCls}
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Overtime rates</div>
          <OvertimeRateFields
            mode="grade"
            form={editGradeForm}
            onChange={(key, v) => gradeEditor.setField(key, v)}
            resolved={g.overtime_rates} agreementRates={g.agreement_rates}
            formatMoney={formatMoney}
            inputClassName={`${fieldCls} text-right`}
            testIdPrefix={`edit-grade-overtime-${variant}`}
          />
        </div>
        {allowanceFields}
        <RowEditActions
          onSave={gradeEditor.saveAndCollapse}
          onCancel={gradeEditor.collapse}
          onDelete={() => handleDeleteGrade(g)}
          deleteLabel="Delete grade"
          saving={savingGrade}
          dirty={gradeEditor.isDirty}
          testIdPrefix={`grade-panel-${variant}-${g.id}`}
        />
      </div>
    );
  };

  /* ═══════════════════════════════════════════════
     CREW MEMBERS CRUD
     ═══════════════════════════════════════════════ */
  // ── Double-submit guard + Idempotency-Key on the crew-member create form
  // (2026-08-13). `savingMember` was a state flag set before the first await —
  // the fast-double-click hole; a doubled submit is two crew rows in the
  // version. The guard's ref drops the second call synchronously; the key is
  // minted on arm, reused if the attempt fails and is retried, cleared on
  // success (the server treats the header as optional). The invite below gets
  // its own pair — a create and an invitation are separate attempts.
  const { guard: guardAddMember } = useSubmitGuard();
  const memberIdem = useIdempotencyKey();
  const { guard: guardSendInvite } = useSubmitGuard();
  const invitationIdem = useIdempotencyKey();

  const handleAddMember = (e) => {
    e.preventDefault();
    if (!memberForm.name.trim() || !memberForm.crew_grade_id) return;
    // Phase 11 / Task 18: writes must target a specific budget version. If the
    // user has no version selected (rare — they'd need to dismiss the selector
    // before clicking + Add Member), surface a clear error instead of letting
    // the server auto-create a Baseline that may not match user intent.
    if (!selectedVersionId) {
      setError('Select a budget version first');
      return;
    }
    // 🔴 MONEY 2026-08-23 — see handleAddGrade. Placed OUTSIDE guardAddMember so
    // a refused entry never arms the double-submit guard or mints an
    // idempotency key for a request that is not going to be sent.
    const moneyError = crewMoneyFormError(memberMoneyEntries(memberForm));
    if (moneyError) {
      setError(moneyError);
      return;
    }
    return guardAddMember(async () => {
    try {
      setSavingMember(true);
      setError(null);
      const payload = {
        department_id: departmentId,
        version_id: selectedVersionId,
        crew_grade_id: parseInt(memberForm.crew_grade_id, 10),
        name: memberForm.name.trim(),
        user_id: memberForm.user_id || null,
        location: memberForm.location.trim(),
        start_date: memberForm.start_date || null,
        end_date: memberForm.end_date || null,
        // 🚨 PAYLOAD — `toPayloadAmount`, never `parseFloat`. The empty-string
        // ternaries are GONE, not because they were wrong (they correctly kept
        // a typed `0` off `||`) but because `toPayloadAmount` already treats
        // blank as the caller's default and it also treats a WHITESPACE-ONLY
        // field as blank, which `!== ''` does not. A typed `0` still sends `0`.
        // 🚨 `toPayloadNumber` — ALLOCATION IS NOT MONEY (2026-09-22). See the
        // PUT twin in `buildMemberPutPayload` for the widening this repairs.
        default_allocation: toPayloadNumber(memberForm.default_allocation, null),
        computer_rental: toPayloadAmount(memberForm.computer_rental, null),
        computer_rental_cap: toPayloadAmount(memberForm.computer_rental_cap, null),
        weekly_rate_override: toPayloadAmount(memberForm.weekly_rate_override, null),
        // See buildMemberPutPayload for the null-vs-zero rule.
        sixth_day_rate_override: nullableRate(memberForm.sixth_day_rate_override),
        seventh_day_rate_override: nullableRate(memberForm.seventh_day_rate_override),
        notes: memberForm.notes.trim(),
        sort_order: members.length > 0 ? Math.max(...members.map(m => m.sort_order ?? 0)) + 1 : 0,
      };
      // Include user setup fields if provided
      if (memberForm.user_email) payload.user_email = memberForm.user_email;
      if (memberForm.user_phone) payload.user_phone = memberForm.user_phone;
      const res = await apiPost('/api/budget/crew-members', payload, {
        headers: { 'Idempotency-Key': memberIdem.mint() },
      });
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to add crew member');
      memberIdem.clear();
      // Refresh from server to get computed fields
      await fetchMembers();
      fetchAllowanceNames();
      setMemberForm({
        crew_grade_id: '', name: '', user_id: null, location: '', start_date: '', end_date: '',
        default_allocation: 1.0, computer_rental: '', computer_rental_cap: '', weekly_rate_override: '', sixth_day_rate_override: '', seventh_day_rate_override: '', notes: '',
      });
      setShowAddMember(false);

      toast.success(`Crew member "${data.name}" added`);
      // Tell other tabs / Budget Overview that crew labour + allowance totals changed.
      notifyDataBus('budget-changed', { departmentId, reason: 'crew-member' });
      // 🔑 THE CREATED ROW IS THE RESULT (2026-09-23, P6). `Add & Invite`
      // needs its id to thread `crew_member_id` into the invitation, and a
      // FAILED add must resolve to `null` so no invite goes out for a row that
      // does not exist (it used to invite regardless — the catch swallowed).
      return data;
    } catch (err) {
      // The placeholder email hand-off (constants/emailInUse.js): the address
      // belongs to an account, so say what to do rather than a bare 409.
      toast.error(isEmailInUse(err) ? emailInUseHandoffMessage(err.body, { onBudget: true }) : err);
      return null;
    } finally {
      setSavingMember(false);
    }
    });
  };

  const handleAddMemberAndInvite = async () => {
    // Capture values before form resets
    const email = memberForm.user_email;
    const name = memberForm.name;
    // Look up role from selected grade's default_role_id
    const selectedGrade = grades.find(g => String(g.id) === String(memberForm.crew_grade_id));
    const roleDef = selectedGrade?.default_role_id ? roleDefinitions.find(r => r.id === selectedGrade.default_role_id) : null;
    const role = roleDef?.name || 'user';

    const fakeEvent = { preventDefault: () => {} };
    const created = await handleAddMember(fakeEvent);
    // Invite ONLY for a row that was really created, and carry its id so the
    // server can link the person to this crew line when they join (R23.8).
    if (created && created.id != null && email?.trim()) {
      await handleSendInvite(email, name, role, created.id);
    }
  };

  // `crewMemberId` — the crew row the invite is FOR (R23.8, invite-on-hire).
  // The server binds it to the invite's own department and ignores a foreign
  // or missing id; it never errors on it.
  const handleSendInvite = (email, name, role, crewMemberId) => {
    if (!email || !email.trim()) return;
    // Guard + attempt-key: an invitation is a protected create — see the
    // comment above `guardAddMember`.
    return guardSendInvite(async () => {
    try {
      setSendingInvite(true);
      const inviterName = localStorage.getItem('sfx_requested_by') || 'Admin';
      const res = await apiPost('/api/invitations', {
        email: email.trim(),
        name: name || '',
        role: role || 'user',
        invited_by: inviterName,
        production_id: activeDepartment?.production_id,
        department_id: departmentId,
        ...(crewMemberId != null ? { crew_member_id: crewMemberId } : {}),
      }, {
        headers: { 'Idempotency-Key': invitationIdem.mint() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invite');
      invitationIdem.clear();
      toast.success(`Invite sent to ${email.trim()}`);
      await fetchMembers();
    } catch (err) {
      toast.error(err);
    } finally {
      setSendingInvite(false);
    }
    });
  };

  /**
   * The `PUT /crew-members/:id/days` body for the member being edited in the
   * TABLE panel's day grid, or NULL when there is nothing to send.
   *
   * 🔑 A THIN WRAPPER, DELIBERATELY. Everything that decides WHICH DAYS and
   * WHAT RANGE lives in `utils/crewDayResolution.js#buildDayPayload`; this
   * function's whole job is to hand it this surface's state. The Crew Gantt's
   * day toggles call the SAME function with THEIR state (see
   * `buildGanttDayPayload`), which is the only reason the two editors cannot
   * disagree about a member's schedule. **Do not move logic back up into
   * either wrapper.**
   */
  const buildDayPayloadForSave = () => buildDayPayload({
    startDate: editMemberForm.start_date,
    endDate: editMemberForm.end_date,
    overlay: weekDayOverlay,
    baseline: weekDayBaseline,
  });

  /**
   * THE `PUT /api/budget/crew-members/:id` BODY, BUILT FROM THE EDIT FORM.
   * ONE DEFINITION - extracted 2026-08-22 when the Gantt's level-2 Save became
   * a second caller. This body carries `weekly_rate_override`, the three
   * allowances and `computer_rental`; a second copy of it is a second money
   * form payload, which is the defect class this feature spent the week
   * closing (`hireCost.js`: three implementations, one of them wrong, and the
   * wrong one was the money path).
   * TRAP: the coercions are empty-string-to-null plus `parseFloat`, NEVER
   * `||` - a legitimately ZERO allowance or rate override is falsy and `||`
   * would silently store `null` for it.
   */
  const buildMemberPutPayload = (id, form) => {
    // 🚨 `toPayloadAmount`, not `parseFloat` — `parseFloat('1,000')` is `1` and
    // these six allowance fields would have posted it. Blank still → `null`,
    // a typed `0` still → `0` (the docblock's rule, unchanged).
    const numOrNull = (v) => toPayloadAmount(v, null);
    const strOrNull = (v) => {
      const t = String(v || '').trim();
      return t || null;
    };
    return {
      // Phase 11 / Task 18: forward selectedVersionId on PUTs for consistency.
      // Server reads version_id from the existing row, but we send it so the
      // payload reflects the version the user is editing.
      version_id: selectedVersionId,
      crew_grade_id: parseInt(form.crew_grade_id, 10),
      name: String(form.name || '').trim(),
      user_id: form.user_id || null,
      location: String(form.location || '').trim(),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      // 🚨 PAYLOAD — see the POST twin in `handleAddMember` for why these are
      // `toPayloadAmount` and why the `!== ''` ternaries went. A typed `0`
      // still sends `0`; the docblock's warning about `||` stands unchanged.
      // 🚨 `toPayloadNumber`, NOT `toPayloadAmount` — **ALLOCATION IS NOT
      // MONEY** (corrected 2026-09-22). It took the money builder for one day
      // and inherited `roundMoney` with it, so a typed value was rounded before
      // `helpers/allocation.js` ever saw it and the server ACCEPTED at 200 a
      // value it exists to REFUSE. There is no CHECK constraint on either
      // engine, so that helper is the only gate.
      // 📏 An allocation is a **FRACTION of a five-day week** — legal values
      // `{0, 0.2, 0.4, 0.6, 0.8, 1}` — and the MEASURED flips were
      // `0.599 → 0.6`, `0.999 → 1`, `0.204 → 0.2` and `1e-7 → 0`. 🥇 The last
      // is the sharpest: `0` is the real "Off" state, so rounding turns a typo
      // into a deliberate-looking setting rather than an error.
      // 🔻 This comment said `2.999 → 3` until the same day, and that instance
      // is IMPOSSIBLE — `2.999` and `3` are both refused, being out of range
      // for a fraction. It came from a task brief, was never measured, and
      // reached four artifacts before anyone checked it.
      // 🔑 An unrelated guard widened by a money change, through a helper the
      // two columns happened to share — the shape to look for whenever a rule
      // is added to a shared builder. The SHAPE screen is still shared and
      // still wanted: `parseFloat('1,000')` is `1` on an allocation box too.
      default_allocation: toPayloadNumber(form.default_allocation, null),
      computer_rental: toPayloadAmount(form.computer_rental, null),
      computer_rental_cap: toPayloadAmount(form.computer_rental_cap, null),
      weekly_rate_override: toPayloadAmount(form.weekly_rate_override, null),
      // 🚨 THE 6th/7th-DAY PREMIUM OVERRIDES. `nullableRate` — blank sends
      // `null` (UNSTATED: the server applies the 1.5×/2× default), and a typed
      // `0` is refused at save (no free days, 2026-08-23) rather than stored.
      // The docblock above already warns that `||` would store null for a
      // legitimately zero allowance or cap; these two are the fields where the
      // mistake moves the MOST money, because an unstated rate now costs 1.5×
      // or 2× the daily rate rather than nothing.
      // 🔑 They live HERE and not in a second builder — the Gantt's level-2
      // Save shares this body, so a copy would let a save from the Gantt drop
      // what a save from the table keeps.
      sixth_day_rate_override: nullableRate(form.sixth_day_rate_override),
      seventh_day_rate_override: nullableRate(form.seventh_day_rate_override),
      // S2 overtime-rate overrides — HERE, not in a second builder, for the
      // same reason as the two above. A key the form lacks is omitted.
      ...overtimeRatePayload(form, 'member'),
      notes: String(form.notes || '').trim(),
      sort_order: members.find(m => m.id === id)?.sort_order ?? 0,
      allowance_name: strOrNull(form.allowance_name),
      allowance_amount: numOrNull(form.allowance_amount),
      allowance_cap: numOrNull(form.allowance_cap),
      allowance_name_2: strOrNull(form.allowance_name_2),
      allowance_amount_2: numOrNull(form.allowance_amount_2),
      allowance_cap_2: numOrNull(form.allowance_cap_2),
      allowance_name_3: strOrNull(form.allowance_name_3),
      allowance_amount_3: numOrNull(form.allowance_amount_3),
      allowance_cap_3: numOrNull(form.allowance_cap_3),
    };
  };

  /**
   * The linked person's contact details, saved alongside the member row.
   * Extracted with the payload above and for the same reason - the level-2
   * Save is a second caller and must not carry a second copy of the
   * has-it-actually-changed test.
   * NOTE: `email` is a CREDENTIAL (`forgot-password` mails a reset token to
   * whatever address is on the row); the server gates it with
   * `canSetUserEmail` and this call is a convenience, never the gate.
   */
  const saveLinkedUserContact = async (id, form) => {
    if (!form.user_id) return;
    if (form.user_email === undefined && form.user_phone === undefined) return;
    const member = members.find(m => m.id === id);
    const emailChanged = form.user_email !== (member?.user_email || '');
    const phoneChanged = form.user_phone !== (member?.user_phone || '');
    if (!emailChanged && !phoneChanged) return;
    const res = await apiPut(`/api/users/${form.user_id}`, {
      email: form.user_email || null,
      phone: form.user_phone || null,
    });
    // 🚨 THE RESPONSE IS READ (2026-09-23, P6). It was ignored, so a 409
    // `email_in_use` — or a 403 — was swallowed and the caller toasted
    // "Crew member updated" over an email that never saved. Both callers run
    // this inside their own try, so a throw here lands in their toast.error.
    if (!res.ok) {
      let data = {};
      try { data = await res.json(); } catch { /* tolerate non-JSON */ }
      if (isEmailInUse(data)) {
        // Both callers run this LAST, after the crew-row writes succeeded.
        const err = new Error(emailInUseHandoffMessage(data, { onBudget: true, detailsSaved: true }));
        err.code = data.code;
        err.status = res.status;
        err.body = data;
        throw err;
      }
      throwApiError(res, data, 'Failed to save the contact details');
    }
  };

  // 🔑 RESOLVES `true` ONLY WHEN EVERY WRITE LANDED, `false` otherwise — the
  // same success/failure shape as `handleAddMember` (fix round 1, 2026-09-23).
  // Send Invite on a dirty row saves first and must not invite after a failed
  // save: an email 409 used to toast the hand-off AND invite an unsaved address.
  const handleSaveEditMember = async (id, collapseAfter = false) => {
    // See handleAddGrade for why this runs ahead of the request.
    const moneyError = crewMoneyFormError([...memberMoneyEntries(editMemberForm), ...overtimeRateMoneyEntries(editMemberForm, 'member')]);
    if (moneyError) {
      setError(moneyError);
      return false;
    }
    try {
      setSavingMember(true);
      setError(null);
      const res = await apiPut(`/api/budget/crew-members/${id}`, buildMemberPutPayload(id, editMemberForm));
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to update crew member');

      // Also save week overrides — ONLY once this member's weeks have LOADED
      // (`originalWeekData !== null`). Before that, `weekOverrides` is empty
      // by construction, and PUTting it would wipe the stored overrides of a
      // member whose week fetch had not landed or had failed (review C8).
      // 🔀 2026-10-19 (ruling A): the body is `tableWeekOverrides` — the Gantt's
      // rule over this form's dates and draft default — never the map as loaded.
      if (originalWeekData !== null && tableWeekOverrides) {
        // Ruling B (10-20): a moved date marks it `date_change_save` — `weeksSaveBody`.
        const weekRes = await apiPut(`/api/budget/crew-members/${id}/weeks`, weeksSaveBody({
          member: tableStoredMember,
          startDate: editMemberForm.start_date,
          endDate: editMemberForm.end_date,
          overrides: tableWeekOverrides,
        }));
        if (!weekRes.ok) {
          const weekErrData = await weekRes.json();
          throwApiError(weekRes, weekErrData, 'Failed to save week overrides');
        }
      }

      // ─── WORKED DAYS — 6th/7th-day premium wage feature, Task 6 ─────────
      //
      // 🔑 LAST, DELIBERATELY. `PUT /crew-members/:id` and
      // `PUT /:id/weeks` both RE-DERIVE presumed day rows when they move the
      // schedule (`server/helpers/crewDays.js`). Sending days before them
      // would have the server overwrite what the user just saved; sending
      // them after means the grid's own state is what lands, which is the
      // ruled behaviour — the save sends what the UI shows.
      //
      // 🔑 ONLY WHEN THE DAY SET ACTUALLY CHANGED, and this is not an
      // optimisation. Every row this endpoint writes carries
      // `is_presumed = 0` — "a human opened the grid and saved" — and a
      // member holding ANY such row is thereafter LEFT COMPLETELY ALONE by
      // the re-derivation. Posting an untouched, still-presumed day set on a
      // rename would silently freeze that member's schedule: their labour
      // would stop following their dates forever, on a save that had no
      // business touching their days.
      //
      // ⚠️ The RANGE is the visible week span, not the posted days' min/max —
      // an empty `days` array has no min and no max, so a derived range could
      // never clear a week. It is the caller's statement of what is being
      // REPLACED, independent of what it is replaced with. The server
      // validates it against `crewWeekSpan(enumerateCrewWeeks(...))`, and
      // since 2026-08-24 the two loops carry the IDENTICAL end-inclusive
      // bound (`current <= end`), so a range built this way is exactly the
      // permitted window — never outside it.
      const daysPayload = buildDayPayloadForSave();
      if (daysPayload) {
        const daysRes = await apiPut(`/api/budget/crew-members/${id}/days`, daysPayload);
        if (!daysRes.ok) {
          const daysErrData = await daysRes.json();
          throwApiError(daysRes, daysErrData, 'Failed to save worked days');
        }
      }

      // Update linked user's contact details if changed
      await saveLinkedUserContact(id, editMemberForm);

      toast.success(`Crew member "${data.name}" updated`);
      // Refresh autocomplete options so newly typed allowance names appear
      // in the dropdown on the next edit.
      fetchAllowanceNames();
      // Tell other tabs / Budget Overview that crew labour + allowance totals changed.
      notifyDataBus('budget-changed', { departmentId, reason: 'crew-member' });

      if (collapseAfter) {
        // Animate close first, then refresh in background
        collapseMember();
        fetchMembers(true); // silent background refresh (no loading state)
      } else {
        const refreshed = await fetchMembers();
        // Update originals so dirty tracking resets
        setOriginalMemberForm({ ...editMemberForm });
        setOriginalWeekData({ ...weekOverrides });
        // The day grid's baseline is now whatever the server just stored, and
        // the overlay is spent. Re-seeding from the REFRESHED member (not the
        // stale one in `members`) is what stops the grid reporting itself
        // dirty the instant a save succeeds.
        const savedMember = Array.isArray(refreshed) ? refreshed.find((x) => x.id === id) : null;
        if (savedMember) {
          setWeekDayBaseline(buildWeekDayBaseline(
            savedMember,
            generateWeeks(editMemberForm.start_date, editMemberForm.end_date)
          ));
        }
        setWeekDayOverlay({});
        // Refresh week data
        await fetchWeekData(id, true, true);
      }
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    } finally {
      setSavingMember(false);
    }
  };

  /**
   * Deactivation with the day-strip question already answered.
   *
   * 🚨 SPLIT FROM `handleDeleteMember` FOR THE SAME REASON `applyGanttUpdate`
   * IS SPLIT FROM `handleGanttUpdate`: the prompt's `proceed` runs from the
   * closure of the render that OPENED the dialog, where `ganttDayDirty` is
   * still `true`, so a `proceed` that re-entered the guarded function would
   * re-open the dialog forever. The guard lives on the entry point; `proceed`
   * calls the work. **Any new guarded exit must follow this shape.**
   *
   * ⚠️ The `window.confirm` here is PRE-EXISTING and deliberately left alone —
   * replacing a destructive control's confirmation is an unrequested product
   * change and does not belong in this commit. Flagged as owed work.
   */
  const applyDeleteMember = async (m) => {
    if (!window.confirm(`Deactivate crew member "${m.name}"? They will no longer appear in lists.`)) return;
    try {
      setError(null);
      const res = await apiDelete(`/api/budget/crew-members/${m.id}`);
      const data = await res.json();
      if (!res.ok) throwApiError(res, data, 'Failed to deactivate crew member');
      setMembers(prev => prev.filter(x => x.id !== m.id));
      if (expandedMemberId === m.id) {
        collapseMember();
      }

      toast.success(`Crew member "${m.name}" deactivated`);
      // Tell other tabs / Budget Overview that crew labour + allowance totals changed.
      notifyDataBus('budget-changed', { departmentId, reason: 'crew-member' });
    } catch (err) {
      toast.error(err);
    }
  };

  /**
   * 🚨 Deactivating the member whose day strip is open removes them from
   * `members`, so `ganttDayMember` resolves to nothing, `ganttDayDirty` goes
   * FALSE and the overlay is orphaned — the same unrecoverable shape as a
   * version switch. Asked BEFORE the deactivation confirm: the day edit is the
   * recoverable half, and nobody should have to answer the destructive
   * question first to find out their day edit is at stake.
   */
  const handleDeleteMember = (m) => {
    if (m.id === ganttDayMemberId && ganttDayDirty) {
      setGanttDayExit({ what: `deactivate ${m.name}`, proceed: () => { applyDeleteMember(m); } });
      return;
    }
    applyDeleteMember(m);
  };

  /* ─── Gantt drag preview (live table update during drag) ─── */
  /* 🔻 DATES ONLY SINCE 2026-08-23 — it was
     `{ memberId, start_date, end_date, budget_cost, total_weeks }`. The two
     money fields were `weeks × rate` computed in `CrewGanttView`, which cannot
     see a member's day rows; they are the figure the owner reported as
     ignoring off days, and they are now supplied by the server through
     `ganttStripPreview`. **Do not put money back on this state** — it is
     written from a pointer handler and nothing in that path can cost a day. */
  const [dragPreview, setDragPreview] = useState(null); // { memberId, start_date, end_date }
  const handleGanttDragPreview = useCallback((memberId, { start_date, end_date }) => {
    setDragPreview({ memberId, start_date, end_date });
    // LIVE, not on drop: the form fields are visible under the bar at level 2,
    // and two date readings disagreeing for the length of a drag is exactly the
    // confusion trap 1 names. No-op unless level 2 is open on this member.
    syncGanttDetailsDates(memberId, start_date, end_date);
  }, [syncGanttDetailsDates]);

  /* ─── Gantt drag update (optimistic) with partial week overrides ─── */
  /* ════════ THE CREW GANTT's DAY TOGGLES — handlers, 2026-08-22 ════════
   *
   * 🔑 THE CONSEQUENCE OF ONE CLICK, IN FULL, because it is not obvious from
   * the control and it is permanent. Saving writes `crew_member_days` through
   * `PUT /crew-members/:id/days`, and **every row that endpoint writes carries
   * `is_presumed = 0`** — "a human opened this and said so". A member holding
   * ANY `is_presumed = 0` row is thereafter left completely alone by the
   * automatic re-derivation that keeps a member's days following their dates.
   * So ticking one Saturday FREEZES that member's whole schedule against
   * future backfill, for good. That is the existing, intended semantic of a
   * human day edit — it is what the crew day grid has always done — and going
   * through the shared save path is what gives it to this surface for free
   * rather than approximately. It is also why a save fires only when the day
   * set genuinely CHANGED (`dayPayloadHasChanges`): posting an untouched,
   * still-presumed set would freeze a member on a save that changed nothing.
   */
  /**
   * Open a strip on `member`, unconditionally. The dirty question is asked by
   * the callers, never here — this is the "proceed" half of the guard and is
   * what `Discard changes` and a successful `Save and continue` both run.
   */
  const openGanttDayStrip = useCallback((member) => {
    const startDate = member.start_date ? member.start_date.slice(0, 10) : null;
    const endDate = member.end_date ? member.end_date.slice(0, 10) : null;
    setGanttDayOverlay({});
    setGanttDayWeekOverrides({});
    setGanttDayDates(null);   // a fresh strip has no pending drag
    setDragPreview(null);
    setGanttDayUndoStack([]); // per-strip; never carried between members
    // Seeded ONCE, from the member's STORED rows, through the same builder the
    // day grid seeds from. The overlay stays empty until the user clicks, so a
    // week nobody touched has no entry and cannot drift.
    // ⚠️ SEEDED FROM THE **STORED** DATES, AND THAT IS CORRECT EVEN THOUGH A
    // DRAG CAN NOW MOVE THE RANGE UNDER IT. The baseline answers "what did this
    // member have before the user touched anything", which is a fact about the
    // saved row and must not move. A week a drag brings INTO range is in
    // neither the baseline nor the overlay, and `resolveWeekDays` answers it
    // from the allocation fallback — the same answer for the render, the dirty
    // test and the payload, which is the property that matters.
    setGanttDayBaseline(buildWeekDayBaseline(member, generateWeeks(startDate, endDate)));
    setGanttDayMemberId(member.id);
  }, []);

  /**
   * Close the strip and drop every piece of its state. THE one closer — the
   * exit prompt's `Discard changes`, `Cancel` and the save's success path all
   * call this, so "what does closing a strip clear" has exactly one answer.
   *
   * 🚨 It clears the OVERLAY **and** `ganttDayWeekOverrides`. Those are the
   * two halves of one click (days → labour, allocation → computer rental and
   * all three allowances) and dropping only the first would leave a discarded
   * edit's allocation staged for the next save. Ledger S2-F: do not decouple.
   */
  const closeGanttDayStrip = useCallback(() => {
    setGanttDayMemberId(null);
    setGanttDayOverlay({});
    setGanttDayWeekOverrides({});
    setGanttDayBaseline(EMPTY_DAY_BASELINE);
    // 🔀 THE PENDING DRAG DIES WITH THE STRIP TOO, ADDED 2026-08-22 WITH THE
    // BATCHED DRAG. It is the THIRD half of one edit (days → labour,
    // allocation → rental + allowances, dates → both), and leaving it behind
    // would stage a discarded date change for the next save — the same reason
    // `ganttDayWeekOverrides` is cleared here and not just the overlay. It is
    // also what makes Cancel put the BAR back: the bar is drawn from this
    // overlay, so dropping it is the revert.
    setGanttDayDates(null);
    setDragPreview(null);
    // The undo stack dies with the strip — it is not persisted and it is never
    // carried across a close, a save or a member change. A stack that outlived
    // its strip would let Ctrl+Z write day sets into a baseline that no longer
    // describes them.
    setGanttDayUndoStack([]);
    // 🔻 LEVEL 2 DIES WITH LEVEL 1, AND SO DOES THE FORM. The panel is a
    // deeper disclosure of this strip; leaving it up over a closed strip would
    // show a form whose Save button no longer exists. `collapseMember` is the
    // table's own closer — one closer for one piece of state — and it
    // early-returns when nothing is expanded, so a strip closed from the table
    // view (unreachable today) is still a no-op here.
    hideGanttDetails(ganttDetailsMemberId);
    setGanttFormMemberId(null);
    ganttDetailsFormDirtyRef.current = false;
    if (ganttFormMemberId != null) collapseMember();
  }, [collapseMember, hideGanttDetails, ganttDetailsMemberId, ganttFormMemberId]);

  /**
   * THE SECOND EXPAND. Level 1 ⇄ level 2 on one member.
   *
   * 🔑 COLLAPSING LEVEL 2 IS NON-DESTRUCTIVE (brief, trap 2). It clears only
   * `ganttDetailsMemberId`; `expandedMemberId`, `editMemberForm` and
   * `originalMemberForm` survive, so re-expanding shows exactly what was typed
   * and `isMemberDirty` never lied in between. What it DOES do is drop the
   * guard's ref — which is correct and is the reason the ref is written by an
   * effect rather than here: the effect puts it back on the next render if the
   * form really is dirty, so hiding the panel cannot wave an edit through.
   *
   * 🔒 IT REFUSES TO OPEN OVER A STRIP THAT IS NOT THIS MEMBER'S. Level 2 is a
   * deeper disclosure of level 1, and `CrewGanttView` only offers the control
   * inside the open strip's own save bar — the guard is defence in depth.
   */
  const handleGanttDetailsToggle = (member) => {
    if (!member) return;
    if (ganttDetailsMemberId === member.id) { hideGanttDetails(member.id); return; }
    if (ganttDayMemberId !== member.id) return;
    // 🪤 RE-RESOLVED THROUGH THE REF, not the object the component handed us:
    // a batched drag means the row `CrewGanttView` renders can carry pending
    // dates, and `handleExpandMember` must seed the form from the STORED row.
    // The pending dates are then laid over it by `syncGanttDetailsDates`.
    if (expandedMemberId !== member.id) {
      handleExpandMember(membersRef.current.find((x) => x.id === member.id) || member, { ask: false });
    }
    setGanttFormMemberId(member.id);
    setGanttDetailsMemberId(member.id);
  };

  const handleGanttDayEditToggle = (member, withDetails = false) => {
    if (!member) return;
    if (ganttDayMemberId === member.id) {
      // Collapsing this member's own strip — which also closes its details
      // (`closeGanttDayStrip`: level 2 dies with level 1).
      guardGanttDayEdit('close the worked-days strip', closeGanttDayStrip);
      return;
    }
    /* 🔀 R8 FIX ROUND — THE CREW TABLE'S UNSAVED FORM IS NOT THE GANTT'S TO
       DROP. Table view → expand A → type → Gantt view (an unguarded switch) →
       expand B: nothing on the Gantt owns A's form, so `guardGanttDayEdit`
       sees a clean surface, and taking B's details would either discard A's
       typing in silence or (with the old `ask: false`) hand B's strip A's
       form, whose Save writes A's name and grade onto B. Ask with the table's
       OWN prompt first; Discard / a landed Save re-runs this click. */
    if (withDetails && ganttFormMemberId == null && expandedMemberId != null
      && expandedMemberId !== member.id && isMemberDirty) {
      setGanttDayExit({
        /* 🔀 R8 2nd fix round (review F5): when an UNOWNED strip is dirty too
           (the embedded chart's ticks), this is TWO surfaces — `kind: 'both'`,
           which offers no Save (see `guardGanttDayEdit`'s docblock). */
        kind: ganttDayDirty ? 'both' : 'table',
        memberName: editMemberForm.name || 'This crew member',
        what: `open ${member.name}`,
        proceed: () => {
          openGanttDayStrip(member);
          handleExpandMemberRef.current(
            membersRef.current.find((x) => x.id === member.id) || member, { force: true }
          );
          setGanttFormMemberId(member.id);
          setGanttDetailsMemberId(member.id);
        },
      });
      return;
    }
    // Another member's chevron — same hazard, different destination.
    guardGanttDayEdit(
      `open the worked-days strip for ${member.name}`,
      () => {
        openGanttDayStrip(member);
        /* 🔀 R8 — OWNER RULING, Pete, 2026-10-19: *"by default can we have the
           details section expanded when we expand a gantt chart to see the
           days"*. Opening a member's strip also opens its Details panel — the
           same three writes `handleGanttDetailsToggle` makes (it cannot be
           called here: its "this member's strip is open" guard reads state
           this same click has only just set). Still ONE panel at a time
           (one `ganttDetailsMemberId`); "Hide details" still collapses it
           inside the open row; collapsing the row closes both. The drag lock
           is untouched — it keys on the strip (`dayEditMemberId`). */
        if (!withDetails) return;
        /* 🔀 R8 FIX ROUND — `force`, NOT `ask: false`. This closure runs in
           the SAME tick as the prompt's Discard / Save and continue, so it
           still sees the PREVIOUS render's `expandedMemberId` and
           `isMemberDirty` — the form that the prompt has just discarded or
           saved. `ask: false` then took the "dirty, hold for a prompt" branch
           and returned silently, leaving B's strip owned by A's (soon empty)
           form. `force` is safe here because every dirty form that can reach
           this line was already ANSWERED: the Gantt-owned one by
           `guardGanttDayEdit`, the table's unowned one by the `kind: 'table'`
           prompt below, before the strip opens. (The collapse timer race is
           closed separately, by `expandGenerationRef`.) */
        if (expandedMemberId !== member.id) {
          handleExpandMember(membersRef.current.find((x) => x.id === member.id) || member, { force: true });
        }
        setGanttFormMemberId(member.id);
        setGanttDetailsMemberId(member.id);
      }
    );
  };
  /** The PAGE-LEVEL chart's expand (R8). The chart embedded in the crew table's
   *  own expanded row keeps the plain toggle — its member's form is already the
   *  table's, and a level-2 panel there would be a second form surface. */
  const handleGanttRowExpandToggle = (member) => handleGanttDayEditToggle(member, true);

  /**
   * One click on one day.
   *
   * 🚨 IT WRITES **TWO** THINGS, AND THE SECOND ONE IS ALSO MONEY. The day set
   * drives LABOUR (via `crew_member_days`); `crew_week_overrides.allocation`
   * still drives COMPUTER RENTAL and all three ALLOWANCES (via
   * `computeCrewWeeks`) — a laptop does not cost more because someone worked a
   * Saturday, but a 2-day week must not pay a full week of rental either.
   * `handleDayToggle` next door does exactly this and its comment says
   * *"the preset and the ticks are ONE state"*; writing only the day set here
   * would make the Gantt and the grid disagree about the same week.
   * The mapping and its clamp at five live in ONE place,
   * `constants/budget.js#allocationForDayCount`.
   */
  const handleGanttDayToggle = (member, dateStr) => {
    if (!member || member.id !== ganttDayMemberId) return;
    const slot = weekSlotForDate(ganttDayWeeks, dateStr);
    if (!slot) return; // outside the engagement's week span — nothing to edit
    const { weekStart, dayIndex } = slot;
    const current = resolveWeekDays(
      ganttDayOverlay, ganttDayBaseline, weekStart, ganttDayCurrentRange
    );
    // 🚨 DISARM (2026-08-25, docs/plans/2026-08-25-gantt-segment-label-vs-day-strip.md
    // §8.1): a week whose resolved set holds a charged day OUTSIDE the
    // engagement dates cannot be edited from this strip. The strip clamps
    // those days out of view, so a tick here would silently RETAIN an
    // invisible day in the new set and its allocation — measured +£230/week
    // on the worked example — and the save's `is_presumed = 0` rows freeze
    // the member against re-derivation permanently. The crew day grid
    // (Table view, the member panel's "Working Days" section) renders all
    // seven days of every week, so the same edit is fully informed there.
    // The guard sits BEFORE the undo push and both state writes: a blocked
    // click writes nothing and pushes nothing. Weeks whose resolved days all
    // sit inside the engagement never reach the `return` — every ordinary
    // week behaves exactly as before.
    if (ganttDayCurrentRange && current.some((i) => {
      const d = dateForWeekDay(weekStart, i);
      return d && (d < ganttDayCurrentRange.start || d > ganttDayCurrentRange.end);
    })) {
      toast.info(
        'This week can’t be edited here — it includes charged day(s) outside '
        + 'the engagement dates, which this strip can’t show. Open this member '
        + 'in Table view and edit the week under “Working Days”, where all '
        + 'seven days are shown.'
      );
      return;
    }
    const next = current.includes(dayIndex)
      ? current.filter((i) => i !== dayIndex)
      : [...current, dayIndex].sort((a, b) => a - b);
    // 🔑 THE UNDO ENTRY IS PUSHED HERE, IN THE ONE PLACE THAT WRITES BOTH
    // HALVES, and it records BOTH — see `handleGanttDayUndo`.
    setGanttDayUndoStack((prev) => [...prev, {
      kind: 'day',
      weekStart,
      prevDays: ganttDayOverlay[weekStart],
      prevAlloc: ganttDayWeekOverrides[weekStart],
    }]);
    setGanttDayOverlay((prev) => ({ ...prev, [weekStart]: next }));
    setGanttDayWeekOverrides((prev) => ({ ...prev, [weekStart]: allocationForDayCount(next.length) }));
  };

  /* ═══════ CTRL+Z / ⌘+Z — UNDO, SCOPED TO AN OPEN DAY STRIP ═══════════════
   *
   * Owner: *"ctrl Z undo would be a great addition if possible."*
   *
   * ─── WHAT IT IS SCOPED TO, EXACTLY ───
   * It exists only while a Gantt day strip is OPEN, it only ever reverses day
   * toggles made in THAT strip, and the stack is dropped when the strip opens,
   * closes, saves or is cancelled. It is NOT a page undo and must not become
   * one: this page also edits cost codes, crew grades, ad-hoc lines and a
   * whole crew form, and a shortcut that reversed the last thing the user did
   * ANYWHERE would be a money control with no visible target.
   *
   * ─── GRANULARITY: ONE TOGGLE, ONE STEP ───
   * Each day click is exactly one undoable step, because on THIS surface each
   * day click is exactly one user decision. **The preset question does not
   * arise here** — the Gantt strip has no preset control at all (the
   * `WEEK_SHAPE_PRESETS` buttons belong to `CrewDayGrid`, the other editor,
   * which this shortcut does not reach). If a preset ever lands on this strip
   * it must push ONE entry for the whole preset, not one per day, or a single
   * click would take five presses to undo.
   *
   * ─── REDO IS OUT OF SCOPE, DELIBERATELY ───
   * No Ctrl+Shift+Z, no Ctrl+Y. Stated rather than left ambiguous: a redo
   * stack has to be invalidated by the next forward edit, and getting that
   * wrong on a surface that writes money is worse than not offering it. The
   * shortcut explicitly ignores Ctrl+Shift+Z rather than treating it as undo.
   *
   * ─── 🚨 IT MUST RESTORE BOTH HALVES, WHICH IS THE WHOLE TRAP ───
   * A toggle writes the day set (labour) AND `crew_week_overrides.allocation`
   * (computer rental + all three allowances). An undo that put back only the
   * days would leave the allocation on the value the undone click computed —
   * a member drawn as working five days while their rental is billed at four,
   * with nothing on screen disagreeing. Ledger S2-F says do not decouple the
   * two writes; this is that rule read backwards.
   *
   * ─── 🪤 `undefined` IS NOT `[]`, SO UNDO DELETES KEYS ───
   * `resolveWeekDays` treats an ABSENT overlay key as "nobody has said
   * anything about this week" and an explicit `[]` as "deliberately empty".
   * The first toggle of a week therefore has to be undone by DELETING the key,
   * never by writing `undefined` or `[]` into it: `[]` would silently convert
   * a fully-undone week into a deliberate zero-pay week, and `undefined` would
   * survive `Object.entries` in the save and post
   * `{ week_start, allocation: undefined }`.
   */
  const handleGanttDayUndo = useCallback(() => {
    if (ganttDayUndoStack.length === 0) return;
    const entry = ganttDayUndoStack[ganttDayUndoStack.length - 1];
    /* 🔀 TWO KINDS OF STEP SINCE THE DRAG BECAME BATCHED (2026-08-22), AND THE
     * OWNER NOTICED THE GAP BEFORE THE CHANGE CLOSED IT. A drag used to be
     * persisted the instant the pointer was released, so Ctrl+Z could not touch
     * it without a compensating write to the server. Now it is local state of
     * exactly the same kind as a day toggle, so it undoes for free — ONE drag
     * is ONE step, restoring the whole `{ start_date, end_date }` pair plus its
     * captured preview figures, because a drag is one user decision even when
     * it moved both ends.
     * 🪤 `prevDates` is legitimately `null` (no pending drag before this one),
     * so this branch must not use a delete-or-set shape or a falsy test —
     * `null` IS the value to restore. */
    if (entry.kind === 'dates') {
      setGanttDayDates(entry.prevDates);
      /* 🔀 R8 (2026-10-19): with Details open on every expand the drag also
         wrote its dates into the FORM (`syncGanttDetailsDates`), so undoing it
         must put the form back too — or the bar shows the old range while the
         form's date fields keep the dragged one and Save stays armed on a
         "details" edit nobody made. `null` = no pending drag = stored dates. */
      if (ganttDayMember) {
        const back = entry.prevDates || {
          start_date: ganttDayMember.start_date ? ganttDayMember.start_date.slice(0, 10) : null,
          end_date: ganttDayMember.end_date ? ganttDayMember.end_date.slice(0, 10) : null,
        };
        syncGanttDetailsDates(ganttDayMember.id, back.start_date, back.end_date);
      }
      setGanttDayUndoStack((stack) => stack.slice(0, -1));
      return;
    }
    // Both restores use the same delete-or-set shape, on purpose: the two maps
    // are written together by one click and must be unwound together.
    // ⚠️ Read straight off state and set three times, rather than nesting the
    // overlay writes inside the stack's updater — an updater must be pure, and
    // React StrictMode double-invokes them in development.
    const restore = (prev, value) => {
      const next = { ...prev };
      if (value === undefined) delete next[entry.weekStart];
      else next[entry.weekStart] = value;
      return next;
    };
    setGanttDayOverlay((prev) => restore(prev, entry.prevDays));
    setGanttDayWeekOverrides((prev) => restore(prev, entry.prevAlloc));
    setGanttDayUndoStack((stack) => stack.slice(0, -1));
  }, [ganttDayUndoStack, ganttDayMember, syncGanttDetailsDates]);

  /**
   * 🪤 CTRL+Z BELONGS TO A TEXT FIELD WHEN ONE HAS FOCUS, AND THIS PAGE IS
   * FULL OF THEM — the crew search box, every `CurrencyInput`, the notes
   * textarea, the version-name input. Stealing the browser's native undo
   * inside a field the user is typing in would be a worse bug than the one
   * this feature fixes, so a shortcut fired from an editable element is left
   * entirely alone: no `preventDefault`, no state change, no return.
   *
   * ⚠️ There is NO existing keyboard-shortcut helper or convention in
   * `client/src` to follow — every other `keydown` listener in the tree
   * handles Escape or arrows only, and nothing anywhere reads `ctrlKey` or
   * `metaKey`. This is the codebase's first modifier shortcut. It follows the
   * house listener idiom (`window.addEventListener` in an effect with a
   * matching removal) and nothing more; if a second shortcut is ever added,
   * extract a hook rather than copying this.
   */
  useEffect(() => {
    if (!ganttDayMemberId) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      // Ctrl+Shift+Z is REDO on every platform that has one. Redo is out of
      // scope, so it is ignored rather than quietly treated as another undo.
      if (e.shiftKey) return;
      if (typeof e.key !== 'string' || e.key.toLowerCase() !== 'z') return;
      const el = document.activeElement;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (el && el.isContentEditable)) {
        return; // the field's own undo — not ours to take
      }
      // Not while the exit prompt is open (the user is answering a question
      // about this very state) and not mid-save (the request is already out).
      if (ganttDayExit != null || ganttDaySaving) return;
      e.preventDefault();
      handleGanttDayUndo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ganttDayMemberId, ganttDayExit, ganttDaySaving, handleGanttDayUndo]);

  const handleGanttDayCancel = () => { closeGanttDayStrip(); };

  /**
   * @returns {Promise<boolean>} whether the save actually landed.
   *
   * 🔑 THE RETURN VALUE IS LOAD-BEARING, added 2026-08-22 with the exit
   * prompt. `Save and continue` must not continue on a save that failed —
   * the row deliberately STAYS OPEN with the overlay intact on failure (see
   * the catch), and proceeding anyway would navigate away from the only copy
   * of the user's work while a toast explained why it had not been
   * saved. A void save cannot be sequenced with anything.
   */
  const handleGanttDaySave = async () => {
    const member = ganttDayMember;
    // TRAP 4 - ONE SAVE BUTTON, AND IT NOW COMMITS THE FORM TOO. At level 2
    // the details panel renders WITHOUT a Save/Cancel row of its own, so this
    // is the only commit control on the surface and it has to arm on a form
    // edit as well as on a day tick or a drag.
    const detailsOpen = ganttFormMemberId != null && ganttFormMemberId === ganttDayMemberId;
    const formDirty = ganttDetailsFormDirty;   // ONE definition, declared above
    if (!member || !ganttSurfaceDirty) return false;
    // 🔴 MONEY 2026-08-23 — the level-2 panel commits `buildMemberPutPayload`,
    // so it is a FIFTH writer of the premium overrides and needs the same
    // pre-save check as the crew table's Save. Gated on `detailsOpen` (=
    // OWNERSHIP, so every page-chart save since R8): at level 1 — only the
    // embedded single-member chart now — the strip sends the member's STORED fields with only the dragged dates
    // laid over them, so `editMemberForm` is not what goes on the wire and
    // checking it would refuse a pure day/date save over an unrelated field.
    // Returns `false`, not a throw — the caller sequences on this value and a
    // refused save must leave the row open exactly as a failed one does.
    if (detailsOpen) {
      const moneyError = crewMoneyFormError([...memberMoneyEntries(editMemberForm), ...overtimeRateMoneyEntries(editMemberForm, 'member')]);
      if (moneyError) {
        setError(moneyError);
        return false;
      }
    }
    try {
      setGanttDaySaving(true);
      setError(null);

      /* ═══ THE SAVE ORDER: **DATES → WEEKS → DAYS**. THREE STEPS SINCE
       *     2026-08-22, AND EACH ARROW IS LOAD-BEARING. ═══════════════════
       *
       * It was WEEKS → DAYS, because `PUT /weeks` RE-DERIVES presumed day rows
       * when it moves a week's allocation (`server/helpers/crewDays.js`), so
       * days-first is silently overwritten: the UI reports success and the
       * schedule reverts.
       *
       * 🚨 BATCHING THE DRAG ADDS A THIRD WRITE **IN FRONT**, AND IT IS THE
       * SAME DEFECT ONE LAYER UP. `PUT /crew-members/:id` changes the member's
       * DATES, and changing the dates ALSO re-derives their presumed day rows.
       * Send it after `/days` — or after `/weeks` — and it wipes the very days
       * the user just ticked, in exactly the way days-before-weeks did. Dates
       * must land FIRST so that both re-derivations happen before the day set
       * the user actually chose is written last and wins.
       * 🔒 Pinned by `BudgetCostCodes.ganttBatchedDrag.test.js` §2, which
       * asserts the ORDER of the three URLs rather than their presence — an
       * ordering that is correct only by comment is one refactor from being
       * wrong, and this one costs money when it breaks. */

      // ── STEP 1: DATES ───────────────────────────────────────────────────
      // Only when they actually moved. A day-only edit must not PUT the member
      // row: that write re-derives presumed days for nothing and would make
      // every day save look like a date change in the audit trail.
      // TRAP 4, THE OTHER HALF - **WHICH** BODY STEP 1 SENDS.
      // Level 1 (strip only): the member's STORED fields with the dragged dates
      // laid over them. That narrow body is deliberate - a day/date edit must
      // not write a rate or an allowance nobody touched, and it is exactly what
      // shipped before this change.
      // 🔀 R8 (2026-10-19): the PAGE chart's expand now owns the form, so this
      // narrow body is reached ONLY from the embedded single-member chart in
      // the crew table's panel (plain toggle, no level 2). Pinned there by
      // `BudgetCostCodes.singleMemberGantt.test.js` §6. Level 2 is keyed on
      // OWNERSHIP, so hiding the panel does NOT bring this body back.
      // Level 2 (Gantt owns the form): `buildMemberPutPayload` - the SAME body the crew
      // table's Save sends, so grade, rate override, allocation, computer
      // rental, the three allowances and notes all land, from ONE builder.
      // The dates are forced to `ganttDayEffectiveDates` even though the
      // two-way binding keeps the form equal to them: steps 2 and 3 are cut
      // from that value, and three writes disagreeing about the engagement's
      // span is the class of bug `ganttDayEffectiveDates` exists to prevent.
      if (ganttDayDatesDirty || formDirty) {
        const memberBody = detailsOpen
          ? {
            ...buildMemberPutPayload(member.id, editMemberForm),
            start_date: ganttDayEffectiveDates.start_date,
            end_date: ganttDayEffectiveDates.end_date,
          }
          : {
            // Phase 11 / Task 18: forward selectedVersionId for consistency.
            version_id: selectedVersionId,
            crew_grade_id: member.crew_grade_id,
            name: member.name,
            location: member.location || '',
            start_date: ganttDayEffectiveDates.start_date,
            end_date: ganttDayEffectiveDates.end_date,
            default_allocation: member.default_allocation,
            computer_rental: member.computer_rental,
            notes: member.notes || '',
            sort_order: member.sort_order ?? 0,
          };
        const memberRes = await apiPut(`/api/budget/crew-members/${member.id}`, memberBody);
        const memberData = await memberRes.json();
        if (!memberRes.ok) throwApiError(memberRes, memberData, 'Failed to update crew member');
      }

      // ── STEP 2: WEEKS ───────────────────────────────────────────────────
      // 🔑 ONE MERGED PAYLOAD, NOT TWO CALLS — `PUT /weeks` replaces the set.
      // `weekOverridesForEdit` (utils/crewDayResolution.js) is THE rule, shared
      // with the crew table's save since 2026-10-19 (ruling A): the
      // date-derived array when the dates MOVED (a form-only save does not
      // rewrite allocations from unchanged dates), counted at the form's
      // default at level 2 (undefined at level 1 = stored); the stored set
      // otherwise; the day toggles WIN on any week they name, filtered to the
      // effective week span. Reasons at the helper.
      const overrides = weekOverridesForEdit({
        member,
        startDate: ganttDayEffectiveDates.start_date,
        endDate: ganttDayEffectiveDates.end_date,
        draftDefault: ganttDraftDefaultAllocation,
        touched: ganttDayWeekOverrides,
      });
      // Ruling B (10-20): a moved date marks it `date_change_save`, so the server
      // re-lays nothing and the days step 1 laid (the drawn ones) stand.
      const weekRes = await apiPut(`/api/budget/crew-members/${member.id}/weeks`, weeksSaveBody({
        member,
        startDate: ganttDayEffectiveDates.start_date,
        endDate: ganttDayEffectiveDates.end_date,
        overrides,
      }));
      if (!weekRes.ok) {
        const weekErr = await weekRes.json();
        throwApiError(weekRes, weekErr, 'Failed to save week overrides');
      }

      // ── STEP 3: DAYS ────────────────────────────────────────────────────
      // Cut from the EFFECTIVE dates, so a drag that widened the engagement
      // posts a range covering the new weeks. `buildDayPayload` returns null
      // when no week's day set changed, so a dates-only save sends no `/days`
      // at all and the server's own re-derivation from the new dates stands —
      // which is right: the user moved dates, they did not record days, and
      // posting a set here would freeze the member (`is_presumed = 0`).
      const daysPayload = buildDayPayload({
        startDate: ganttDayEffectiveDates.start_date,
        endDate: ganttDayEffectiveDates.end_date,
        overlay: ganttDayOverlay,
        baseline: ganttDayBaseline,
      });
      if (daysPayload) {
        const daysRes = await apiPut(`/api/budget/crew-members/${member.id}/days`, daysPayload);
        if (!daysRes.ok) {
          const daysErr = await daysRes.json();
          throwApiError(daysRes, daysErr, 'Failed to save worked days');
        }
      }

      // The linked person's email / phone, LAST and only at level 2. It is a
      // different table (`users`) and a different authority question, so it
      // sits outside the three-step ordering rather than inside it.
      if (detailsOpen) await saveLinkedUserContact(member.id, editMemberForm);

      // The message names what actually changed - a save can now carry dates,
      // days, details, or any combination, and "Worked days saved" after a
      // pure date drag would be a report of work that did not happen.
      const savedParts = [
        ganttDayDatesDirty && 'dates',
        ganttDayDaysDirty && 'worked days',
        formDirty && 'details',
      ].filter(Boolean);
      const savedWhat = savedParts.length > 1
        ? `${savedParts.slice(0, -1).join(', ')} and ${savedParts[savedParts.length - 1]}`
        : savedParts[0];
      toast.success(
        `${savedWhat.charAt(0).toUpperCase()}${savedWhat.slice(1)} saved for "${member.name}"`
      );
      closeGanttDayStrip();
      // 🔀 SILENT (2026-08-24) — was a bare `fetchMembers()`. The unqualified
      // call flips `membersLoading` true (see `fetchMembers`'s own `if
      // (!silent) setMembersLoading(true)`), which unmounts the ENTIRE
      // "Members Table + Gantt" block into `<LoadingSpinner>` and remounts it
      // a beat later — the surface the user is looking at disappears, which
      // reads as a page refresh (owner report: "clicking save on an edited
      // gantt chart seems to refresh the page"). The neighbour call below has
      // passed `silent` since this file existed; this call simply never did.
      // The save is still confirmed three other ways: the success toast set
      // above, `closeGanttDayStrip()` synchronously collapsing the open strip
      // back to its normal row, and the row's bar/price updating in place
      // once this fetch resolves — so silencing the spinner here removes no
      // feedback, only the flash.
      await fetchMembers(true);
      // Refresh week data if this member's row is also expanded in the table.
      if (expandedMemberId === member.id) fetchWeekData(member.id, true);
      notifyDataBus('budget-changed', { departmentId, reason: 'crew-gantt-days' });
      return true;
    } catch (err) {
      // 🔴 The row STAYS OPEN and the overlay is NOT cleared on failure — the
      // user's edit is the only copy of it, and discarding it here would lose
      // work to a dropped connection.
      toast.error(err);
      return false;
    } finally {
      setGanttDaySaving(false);
    }
  };

  /* ─── The exit prompt's three outcomes ───
   *
   * `Keep editing` (cancel) simply drops the pending action — the control the
   * user pressed does nothing, which is now an ANSWER rather than the silent
   * refusal it replaced.
   */
  const handleGanttDayExitCancel = () => setGanttDayExit(null);

  const handleGanttDayExitDiscard = () => {
    const pending = ganttDayExit;
    setGanttDayExit(null);
    // The crew TABLE's held click re-runs with `force`, which re-seeds (open
    // another member) or clears (collapse) the form — that IS the discard.
    if (pending?.kind === 'table') { pending.proceed(); return; }
    // `kind: 'both'` falls through: the strip's half is dropped by
    // `closeGanttDayStrip` (it collapses nothing — no Gantt ownership), and
    // the table form's half by `proceed`'s forced re-seed. Nothing is written.
    // 🚨 DISCARD RESTORES BOTH HALVES. `closeGanttDayStrip` drops the day
    // overlay AND `ganttDayWeekOverrides`; restoring only the days would leave
    // the allocation those same clicks wrote staged for the next save, and the
    // allocation is what drives computer rental and all three allowances. The
    // pre-edit state is the STORED state, which nothing here has touched — the
    // whole edit is an overlay, so discarding it is a delete, not a rollback.
    closeGanttDayStrip();
    pending?.proceed();
  };

  const handleGanttDayExitSave = async () => {
    const pending = ganttDayExit;
    // No single Save can land both surfaces safely (see `guardGanttDayEdit`).
    // The button is disabled for this kind; this is the belt behind it.
    if (pending?.kind === 'both') return;
    const saved = pending?.kind === 'table'
      ? await handleSaveEditMember(expandedMemberId, false)
      : await handleGanttDaySave();
    // 🔴 Only proceed on a save that LANDED. A failed save leaves the strip
    // open holding the only copy of the edit; navigating away from it would
    // turn a recoverable error into the data loss this dialog exists to stop.
    if (!saved) return;
    setGanttDayExit(null);
    pending?.proceed();
  };

  /* 🌐 LEAVING THE PAGE — the exits the in-page dialog above cannot see.
   * 🔀 R14 (2026-09-15): was a bare `beforeunload` on `ganttSurfaceDirty`, and
   * `BudgetCostCodes.ganttDayExitGuard.test.js` N4 declared in-app navigation
   * (the sidebar links) *"the single biggest remaining hole"*, because blocking
   * one needed a router-level blocker. `hooks/useUnsavedChangesGuard.js` is that
   * blocker (plus the same `beforeunload`), so N4 is now COVERED.
   * 🔑 ONE guard for the page over the UNION of its two surfaces — the Gantt
   * strip (`ganttSurfaceDirty`: days, pending dates, the level-2 form) OR the
   * crew TABLE's expand-to-edit form (`isMemberDirty`, which had no leave
   * protection at all). react-router asks only one blocker, so two guards here
   * would silently drop one surface. The Budget tab links (`budget/overview`)
   * change the pathname and prompt; the section tabs change only the hash and
   * do not (they keep their own in-page exit prompt above). */
  const leaveGuard = useUnsavedChangesGuard({
    when: ganttSurfaceDirty || isMemberDirty || ptList.dirty,
    message: (ganttSurfaceDirty || isMemberDirty)
      ? "Your changes to this crew member haven't been saved. If you leave, they will be lost."
      : "Your changes to this list haven't been saved. If you leave, they will be lost.",
  });

  // 🔻 `computeDateChangeOverrides` (a one-line wrapper over
  // `crewDayResolution.js#dateChangeWeekOverrides`) is DELETED 2026-10-19 —
  // pre-deletion SHA b6b2c9c1. Every save and preview now calls
  // `weekOverridesForEdit`, which applies it (ruling A: table = Gantt).

  /**
   * THE GANTT'S DRAG-END ENTRY POINT. It writes NOTHING — owner ruling,
   * 2026-08-22: *"once the gantt is expanded we should be able to drag and see
   * the price and bar update etc but it doesnt 'Lock in' until saved."*
   *
   * 🔻 IT USED TO BE AN EXIT AND IS NOW AN EDIT. A date drag on the member
   * being day-edited was the least obvious way to destroy that edit (nothing
   * collapses, nothing navigates) and so it raised the exit prompt. With the
   * drag batched it no longer destroys anything: it becomes the THIRD pending
   * half of the same edit, alongside the day set and the allocations, and it is
   * saved and discarded with them. The prompt therefore does NOT fire here any
   * more, and `ganttDayDirty` covers the pending dates instead.
   *
   * 🔑 THE `!==` GUARD IS DEFENCE IN DEPTH, NOT A REACHABLE BRANCH. Since
   * `CrewGanttView#canDragMember` gates dragging on the row being expanded,
   * there is exactly ONE draggable bar at any moment and it is this strip's.
   * The guard is here so that a future change loosening the drag gate cannot
   * silently start writing another member's dates into this strip's overlay.
   */
  const handleGanttUpdate = (memberId, { start_date, end_date }) => {
    if (memberId !== ganttDayMemberId) { setDragPreview(null); return; }
    /* 🔻 IT USED TO CAPTURE `budget_cost` / `total_weeks` OFF THE LAST DRAG
       FRAME AND NO LONGER DOES (2026-08-23). Those figures were
       `weeks × rate`, computed in `CrewGanttView`, and they are the number the
       owner reported as ignoring off days. `ganttDayDates` now carries DATES
       ONLY; the money for the pending drag comes from `ganttStripPreview`
       below — the server's answer for this exact draft — and it survives the
       release for the same reason the captured figure used to, because the
       draft it is costed from survives. */
    setGanttDayUndoStack((prev) => [...prev, { kind: 'dates', prevDates: ganttDayDates }]);
    setGanttDayDates({ start_date, end_date });
    setDragPreview(null);
    syncGanttDetailsDates(memberId, start_date, end_date);
  };

  /**
   * The FORM → BAR half of the same binding. The date inputs in the details
   * panel call this instead of `setEditMemberForm` directly.
   *
   * 🔑 IT WRITES THE PENDING-DRAG OVERLAY, NOT THE MEMBER. Typing a date at
   * level 2 is exactly as provisional as dragging the bar — it moves the bar,
   * arms `ganttDayDatesDirty`, joins the same undo stack and the same Save. The
   * owner's ruling that a bar drag *"doesnt 'Lock in' until saved"* is a
   * statement about the DATES, not about the pointer that moved them.
   *
   * 🚨 THE PREVIEW FIGURES ARE DROPPED, NEVER GUESSED. `ganttDayDates` may
   * carry `budget_cost` / `total_weeks` captured off the last drag frame; a
   * typed date has no such frame, and re-deriving one here would be a fourth
   * client-side cost formula in a directory whose whole rule is that the server
   * computes the money. Leaving them `undefined` makes the label column fall
   * back to the member's stored figure, which is honest: it is the last number
   * the server actually said.
   */
  const handleMemberDateFieldChange = (field, value) => {
    setEditMemberForm((prev) => ({ ...prev, [field]: value }));
    if (ganttDetailsMemberId == null || ganttDetailsMemberId !== ganttDayMemberId) return;
    const next = {
      start_date: field === 'start_date' ? (value || null) : (ganttDayEffectiveDates.start_date || null),
      end_date: field === 'end_date' ? (value || null) : (ganttDayEffectiveDates.end_date || null),
    };
    setGanttDayUndoStack((prev) => [...prev, { kind: 'dates', prevDates: ganttDayDates }]);
    setGanttDayDates(next);
  };


  /* ─── Week override editing ───
   * 🔀 `handleWeekChange` (the raw allocation writer behind the old per-week
   * `<select>`) was DELETED with the day grid, not left dangling: allocations
   * are now written only as a consequence of a day-count change, through the
   * ONE mapping in `constants/budget.js#allocationForDayCount`. A second
   * writer that could set an allocation the day set does not imply is exactly
   * the divergence this feature exists to close.
   */

  /* ─── The crew DAY GRID — 6th/7th-day premium wage feature, Task 6 ───────
   *
   * 🔑 BOTH HANDLERS WRITE TWO THINGS, AND THE SECOND ONE IS THE MONEY.
   * `weekDayOverlay` is the day set (drives LABOUR, via `crew_member_days`).
   * `weekOverrides` is the allocation (still drives COMPUTER RENTAL and all
   * three ALLOWANCES, via `computeCrewWeeks` — a laptop does not cost more
   * because someone worked a Saturday). Writing only the first would leave a
   * "2 days" week paying a full week of rental. The mapping and its clamp at
   * five live in ONE place, `constants/budget.js#allocationForDayCount`.
   *
   * 🔴 THE ZERO-CASE SEAM. Setting the preset to "Off" is `dayCount === 0`,
   * which writes allocation 0 — and THAT is what makes an off week cost
   * nothing today, through the fraction path that already honours an explicit
   * override of 0. "Off" therefore behaves exactly as it did before this
   * feature. Do NOT decouple these two writes so the grid can clear a week's
   * days while leaving its allocation alone: a member whose day rows are all
   * gone is byte-identical to a member who has no day data yet, and is
   * correctly charged the FULL fraction cost. The deliberate-zero marker that
   * would make "no days, on purpose" expressible is ledger entry **S2-F** in
   * `.superpowers/sdd/2026-08-20-sixth-seventh-day-wages/progress.md`, which
   * is OPEN and being decided separately. When it lands it drops in here and
   * in `WEEK_SHAPE_PRESETS`, with no redesign of the grid.
   */
  const handleWeekPreset = (weekStart, dayCount) => {
    setWeekDayOverlay(prev => ({ ...prev, [weekStart]: daysForPreset(dayCount) }));
    setWeekOverrides(prev => ({ ...prev, [weekStart]: allocationForDayCount(dayCount) }));
  };

  const handleDayToggle = (weekStart, dayIndex) => {
    // 🔴 The RANGE matters HERE too, and visibly: without it the first tick in
    // a newly-in-range week read `[]` while the grid drew five ticks, so
    // ticking Saturday COLLAPSED the week to Saturday alone (5 → 1). It is the
    // same argument that used to be made for the allocation fallback this
    // replaced — the tick handler MUST resolve the week exactly as the render
    // drew it, whatever the rule for an unknown day happens to be.
    const current = resolveWeekDays(
      weekDayOverlay, weekDayBaseline, weekStart, weekDayCurrentRange
    );
    const next = current.includes(dayIndex)
      ? current.filter(i => i !== dayIndex)
      : [...current, dayIndex].sort((a, b) => a - b);
    setWeekDayOverlay(prev => ({ ...prev, [weekStart]: next }));
    // The preset and the ticks are ONE state: ticking Saturday on a 5-day
    // week makes the control read "6-day week", and the allocation follows.
    setWeekOverrides(prev => ({ ...prev, [weekStart]: allocationForDayCount(next.length) }));
  };

  const handleToggleWeekExpand = (weekStart) => {
    setExpandedWeek(prev => (prev === weekStart ? null : weekStart));
  };


  /* ─── Reorder helpers (optimistic update) ─── */
  // Reorders the ordinary cost codes only — job codes are listed in their own
  // section in creation order (J01, J02 …) and carry no reorder control.
  const handleReorderCostCode = async (idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= standardCodes.length) return;
    const a = standardCodes[idx], b = standardCodes[swapIdx];
    const sortA = idx, sortB = swapIdx;
    setCostCodes(prev => {
      const updated = prev.map(cc => {
        if (cc.id === a.id) return { ...cc, sort_order: sortB };
        if (cc.id === b.id) return { ...cc, sort_order: sortA };
        return cc;
      });
      return updated.sort((x, y) => (x.sort_order - y.sort_order) || x.code.localeCompare(y.code));
    });
    try {
      setError(null);
      await apiPut(`/api/budget/cost-codes/${a.id}`, { code: a.code, name: a.name, description: a.description || '', sort_order: sortB });
      await apiPut(`/api/budget/cost-codes/${b.id}`, { code: b.code, name: b.name, description: b.description || '', sort_order: sortA });
      notifyDataBus('budget-changed', { departmentId, reason: 'cost-code' });
    } catch (err) { toast.error(err); fetchCostCodes(); }
  };

  const handleReorderGrade = async (idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= grades.length) return;
    const a = grades[idx], b = grades[swapIdx];
    const sortA = idx, sortB = swapIdx;
    setGrades(prev => {
      const updated = prev.map(g => {
        if (g.id === a.id) return { ...g, sort_order: sortB };
        if (g.id === b.id) return { ...g, sort_order: sortA };
        return g;
      });
      return updated.sort((x, y) => (x.sort_order - y.sort_order) || x.name.localeCompare(y.name));
    });
    try {
      setError(null);
      await apiPut(`/api/budget/crew-grades/${a.id}`, { name: a.name, weekly_rate: a.weekly_rate, computer_rental: a.computer_rental ?? 0, sort_order: sortB });
      await apiPut(`/api/budget/crew-grades/${b.id}`, { name: b.name, weekly_rate: b.weekly_rate, computer_rental: b.computer_rental ?? 0, sort_order: sortA });
    } catch (err) { toast.error(err); fetchGrades(); }
  };

  const handleReorderMember = async (idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= members.length) return;
    const a = members[idx], b = members[swapIdx];
    const sortA = idx, sortB = swapIdx;
    setMembers(prev => {
      const updated = prev.map(m => {
        if (m.id === a.id) return { ...m, sort_order: sortB };
        if (m.id === b.id) return { ...m, sort_order: sortA };
        return m;
      });
      return updated.sort((x, y) => (x.sort_order - y.sort_order) || x.name.localeCompare(y.name));
    });
    try {
      setError(null);
      // Phase 11 / Task 18: forward selectedVersionId on reorder PUTs.
      // Phase 11 polish: surface lock-guard 423 via apiThrow.
      const resA = await apiPut(`/api/budget/crew-members/${a.id}`, { version_id: selectedVersionId, crew_grade_id: a.crew_grade_id, name: a.name, location: a.location || '', start_date: a.start_date, end_date: a.end_date, default_allocation: a.default_allocation, computer_rental: a.computer_rental, notes: a.notes || '', sort_order: sortB });
      if (!resA.ok) await apiThrow(resA);
      const resB = await apiPut(`/api/budget/crew-members/${b.id}`, { version_id: selectedVersionId, crew_grade_id: b.crew_grade_id, name: b.name, location: b.location || '', start_date: b.start_date, end_date: b.end_date, default_allocation: b.default_allocation, computer_rental: b.computer_rental, notes: b.notes || '', sort_order: sortA });
      if (!resB.ok) await apiThrow(resB);
    } catch (err) { toast.error(err); fetchMembers(true); }
  };

  /* ─── Always-editable lists: ONE save for the whole list (2026-10-25) ───
     Deletes first, then updates (new sort_order = list position), then creates.
     Every write is the SAME call the old per-row paths made. A failed write
     stops the run, reports it, and reloads the saved list. */
  const runListSave = async ({ list, noun, nameOf, validate, del, put, post, refetch, setSaving }) => {
    const { creates, updates, deletes } = list.changes();
    const problem = validate([...creates, ...updates]);
    if (problem) { setError(problem); return; }
    if (deletes.length && !window.confirm(`Delete ${deletes.map((d) => `"${nameOf(d)}"`).join(', ')}? This happens when you save.`)) return;
    setSaving(true);
    setError(null);
    try {
      for (const d of deletes) { const res = await del(d); if (!res.ok) await apiThrow(res); }
      for (const u of updates) { const res = await put(u); if (!res.ok) await apiThrow(res); }
      for (const c of creates) { const res = await post(c); if (!res.ok) await apiThrow(res); }
      const fresh = await refetch();
      list.reset(fresh);
      toast.success(`${noun} saved`);
    } catch (err) {
      toast.error(err);
      list.reset(await refetch());
    } finally {
      setSaving(false);
    }
  };

  const savePaymentTypeList = () => runListSave({
    list: ptList,
    noun: 'Payment types',
    nameOf: (pt) => pt.name,
    validate: (rows) => (rows.some((r) => !String(r.name ?? '').trim()) ? 'Every payment type needs a name.' : null),
    del: (pt) => apiDelete(`/api/budget/payment-types/${pt.id}`),
    put: (r) => apiPut(`/api/budget/payment-types/${r.id}`, { name: r.name.trim(), description: (r.description || '').trim(), sort_order: r.sort_order }),
    post: (r) => apiPost('/api/budget/payment-types', { department_id: departmentId, name: r.name.trim(), description: (r.description || '').trim(), sort_order: r.sort_order }),
    refetch: async () => { const res = await apiGet(`/api/budget/payment-types?department_id=${departmentId}`); const data = res.ok ? await res.json() : paymentTypes; setPaymentTypes(data); return data; },
    setSaving: setSavingPaymentType,
  });

  // --- Computed values ---
  const selectedVersion = versions.find(v => v.id === selectedVersionId);
  const isSelectedCurrent = !!selectedVersion?.is_current;
  // Phase 11 / Task 19: when the selected version is locked, every editing
  // surface (Add buttons, inline edit forms, delete, reorder) must disable.
  // The cleanest path is to fold lock state INTO `canEdit` so all existing
  // gates pick it up for free — we still keep the raw flag around for the
  // banner.
  const isVersionLocked = !!selectedVersion?.is_locked;
  // Anchor flag for Variance Analysis. Drives the "Make Baseline" button's
  // disabled state + the inline "(Baseline)" indicator. Server contract:
  // exactly one version per dept can have is_baseline=1 at a time.
  const isSelectedBaseline = !!selectedVersion?.is_baseline;
  // Phase 11 / Task 20: `canManageVersions` is the raw role check — needed
  // by the version-selector controls (New / Rename / Clone / Lock / Unlock /
  // Delete / Set Current). These must stay accessible even when the selected
  // version is locked, so the user has a way to unlock or clone out of the
  // locked state.
  //
  // ⚠️ 2026-08-05 — this was `isAdmin || isBuyer || canManageBudget`, where both
  // of the latter are ANY-DEPARTMENT flags from `GET /api/roles/my-assignments`
  // (`canManageBudget` is OR'd across every assignment; `isBuyer` is the single
  // global `user_roles` row). This page acts on ONE department — the
  // `activeDepartment.department_id` resolved at the top of this component — so
  // the old flags showed version controls that the server 403s to anyone holding
  // budget authority in a DIFFERENT department. Seeding
  // supervisor.can_manage_budget = 1 would have made that visible for the first
  // time, supervisors commonly being in several departments. See
  // `canManageBudgetVersions` for the full note.
  const canManageVersions = canManageBudgetVersions({ isAdmin, capabilities });
  const canEdit = canManageVersions && !isVersionLocked;
  const canInvite = canSendCrewInvites({ isAdmin, capabilities });

  /* ═══ THE LIVE COST PREVIEW (2026-08-23) ═══════════════════════════════════
     Owner: *"updating values in the expanded details section should live update
     the totals and then lock them in when save is pressed. Currently I update
     computer hire and no prices change."*

     🔑 IT SENDS `buildMemberPutPayload` — THE SAME BODY THE SAVE SENDS. That is
     the whole design: the preview request and the save request differ in their
     URL and in nothing else, so "what you see is what you will get" is a
     structural property rather than something to hope for and test around. The
     server then runs the SAME assembly `GET /crew-members` runs. There is no
     arithmetic on this page.

     🔻 GATED ON THE FORM BEING DIRTY, not merely on the panel being open —
     otherwise expanding a row fires a costing that can only return the figures
     already on screen. `isMemberDirty` is deliberately NOT the gate: it also
     goes true for week and day edits, which this endpoint does not preview (they
     have their own editors and their own saves), so using it would fire a
     request that changes nothing.

     🔻 AND NOT WHEN THE USER CANNOT EDIT. A read-only viewer has no draft, so
     there is nothing provisional and no request. */
  const memberFormDirty = !!originalMemberForm
    && JSON.stringify(editMemberForm) !== JSON.stringify(originalMemberForm);

  /* ═══ THE DRAFT SCHEDULE GOES WITH THE DRAFT FORM (2026-08-23) ════════════
     Owner: *"I just added a 6 day to a person with no current 6th days and the
     summary did not update. Presume because there were no initial 6th days to
     show in the summary."*

     🔻 HIS DIAGNOSIS IS WRONG AND THE TRUTH IS WORSE — SAY SO RATHER THAN
     FIXING WHAT HE GUESSED. Conditional rendering does not stop a re-render
     when a count goes 0 → 1; React would simply start showing it. **The preview
     fired on MEMBER FORM changes only, and a day toggle is a DAY-STRIP edit
     writing different state — so the one edit that most directly changes
     premium cost was the one edit that refreshed nothing.** And it was broken
     TWICE OVER: even had the request fired, the endpoint read
     `crew_member_days` from the DATABASE, so it would have costed the SAVED
     days and returned figures identical to the ones already on screen. Fixing
     either half alone leaves a control that still does not move.

     🔑 THE DRAFT DAY SET COMES FROM `buildDayPayload` — THE SAME RESOLVER THE
     SAVE USES, NOT A SECOND ONE. Contract item 1: a member's day set is
     resolved ONCE, through `utils/crewDayResolution.js`. This adds a READER of
     that resolution, never a rival loop.

     🔑 AND `buildDayPayload` RETURNING `null` IS EXACTLY THE SIGNAL WANTED. It
     answers null when no week's day set actually changed — which on the save
     path means "send no `/days` at all, do not freeze the member", and here
     means "send no `days`, the server's stored rows are already right". One
     predicate, two correct readings, and no second test of changedness.

     🪤 TWO SURFACES, TWO OVERLAYS, ONE PANEL. The crew table's day grid
     writes `weekDayOverlay`; the Gantt strip writes `ganttDayOverlay`. The
     panel showing the cost summary can be either, so the triple is chosen by
     OWNERSHIP (`ganttFormMemberId`), never by visibility — contract item 13,
     and the same key the Save and the `PUT` body already use. Reading the wrong
     overlay would cost one surface's draft while the user looked at the
     other's. */
  const previewOwnedByGanttStrip = ganttFormMemberId != null
    && ganttFormMemberId === expandedMemberId;

  /** The dates the strip is costed against: mid-gesture the LIVE drag frame,
   *  otherwise the effective (possibly pending) dates.
   *  🔑 Reading `dragPreview` here is what makes the figures settle DURING the
   *  gesture rather than on release — which is the owner's complaint stated
   *  positively. The BAR still follows the pointer with no request in the way;
   *  only the numbers lag, by the debounce.
   *  🔀 R8 (2026-10-19) — declared HERE, above the edit preview, because since
   *  "expand opens Details" the Gantt owns the form on EVERY expand, so the
   *  EDIT preview is the strip's costing and must cut its dates, `days` and
   *  `week_overrides` from this same range. Cut from `ganttDayEffectiveDates`
   *  instead (pre-drag until the drop) it would cost one set while the bar
   *  draws another — contract item 15, "drawn = costed", broken mid-drag. */
  const ganttStripCostDates = useMemo(() => {
    if (ganttDayMemberId != null && dragPreview && dragPreview.memberId === ganttDayMemberId) {
      return { start_date: dragPreview.start_date, end_date: dragPreview.end_date };
    }
    return ganttDayEffectiveDates;
  }, [ganttDayMemberId, dragPreview, ganttDayEffectiveDates]);
  const ganttStripDragging = !!(ganttDayMemberId != null && dragPreview
    && dragPreview.memberId === ganttDayMemberId);

  const previewDayPayload = useMemo(() => {
    if (expandedMemberId == null) return null;
    return previewOwnedByGanttStrip
      ? buildDayPayload({
        startDate: ganttStripCostDates.start_date,
        endDate: ganttStripCostDates.end_date,
        overlay: ganttDayOverlay,
        baseline: ganttDayBaseline,
      })
      : buildDayPayload({
        startDate: editMemberForm.start_date,
        endDate: editMemberForm.end_date,
        overlay: weekDayOverlay,
        baseline: weekDayBaseline,
      });
  }, [
    expandedMemberId, previewOwnedByGanttStrip,
    ganttStripCostDates, ganttDayOverlay, ganttDayBaseline,
    editMemberForm.start_date, editMemberForm.end_date,
    weekDayOverlay, weekDayBaseline,
  ]);

  /* The REPLACE-ALL week-override array — what both saves post.
     🔑 A DAY TOGGLE WRITES TWO THINGS (contract item 2): the day set AND
     `crew_week_overrides.allocation` via `allocationForDayCount`. So previewing
     the days WITHOUT the allocations would move LABOUR while leaving computer
     rental and all three allowances on their saved figures — an internally
     inconsistent total, which is this feature's own defect shape reappearing
     one level down.
     🪤 The Gantt strip's overlay is a PATCH over the member's stored set while
     the table's `weekOverrides` is already the whole set, so the two are
     assembled differently — mirroring each surface's own save exactly rather
     than inventing a third rule. `null` means "no draft, use the stored rows". */
  const previewWeekOverrides = useMemo(() => {
    if (expandedMemberId == null) return null;
    if (!previewOwnedByGanttStrip) {
      // 🔀 2026-10-19 (ruling A): what the table's Save posts to `/weeks`.
      return originalWeekData === null || !tableWeekOverrides
        ? null
        : draftWeekOverridesOrNull(tableStoredMember, tableWeekOverrides);
    }
    /* 🔑 THE STRIP'S OWN DIRTY SIGNAL IS THE OUTER GATE, and reusing it
       rather than inventing a second test is the point. `ganttDayWeekOverrides`
       keeps an entry for every week the user has clicked in, so a toggle UNDONE
       leaves a key behind whose value has returned to what the week implies —
       and `allocationForDayCount(5)` on a member with no stored override is a
       BRAND-NEW row carrying the value they already had by default. Comparing
       maps alone therefore still reports a draft, holds the preview open after
       the strip has gone clean, and leaves stale provisional figures on screen
       with nothing to justify them (measured: §6.1 of
       `BudgetCostCodes.dayTogglePreview.test.js` reds on exactly that). The
       strip's Save consults `ganttDayDirty` to decide whether to run at all, so
       the preview consults it to decide whether there is anything to cost — one
       rule, two readers. */
    /* 🔀 THROUGH `buildGanttStripWeekOverrides` SINCE 2026-08-23. This branch
       was the only assembler of the strip's override array until the strip
       gained its OWN cost preview at level 1, which needs the identical answer
       from the identical state. Two copies of a money payload is the defect
       class this feature spent the week collapsing; the rules and their
       measurements moved WITH the code, to that function's docblock. */
    // R8: the LIVE range, exactly as the level-1 strip body cuts it.
    return buildGanttStripWeekOverrides(ganttStripCostDates);
  }, [
    expandedMemberId, previewOwnedByGanttStrip,
    originalWeekData, tableWeekOverrides, tableStoredMember,
    ganttStripCostDates, buildGanttStripWeekOverrides,
  ]);

  /* 🔻 THE GATE WIDENED WITH THE PAYLOAD, AND IT HAD TO. The old comment said
     `isMemberDirty` was deliberately NOT the gate because it "also goes true for
     week and day edits, which this endpoint does not preview" — and that
     premise is precisely what this change deletes. A schedule edit is now
     previewable, so it must also be able to FIRE one; leaving the gate on the
     form alone would ship a server able to answer a question the client never
     asks.
     🔑 Still NOT `isMemberDirty` itself, for a different reason: that flag is
     the crew TABLE's, and the Gantt strip's day edits never reach it. The union
     below is the honest "is there a draft to cost?". */
  /* 🔀 R8: while the Gantt owns the form, a strip drag or a moved date is a
     draft to cost even when the form itself reads clean — with the panel
     HIDDEN `syncGanttDetailsDates` does not write the form, and a date-only
     drag carries no `days`, so neither half above would fire. */
  const previewStripDirty = previewOwnedByGanttStrip && (ganttDayDirty || ganttStripDragging);
  const previewScheduleDirty = previewDayPayload != null || previewWeekOverrides != null
    || previewStripDirty;
  const previewMemberId = expandedMemberId != null && canEdit
    && (memberFormDirty || previewScheduleDirty)
    ? expandedMemberId
    : null;

  /* 🔑 ONE BODY, THE UNION OF THE THREE SAVE BODIES. `buildMemberPutPayload` is
     what `PUT /crew-members/:id` sends, `days` is what `PUT /:id/days` sends and
     `week_overrides` is what `PUT /:id/weeks` sends — so *"the preview is what
     you will get"* becomes a structural property of the WHOLE save rather than
     of its first step alone.
     🪤 A key is OMITTED, never sent as `null`, when there is no draft for it:
     the server reads `undefined` as "use the stored rows" and `[]` as "none",
     and conflating the two would cost a member their saved schedule the moment
     they typed in an unrelated field. */
  const previewDraftBody = useMemo(() => {
    if (previewMemberId == null) return null;
    /* 🔴 MONEY (N-3, 2026-10-29): a form Save would REFUSE is never costed. The payload builder
       maps an unreadable box to `null` = "inherit", so the preview priced the GRADE's multiplier
       while the box read "Invalid number" — a figure Save will not store (the 10-06 "preview =
       what Save stores" rule, inverted). Same entries as both member Saves' guard; `null` here
       = no request, and the hook clears the last figure. */
    if (crewMoneyFormError([...memberMoneyEntries(editMemberForm), ...overtimeRateMoneyEntries(editMemberForm, 'member')])) return null;
    const body = buildMemberPutPayload(previewMemberId, editMemberForm);
    /* 🔀 R8: owned by the Gantt, the dates are the strip's LIVE range — the
       same override the Save applies (`ganttDayEffectiveDates` over the form),
       taken one frame earlier so a held drag is costed as drawn. */
    if (previewOwnedByGanttStrip) {
      body.start_date = ganttStripCostDates.start_date;
      body.end_date = ganttStripCostDates.end_date;
    }
    if (previewDayPayload) body.days = previewDayPayload.days;
    if (previewWeekOverrides) body.week_overrides = previewWeekOverrides;
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMemberId, editMemberForm, previewDayPayload, previewWeekOverrides, members, selectedVersionId,
    previewOwnedByGanttStrip, ganttStripCostDates]);

  const {
    preview: costPreview, pending: costPreviewPending, failed: costPreviewFailed,
  } = useCrewCostPreview({
    memberId: previewMemberId,
    draftBody: previewDraftBody,
    enabled: previewMemberId != null,
    /* 🔀 R8: owned by the Gantt this IS the strip's costing, so it keeps the
       strip's timing — the 2026-10-06 "footer changes live while dragging"
       ruling (`maxWaitMs`). The crew table keeps the hook's own default. */
    ...(previewOwnedByGanttStrip
      ? { delayMs: GANTT_COST_PREVIEW_DEBOUNCE_MS, maxWaitMs: GANTT_COST_PREVIEW_DEBOUNCE_MS }
      : {}),
  });


  /* ═══ THE GANTT STRIP'S OWN LIVE COSTING — 2026-08-23 ══════════════════
   *
   * Owner: *"when dragging the totals update but seems to not calculate the
   * off days until the mouse is released and then the off days are factored
   * into the calculation."*
   *
   * 🔻 WHAT HE WAS LOOKING AT WAS A **DECLARED LIMITATION**, NOT A NEW BUG.
   * The drag preview was `weeks × rate` computed in `CrewGanttView`, while
   * day-model labour is `days / 5 × rate`; §4 of
   * `server/tests/regressions/crew-cost-path-parity-manifest.test.js` recorded
   * that it over- or under-states and left it, because a wrong number during a
   * transient gesture was an acceptable trade. **Batching the drag broke the
   * trade** — the figure now stands from the first pointer move until Save, so
   * the user reads it, believes it, and acts on it.
   *
   * 🔑 THE TOOL THAT DID NOT EXIST THEN. `POST /crew-members/:id/cost-preview`
   * runs `buildCrewMemberCostFields` — the SAME assembly `GET /crew-members`
   * runs — over a draft, and since 2026-08-23 it takes a draft `days` array and
   * `week_overrides` too. So the strip can be costed by the real formula
   * instead of an approximation of it. **The alternative — re-deriving
   * day-aware labour in the browser to keep the drag instant — is refused:
   * this subsystem has four duplicated cost formulas in its history and one of
   * them understated real wages.**
   *
   * 🔑 IT IS A SECOND INSTANCE OF THE HOOK, NOT A SECOND FORMULA. Same hook,
   * same endpoint, same stale-response guard. What differs is the DRAFT: this
   * one is assembled from the STRIP's state, because at level 1 there is no
   * member form and `editMemberForm` is empty.
   * 🪤 So it stands down when level 2 owns the member (`ganttFormMemberId`),
   * where the edit preview already covers it and is strictly more complete —
   * two live previews for one member is the "which figure is on screen"
   * question this whole feature exists to have one answer to.
   */

  /* `ganttStripCostDates` — declared above the edit preview since R8. */

  /**
   * The worked DATES the Gantt should draw — one set, resolved once. Handed to
   * `CrewGanttView` as `dayEditWorkedDates`, where it becomes both the bar's
   * per-day fade and the toggles' pressed state.
   * 🔀 2026-10-06 (owner: *"make the days populate live as we drag"*): it is
   * resolved over `ganttStripCostDates` — the LIVE drag frame mid-gesture, the
   * effective dates otherwise — i.e. exactly the range `ganttStripDraftBody`
   * sends to `cost-preview` through the same `buildDayPayload` inputs. So the
   * days drawn during a drag are the days being COSTED, and the bar fills in
   * as it moves instead of waiting for the drop.
   */
  const ganttDayWorkedDates = useMemo(() => {
    if (!ganttDayMember) return null;
    return resolveWorkedDateSet({
      startDate: ganttStripCostDates.start_date,
      endDate: ganttStripCostDates.end_date,
      overlay: ganttDayOverlay,
      baseline: ganttDayBaseline,
    });
  }, [ganttDayMember, ganttStripCostDates, ganttDayOverlay, ganttDayBaseline]);

  /** The strip's `cost-preview` body, or null when there is nothing to cost.
   *  💡 The three keys mirror the strip's own three-step save exactly
   *  (`dates → /weeks → /days`), which is what makes *"the preview is what you
   *  will get"* structural rather than hoped for.
   *  🪤 A key is OMITTED, never sent as `null`: the server reads `undefined`
   *  as "use the stored rows" and `[]` as "none", and conflating them would
   *  cost a member their saved schedule. */
  const ganttStripDraftBody = useMemo(() => {
    if (ganttDayMemberId == null || !ganttDayMember) return null;
    if (ganttFormMemberId === ganttDayMemberId) return null;   // level 2 owns it
    if (!canEdit) return null;
    const dragging = !!(dragPreview && dragPreview.memberId === ganttDayMemberId);
    if (!ganttDayDirty && !dragging) return null;
    const startDate = ganttStripCostDates.start_date;
    const endDate = ganttStripCostDates.end_date;
    const body = { start_date: startDate, end_date: endDate };
    const dayPayload = buildDayPayload({
      startDate,
      endDate,
      overlay: ganttDayOverlay,
      baseline: ganttDayBaseline,
    });
    if (dayPayload) body.days = dayPayload.days;
    const weekOverrides = buildGanttStripWeekOverrides({ start_date: startDate, end_date: endDate });
    if (weekOverrides) body.week_overrides = weekOverrides;
    return body;
  }, [
    ganttDayMemberId, ganttDayMember, ganttFormMemberId, canEdit, dragPreview,
    ganttDayDirty, ganttStripCostDates, ganttDayOverlay, ganttDayBaseline,
    buildGanttStripWeekOverrides,
  ]);

  const {
    preview: ganttStripPreview,
    pending: ganttStripPreviewPending,
    failed: ganttStripPreviewFailed,
  } = useCrewCostPreview({
    memberId: ganttStripDraftBody ? ganttDayMemberId : null,
    draftBody: ganttStripDraftBody,
    enabled: ganttStripDraftBody != null,
    delayMs: GANTT_COST_PREVIEW_DEBOUNCE_MS,
    // ⏱️ 2026-10-06 (owner: the sticky footer should "change live" while
    // dragging): a drag changes the dates every few pixels, so the debounce
    // alone never fired until the pointer stopped. Cost at least this often.
    maxWaitMs: GANTT_COST_PREVIEW_DEBOUNCE_MS,
  });

  /** Does the strip preview apply to this member? */
  const ganttStripPreviewApplies = (member) => (
    ganttStripPreview != null && member != null && member.id === ganttDayMemberId
  );

  /** The three-state signal `CrewGanttView` draws beside the strip member's
   *  price. `failed` beats `pending`: a failed costing has no answer for what
   *  is on screen, and the saved figure must not stand in for it.
   *
   *  🔑 IT READS WHICHEVER PREVIEW OWNS THE MEMBER, which is the same
   *  ownership test everything else on this surface uses. At level 2 the strip
   *  stands down and the EDIT preview is the costing in flight — so the price
   *  under the bar must mark ITS state, or the one figure on that row would be
   *  settling silently while the panel six inches below says `unsaved`. */
  const ganttStripCostState = (() => {
    if (ganttDayMemberId == null) return 'idle';
    if (ganttFormMemberId === ganttDayMemberId) {
      if (previewMemberId !== ganttDayMemberId) return 'idle';
      if (costPreviewFailed) return 'failed';
      // 🔀 R8: the same first-window gap as the strip branch below — a draft
      // the server has not answered for yet is pending whatever the flag says.
      return (costPreviewPending || costPreview == null) ? 'pending' : 'idle';
    }
    if (ganttStripDraftBody == null) return 'idle';
    if (ganttStripPreviewFailed) return 'failed';
    /* 🪤 `pending` ALONE IS NOT ENOUGH, AND THE GAP IS IN THE HOOK'S FIRST
       WINDOW. `useCrewCostPreview` raises `pending` from the effect that
       compares the raw key with the debounced one — but on the very first
       change the debounced key is still `null`, and the fetch effect's
       `if (debouncedKey == null) { … setPending(false); }` runs AFTER it and
       clears it again. So the first costing of a draft is in flight with
       `pending === false`, and the price would sit there unmarked showing the
       SAVED figure while the bar has already moved — the one thing this signal
       exists to prevent. `preview == null` closes it: a draft exists and the
       server has not answered for it yet, whatever the flag says. */
    return (ganttStripPreviewPending || ganttStripPreview == null) ? 'pending' : 'idle';
  })();

  /* 🚨 THE FALLBACK IS THE SAVED MEMBER, NEVER A HALF-FILLED OBJECT. When the
     preview is absent — not requested, in flight for the first time, or FAILED —
     the panel prints the last saved figures, which are true and merely out of
     date. It must never print `Number(null) → 0`, and merging the two objects
     would do exactly that for any key the preview did not carry. So it is one
     object or the other, chosen whole. */
  /* 🔑 ONE PREDICATE, TWO SUBSTITUTIONS. Extracted 2026-08-23 when the sticky
     footer's sums became preview-aware: the panel and the footer must agree
     about WHICH member the preview is for, or they can print different money
     for the same person on the same screen — the defect this whole feature
     exists to close. The expression is unchanged; it has only stopped being
     written twice. */
  const editPreviewApplies = (member) => (
    costPreview != null && member != null && member.id === previewMemberId
  );
  const costSummaryMemberFor = (member) => (
    editPreviewApplies(member) ? { ...member, ...costPreview } : member
  );

  /* ═══ THE ONE MEMBER ROW EVERY LIVE SURFACE READS (2026-08-23) ════════════
     Owner: *"we also need the sticky footer to update 'Cost Code budget: … |
     Labour inc allowances: … | Total: …' when editing"*.

     🔑 A SUBSTITUTION, NOT A CALCULATION. Every figure below is still a
     server-computed one; this only chooses WHICH server-computed figure to
     read for the ONE member currently being previewed. There is no arithmetic
     here and there must never be — this subsystem has four duplicated cost
     formulas in its history and one of them understated real wages.

     ═══ PRECEDENCE — DECIDED, NOT EMERGENT ═══════════════════════════════
     Both previews can be live for the same member at once: at the Gantt's
     level 2 the panel is open with unsaved edits while its own bar is
     draggable. Letting code order pick the winner produces a total that is
     wrong in a way nobody can see, so:

       🥇 THE EDIT PREVIEW WINS, WHOLE. 🥈 The drag preview otherwise.

     Three reasons, in the order they matter:

     1. IT IS THE SERVER'S ANSWER AND THE OTHER IS THE BROWSER'S. The edit
        preview is `POST /crew-members/:id/cost-preview` running the real
        assembly. The drag preview is `weeks × rate` computed in
        `CrewGanttView`, with a KNOWN inaccuracy that is declared rather than
        fixed (§4 of `regressions/crew-cost-path-parity-manifest.test.js`: a
        member holding any hand-recorded day is costed `days / 5 × rate`, which
        the drag figure cannot reproduce). Preferring the browser's arithmetic
        over the server's would invert this directory's founding rule.

     2. IT IS THE ONLY COMPLETE ONE. The edit preview carries every term the
        footer sums — `budget_cost`, `computer_cost`, `allowance_cost{,_2,_3}`.
        The drag preview carries `budget_cost` and `total_weeks` and nothing
        else. Choosing it for a member would move labour while leaving rental
        and allowances at their saved values, i.e. an internally inconsistent
        total.

     3. 🔑 CHOOSING THE EDIT PREVIEW DOES NOT DISCARD THE DRAG — IT COSTS IT
        PROPERLY. Wherever the two are genuinely simultaneous, the drag has
        ALREADY written its dates into `editMemberForm` through
        `syncGanttDetailsDates`, so those dates are inside the very request
        body this preview was costed from. The owner's instinct — *"the drag
        wins for dates and the edit wins for everything else"* — is therefore
        satisfied by "the edit wins", without the subtle merge he rightly
        distrusted. The drag's dates reach the footer THROUGH the edit preview,
        as server-computed money rather than as a client-side estimate.

     🚨 WHOLE OBJECTS, NEVER A MERGE OF THE TWO. Blending them would mean
     labour from one source and rental from another for one person, and a
     "preferred field" list is exactly where an absent field becomes
     `Number(undefined) → NaN` or a spurious 0. One or the other, chosen whole
     — the same rule `costSummaryMemberFor` above already states.

     🚨 A FAILED PREVIEW CHANGES NOTHING. `useCrewCostPreview` clears to `null`
     on any failure, so `editPreviewApplies` goes false and the footer falls
     back to the drag preview or to the saved row. It can never present a stale
     preview as current, and it never reads an absent field as a zero
     contribution because it never takes a field from an object it did not
     choose.

     🚨 SAVE CANNOT DOUBLE-COUNT, STRUCTURALLY. This REPLACES a member's row in
     the sums; it never adds a second contribution, so there is no arithmetic
     shape in which the edited member could be counted twice. And the gate
     drops on the same render as the save: `handleSaveEditMember` sets
     `originalMemberForm` to the saved form, `memberFormDirty` goes false,
     `previewMemberId` goes null — none of which is debounced — so the
     refreshed row is what the sums read from that render on.

     🔻 THE PENDING DRAG (`ganttDayDates`) IS DELIBERATELY NOT A THIRD SOURCE.
     It is the drag OVERLAY that survives until Save, and `ganttMembers`
     applies it for the chart. At level 2 its dates reach the footer through
     the edit preview exactly as above; at level 1 (strip open, no panel) they
     do not, and the footer keeps the saved figures until Save. That is the
     behaviour this page already had and it is left alone: the only way to move
     it would be a client-side re-derivation of capped rental and allowances
     from a weeks figure, which is the fifth cost formula this directory
     forbids. */
  const memberWithLivePreview = (m) => {
    /* 🔻 THE DRAG PREVIEW IS DATES ONLY SINCE 2026-08-23 — it no longer
       carries `budget_cost` / `total_weeks`, because those were the client-side
       `weeks × rate` the owner reported as ignoring off days. The dates still
       ride over whichever money source wins below; dates are not money and the
       cost objects carry none, so this is not the forbidden MERGE of two money
       sources. */
    const dp = dragPreview && dragPreview.memberId === m.id ? dragPreview : null;
    const dates = dp ? { start_date: dp.start_date, end_date: dp.end_date } : null;
    /* 🥇 THE EDIT PREVIEW STILL WINS, WHOLE, for the three reasons argued
       above — and reason 1 has stopped being a reason to distrust the runner-up:
       the runner-up is now the SAME endpoint. What still separates them is
       COMPLETENESS and OWNERSHIP: the edit preview is costed from
       `buildMemberPutPayload`, which carries the rate override, the three
       allowances and the computer rental the level-2 form can edit, and it is
       the one the level-2 Save will commit. */
    if (editPreviewApplies(m)) {
      const edited = costSummaryMemberFor(m);
      return dates ? { ...edited, ...dates } : edited;
    }
    /* 🥈 THE STRIP'S OWN COSTING — also the server's, also the real assembly,
       and it covers the case the edit preview cannot: a strip open at LEVEL 1,
       where there is no member form at all. It replaces the drag preview that
       used to sit here. */
    if (ganttStripPreviewApplies(m)) {
      return { ...m, ...ganttStripPreview, ...(dates || {}) };
    }
    /* 🥉 Nothing costed yet — the saved row, with the dragged dates over it.
       The money is the SAVED money and is true; it is merely about to be
       superseded, which `ganttStripCostState` says out loud on the row. */
    return dates ? { ...m, ...dates } : m;
  };
  /* Provisional while a costing is in flight OR showing, and it drops the moment
     the preview is cleared — so a FAILED preview shows saved figures with NO
     marker, rather than stale figures labelled as current. */
  const costSummaryProvisional = (member) => (
    member != null && member.id === previewMemberId && (costPreviewPending || costPreview != null)
  );

  // The Adjustment COLUMN renders for budget managers only (the ruled "who");
  // the input inside it is additionally disabled while the version is locked
  // (the server refuses 423 regardless). Viewers still see the adjusted Spent
  // figure + marker + note popup — the adjustment is data, not a secret.
  const showAdjustmentColumn = canManageVersions && !!selectedVersionId;
  const assignedUserIds = members.filter(m => m.user_id).map(m => m.user_id);

  // Desktop cost-codes table: adjustable, evenly-distributed columns — see
  // BUDGET_COST_CODES_COLUMNS above. `visibleBudgetCostCodesColumns` is the
  // ONE source the colgroup, the header row and the body cells all read, so
  // `adjustment`/`reorder`/`actions` appearing or not can never desync the
  // grid. `sfx_budget_cost_codes_col_widths` is its own localStorage key —
  // a different column set from every other resizable table in the app.
  //
  // 🪤 `useColumnWidths` is given the FULL, STATIC column list — never the
  // filtered `costCodesColumns` below — because `adjustment`/`reorder`/
  // `actions` can appear or vanish AFTER mount (a budget version gets
  // selected, a lock toggles) and the hook only sanitises its `widths` map
  // ONCE, at mount, from whatever `columns` it was FIRST called with. Every
  // other caller in this app (the order tables, the Breakdown) passes a
  // column list that is a module-level constant and never changes shape
  // after mount, so this gap had no caller to expose it. Passing a
  // dynamically-filtered list here reproduces it immediately: a column that
  // becomes visible later has no entry in `widths`, and `totalWidth` (a sum
  // over the CALLER's `columns` argument) turns to `NaN` the moment it does
  // — reproduced and confirmed by `BudgetCostCodes.columnResize.test.js`.
  // Passing the full 8-column list keeps every key populated from the first
  // render, whether or not that column is on screen yet.
  const costCodesColumns = useMemo(
    () => visibleBudgetCostCodesColumns({ showAdjustmentColumn, canEdit }),
    [showAdjustmentColumn, canEdit]
  );
  // Derived from `costCodesColumns` itself, not from `showAdjustmentColumn`/
  // `canEdit` a second time — the colgroup, the header row and these three
  // booleans all read the ONE filtered array, so a column appearing in the
  // header but not the body (or the reverse) cannot happen by construction.
  const hasAdjustmentColumn = costCodesColumns.some((c) => c.key === 'adjustment');
  const hasReorderColumn = costCodesColumns.some((c) => c.key === 'reorder');
  const hasRowMenuColumn = costCodesColumns.some((c) => c.key === 'row_menu');
  const {
    widths: storedCostCodesColWidths,
    setWidth: setStoredCostCodesColWidth,
    resetColumn: resetCostCodesColumn,
    isCustomised: costCodesWidthsCustomised,
  } = useColumnWidths(
    BUDGET_COST_CODES_COLUMNS,
    'sfx_budget_cost_codes_col_widths',
    BUDGET_COST_CODES_MAX_COLUMN_WIDTH
  );
  // Measured, never assumed — `w-full` + a declared `minWidth` means the
  // table stretches to fill a wide viewport, and a drag has to undo that
  // stretch to land the column edge under the pointer (see
  // `ColumnResizeHandle`'s own doc). This wrapper IS the scrollport (no
  // bordered card around it), so — like `OrderTableShell` and unlike
  // `GagGroupTable` — nothing is subtracted from the measurement.
  // 🔀 TWO SECTIONS, ONE WIDTH STATE (2026-10-29, owner: *"two equaly important
  // sections which can collpase and expand"*). Cost codes and Job codes are
  // separate tables in separate collapsible sections plus the combined Total
  // table under both — the SAME columns and the SAME width map, so a drag in
  // either moves both. Each scrollport is measured and the WIDEST answer is the
  // table width: a FOLDED section's scrollport is `display:none` and measures
  // 0, so measuring only one of them breaks Fit + the panel width the moment
  // that section is folded. (All three sit at the same x with the same width
  // when shown.) The three panes also scroll sideways TOGETHER
  // (`useLinkedScrollLeft`), or an overflowing table's columns would part.
  const [costCodesScrollportRef, costCodesStandardWidth] = useMeasuredWidth();
  const [jobCodesScrollportRef, jobCodesScrollportWidth] = useMeasuredWidth();
  const [codesTotalScrollportRef, codesTotalScrollportWidth] = useMeasuredWidth();
  // 🪤 Review 1029B A-3: `useMeasuredWidth` KEEPS its last width once its element
  // unmounts (it only measures a live `ref.current`), so a pane whose scroller is
  // no longer RENDERED (its list emptied — the last job code deleted) would hold a
  // stale, wider figure and widest-wins would fit to a screen that is gone. Only
  // a rendered pane is asked: the same conditions that render each scroller below.
  // (A FOLDED pane is still mounted and honestly reads 0.) The Total's pane needs
  // no condition: it unmounts only when there are NO codes — no table to fit —
  // and remounts (re-measured) with the first table that comes back.
  const costCodesScrollportWidth = widestMeasured([
    standardCodes.length > 0 ? costCodesStandardWidth : null,
    jobCodes.length > 0 ? jobCodesScrollportWidth : null,
    codesTotalScrollportWidth,
  ]);
  useLinkedScrollLeft([costCodesScrollportRef, jobCodesScrollportRef, codesTotalScrollportRef]);
  /**
   * 🔀 FIT ON LOAD (owner, 2026-10-25): with NO widths of the user's own (the
   * hook's `isCustomised` is false) the table RENDERS `fitColumnWidths` for
   * its scrollport — computed, NEVER written: `hooks/CLAUDE.md`'s rule, a
   * persisting hook must not write on hydration, so a visit stamps nothing
   * and a later change to a default still reaches everyone. A user's stored
   * (dragged or Fit) widths WIN whenever present. Fit basis frozen while a row
   * is open, as on Crew Grades (a scrollbar appearing must not move a column).
   * A first DRAG from fitted widths adopts the whole fitted map first, so the
   * columns the user did not touch stay where they were shown rather than
   * snapping back to their defaults — a user action, so writing is correct.
   */
  const costCodesFitBasisRef = useRef(null);
  if (costCodeEditor.expandedId == null || costCodesFitBasisRef.current == null) {
    costCodesFitBasisRef.current = costCodesScrollportWidth;
  }
  const costCodesFitted = costCodesWidthsCustomised
    ? null
    : fitColumnWidths(costCodesFitBasisRef.current, costCodesColumns, BUDGET_COST_CODES_MAX_COLUMN_WIDTH);
  const costCodesColWidths = costCodesFitted
    ? { ...storedCostCodesColWidths, ...costCodesFitted }
    : storedCostCodesColWidths;
  const setCostCodesColWidth = useCallback((key, px) => {
    if (costCodesFitted) {
      for (const [k, v] of Object.entries(costCodesFitted)) if (k !== key) setStoredCostCodesColWidth(k, v);
    }
    setStoredCostCodesColWidth(key, px);
  }, [costCodesFitted, setStoredCostCodesColWidth]);
  // The DECLARED width sum of the columns actually ON SCREEN right now —
  // never the hook's own `totalWidth`, which (given the full list above)
  // would include hidden columns and overstate both the table's `minWidth`
  // and the resize handle's stretch-to-fit arithmetic.
  const costCodesTableTotalWidth = costCodesColumns.reduce(
    (sum, c) => sum + (Number.isFinite(costCodesColWidths[c.key]) ? costCodesColWidths[c.key] : c.width),
    0
  );
  // The other three in-place-edit tables' scrollports — each sizes its
  // sticky expansion (`RowExpansion` → the shared `InlineEditPanelRow`) so the
  // "Editing this row" bar and Save stay in view.
  const [gradesScrollportRef, gradesScrollportWidth] = useMeasuredWidth();
  // 🔑 The grade table FITS its scrollport (`GRADE_TABLE_COLUMNS`), and the
  // width it fits to is FROZEN while a row is open: opening a row lengthens
  // the page, a classic scrollbar can then appear and narrow the scrollport,
  // and a live re-fit would change every column between collapsed and
  // editing — the one thing in-place editing must never do.
  const gradesFitBasisRef = useRef(null);
  if (gradeEditor.expandedId == null || gradesFitBasisRef.current == null) {
    gradesFitBasisRef.current = gradesScrollportWidth;
  }
  const gradeTable = gradeTableWidths(gradesFitBasisRef.current, { canEdit });
  const [paymentTypesScrollportRef, paymentTypesScrollportWidth] = useMeasuredWidth();

  /**
   * `Fit columns to screen` (owner ruling 2026-09-28, applying his
   * 2026-09-27 Breakdown ruling here too: *"add it in and a export and
   * print options like the other pages have"*). This table already measures
   * its own scrollport directly (`costCodesScrollportWidth`, above) — unlike
   * the order/hire tables it does not go through `PageScrollTable`, so there
   * is no second `onRegionWidthChange` route to build; the existing
   * measurement IS the `available` this hook needs.
   *
   * 🔑 Fits `costCodesColumns` — the columns CURRENTLY ON SCREEN — never the
   * full 8-column `BUDGET_COST_CODES_COLUMNS` the width hook is seeded with
   * (see that call's own comment on why the hook needs the full list).
   * Fitting a hidden column would write a width for a key nobody can see.
   */
  const {
    fitColumnsToScreen: fitCostCodesColumnsToScreen,
    canFitColumns: canFitCostCodesColumns,
    alreadyFit: costCodesColumnsAlreadyFit,
  } = useFitColumnsToScreen({
    columns: costCodesColumns,
    widths: costCodesColWidths,
    setWidth: setCostCodesColWidth,
    maxWidth: BUDGET_COST_CODES_MAX_COLUMN_WIDTH,
    available: costCodesScrollportWidth,
  });

  /**
   * ── Cost Codes Export CSV + Fit columns to screen — ADDED 2026-09-28,
   *    MOVED 2026-10-05 into the page's ONE `Actions ▾` ──────────────────
   *
   * Owner, asked whether this page and My Orders should get `Fit columns to
   * screen` too: *"add it in and a export and print options like the other
   * pages have"*. Built CLIENT-SIDE from `costCodes` — the rows already
   * fetched and on screen for the selected version, no search/sort/filter on
   * this table to narrow — so it discloses nothing new, same argument as
   * `utils/orderListExport.js`'s own docblock. 🔀 2026-10-05 (owner, *"fab
   * lets build the preview"*): the section-header menu, its cost-codes-only
   * `Download list (PDF)` / `Print all`, AND the band's separate `Download /
   * Print` button are all GONE — one `Actions ▾` at the right end of the
   * tabs row (`components/budget/BudgetManageActionsMenu.js`) carries this
   * tab's items plus a ticked-sections print through `buildBudgetManagePdf`.
   *
   * 🔑 **`Adjustment` is INCLUDED ONLY when the table itself shows it**
   * (`hasAdjustmentColumn`) — exporting a column the viewer cannot see on
   * screen would disclose more than the table does. `Spent to Date` always
   * includes the adjustment (`spentWithAdjustment`), matching the ruled
   * *"every budget-surface spent figure includes the adjustment"*.
   *
   * 🔴 **Uses `formatMoney` (`useVatMode`), never `formatCurrency` directly**
   * — this page deliberately routes every money string through the one hook
   * (see the import note at the top of this file); a fresh `formatCurrency`
   * call here would be the exact re-introduction that comment warns against.
   */
  /**
   * The DISPLAYED budget figure for one row — same source `budgetAmounts`
   * (the live-edited map) falling back to the stored `budget_amount`, same
   * as the whole-budget PDF's own `PDF_DISPLAY_SITE`. Written WITHOUT
   * `parseFloat(...) || 0` — never a payload shape, but the source-scan
   * ratchet in `BudgetCostCodes.payloadMoney.test.js` §5.1 matches on TEXT,
   * not on intent, so a second `budget_amount: parseFloat(...) || 0` line
   * would need its own allowlist entry there; `Number.isFinite` says the
   * same thing without tripping it.
   */
  const costCodeDisplayBudget = useCallback((cc) => {
    const n = parseFloat(budgetAmounts[cc.id] ?? cc.budget_amount ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [budgetAmounts]);

  const costCodesExportHeaders = useMemo(() => {
    const headers = ['Code', 'Name', 'Description', 'Budget'];
    if (hasAdjustmentColumn) headers.push('Adjustment');
    headers.push('Spent to Date');
    return headers;
  }, [hasAdjustmentColumn]);

  const costCodesExportRow = useCallback((cc) => {
    const row = [
      cc.code, cc.name, cc.description,
      formatMoney(costCodeDisplayBudget(cc)),
    ];
    if (hasAdjustmentColumn) row.push(formatMoney((adjustments[cc.id] && adjustments[cc.id].amount) || 0));
    row.push(formatMoney(spentWithAdjustment(cc)));
    return row;
  }, [hasAdjustmentColumn, costCodeDisplayBudget, adjustments, formatMoney, spentWithAdjustment]);

  /** ONE file writer for every tab's Export CSV — the house shape
   *  (`gagCsvExport#rowsToCsv`: BOM, `csvEscape`, saved as `CSV_MIME`). */
  const downloadManageExport = useCallback((headers, rows, suffix) => {
    const csv = rowsToCsv(headers, rows);
    const slug = (activeDepartment?.department_name || 'budget').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'budget';
    downloadTextFile(csv, `${slug}${suffix}-${toLocalISO(new Date())}${CSV_EXT}`, CSV_MIME);
  }, [activeDepartment]);

  const downloadCostCodesExport = useCallback(() => {
    downloadManageExport(costCodesExportHeaders, costCodes.map(costCodesExportRow), '-cost-codes');
  }, [downloadManageExport, costCodesExportHeaders, costCodesExportRow, costCodes]);

  /* ═══ THE FIXED TOTALS BAR'S RESERVATION, MEASURED EVERY LAYOUT ═══════════
     Full reasoning, the Chromium figures and the three false beliefs that
     preceded them are in `utils/saveBarSpacer.js`.

     🪤 IT OBSERVES `document.body`, NOT THE SPACER. The quantity that decides
     the answer is where the page ENDS relative to the last content, and that
     moves when a section expands, a tab switches, a crew row opens or the
     Gantt swaps for the table — none of which resize the spacer itself. A
     ResizeObserver on the spacer would fire only in response to our own write.

     🔑 IT CANNOT OSCILLATE, and that is a property of the arithmetic rather
     than of a guard. `reservedElsewhere` is invariant under the spacer's
     height: setting the spacer to N grows `scrollHeight` by exactly N, so the
     next measurement computes the same shortfall and `setState` bails on an
     equal value. The `=== next` check below is belt-and-braces for the
     fractional case, not the thing that stops a loop.

     ⚠️ jsdom answers 0 to every rect, so this whole effect is inert under
     test: `occluderPx` measures 0, the guard sees content already clear of a
     zero-height occluder and returns 0. The ARITHMETIC is unit-tested in
     `utils/saveBarSpacer.test.js` against the real measured numbers; the
     GEOMETRY is a browser check. */
  const saveBarSpacerRef = useRef(null);
  const [saveBarSpacerPx, setSaveBarSpacerPx] = useState(null);
  /* 🔀 2026-09-26: the crew table's pinned TOTAL row + scrollbar must sit
     ABOVE the same occluder the spacer measures — the fade over the fixed
     save bar — or they pin underneath it. Measured here, beside the
     measurement it shares, as the fade's top edge's distance from the
     viewport bottom; `null` (no bar on the page) falls back to the frame's
     bottom-nav classes. */
  const [crewTableStickyBottomPx, setCrewTableStickyBottomPx] = useState(null);
  /* …and its header pins under the Labour Flow TOOLBAR (2026-10-05, desktop
     tightening): `toolbarRef` is attached to that `ui/Toolbar`, so `headerTop`
     is the app bar (56px at lg, 0 below) PLUS the bar's MEASURED height — it
     wraps when the version is locked. Below `md` the toolbar is not mounted,
     the ref stays null and `headerTop` is the app bar alone, exactly as
     before (the phone keeps its section header). 🪤 The hook keeps its LAST
     measured height when the bar unmounts (a live desktop → phone resize), so
     the phone reads `toolbarTop` — the app bar alone — never `headerTop`. */
  const {
    toolbarRef: labourToolbarRef,
    toolbarTop: appBarTop,
    headerTop: labourToolbarBottom,
  } = useStickyToolbar();
  const crewTableStickyTop = isPhoneViewport ? appBarTop : labourToolbarBottom;
  /* The Gantt's Sort/Zoom draw into this toolbar slot (CrewGanttView's
     controlsTarget, 2026-10-05) — STATE, not a ref, so the chart re-renders
     when the slot mounts with the Gantt view and portals into it. */
  const [ganttControlsSlot, setGanttControlsSlot] = useState(null);

  useEffect(() => {
    const measure = () => {
      const el = saveBarSpacerRef.current;
      if (!el) return;
      // The occluder is the FADE, measured. With no bar on the page there is
      // nothing to occlude and nothing to reserve — that is a real 0, not an
      // unmeasured null.
      const fade = document.querySelector('[data-save-bar-fade]');
      if (!fade) { setSaveBarSpacerPx((prev) => (prev === 0 ? prev : 0)); return; }

      const rect = el.getBoundingClientRect();
      const marginTop = parseFloat(window.getComputedStyle(el).marginTop) || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const docEl = document.documentElement;

      // Everything below the spacer (container padding + the app footer) plus
      // the spacer's own collapsed margin above it. Invariant under `height`.
      const reservedElsewhere = marginTop + (docEl.scrollHeight - (rect.bottom + scrollY));
      const contentBottom = (rect.top + scrollY) - marginTop;

      const next = saveBarSpacerHeight({
        occluderPx: Math.ceil(fade.getBoundingClientRect().height),
        reservedElsewhere,
        contentBottom,
        documentHeightWithoutSpacer: contentBottom + reservedElsewhere,
        viewportHeight: docEl.clientHeight,
      });
      setSaveBarSpacerPx((prev) => (prev === next ? prev : next));
    };

    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(document.body);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  /* The crew table's footer clearance (see `crewTableStickyBottomPx`). Its
     own effect, keyed on `selectedVersionId`, because the fade is FIXED and
     mounts with the version: its arrival resizes nothing, so the body
     observer above can miss it — measured 2026-09-26 at 375px, the footer
     sat on the bottom-nav fallback (107px under the fade) until some unrelated
     resize. The fade's geometry depends only on the viewport after that. */
  useEffect(() => {
    const measureClearance = () => {
      const fade = document.querySelector('[data-save-bar-fade]');
      const clearance = fade
        ? Math.ceil(document.documentElement.clientHeight - fade.getBoundingClientRect().top)
        : 0;
      // jsdom answers 0 for every rect: a non-positive clearance is "nothing
      // measured", never an offset — keep the class fallback.
      const next = clearance > 0 ? clearance : null;
      setCrewTableStickyBottomPx((prev) => (prev === next ? prev : next));
    };
    measureClearance();
    window.addEventListener('resize', measureClearance);
    return () => window.removeEventListener('resize', measureClearance);
  }, [selectedVersionId]);

  const totalBudget = costCodes.reduce((sum, cc) => {
    return sum + (parseFloat(budgetAmounts[cc.id] || '0') || 0);
  }, 0);

  /* ─── Crew list filter (live search) ─── */
  // Name / grade / location substring match, case-insensitive. Totals
  // intentionally still come from the FULL members list — filtering is a
  // viewing convenience, not a scope change to the budget.
  const filteredMembers = useMemo(() => {
    const q = crewSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m => {
      const haystack = [m.name, m.grade_name, m.location].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [members, crewSearch]);

  /**
   * The members handed to `CrewGanttView`, with the pending drag laid over the
   * one being edited.
   *
   * 🔑 THE OVERLAY LIVES HERE, NOT IN THE COMPONENT, because this directory's
   * rule is that the page owns the state and the components stay
   * presentational. `CrewGanttView` draws bars and prices from whatever member
   * rows it is handed; it does not need to learn that some of them are pending.
   * 🚨 `setMembers` IS NOT TOUCHED. The old immediate path mutated `members`
   * optimistically; doing that now would make the pending dates indistinguish-
   * able from saved ones for every other reader on this page — the crew table,
   * the totals, `isMemberDirty` — and Cancel would have nothing to revert to.
   */
  /* 🔀 R8 (2026-10-19) — the bar's price reads WHICHEVER preview owns the
     member, the same precedence `memberWithLivePreview` states (🥇 edit, whole;
     🥈 strip). Since expand opens Details the Gantt owns the form on every
     expand, the strip preview stands down, and reading `ganttStripPreview`
     alone left the price under the bar on the SAVED figure while the footer
     and the panel showed the costed draft — one row, two numbers. */
  const ganttRowPreview = previewMemberId != null && previewMemberId === ganttDayMemberId
    ? costPreview
    : ganttStripPreview;
  const ganttMembers = useMemo(() => {
    if (!ganttDayMemberId) return filteredMembers;
    if (!ganttDayDates && !ganttRowPreview) return filteredMembers;
    return filteredMembers.map((m) => {
      if (m.id !== ganttDayMemberId) return m;
      /* 🔻 THE MONEY COMES FROM THE SERVER NOW (2026-08-23). It used to be
         `ganttDayDates.budget_cost` — the last drag frame's `weeks × rate`,
         captured by `handleGanttUpdate` — which is the figure the owner
         reported as ignoring off days. `ganttStripPreview` is the answer
         `POST /crew-members/:id/cost-preview` gives for this exact draft, so
         the price under the bar and the price the Save produces are the same
         arithmetic.
         🪤 THE WHOLE OBJECT OR NONE OF IT. A per-field merge is how a row
         ends up with labour from one source and rental from another; and a
         FAILED costing clears the preview to `null`, which correctly leaves
         the SAVED figures here rather than `Number(undefined) → NaN`. The row
         says which of the two it is showing through `ganttStripCostState`. */
      const patched = ganttRowPreview ? { ...m, ...ganttRowPreview } : { ...m };
      if (ganttDayDates) {
        patched.start_date = ganttDayDates.start_date;
        patched.end_date = ganttDayDates.end_date;
      }
      return patched;
    });
  }, [filteredMembers, ganttDayMemberId, ganttDayDates, ganttRowPreview]);

  /**
   * The ONE member the prototype Gantt in the Full Details panel draws.
   *
   * 🔑 IT IS A SLICE OF `ganttMembers`, NOT A SECOND MEMBER SHAPE. The
   * page-level chart draws that array; taking one row out of it is what makes
   * "the same chart, one person" literally true — same batched-drag overlay,
   * same `budget_cost` / `total_weeks`, same everything. Building a member
   * object here instead would be a second answer to "what is this member",
   * which is the defect class this whole feature spent the week collapsing.
   *
   * ⚠️ Keyed on `expandedMemberId || closingMemberId`, matching `weekMember`
   * next door: the panel keeps rendering for 250ms after a collapse, and an
   * empty array during that window would flash the Gantt's "no crew members
   * with start/end dates" empty state on the way out.
   */
  const detailsGanttMembers = useMemo(() => {
    const id = expandedMemberId || closingMemberId;
    if (!id) return [];
    const row = ganttMembers.find((x) => x.id === id);
    return row ? [row] : [];
  }, [ganttMembers, expandedMemberId, closingMemberId]);

  /* ─── Crew Summary totals ─── */
  /* 🔑 EVERY SUM BELOW READS THE SAME RESOLVED ROWS. `memberWithLivePreview`
     is applied ONCE per member here rather than inside each `reduce`, so the
     three terms cannot end up disagreeing about which preview is live for a
     member — which is the whole failure mode being closed. It is a `map` of a
     selection, not a computation.

     🔻 `totalLabour` WAS THE ONLY PREVIEW-AWARE TERM UNTIL 2026-08-23, AND
     THAT WAS A REAL GAP RATHER THAN A CHOICE: a bar drag moves DATES, which
     moves weeks, which moves computer rental and all three allowances — so a
     footer that previewed only labour was already showing a total whose parts
     came from two different drafts. Nobody had noticed. It is closed here for
     the EDIT preview (the server sends every term). It is NOT closed for the
     drag preview, which carries no rental or allowance opinion — see the
     `memberWithLivePreview` docblock. */
  const previewedMembers = members.map(memberWithLivePreview);
  const totalLabour = previewedMembers.reduce(
    (sum, m) => sum + (parseFloat(m.budget_cost) || 0), 0,
  );
  const totalComputer = previewedMembers.reduce(
    (sum, m) => sum + (parseFloat(m.computer_cost) || 0), 0,
  );
  /* ─── The three per-member allowances (owner ruling, 2026-08-23) ──────────
     Owner: *"'Expenses' should be renamed to 'Cost Code budget' and labour
     should be 'Labour inc allowances' then include allowances in the labour
     totals."*

     🔑 THIS MAKES TWO SURFACES AGREE — IT IS NOT A NEW OPINION ABOUT THE
     MONEY. `computeVersionAggregates` (`server/helpers/budgetHelpers.js`) has
     always returned `total = labour + computer + allowance + adhoc`, so
     `GET /api/budget/report` already counted these. The footer here was the
     one surface that did not, and it was understating the crew total by the
     whole allowance bill.

     🪤 IT SUMS THE THREE `allowance_cost*` FIELDS AND **NOT** THE PUBLISHED
     `allowance_total`, AND THAT IS THE ONE THING TO GET RIGHT HERE.
     `allowance_total` is `computer_cost + allowance_cost + _2 + _3` — it
     ALREADY CONTAINS THE COMPUTER RENTAL (`server/routes/budget.js`, the
     `allowance_total:` line). `totalComputer` above is a separate term in
     `crewGrandTotal`, so reaching for the tidier-looking published aggregate
     here would charge every laptop twice. The server's own aggregate keeps
     `allowance` (a1+a2+a3) and `computer` apart for exactly this reason, and
     mirroring its decomposition term for term is what makes the two totals
     comparable at all. ⚠️ The per-member `allowance_total` and the version
     aggregate's `allowance` are DIFFERENT QUANTITIES sharing most of a name.

     Each `allowance_cost*` is already rounded to 2dp server-side, so adding
     them here is the same "sum the displayed components" the server does
     rather than a re-derivation — no rate is touched and nothing is divided.

     🪤 `0` IS A REAL ALLOWANCE. `parseFloat(x) || 0` maps both an absent field
     and a genuine £0 to 0, which is the right answer for a SUM either way —
     but do not turn this into a filter that skips falsy members, which would
     be the falsy-collapse this subsystem has produced three times.

     ✅ A DRAGGED MEMBER'S ALLOWANCES **DO** MOVE WITH THE DRAG SINCE
     2026-08-23, AND THE PARAGRAPH THAT SAID THEY COULD NOT IS SUPERSEDED
     PREMISE-AND-ALL. It read: *"`dragPreview` carries `{ memberId, start_date,
     end_date, budget_cost, total_weeks }` and has no allowance opinion …
     inventing a client-side allowance preview would mean re-deriving capped
     money from a weeks figure, which is the thing this directory forbids."*
     Both halves were true and the conclusion has expired: `dragPreview` no
     longer carries money at all, and nothing here re-derives anything — the
     drafted engagement is costed by `POST /crew-members/:id/cost-preview`,
     which returns every allowance term with its cap already applied, and
     `memberWithLivePreview` takes that object WHOLE. **The rule the old
     paragraph was protecting is untouched: no capped money is derived in this
     browser.** */
  /* 🚨 2026-08-23, R2 — THE PANEL NOW USES `allowance_total` AND THIS FOOTER
     STILL MUST NOT. Read this before "making them consistent".

     The cost-summary panel's Allowances figure changed to the published
     `allowance_total` on 2026-08-23, because the owner ruled that computer
     rental IS an allowance and the panel's separate Computer rental figure was
     folded into it. That is correct THERE and would be wrong HERE, and the two
     sites are not in conflict — they are drawing DIFFERENT GROUPINGS of the
     same money:

       panel:  Allowances (incl. laptop) + standard + 6th + 7th = member_total
       footer: labour + COMPUTER + allowances(a1+a2+a3) + adhoc = crewGrandTotal

     `totalComputer` above is already a separate term in `crewGrandTotal`, so
     swapping this sum for `allowance_total` charges every laptop twice — the
     defect caught and measured yesterday (£3,755 against £3,455). A diff that
     does it will look exactly like tidying two sites onto one field.

     ONE FIELD, TWO GROUPINGS. The same sentence is written at the panel. */
  /* 🚨 THE PREVIEW CARRIES `allowance_total` TOO, AND THIS SUM STILL MUST NOT
     TOUCH IT. Making these rows preview-aware puts the trap field one property
     access away: `memberWithLivePreview` spreads the server's whole field
     object, so a previewed row carries `allowance_total` (= computer + a1 + a2
     + a3) exactly as a saved row does. `totalComputer` above is already its own
     term in `crewGrandTotal`, so reaching for it here charges every laptop
     twice — the £3,755-against-£3,455 defect, now reachable from a second
     source. Three fields, summed; nothing else. */
  const totalAllowance = previewedMembers.reduce((sum, m) => sum
    + (parseFloat(m.allowance_cost) || 0)
    + (parseFloat(m.allowance_cost_2) || 0)
    + (parseFloat(m.allowance_cost_3) || 0), 0);
  /* ─── THE CREW TABLE'S `Allowances` COLUMN TOTAL (2026-08-23) ─────────────
     Owner: *"on table view please change 'Computer' to 'Allowances' and make
     show the values are the sum of all allowances for each user and not just
     computer."*

     🔑 COMPUTER RENTAL **IS** AN ALLOWANCE. Owner, same day: *"remember
     computer is one type of allowance and not different. Its just a common
     allowance so thats why its included as a default example."* The built-in
     Computer Rental slot is allowance #0 with its name locked — offered
     because every production has one, not because it is a different KIND of
     thing. So `allowance_total = computer_cost + a1 + a2 + a3` is correct BY
     THE DOMAIN MODEL rather than by naming accident, and this column showing
     it is the table agreeing with the domain rather than adopting a
     preference.

     🚨 `crewGrandTotal` BELOW IS UNCHANGED AND MUST STAY SO. It keeps
     `totalComputer` and `totalAllowance` as separate terms, which is
     arithmetically correct and is the ONLY arrangement that does not charge
     every laptop twice — measured at £3,755 against £3,455. **Do not
     "simplify" it to read `allowance_total` now that this column does.**
     ⚠️ And be honest about which line is the odd one out: the footer's
     separate `totalComputer` term is a MODELLING ARTEFACT, not a domain
     distinction. It is correct and must not be touched; but if that footer is
     ever restructured, folding computer INTO allowances is the direction that
     matches the domain, not a further divergence.
     🔀 2026-08-23: THE GANTT'S FOOTER NOW READS THIS FIGURE TOO, under the label
     `Allowances`, via `CrewGanttView`'s `totalAllowances` prop (it was
     `totalComputer` under a `Computer` label). So the crew table's column total,
     the Gantt's footer and the cost-summary panel all now express the same
     grouping. `crewGrandTotal` below is STILL the exception and is STILL right.

     🔑 IT IS `totalComputer + totalAllowance`, NOT A FOURTH REDUCE OVER
     `allowance_total`. Term for term it is the same money, to the penny (each
     member's `allowance_total` is `round2` of those same four already-rounded
     components), and expressing it from the two terms the footer already
     computes is what makes "the column total and the footer disagree" a
     visible impossibility rather than a thing to test for. It reads the SAME
     `previewedMembers` both terms do, so the column, its total and the footer
     are live or stale together. */
  const totalAllowancesInclComputer = totalComputer + totalAllowance;
  // 🔀 JOB COST CODES (2026-10-28): the ad-hoc term left this total — an
  // ad-hoc line is a job code now, i.e. purchases budget, so the crew total
  // is crew only (the server's `/report` labour total changed the same way).
  const crewGrandTotal = totalLabour + totalComputer + totalAllowance;

  // (The Cost Report print layout now lives only on the Cost Reports page —
  // /budget/overview. This page no longer assembles report data for printing.)

  /**
   * 🔀 TWO SECTIONS (2026-10-29, owner: *"It looks liek cost codes is the main
   * page with Job codes stuck on the bottom. Make it two equaly important
   * sections which can collpase and expand. use comon components"*). The tab
   * holds two nested `CollapsibleSection`s — Cost codes, Job codes — with the
   * same header (title + count, a one-line explainer, its own `+ Add …`), each
   * with its own SUBTOTAL, and ONE combined Total under both that stays visible
   * whichever is folded. All three tables are THIS one builder on ONE column
   * model and ONE width map (`costCodesColWidths`), so columns line up across
   * them and a drag in either moves both; their panes scroll sideways together.
   * Previously ONE table with a "Job codes" heading row — see
   * `components/budget/CLAUDE.md` (Manage Budgets).
   *
   * `kind`: 'standard' | 'job' — that section's codes + its subtotal row/card;
   * 'total' — no codes, no header row, just the combined Total.
   */
  const codeListKinds = {
    standard: { scrollerRef: costCodesScrollportRef, testId: 'budget-cost-codes-table-scroller', resizePrefix: 'budget-cost-codes-col-resize', sumTestId: 'budget-cost-codes-subtotal', sumLabel: 'Cost codes subtotal' },
    job: { scrollerRef: jobCodesScrollportRef, testId: 'budget-job-codes-table-scroller', resizePrefix: 'budget-job-codes-col-resize', sumTestId: 'budget-job-codes-subtotal', sumLabel: 'Job codes subtotal' },
    total: { scrollerRef: codesTotalScrollportRef, testId: 'budget-codes-total-table-scroller', resizePrefix: null, sumTestId: 'budget-codes-total', sumLabel: jobCodes.length > 0 ? 'Total — cost codes + job codes' : 'Total' },
  };
  // Folding a section closes its open row only when that row has nothing
  // unsaved — a dirty edit is KEPT (hidden, still guarded by the page's leave
  // prompt and the other rows' dim) rather than silently discarded.
  const foldCodeSection = (list) => {
    const open = costCodeEditor.expandedId;
    if (open != null && !costCodeEditor.isDirty && list.some((cc) => cc.id === open)) costCodeEditor.reset();
  };
  const renderCodeList = (list, kind) => {
    const { scrollerRef, testId: scrollerTestId, resizePrefix, sumTestId, sumLabel } = codeListKinds[kind];
    const isTotal = kind === 'total';
    // The combined Total keeps the expressions it has always used (over ALL
    // `costCodes`); a section's subtotal is the same sums over its own list.
    const sums = isTotal
      ? {
        budget: totalBudget,
        adjustment: costCodes.reduce((sum, cc) => sum + ((adjustments[cc.id] && adjustments[cc.id].amount) || 0), 0),
        computed: costCodes.reduce((sum, cc) => sum + (cc.expenditure_to_date || 0), 0),
      }
      : codeListTotals(list, budgetAmounts, adjustments);
    const ordered = list;
    return (
            <>
              {/* Mobile: card layout */}
              <div className={isTotal ? 'md:hidden' : 'md:hidden space-y-2 p-3'}>
                {/* 🔀 EXPAND-TO-EDIT (2026-10-24): the card header taps to open
                    ONE panel (`renderCostCodePanel`); no Edit button. */}
                {ordered.map((cc, idx) => {
                  const isJob = !!cc.is_job;
                  const isExpanded = costCodeEditor.isExpanded(cc.id);
                  const spent = cc.expenditure_to_date || 0;
                  return (
                    <React.Fragment key={cc.id}>
                    <div
                      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-opacity ${costCodeEditor.isDimmed(cc.id) ? 'opacity-40' : ''}`}
                      data-testid={`cc-card-${cc.id}`}
                    >
                      {/* Header: tap to open — chevron + code + name + reorder */}
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onClick={() => costCodeEditor.toggle(cc)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); costCodeEditor.toggle(cc); }
                        }}
                        className={`flex items-start gap-2 p-3 min-h-[44px] cursor-pointer rounded-t-lg ${isExpanded ? 'bg-gray-100 dark:bg-slate-700' : ''}`}
                        data-testid={`cc-card-toggle-${cc.id}`}
                      >
                        <span className="mt-[3px] shrink-0 inline-flex"><ExpandChevron expanded={isExpanded} /></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-mono text-gray-500 dark:text-gray-400">{cc.code}</div>
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={cc.name}>
                            {cc.name}
                            {isJob && <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400" data-testid={`job-gag-card-${cc.id}`}>{jobGagLabel(cc)}</span>}
                          </div>
                          {isJob && renderRelinkPrompt(cc)}
                          {cc.description && (
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate" title={cc.description}>{cc.description}</div>
                          )}
                        </div>
                        {canEdit && !isJob && (
                          <RowReorderButtons index={idx} count={list.length} onMove={handleReorderCostCode} layout="card" />
                        )}
                      </div>

                      {/* Budget + Spent grid (read-only) — stays while open */}
                      <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Budget</div>
                          <div className="text-base font-semibold text-gray-800 dark:text-gray-200 text-right pr-2">
                            {formatMoney(budgetAmounts[cc.id] || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Spent</div>
                          <div className="text-base text-gray-600 dark:text-gray-400 text-right pr-2">
                            <SpendToDateCell
                              computed={spent}
                              adjustment={adjustments[cc.id]?.amount || 0}
                              note={adjustments[cc.id]?.note}
                              formatMoney={formatMoney}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Manual spend adjustment — READ-ONLY at rest; it is
                          edited inside the panel (owner, 2026-08-15). */}
                      {showAdjustmentColumn && (
                        <div className="px-3 pb-3 flex items-center justify-between gap-2">
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Adjustment</div>
                          <div className="text-base text-gray-600 dark:text-gray-400 text-right pr-2">
                            <AdjustmentValue amount={adjustments[cc.id]?.amount || 0} formatMoney={formatMoney} />
                          </div>
                        </div>
                      )}

                      {costCodeEditor.isPanelMounted(cc.id) && (
                        <div className={`px-3 pb-3 pt-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-lg ${rowPanelFadeClass(costCodeEditor.isFading(cc.id))}`}>
                          {renderCostCodePanel(cc, 'card')}
                        </div>
                      )}
                    </div>
                    </React.Fragment>
                  );
                })}
                {/* Phone subtotal card (a section) / the combined Total card. */}
                <div
                  className={isTotal
                    ? 'bg-gray-100 dark:bg-gray-700/50 rounded-lg border-2 border-gray-300 dark:border-gray-600 p-3'
                    : 'bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 p-3'}
                  data-testid={`${sumTestId}-card`}
                >
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">{sumLabel}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Budget</div>
                      <div className="text-base font-bold text-gray-800 dark:text-gray-200 text-right pr-2">{formatMoney(sums.budget)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Spent</div>
                      <div className="text-base font-bold text-gray-600 dark:text-gray-400 text-right pr-2">
                        <SpendToDateCell
                          computed={sums.computed}
                          adjustment={sums.adjustment}
                          formatMoney={formatMoney}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop: original table — now adjustable + evenly distributed.
                  Column model: BUDGET_COST_CODES_COLUMNS above. The colgroup,
                  the header row and the body's hasAdjustmentColumn /
                  hasReorderColumn and the panel colSpan all read `costCodesColumns`
                  (or booleans derived from it), never a second copy of
                  `showAdjustmentColumn`/`canEdit` — see
                  `visibleBudgetCostCodesColumns`'s own doc for why. */}
              <BudgetTableScroller
                scrollerRef={scrollerRef}
                wrapperClassName={isTotal ? 'hidden md:block rounded-lg border border-gray-300 dark:border-gray-600 overflow-clip' : 'hidden md:block'}
                testId={scrollerTestId}
                stickyBottom={crewTableStickyBottomPx}
              >
                <table className="w-full table-fixed text-sm" style={{ minWidth: costCodesTableTotalWidth }}>
                  <colgroup>
                    {costCodesColumns.map((c) => (
                      <col key={c.key} style={{ width: costCodesColWidths[c.key] }} />
                    ))}
                  </colgroup>
                  {!isTotal && <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      {costCodesColumns.map((c) => (
                        <th
                          key={c.key}
                          className={`relative ${budgetCostCodesAlignClass(c.align)} ${c.key === 'reorder' || c.key === 'row_menu' ? 'px-1' : 'px-3'} py-2 font-semibold text-gray-600 dark:text-gray-400`}
                        >
                          {/* Owner's original wording for Adjustment was "Overide
                              cost to date"; the semantics are a signed added
                              delta, so the header says Adjustment — flagged for
                              him to rename. */}
                          {c.label}
                          <ColumnResizeHandle
                            columnKey={c.key}
                            label={c.label || 'Reorder'}
                            width={costCodesColWidths[c.key]}
                            min={c.min}
                            max={BUDGET_COST_CODES_MAX_COLUMN_WIDTH}
                            totalWidth={costCodesTableTotalWidth}
                            scrollportWidth={costCodesScrollportWidth}
                            onResize={setCostCodesColWidth}
                            onReset={resetCostCodesColumn}
                            testIdPrefix={resizePrefix}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>}
                  <tbody>
                    {ordered.map((cc, idx) => {
                  const isJob = !!cc.is_job;
                      const isExpanded = costCodeEditor.isExpanded(cc.id);
                      // 🔀 IN-PLACE EDITING (2026-10-25, the Breakdown's model):
                      // an editor's open row turns code, name, description,
                      // budget (with a version selected) and the adjustment
                      // into the shared row controls in their own cells. Spent
                      // is DERIVED (server spend + adjustment) and stays text.
                      const editing = canEdit && costCodeEditor.isEditing(cc.id);
                      const pad = editing ? ROW_CELL_PAD_EDIT : ROW_CELL_PAD;
                      const textControl = (key, testId, label) => (
                        <div onClick={stopRowToggleOnControl}>
                          <input type="text" className={ROW_EDIT_CONTROL} value={editCostCodeForm[key] ?? ''}
                            aria-label={`${label} for ${cc.code}`}
                            onChange={(e) => costCodeEditor.setField(key, e.target.value)} data-testid={testId} />
                        </div>
                      );
                      return (
                        <React.Fragment key={cc.id}>
                        <tr
                          data-testid={`cc-row-${cc.id}`}
                          className={`border-b border-gray-100 dark:border-gray-700/50 ${editableRowClass({ expanded: isExpanded, dimmed: costCodeEditor.isDimmed(cc.id) })}`}
                          onClick={() => costCodeEditor.toggle(cc)}
                        >
                          <td className={`${pad} font-mono text-gray-800 dark:text-gray-200`} title={cc.code}>
                            <span className="flex items-center gap-1 min-w-0">
                              <RowToggleChevron
                                expanded={isExpanded}
                                onToggle={() => costCodeEditor.toggle(cc)}
                                expandLabel={`Expand ${cc.code}`}
                                collapseLabel={`Collapse ${cc.code}`}
                                testId={`cc-chevron-${cc.id}`}
                              />
                              {editing
                                ? <span className="min-w-0 flex-1">{textControl('code', 'edit-cc-code-row', 'Code')}</span>
                                : <span className="truncate">{cc.code}</span>}
                            </span>
                          </td>
                          <td className={`${pad} text-gray-800 dark:text-gray-200 ${isJob ? '' : 'truncate'}`} title={cc.name}>
                            {editing ? (
                              <>
                                {textControl('name', 'edit-cc-name-row', isJob ? 'Job' : 'Name')}
                                {isJob && cc.gag_id != null && (
                                  <RenameSyncNote where="Breakdown" testId={`cc-name-sync-note-row-${cc.id}`}
                                    changed={String(editCostCodeForm.name ?? '').trim() !== String(cc.name || '').trim()} />
                                )}
                              </>
                            ) : isJob ? (
                              <>
                                <span className="block truncate">
                                  {cc.name}
                                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400" data-testid={`job-gag-${cc.id}`}>{jobGagLabel(cc)}</span>
                                </span>
                                {renderRelinkPrompt(cc)}
                              </>
                            ) : cc.name}
                          </td>
                          <td className={`${pad} text-gray-500 dark:text-gray-400 truncate`} title={cc.description || undefined}>
                            {editing ? textControl('description', 'edit-cc-description-row', 'Description') : (cc.description || '—')}
                          </td>
                          <td className={`${pad} text-center`}>
                            {/* No version selected → no budget to edit: the
                                cell stays text (the page's own rule). */}
                            {editing && selectedVersionId ? (
                              <div onClick={stopRowToggleOnControl}>
                                <CurrencyInput value={editCostCodeForm.budget_amount} onChange={(v) => costCodeEditor.setField('budget_amount', v)}
                                  placeholder="£0.00" className={ROW_EDIT_MONEY_CONTROL} testId="edit-cc-budget-row" />
                              </div>
                            ) : (
                              <span className="text-gray-800 dark:text-gray-200">
                                {formatMoney(budgetAmounts[cc.id] || 0)}
                              </span>
                            )}
                          </td>
                          {hasAdjustmentColumn && (
                            <td className={`${pad} text-right text-gray-600 dark:text-gray-400`}>
                              {/* AT REST: read-only (owner, 2026-08-15); an open
                                  editor's row edits it in place. A changed value
                                  still raises the ruled warning at Save. */}
                              {editing ? (
                                <div onClick={stopRowToggleOnControl}>
                                  <CurrencyInput allowNegative value={editCostCodeForm.spend_adjustment} onChange={(v) => costCodeEditor.setField('spend_adjustment', v)}
                                    placeholder="£0.00" className={ROW_EDIT_MONEY_CONTROL} testId="edit-cc-adjustment-row" />
                                </div>
                              ) : (
                                <AdjustmentValue amount={adjustments[cc.id]?.amount || 0} formatMoney={formatMoney} />
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">
                            <SpendToDateCell
                              computed={cc.expenditure_to_date || 0}
                              adjustment={adjustments[cc.id]?.amount || 0}
                              note={adjustments[cc.id]?.note}
                              formatMoney={formatMoney}
                            />
                          </td>
                          {hasReorderColumn && (
                            <td className="px-1 py-1 text-center" onClick={stopRowToggle}>
                              {!isJob && <RowReorderButtons index={idx} count={list.length} onMove={handleReorderCostCode} />}
                            </td>
                          )}
                          {hasRowMenuColumn && (
                            <RowMenuCell label={`Actions for ${cc.code}`} items={[{ key: 'delete', label: isJob ? 'Delete job code' : 'Delete cost code', danger: true, onClick: () => handleDeleteCostCode(cc) }]} />
                          )}
                        </tr>
                        <RowExpansion
                          editor={costCodeEditor}
                          id={cc.id}
                          colSpan={costCodesColumns.length}
                          panelWidth={costCodesScrollportWidth}
                          testId={`cc-panel-row-${cc.id}`}
                          bar={canEdit ? (
                            <RowEditBar
                              onSave={() => handleSaveEditCostCode(cc.id)}
                              onCancel={costCodeEditor.collapse}
                              saving={savingCostCode}
                              dirty={costCodeEditor.isDirty}
                              testIdPrefix={`cc-row-edit-${cc.id}`}
                            />
                          ) : null}
                        >
                          {renderCostCodePanel(cc, 'row')}
                        </RowExpansion>
                        </React.Fragment>
                      );
                    })}
                    {/* Total row — Spent total is the sum of the DISPLAYED
                        (adjusted) cells, so the column and its total agree.
                        Every money cell's alignment comes from
                        BUDGET_COST_CODES_ALIGN_BY_KEY (see its docblock) —
                        never a hard-coded text-right/text-center, which is
                        exactly how Budget's total drifted from its own
                        header and column before this. */}
                    <tr
                      className={isTotal
                        ? 'bg-gray-50 dark:bg-gray-700/50 font-semibold'
                        : 'border-t border-gray-200 dark:border-gray-700 font-semibold'}
                      data-testid={sumTestId}
                    >
                      <td className={`px-3 py-2 ${isTotal ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`} colSpan={2}>{sumLabel}</td>
                      <td className="px-3 py-2"></td>
                      <td className={`px-3 py-2 ${budgetCostCodesAlignClass(BUDGET_COST_CODES_ALIGN_BY_KEY.budget)} text-gray-800 dark:text-gray-200`}>{formatMoney(sums.budget)}</td>
                      {hasAdjustmentColumn && (
                        <td className={`px-3 py-2 ${budgetCostCodesAlignClass(BUDGET_COST_CODES_ALIGN_BY_KEY.adjustment)} text-gray-500 dark:text-gray-400`}>
                          {formatMoney(sums.adjustment)}
                        </td>
                      )}
                      <td className={`px-3 py-2 ${budgetCostCodesAlignClass(BUDGET_COST_CODES_ALIGN_BY_KEY.spent)} text-gray-500 dark:text-gray-400`}>
                        <SpendToDateCell
                          computed={sums.computed}
                          adjustment={sums.adjustment}
                          formatMoney={formatMoney}
                        />
                      </td>
                      {hasReorderColumn && <td className="px-1 py-2"></td>}
                      {hasRowMenuColumn && <td className="px-1 py-2"></td>}
                    </tr>
                  </tbody>
                </table>
              </BudgetTableScroller>
            </>
    );
  };

  // --- No department selected ---
  if (!departmentId && !isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <p className="text-gray-600 dark:text-gray-400">Please select a department to manage budget.</p>
      </div>
    );
  }

  // Input class helpers
  const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm';
  // 44px tall on mobile for touch targets, collapses to py-1/text-sm on desktop
  const inputClsSmallTouch = 'w-full border border-gray-300 dark:border-gray-600 rounded px-3 md:px-2 h-11 md:h-auto md:py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base md:text-sm';

  /**
   * THE CREW MEMBER DETAILS PANEL — ONE DEFINITION, TWO SURFACES.
   *
   * 🔑 EXTRACTED, NOT REWRITTEN (2026-08-22, Gantt second expand). This JSX
   * was inline inside `filteredMembers.map()` and is the same markup it was
   * there; the ONLY additions are the two `embedded` gates marked at their
   * sites. A second implementation of a money form is exactly the defect this
   * feature has spent the week eliminating, so the Gantt's level-2 expand
   * renders THIS function rather than a panel of its own.
   *
   * `embedded` = rendered inside `CrewGanttView` (the Gantt's SECOND expand)
   * rather than inside the crew table's expanded `<tr>`. It changes exactly
   * two things and neither is cosmetic:
   *   1. **The Working Days section is not rendered.** Owner: *"No need for
   *      the 'Working Days' section as that info is in the gantt."* It is a
   *      SECOND day editor writing a DIFFERENT overlay (`weekDayOverlay`) from
   *      the strip's (`ganttDayOverlay`); two of them on one screen would give
   *      the member two disagreeing day sets and two Save paths. The section
   *      is NOT deleted — the table view renders it exactly as before.
   *   2. **No Save/Cancel/Delete row.** There is ONE Save at level 2 and it is
   *      the strip's — see `handleGanttDaySave`. Two commit buttons over one
   *      edit is the ambiguity trap 4 of the brief names.
   */
  /**
   * The member the Gantt's level-2 panel renders. `expandedMemberId` is the
   * live one; `closingMemberId` keeps it resolvable for the 250ms
   * `collapseMember` spends fading out, which is what lets the panel animate
   * shut rather than disappear. Same keying as `detailsGanttMembers` (option A)
   * and `weekMember`, deliberately — three readers of one question.
   */
  const ganttDetailsPanelMember = members.find(
    (x) => x.id === (expandedMemberId || closingMemberId)
  ) || null;

  /**
   * 🔀 THE FIVE CREW FIELDS THE ROW EDITS IN PLACE (owner ruling 2026-10-25:
   * *"grade, location, start date, end date, allocation"* — every other cell
   * stays read-only; the rate override stays a "delibrate action in the
   * expanded menu"). ONE definition of each control, rendered by BOTH the
   * desktop row's own cell and the panel, so the two can never write
   * differently: the same `editMemberForm` keys, the same
   * `handleMemberDateFieldChange` (the day-grid / Gantt draft work on a date
   * change), the same grade-disabled-while-a-weekly-override-is-set rule — and
   * so the SAME save (the panel's save bar, unchanged). `where` is `'row'` or
   * `'panel'` and only changes the testid; the class comes from the caller.
   */
  const memberFieldControl = (m, field, { className, where }) => {
    const tid = (base) => (where === 'row' ? `${base}-row-${m.id}` : `${base}-${m.id}`);
    switch (field) {
      case 'grade': {
        const hasRateOverride = Number.isFinite(parseFloat(editMemberForm.weekly_rate_override));
        return (
          <select
            value={editMemberForm.crew_grade_id || ''}
            onChange={(e) => setEditMemberForm(prev => ({ ...prev, crew_grade_id: e.target.value }))}
            className={`${className} ${hasRateOverride ? 'opacity-50' : ''}`}
            disabled={hasRateOverride}
            aria-label={`Grade for ${m.name}`}
            data-testid={tid('crew-grade-select')}
          >
            <option value="">Select...</option>
            {grades.map(g => <option key={g.id} value={g.id}>{g.name} ({formatMoney(g.weekly_rate)}/wk){hasRateOverride && String(editMemberForm.crew_grade_id) === String(g.id) ? ' (Overridden)' : ''}</option>)}
          </select>
        );
      }
      case 'location':
        return (
          <input type="text" value={editMemberForm.location || ''}
            onChange={(e) => setEditMemberForm(prev => ({ ...prev, location: e.target.value }))}
            placeholder={where === 'row' ? undefined : 'e.g. Pinewood'} aria-label={`Location for ${m.name}`}
            className={className} data-testid={tid('crew-location')} />
        );
      case 'start_date':
      case 'end_date':
        return (
          <input type="date" value={editMemberForm[field] || ''}
            onChange={(e) => handleMemberDateFieldChange(field, e.target.value)}
            aria-label={`${field === 'start_date' ? 'Start date' : 'End date'} for ${m.name}`}
            className={className} data-testid={tid(field === 'start_date' ? 'crew-start-date' : 'crew-end-date')} />
        );
      case 'default_allocation':
        return (
          <select
            value={editMemberForm.default_allocation ?? 1.0}
            onChange={(e) => setEditMemberForm(prev => ({ ...prev, default_allocation: e.target.value }))}
            aria-label={`Allocation for ${m.name}`}
            className={className} data-testid={tid('crew-allocation')}
          >
            {DEFAULT_ALLOCATION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        );
      default:
        return null;
    }
  };

  /**
   * Where a panel copy of a row-edited field HIDES: exactly where its row cell
   * shows its control (the row edits from `md`; Location's cell only exists
   * from `lg`). Below that — phones — and in the Gantt-embedded panel (no row
   * above it) the panel keeps the field: it is the only editor there, the
   * Breakdown's precedent (its phone rows are display-only).
   */
  /* The five in-row columns are given a FLOOR at rest (header min-width, from
     `md`; Location's cell exists from `lg`) wide enough for their control —
     measured 2026-10-25: frozen at their CONTENT width the grade select was
     42px and the date inputs 91px, unusable. A floor at rest is the only way
     to have a usable control AND the same width collapsed and editing. */
  const CREW_ROW_EDIT_MIN = { grade: 'md:min-w-[9rem]', location: 'lg:min-w-[7.5rem]', date: 'md:min-w-[8rem]', alloc: 'md:min-w-[6.5rem]' };
  const ROW_EDITED_PANEL_HIDE = { grade: 'md:hidden', start_date: 'md:hidden', end_date: 'md:hidden', default_allocation: 'md:hidden', location: 'lg:hidden' };

  const renderMemberDetailsPanel = (m, { embedded = false } = {}) => {
    const rowEdited = (field) => (embedded ? '' : ROW_EDITED_PANEL_HIDE[field]);
    /* 🔀 THROUGH `memberWithLivePreview` SINCE 2026-08-23. It was a hand-built
       object laying `dragPreview`'s dates AND its `budget_cost` / `total_weeks`
       over the member — and those two fields were the client-side `weeks × rate`
       the owner reported as ignoring off days. The resolver next door answers
       the same question for the footer, the crew table and this panel, so the
       three cannot print different money for one person on one screen. `dp` is
       still read for the dates only, exactly as the resolver reads it. */
    const dp = dragPreview && dragPreview.memberId === m.id ? dragPreview : null;
    const mEff = memberWithLivePreview(m);
    const allocLabel = DEFAULT_ALLOCATION_OPTIONS.find(o => o.value === parseFloat(m.default_allocation))?.label || m.default_allocation;
    return (
                              /* 🎨 `pb-[50px]` — the owner's measured value, 2026-08-23:
                                 *"increase the bottom padding to 50 so theres a better gap
                                 between the expanded section and rest of the gantt chart when
                                 expanded"*. He read 50px off devtools, so it is written as an
                                 arbitrary value rather than rounded to `pb-12` (48) or `pb-14`
                                 (56) — arbitrary spacing is house-normal here (`min-h-[44px]`,
                                 `basis-[18rem]`, `w-[9px]`).

                                 🪤 BOTH `pb-[50px]` AND `md:pb-[50px]` ARE REQUIRED AND THE
                                 SECOND IS NOT REDUNDANT. Tailwind emits every responsive
                                 variant AFTER all the unprefixed utilities, so the `md:p-4`
                                 already on this element would win over a lone `pb-[50px]` from
                                 `md` up — resetting the bottom padding to 1rem on exactly the
                                 breakpoint the owner was looking at, while the phone kept the
                                 50px he never asked about. Adding both makes the padding
                                 width-independent, which is the decision taken here: this is
                                 ONE panel rendered on TWO surfaces and the gap reads the same
                                 on both.

                                 🔻 APPLIED UNCONDITIONALLY, NOT GATED ON `embedded`. The
                                 docblock above this function states that `embedded` changes
                                 exactly two things and neither is cosmetic; a third,
                                 purely-visual divergence would falsify it. The table view's
                                 panel butts up against the next crew row in the same way.

                                 🔑 The height is MEASURED, not declared — `CrewGanttView`'s
                                 `<div ref={detailsBodyRef}>{detailsPanel}</div>` wraps this
                                 element, and padding sits inside its border box, so the extra
                                 50px flows into `detailsBodyHeight` → `detailsHeight` →
                                 `rowHeightFor`, the row growth, `detailsTop` and the raised
                                 600px cap from the one measurement. Nothing here needs its own
                                 offset, and adding one would double-count. */
                              <div data-crew-edit-form className={`bg-white border-t border-gray-200 dark:border-gray-600 p-3 md:p-4 pb-[50px] md:pb-[50px] space-y-4 transition-opacity duration-200 ease-out ${closingMemberId === m.id || openingMemberId === m.id ? 'opacity-0' : 'opacity-100'}`}>

                                {/* 🔑 THE PANEL NAMES ITS MEMBER WHEN EMBEDDED, and that
                                    is the whole answer to "which row is this about".
                                    Rendered as a SIBLING of the chart body, the panel sits
                                    below the last row rather than below its own — so it has
                                    to be self-identifying wherever it lands.
                                    🎨 TEXT, DELIBERATELY, AND NO NEW VISUAL CHANNEL. In
                                    this directory `opacity-*` is spoken for twice and the
                                    inactive-row greyscale now takes saturation; a connector
                                    line or a caret would be a third. The greyscale is
                                    already doing the isolating work — with a row expanded
                                    it is the only COLOURED row on the chart — and this
                                    header only has to say which one.
                                    In the table view the row is directly above the panel,
                                    so the header would be a duplicate of the name cell. */}
                                {embedded && (
                                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pb-2 border-b border-gray-200 dark:border-gray-600">
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{m.name}</h4>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{m.grade_name || 'No grade'}</span>
                                  </div>
                                )}

                                {/* Edit Fields Grid */}
                                {canEdit ? (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                    {/* Row 1: Who + When */}
                                    <div>
                                      <label className={FIELD_LABEL_CLASS}>Name</label>
                                      <UserAutocomplete
                                        value={editMemberForm.name || ''}
                                        onChange={(name, userId) => setEditMemberForm(prev => ({ ...prev, name, user_id: userId !== null ? userId : prev.user_id }))}
                                        className={inputClsSmallTouch}
                                        excludeUserIds={assignedUserIds.filter(id => id !== m.user_id)}
                                      />
                                    </div>
                                    <div className={rowEdited('grade')}>
                                      <label className={FIELD_LABEL_CLASS}>Grade</label>
                                      {/* ═══ A £0 RATE OVERRIDE IS AN OVERRIDE (fixed 2026-08-23) ═══════
                                          All three expressions below tested
                                          `editMemberForm.weekly_rate_override` for
                                          TRUTHINESS, so a deliberate override of **£0** —
                                          a buyer saying "this person is unpaid" — read as
                                          NO override: the grade select stayed enabled and
                                          un-dimmed, and no `(Overridden)` suffix appeared.

                                          🟢 DISPLAY-ONLY, VERIFIED RATHER THAN ASSUMED, and
                                          this is the part that decides the severity. The
                                          COSTING was always right: `resolveEffectiveRate`
                                          (`server/helpers/crewCost.js`) uses
                                          `Number.isFinite`, so a £0 override has always beaten
                                          the grade rate in the money; and both save paths
                                          coerce with `!== ''`, never `||`, so a typed 0 has
                                          always been STORED as 0. What was wrong was the
                                          label and the disabled state — the form said "no
                                          override" about a member the server was correctly
                                          charging £0.

                                          🔑 `Number.isFinite(parseFloat(x))`, MATCHING
                                          `resolveEffectiveRate`'s own test, because the
                                          display must agree with the money rather than hold a
                                          second opinion about what counts as an override.
                                          `''` (the empty field) parses to NaN — not an
                                          override; `'0'` parses to 0 — an override.

                                          🔑 ONE named predicate for all three sites. Three
                                          copies of a subtle test is exactly how two of them
                                          get fixed and the third does not — which is how this
                                          shape keeps recurring in this subsystem. */}
                                      {/* 🔀 2026-10-25: the ONE definition, `memberFieldControl`
                                          (the predicate above now lives there). */}
                                      {memberFieldControl(m, 'grade', { className: inputClsSmallTouch, where: 'panel' })}
                                    </div>
                                    <div className={PANEL_FIELD_CELL}>
                                      <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="Rate Override (£/wk)">Rate Override (£/wk)</label>
                                      <CurrencyInput value={editMemberForm.weekly_rate_override ?? ''}
                                        onChange={(v) => setEditMemberForm(prev => ({ ...prev, weekly_rate_override: v }))}
                                        placeholder={m.grade_weekly_rate ? formatMoney(m.grade_weekly_rate) : 'Grade rate'}
                                        className={inputClsSmallTouch} />
                                    </div>
                                    {/* THE 6th/7th-DAY PREMIUM OVERRIDES (Stage 3 Task C).
                                        The owner went looking for these on staging and they
                                        did not exist: the schema and the costing shipped
                                        without the field that lets a human enter a number.

                                        🚨 BLANK IS NOT ZERO. Blank means the GRADE's rate
                                        applies, and if the grade has none either the
                                        DEFAULT UPLIFT applies — it is not free. A typed 0 is
                                        REFUSED at save (2026-08-23, "no free days"), so the
                                        two answers this field offers are a blank and a real
                                        rate. The placeholder names whichever the member
                                        would fall back to, so a blank field never reads
                                        as £0.

                                        🔻 CORRECTED 2026-08-23. This said the day "stays in
                                        the standard pool at rate/5" and the placeholder read
                                        'Unpriced' when the grade had no rate. Both became
                                        wrong the moment the resolve-time default landed: a
                                        blank field on an unpriced grade now costs 1.5× / 2×
                                        the daily rate, and a placeholder saying 'Unpriced'
                                        would have been the interface asserting the opposite
                                        of the money.
                                        🔀 2026-10-25 OWNER RULING: it now shows the £ FIGURE
                                        too (`premiumPlaceholder`) — the grade's stated rate,
                                        else the default uplift on the member's weekly rate
                                        (typed override, else the grade's). This REVERSES
                                        "a figure here would be a client-side money
                                        derivation"; it is placeholder text only and no body
                                        reads it. */}
                                    <div className={PANEL_FIELD_CELL} title={premiumPlaceholder(6, { weekly: memberPremiumWeekly(editMemberForm, m), gradeRate: m.grade_sixth_day_rate, formatMoney, rates: m.agreement_rates }).title}>
                                      <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="6th Day Rate (£)">6th Day Rate (£)</label>
                                      <CurrencyInput value={editMemberForm.sixth_day_rate_override ?? ''}
                                        onChange={(v) => setEditMemberForm(prev => ({ ...prev, sixth_day_rate_override: v }))}
                                        placeholder={premiumPlaceholder(6, { weekly: memberPremiumWeekly(editMemberForm, m), gradeRate: m.grade_sixth_day_rate, formatMoney, rates: m.agreement_rates }).placeholder}
                                        className={inputClsSmallTouch}
                                        testId="member-sixth-day-rate-override" />
                                    </div>
                                    <div className={PANEL_FIELD_CELL} title={premiumPlaceholder(7, { weekly: memberPremiumWeekly(editMemberForm, m), gradeRate: m.grade_seventh_day_rate, formatMoney, rates: m.agreement_rates }).title}>
                                      <label className={`${FIELD_LABEL_CLASS} ${PANEL_FIELD_LABEL_ONE_LINE}`} title="7th Day Rate (£)">7th Day Rate (£)</label>
                                      <CurrencyInput value={editMemberForm.seventh_day_rate_override ?? ''}
                                        onChange={(v) => setEditMemberForm(prev => ({ ...prev, seventh_day_rate_override: v }))}
                                        placeholder={premiumPlaceholder(7, { weekly: memberPremiumWeekly(editMemberForm, m), gradeRate: m.grade_seventh_day_rate, formatMoney, rates: m.agreement_rates }).placeholder}
                                        className={inputClsSmallTouch}
                                        testId="member-seventh-day-rate-override" />
                                    </div>
                                    {/* S2 OVERTIME-RATE OVERRIDES — blank inherits; the
                                        placeholder is the SERVER's inherited value (the
                                        grade's, or the derived hourly), never computed here. */}
                                    <div className="col-span-full" data-testid={`member-overtime-${m.id}`}>
                                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Overtime rates (blank uses the grade)</div>
                                      <OvertimeRateFields
                                        mode="member"
                                        form={editMemberForm}
                                        onChange={(key, v) => setEditMemberForm(prev => ({ ...prev, [key]: v }))}
                                        resolved={m.overtime_rates} agreementRates={m.agreement_rates}
                                        formatMoney={formatMoney}
                                        inputClassName={inputClsSmallTouch}
                                        testIdPrefix="member-overtime"
                                      />
                                    </div>
                                    <div className={rowEdited('start_date')}>
                                      <label className={FIELD_LABEL_CLASS}>Start Date</label>
                                      {memberFieldControl(m, 'start_date', { className: inputClsSmallTouch, where: 'panel' })}
                                    </div>
                                    <div className={rowEdited('end_date')}>
                                      <label className={FIELD_LABEL_CLASS}>End Date</label>
                                      {memberFieldControl(m, 'end_date', { className: inputClsSmallTouch, where: 'panel' })}
                                    </div>
                                    {/* Sub-department — a membership attribute on the linked person (synced with
                                        the Users tab), saved immediately via the shared endpoint. Disabled for
                                        placeholders (no linked person). */}
                                    <div>
                                      <label className={FIELD_LABEL_CLASS}>Sub-Department</label>
                                      {/* 🚨 THE LIST FAILED TO LOAD — RENDER NO CONTROL, 2026-08-22.
                                          A controlled `<select>` whose value has no matching
                                          `<option>` falls back to `selectedIndex = 0`, which here is
                                          `— No sub-dept —`, so a crew member who IS in a
                                          sub-department reads as being in none. The only correction
                                          on offer is that same empty option, and `handleSetCrewSubDept`
                                          saves it IMMEDIATELY — no submit, no confirmation — against
                                          `department_assignments.sub_department_id`, i.e. a real
                                          membership edit on the linked person, off a request that
                                          never landed. */}
                                      {subDepartmentsFailed ? (
                                        <p
                                          className="text-xs text-amber-700 dark:text-amber-300"
                                          role="status"
                                          data-testid={`crew-subdept-load-failed-${m.id}`}
                                        >
                                          Sub-departments couldn’t be loaded — this person’s is unchanged
                                        </p>
                                      ) : (
                                      <select
                                        value={m.user_sub_department_id ? String(m.user_sub_department_id) : ''}
                                        disabled={!canManageVersions || !m.user_id || savingSubDeptMemberId === m.id}
                                        title={!m.user_id ? 'Assign a person to this crew line first' : 'Saved immediately'}
                                        onChange={(e) => handleSetCrewSubDept(m, e.target.value)}
                                        className={inputClsSmallTouch}
                                        data-testid={`crew-subdept-${m.id}`}
                                      >
                                        <option value="">{m.user_id ? '— No sub-dept —' : '— Assign a person first —'}</option>
                                        {subDepartments.map(sd => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                                      </select>
                                      )}
                                    </div>
                                    {/* Row 2: Where + Details + Contact */}
                                    <div className={rowEdited('location')}>
                                      <label className={FIELD_LABEL_CLASS}>Location</label>
                                      {memberFieldControl(m, 'location', { className: inputClsSmallTouch, where: 'panel' })}
                                    </div>
                                    <div className={rowEdited('default_allocation')}>
                                      <label className={FIELD_LABEL_CLASS} data-testid="crew-edit-default-allocation-label"><DefaultAllocationLabel /></label>
                                      {memberFieldControl(m, 'default_allocation', { className: inputClsSmallTouch, where: 'panel' })}
                                    </div>
                                    <div>
                                      <label className={FIELD_LABEL_CLASS}>Email</label>
                                      <input type="email" value={editMemberForm.user_email || ''}
                                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, user_email: e.target.value }))}
                                        placeholder="name@example.com"
                                        className={inputClsSmallTouch} />
                                    </div>
                                    <div>
                                      <label className={FIELD_LABEL_CLASS}>Phone</label>
                                      <input type="tel" value={editMemberForm.user_phone || ''}
                                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, user_phone: e.target.value }))}
                                        placeholder="e.g. 07700 900000"
                                        className={inputClsSmallTouch} />
                                    </div>
                                    {/* Rows 3-6: Notes (cols 3-5, spans 4 rows) + allowances in cols 1-2.
                                        ⚠️ CORRECTED 2026-08-23 — this said "Notes (cols 1-3)" and
                                        "allowances in cols 4-5", and both were the wrong way round.
                                        MEASURED in Chromium: the Notes block's left edge is 415px
                                        from the grid's, i.e. two columns plus two gaps, so it starts
                                        at column 3. Email and Phone occupy cols 1-2 of that same
                                        row and a `col-span-3` item cannot fit in the two columns
                                        left beside them, so auto-placement puts Notes at 3-5 and
                                        the allowance stack lands in 1-2. Two comments below still
                                        said "cols 4-5 (cols 1-3 empty)" and are corrected with it.
                                        Computer Rental is the first allowance row (name locked); it binds to
                                        the dedicated computer_rental / computer_rental_cap fields. */}
                                    <div className="sm:col-span-2 lg:col-span-3 lg:row-span-4 flex flex-col">
                                      <label className={FIELD_LABEL_CLASS}>Notes</label>
                                      <textarea
                                        value={editMemberForm.notes || ''}
                                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Optional notes..."
                                        rows={2}
                                        className={`${inputClsSmallTouch} resize-y flex-1 min-h-[5rem]`}
                                      />
                                    </div>
                                    {/* THE FOUR ALLOWANCE SLOTS — Computer Rental first (name locked,
                                        bound to computer_rental / computer_rental_cap), then the three
                                        named slots, in the grid cells they always had (cols 1-2 beside
                                        Notes). ONE map over `ALLOWANCE_SLOTS`, so all four are the same
                                        shape and only the NAME differs (the 2026-08-23 ruling: `Computer
                                        Allowance` / `Allowance Cap`, never a different KIND of row).
                                        🔀 GRADE ALLOWANCES (2026-10-25): a slot FOLLOWS the grade while
                                        its £/week box is BLANK — name, amount AND cap together (the
                                        server's per-slot rule) — and is the member's OWN once any amount,
                                        0 included, is typed. Placeholders are the SERVER's
                                        `allowances.<slot>.inherited_*` (`memberAllowanceHints`); the pen
                                        marks a slot the SAVED row overrides. Clearing the amount sends
                                        `null` = follow (`buildMemberPutPayload`, unchanged). */}
                                    {ALLOWANCE_SLOTS.map((slot, i) => {
                                      const hints = memberAllowanceHints(slot, m.allowances, editMemberForm, formatMoney);
                                      const setKey = (key) => (v) => setEditMemberForm(prev => ({ ...prev, [key]: v }));
                                      return (
                                        <React.Fragment key={slot.key}>
                                          {/* 📏 F2 (2026-10-25): every cell is `PANEL_FIELD_CELL` (box
                                              pinned to the bottom) and every label ONE line
                                              (`MEMBER_ALLOWANCE_LABEL`), so the three boxes of a slot share a
                                              top edge — "Computer (grade)" wrapped at 1024–1440 and dropped
                                              its £/week box 16px below the cap box. */}
                                          <div className={PANEL_FIELD_CELL}>
                                            <label className={i === 0 ? `${FIELD_LABEL_CLASS} flex items-center gap-1` : FIELD_LABEL_CLASS}>
                                              Allowance Name
                                              {i === 0 && <InfoTip title={MEMBER_ALLOWANCE_FOLLOW_HELP} />}
                                            </label>
                                            {slot.nameField ? (
                                              <AllowanceNameInput
                                                value={editMemberForm[slot.nameField]}
                                                onChange={setKey(slot.nameField)}
                                                options={allowanceNameOptions}
                                                placeholder={hints.name}
                                                className={inputClsSmallTouch}
                                                testId={`member-allowance-${slot.key}-name`}
                                              />
                                            ) : (
                                              <input type="text" value={COMPUTER_ALLOWANCE_NAME} disabled title="Built-in allowance — name is fixed"
                                                className={`${inputClsSmallTouch} opacity-70 cursor-not-allowed`} />
                                            )}
                                          </div>
                                          <div className={PANEL_FIELD_CELL}>
                                            <div className="flex gap-1.5">
                                              <div className={`flex-1 ${PANEL_FIELD_CELL}`}>
                                                <label className={MEMBER_ALLOWANCE_LABEL} title={hints.amountLabel}>
                                                  <span className={PANEL_FIELD_LABEL_ONE_LINE}>{hints.amountLabel}</span>
                                                  {hints.penTitle && (
                                                    <OverrideMarker title={hints.penTitle} className="flex-shrink-0" testId={`member-allowance-${slot.key}-override`} />
                                                  )}
                                                </label>
                                                <CurrencyInput value={editMemberForm[slot.amountField] ?? ''}
                                                  onChange={setKey(slot.amountField)}
                                                  placeholder={hints.amount}
                                                  className={inputClsSmallTouch}
                                                  testId={`member-allowance-${slot.key}-amount`} />
                                              </div>
                                              <div className={`flex-1 ${PANEL_FIELD_CELL}`}>
                                                <label className={MEMBER_ALLOWANCE_LABEL} title={hints.capLabel}>
                                                  <span className={PANEL_FIELD_LABEL_ONE_LINE}>{hints.capLabel}</span>
                                                </label>
                                                <CurrencyInput value={editMemberForm[slot.capField] ?? ''}
                                                  onChange={setKey(slot.capField)}
                                                  placeholder={hints.cap}
                                                  className={inputClsSmallTouch}
                                                  testId={`member-allowance-${slot.key}-cap`} />
                                              </div>
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      );
                                    })}
                                    {/* ═══ THE COST SUMMARY LIVES IN THE GRID (2026-08-23) ══════
                                        Owner: *"we have 3 empty cells under notes, please put the
                                        cost summary in there, you could probably use double lines
                                        if required."* Then, offered the options: *"could merge the
                                        3 cells, or split thoes three into two rows etc etc. Up to
                                        you."*

                                        ✅ MERGED, and merged to the FULL FIVE COLUMNS rather than
                                        three. The strip's own `flex-wrap` then does the degrading —
                                        whole labelled figures move to a second line and nothing
                                        breaks between a count and its cost. Splitting into two rows
                                        would impose a break point the content does not have and
                                        would need re-tuning every time a figure is added; merging
                                        reuses the mechanism that already exists and is pinned (§14).

                                        📏 MEASURED IN CHROMIUM against the real compiled stylesheet
                                        (jsdom has no layout, so none of this can be a jest
                                        assertion). Content 1025px = a 1440 screen with the sidebar
                                        open, which is the owner's own working width:

                                          span   slot width   ordinary   fullest   panel height
                                          ─────  ──────────   ────────   ───────   ────────────
                                          5      1025px       2 lines    3 lines   414px  ← built
                                          3       610px       3 lines    4 lines   438px
                                          2       403px       3 lines    4 lines   458px

                                        🔑 THE MEASUREMENT OVERTURNED THE OBVIOUS READING, WHICH IS
                                        WHY IT WAS TAKEN. The cells that are literally empty are
                                        cols 1-2 of the last Notes row — TWO cells, 403px — and
                                        putting the strip there makes the panel TALLER (458 vs
                                        414px), because a 403px strip wraps to three or four lines
                                        and costs more height than the empty row saved. Filling the
                                        dead space is the thing that creates dead space.

                                        ⚠️ AND THE COMMENT ABOVE THE NOTES BLOCK IS WRONG ABOUT
                                        WHERE IT SITS — corrected there in this change. Notes is at
                                        columns 3-5, not 1-3 (measured: x=415px, i.e. two columns
                                        plus two gaps), because Email and Phone take cols 1-2 of the
                                        same row and a `col-span-3` cannot fit in the two that are
                                        left. The allowance stack is therefore at cols 1-2.

                                        🔻 REJECTED, with the number: dropping Notes to
                                        `lg:row-span-3` removes the dead row and gives the shortest
                                        panel of all (372px) — but it collapses the notes textarea
                                        from 216px to 50px. The owner asked for the summary to be
                                        housed, not for the notes field to be gutted.

                                        🔑 EDIT BRANCH ONLY. The read-only branch is a different,
                                        4-column grid with no spare cells, so its strip stays below
                                        it, full width and unchanged. */}
                                    <div className="sm:col-span-2 lg:col-span-5" data-testid="crew-cost-summary-slot">
                                      <CrewMemberCostSummary
                                        member={costSummaryMemberFor(m)}
                                        formatMoney={formatMoney}
                                        showRate={false}
                                        provisional={costSummaryProvisional(m)}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <span className={FIELD_LABEL_CLASS}>Grade</span>
                                      <span className="text-gray-800 dark:text-gray-200">{m.grade_name || '—'}</span>
                                    </div>
                                    <div>
                                      <span className={FIELD_LABEL_CLASS}>Location</span>
                                      <span className="text-gray-800 dark:text-gray-200">{m.location || '—'}</span>
                                    </div>
                                    <div>
                                      <span className={FIELD_LABEL_CLASS}>{DEFAULT_ALLOCATION_LABEL}</span>
                                      <span className="text-gray-800 dark:text-gray-200">{allocLabel}</span>
                                    </div>
                                    {m.notes && (
                                      <div className="sm:col-span-2 lg:col-span-4">
                                        <span className={FIELD_LABEL_CLASS}>Notes</span>
                                        <span className="text-gray-800 dark:text-gray-200">{m.notes}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* 🔻 READ-ONLY BRANCH ONLY since 2026-08-23. In the
                                    EDIT branch the summary moved INSIDE the field
                                    grid, into the three empty cells under Notes
                                    (owner ruling — the reasoning is at that site).
                                    The read-only branch is a DIFFERENT, 4-column
                                    grid with no spare cells, so its strip stays
                                    here, full width, exactly as it was.
                                    🔑 And it KEEPS the Rate figure (R1): that
                                    branch shows Grade / Location / Allocation /
                                    Notes and no rate at all, so this is the only
                                    place a non-editing viewer can see one. */}
                                {!canEdit && (
                                  <CrewMemberCostSummary
                                    member={costSummaryMemberFor(m)}
                                    formatMoney={formatMoney}
                                    showRate
                                    provisional={costSummaryProvisional(m)}
                                  />
                                )}

                                {/* Weekly worked days — the crew day grid, and
                                    (PROTOTYPE) the whole Gantt showing only
                                    this member. See
                                    `SINGLE_MEMBER_GANTT_PROTOTYPE`. */}
                                {/* 🔻 NOT RENDERED IN THE GANTT'S LEVEL-2 PANEL —
                                    `embedded`. Owner: *"No need for the 'Working Days'
                                    section as that info is in the gantt."* It is a SECOND
                                    day editor writing a DIFFERENT overlay (`weekDayOverlay`)
                                    from the Gantt strip's (`ganttDayOverlay`); two of them
                                    on one screen give the member two day sets and two save
                                    paths. 🔴 NOT DELETED — the crew table renders it exactly
                                    as before, and Option A (the whole Gantt inside this
                                    panel) still lives in here untouched. */}
                                {!embedded && (
                                <div>
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                      Working Days
                                    </h4>
                                    {SINGLE_MEMBER_GANTT_PROTOTYPE && (
                                      <div
                                        className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg w-fit"
                                        role="tablist"
                                        aria-label="Working Days view"
                                      >
                                        {[['grid', 'Day grid'], ['gantt', 'Gantt']].map(([v, label]) => (
                                          <button
                                            key={v}
                                            type="button"
                                            data-testid={`single-member-gantt-toggle-${v}`}
                                            onClick={() => setSingleMemberGanttView(v)}
                                            className={`px-3 py-1.5 min-h-[36px] text-xs font-semibold rounded-md transition-colors ${
                                              singleMemberGanttView === v
                                                ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                            }`}
                                            role="tab"
                                            aria-selected={singleMemberGanttView === v}
                                            aria-pressed={singleMemberGanttView === v}
                                          >
                                            {label}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {SINGLE_MEMBER_GANTT_PROTOTYPE && singleMemberGanttView === 'gantt' ? (
                                    /* 🪤 TRAP 5 — `CrewGanttView` FILTERS OUT a
                                       member with no dates and reports it as
                                       "1 member(s) without dates are hidden",
                                       which over ONE member is an empty chart
                                       with a footnote about the person you are
                                       looking at. The PAGE answers instead, in
                                       the day grid's own words for the same
                                       state a few lines down. */
                                    !(detailsGanttMembers[0]?.start_date && detailsGanttMembers[0]?.end_date) ? (
                                      <p
                                        className="text-sm text-gray-500 dark:text-gray-400 italic"
                                        data-testid="single-member-gantt-no-dates"
                                      >
                                        Set start and end dates to see this member on the Gantt.
                                      </p>
                                    ) : (
                                      /* 🚨 TRAP 3 — `w-0 min-w-full` IS LOAD-BEARING AND
                                         IT IS NOT THE OBVIOUS SPELLING. The crew table is
                                         `table-fixed md:table-auto` inside an
                                         `overflow-x-auto` region, and under `table-auto` a
                                         cell sizes to its content's MAX-CONTENT width — so a
                                         Gantt whose timeline is ~29,000px drives the whole
                                         crew table to ~29,000px, the outer region scrolls
                                         instead of the chart, and the label column scrolls
                                         off with it.
                                         📏 MEASURED in headless Chromium at 1280px, five
                                         variants over the real structure: `min-w-0
                                         max-w-full overflow-hidden` — the spelling that
                                         looks right and that this shipped with first — left
                                         the table at 29,438px with the timeline NOT
                                         scrolling at all. `w-0 min-w-full` holds the table
                                         at 998px, gives the timeline a 680px scrollport and
                                         `scrollLeft` takes. `contain:inline-size` measured
                                         identically; a measured pixel width also worked at
                                         desktop and BROKE at 390px, which is why the CSS
                                         answer beats `useMeasuredWidth` here.
                                         🪤 jsdom applies no stylesheet, so nothing in the
                                         suite can see any of this — a revert to the obvious
                                         classes stays entirely green. */
                                      <div
                                        data-testid="single-member-gantt"
                                        className="w-0 min-w-full overflow-hidden"
                                      >
                                        <CrewGanttView
                                          /* 🔑 THE WHOLE CHART, ONE MEMBER — legend, zoom,
                                             header, label column, sort control, all of it.
                                             Owner: "just copy the whole gantt chart but only
                                             show the one user." A cut-down embedded variant
                                             would be a SECOND bar renderer, which is the
                                             defect this feature spent the week collapsing. */
                                          members={detailsGanttMembers}
                                          grades={grades}
                                          canEdit={canEdit}
                                          onMemberUpdate={handleGanttUpdate}
                                          onMemberDragPreview={handleGanttDragPreview}
                                          /* 🚨 THE MEMBER'S OWN FIGURES, NOT THE CREW'S. The
                                             footer is labelled "Total — Labour / Allowances"
                                             (it said "/ Computer" until 2026-08-23);
                                             handing it `totalLabour` here would print the
                                             WHOLE crew's labour under one person's chart,
                                             which is a money claim and a false one. These are
                                             the server-published per-member columns read off
                                             the same row the chart draws — a selection, never
                                             a re-derivation. */
                                          /* 🔀 THROUGH `memberWithLivePreview` SINCE 2026-08-23.
                                             Still a SELECTION of server-published per-member
                                             columns, never a re-derivation — it only chooses the
                                             PREVIEWED row over the saved one while this member's
                                             own panel, just above this footer, is showing
                                             previewed figures. Two numbers for one person on one
                                             screen is the defect this feature exists to close. */
                                          totalLabour={parseFloat(memberWithLivePreview(detailsGanttMembers[0] || {}).budget_cost) || 0}
                                          /* 🔀 `computer_cost` → `allowance_total`, 2026-08-23, with the
                                             footer's label (see `CrewGanttView.js`). Still a SELECTION of a
                                             server-published per-member column, never a re-derivation:
                                             `allowance_total` is `round2(computer_cost + a1 + a2 + a3)` from
                                             `helpers/crewMemberCostFields.js`, and summing those four here
                                             would be one more copy of a cost formula on a surface that only
                                             REPORTS money. The preview objects carry it too, so a live edit
                                             or an open day-strip moves this figure exactly as it moves
                                             `budget_cost` beside it. */
                                          totalAllowances={parseFloat(memberWithLivePreview(detailsGanttMembers[0] || {}).allowance_total) || 0}
                                          /* 🔑 The same live-costing state the page-level
                                             chart gets — it is one strip and one member, so a
                                             second answer here would let the two charts
                                             disagree about whether the figure has settled. */
                                          dayEditPreviewState={ganttStripCostState}
                                          /* 🔑 TRAP 2 — THE DAY-EDIT STATE IS PAGE STATE AND
                                             IT IS SHARED ON PURPOSE. The same four values and
                                             the same four handlers the page-level chart gets,
                                             so there is exactly ONE open strip, ONE dirty
                                             flag, ONE undo stack and ONE save order
                                             (dates → `/weeks` → `/days`) across both mounts.
                                             A confusing half-state is unreachable because
                                             there is no second state to get out of step — and
                                             it is why this adds no save path. Deliberately
                                             NOT auto-opened: opening the strip is a page-state
                                             write, and firing it merely because somebody
                                             expanded a row would leave a strip open in the
                                             page-level chart that nobody opened. */
                                          dayEditMemberId={ganttDayMemberId}
                                          dayEditWorkedDates={ganttDayWorkedDates}
                                          dayEditDirty={ganttDayDirty}
                                          dayEditSaving={ganttDaySaving}
                                          onDayEditToggle={handleGanttDayEditToggle}
                                          onDayToggle={handleGanttDayToggle}
                                          onDayEditSave={handleGanttDaySave}
                                          onDayEditCancel={handleGanttDayCancel}
                                          /* 🚨 TRAP 1 — THE ONLY DEVIATION FROM "COPY IT
                                             WHOLE", AND IT IS A BUG FIX RATHER THAN A CHROME
                                             DECISION. Two mounted charts would read and write
                                             the same `sfx_crew_gantt_sort_*` keys, so sorting
                                             in here would silently re-order the page-level
                                             chart behind it. The CONTROL stays exactly as it
                                             is; only the persistence goes. */
                                          persistSort={false}
                                        />
                                      </div>
                                    )
                                  ) : weekLoading ? (
                                    <LoadingSpinner size="sm" message="Loading weeks..." />
                                  ) : (
                                    (() => {
                                      const startSrc = canEdit ? editMemberForm.start_date : (mEff.start_date ? mEff.start_date.slice(0, 10) : null);
                                      const endSrc = canEdit ? editMemberForm.end_date : (mEff.end_date ? mEff.end_date.slice(0, 10) : null);
                                      const weeks = generateWeeks(startSrc, endSrc);
                                      if (weeks.length === 0) {
                                        return (
                                          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                            Set start and end dates to see the weekly timeline.
                                          </p>
                                        );
                                      }

                                      // Build lookup of server-side overrides
                                      const serverOverrides = {};
                                      for (const w of weekData) {
                                        serverOverrides[w.week_start] = w.allocation;
                                      }

                                      /* 🪤 THE FIFTH INSTANCE OF `parseFloat(default_allocation)
                                         || 1.0`, FOUND 2026-08-27 — FIXED HERE, NOT LEFT AND
                                         DECLARED, because this is a DIFFERENT question from the
                                         four already closed (see `crewDayResolution.js`'s header
                                         and `DEFAULT_WORKED_DAY_INDICES`, which correctly do NOT
                                         consult the allocation at all — that fix answers "which
                                         DAYS default", and reading a real 0 there would have
                                         turned an Off member's newly-in-range days OFF, the
                                         reported defect made worse).

                                         `defaultAlloc` here answers a narrower, still-live
                                         question: "what ALLOCATION would this WEEK carry with no
                                         stored override" — used only to decide whether a stored
                                         `crew_week_overrides` row (`serverOverrides[ws]`) or a
                                         locally-drafted one (`weekOverrides[ws]`) counts as
                                         CHANGED from the member's own baseline, for the
                                         amber/blue chip highlight below. `buildWeekDayBaseline`
                                         (`utils/crewDayResolution.js:165-167`) answers the exact
                                         same question, for the exact same no-override-week case,
                                         and was ALREADY fixed there with `Number.isFinite` rather
                                         than deleted — because for a member on the fraction path
                                         (no day rows at all), the allocation genuinely IS what
                                         they are charged, so reading a real `0` as `0` is
                                         CORRECT here, not the trap. Mirrored verbatim rather than
                                         re-derived, so the two cannot silently disagree about the
                                         one case where the allocation IS still authoritative. An
                                         Off member (`default_allocation === 0`) whose week has no
                                         stored override now correctly reads as "not overridden"
                                         (blue) rather than falsely amber/blue against a phantom
                                         1.0 baseline nobody set. */
                                      const defaultAlloc = Number.isFinite(Number(m.default_allocation))
                                        ? Number(m.default_allocation)
                                        : 1.0;

                                      // Compute drag-implied allocations for edge weeks
                                      // 🔀 2026-10-06: the SHARED rule, not a copy — the preview must show the
                                      // allocations the save will write (`dateChangeWeekAllocations`).
                                      const dragWeekAllocs = dp && mEff.start_date && mEff.end_date
                                        ? dateChangeWeekAllocations(m, mEff.start_date.slice(0, 10), mEff.end_date.slice(0, 10), tableDraftDefaultAllocation)
                                        : {};

                                      // A Gantt drag previews an implied ALLOCATION for the
                                      // edge weeks; the grid speaks in days, so convert with
                                      // the one shared rule rather than a second one here.
                                      const dragWeekDays = {};
                                      for (const [ws, alloc] of Object.entries(dragWeekAllocs)) {
                                        dragWeekDays[ws] = dayCountForAllocation(alloc);
                                      }

                                      // What each visible week shows RIGHT NOW. A DAY the
                                      // date fields have only just brought into range is in
                                      // neither the overlay nor the baseline's range, so it
                                      // takes the owner's ruled default: Mon–Fri worked,
                                      // Sat/Sun off (`DEFAULT_WORKED_DAY_INDICES`).
                                      //
                                      // 🔴 THIS USED TO BE AN INLINE COPY OF THE RULE and the
                                      // copy was the money defect: it fell back where
                                      // `resolveWeekDays` returned `[]`, so the grid drew days
                                      // the save never posted. There is now exactly ONE
                                      // resolver and the render is one of its callers.
                                      // DO NOT re-inline it.
                                      const gridWeekDays = {};
                                      for (const ws of weeks) {
                                        gridWeekDays[ws] = resolveWeekDays(
                                          weekDayOverlay, weekDayBaseline, ws, weekDayCurrentRange
                                        );
                                      }

                                      // ─── Run positions for the premium warning ─────────
                                      // 🔑 Over the member's WHOLE worked-day set, not just
                                      // the visible weeks. A run does not restart because a
                                      // chip wrapped onto the next row. Since 2026-08-24
                                      // `generateWeeks` matches the server's end-inclusive
                                      // enumerator, so every COSTED week is drawn — but
                                      // stored day rows can still sit outside the drawn
                                      // weeks (a shrunk engagement, the deliberately
                                      // unclamped backfill), and they are folded in, or the
                                      // run position of the days that ARE drawn would be
                                      // wrong.
                                      const drawnDates = new Set();
                                      const workedDates = [];
                                      for (const ws of weeks) {
                                        for (const idx of gridWeekDays[ws]) {
                                          const d = dateForWeekDay(ws, idx);
                                          if (d) { workedDates.push(d); drawnDates.add(d); }
                                        }
                                        for (let i = 0; i < 7; i++) {
                                          const d = dateForWeekDay(ws, i);
                                          if (d) drawnDates.add(d);
                                        }
                                      }
                                      for (const row of (mEff.days || [])) {
                                        if (!drawnDates.has(row.work_date)) workedDates.push(row.work_date);
                                      }
                                      const positions = runPositions(workedDates);

                                      // Which premium positions have NO RATE AT ALL.
                                      //
                                      // 🚨 `== null`, NEVER `!(rate > 0)` — corrected
                                      // 2026-08-23 with Stage 3 Task C. The old form read a
                                      // deliberate £0 as "nobody priced this", so a grade
                                      // priced at zero warned FOR EVER: the amber triangle
                                      // would have claimed a rate was missing on exactly the
                                      // days a buyer had decided were unpaid. That is the
                                      // same null-vs-zero collapse the schema is
                                      // nullable-with-no-DEFAULT to avoid, appearing in the
                                      // warning whose whole job is to report it.
                                      //
                                      // 🪤 The comment here used to say the rate columns
                                      // "do not exist yet". They landed with Task A and the
                                      // WRITE ROUTES landed with Task C, so a rate is now
                                      // settable and this warning genuinely clears.
                                      //
                                      // 🔑 `sixth_day_rate` is the RESOLVED rate the server
                                      // publishes (member override winning over the grade),
                                      // so this must not re-resolve the override itself — a
                                      // second opinion about which premium is unpriced is a
                                      // money claim. `?? grade_*` is the fallback for a
                                      // payload predating that field, not a second resolver.
                                      const sixthRate = mEff.sixth_day_rate ?? mEff.grade_sixth_day_rate ?? null;
                                      const seventhRate = mEff.seventh_day_rate ?? mEff.grade_seventh_day_rate ?? null;
                                      const unpricedPositions = new Set();
                                      if (sixthRate == null) unpricedPositions.add(6);
                                      if (seventhRate == null) unpricedPositions.add(7);
                                      // 🔻 THE SECOND WARNING (owner, 2026-08-23): a rate
                                      // somebody HAS set that is below the usual uplift.
                                      // 🔑 THE SERVER DECIDED IT. `*_below_default` is
                                      // computed in `helpers/crewCost.js` from the STATED
                                      // rate, never the resolved one, so a DEFAULTED member
                                      // cannot land here. Comparing a rate against 1.5×
                                      // daily on this side would be a second opinion about
                                      // money and would also put every defaulted member one
                                      // float away from warning at once.
                                      const belowDefaultPositions = new Set();
                                      if (mEff.sixth_day_rate_below_default) belowDefaultPositions.add(6);
                                      if (mEff.seventh_day_rate_below_default) belowDefaultPositions.add(7);
                                      // WHICH RUNG set a below-default rate —
                                      // read from the server's own answer via
                                      // the shared helper (one definition for
                                      // this site and the cost summary's).
                                      const overridePositions = premiumOverridePositionsFor(mEff);

                                      return (
                                        <CrewDayGrid
                                          weeks={weeks}
                                          weekDays={gridWeekDays}
                                          serverWeekDays={weekDayBaseline.base}
                                          presumedWeeks={weekDayBaseline.presumedWeeks}
                                          weekOverrides={weekOverrides}
                                          serverOverrides={serverOverrides}
                                          defaultAlloc={defaultAlloc}
                                          dragWeekDays={dragWeekDays}
                                          runPositions={positions}
                                          unpricedPositions={unpricedPositions}
                                          belowDefaultPositions={belowDefaultPositions}
                                          agreementRates={mEff.agreement_rates}
                                          overridePositions={overridePositions}
                                          canEdit={canEdit}
                                          expandedWeek={expandedWeek}
                                          onToggleWeek={handleToggleWeekExpand}
                                          onPresetChange={handleWeekPreset}
                                          onDayToggle={handleDayToggle}
                                          formatWeekLabel={formatWeekLabel}
                                        />
                                      );
                                    })()
                                  )}
                                </div>
                                )}

                                {/* Mobile-only Save / Cancel / Delete row. On desktop
                                    these buttons live in the top toolbar next to
                                    "+ Add Member" so they stay above the fold.
                                    🔻 SUPPRESSED WHEN `embedded`: level 2 has exactly ONE
                                    Save and it is the day strip's — see the ruling in
                                    `handleGanttDaySave`. Two commit buttons over one edit
                                    is the ambiguity this prototype has to avoid. */}
                                {canEdit && !embedded && (
                                  <div className="md:hidden grid grid-cols-2 gap-2 pb-3 mb-1 border-b border-gray-200 dark:border-gray-600">
                                    <button
                                      onClick={() => handleSaveEditMember(m.id, true)}
                                      disabled={savingMember}
                                      className={`${BTN.confirm} min-h-[44px] inline-flex items-center justify-center col-span-2`}
                                    >
                                      {savingMember ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                      onClick={() => collapseMember()}
                                      className={`${BTN.secondary} min-h-[44px] inline-flex items-center justify-center`}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleDeleteMember(m)}
                                      className={`${BTN['danger-soft']} min-h-[44px] inline-flex items-center justify-center`}
                                    >
                                      Delete
                                    </button>
                                    {isMemberDirty && (
                                      <span className="text-amber-600 dark:text-amber-400 text-xs font-medium ml-2 col-span-2 text-center">
                                        Unsaved changes
                                      </span>
                                    )}
                                  </div>
                                )}
                                {/* Send Invite — visible on both breakpoints when applicable. */}
                                {canEdit && canInvite && m.user_id && m.user_status === 'placeholder' && (
                                  <div className="pb-3 mb-1 border-b border-gray-200 dark:border-gray-600">
                                    <span className="relative group inline-block w-full md:w-auto">
                                        <button
                                          type="button"
                                          data-testid={`crew-send-invite-${m.id}`}
                                          disabled={!editMemberForm.user_email?.trim() || sendingInvite || savingMember}
                                          onClick={async () => {
                                            // A failed save must never invite (fix round 1).
                                            if (isMemberDirty) {
                                              const saved = await handleSaveEditMember(m.id, false);
                                              if (!saved) return;
                                            }
                                            // `m.id` — the crew row this invite is for (R23.8).
                                            await handleSendInvite(editMemberForm.user_email, editMemberForm.name, m.user_role_name || 'user', m.id);
                                          }}
                                          className={`w-full md:w-auto min-h-[44px] md:min-h-0 inline-flex items-center justify-center text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${editMemberForm.user_email?.trim() ? 'bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600' : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'}`}
                                        >
                                          {sendingInvite ? 'Sending...' : 'Send Invite'}
                                        </button>
                                        {!editMemberForm.user_email?.trim() && (
                                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 dark:bg-gray-600 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            Enter an email address to send an invite
                                          </span>
                                        )}
                                      </span>
                                  </div>
                                )}
                              </div>
    );
  };

  // 🔑 LABOUR FLOW HEADER TOOLBAR (added 2026-08-23, completing the
  // 2026-08-22 header-toolbar work — the other four sections
  // (`budget-cost-codes`, `budget-payment-types`, `budget-crew-grades`,
  // `budget-crew-adhoc`) got their `headerActions` that day; this section
  // was missed and the owner noticed. See
  // `.superpowers/briefs/labour-flow-header-toolbar.md`.
  //
  // This section's header slot is ALSO occupied by the "version is locked"
  // badge (`headerActions={isVersionLocked ? <span>…</span> : …}` below),
  // and — unlike the other four sections' single Add button — the pieces
  // moving here do NOT all relate to `isVersionLocked` the same way:
  //   - `+ Add Member` genuinely cannot co-occur with the badge: `canEdit`
  //     is `false` whenever `isVersionLocked` is `true`
  //     (`canEdit = canManageVersions && !isVersionLocked`, verified at its
  //     definition rather than assumed), so gating the button on `canEdit`
  //     makes the two states mutually exclusive by construction — exactly
  //     as the brief predicted.
  //   - The Table/Gantt view switcher is NOT canEdit-gated (a locked,
  //     read-only version still benefits from switching views), so it CAN
  //     genuinely co-occur with the badge's full-sentence text. Rather than
  //     let a long sentence and a two-button toggle fight for one header
  //     row, THE BADGE WINS: while locked, the header shows the badge
  //     alone (byte-identical to before this change) and the view switcher
  //     stays in the section body, where a read-only viewer can still
  //     reach it. Search stays in the body unconditionally — seeing it
  //     compete with the badge for a header row was the reason not to move
  //     it (measured in the browser: see the report).
  // `crewViewToggleNode` is therefore shared between the header (rendered
  // there only while unlocked) and the body (rendered there only while
  // locked) rather than duplicated.
  // 🔀 2026-10-05: all of the above is now the PHONE layout only. On desktop
  // the header row is hidden and `labourFlowToolbar` (below) carries the
  // switch as `ui/Tabs`, the search as `ui/SearchInput`, and the lock badge
  // beside them — see its docblock.
  const crewViewToggleNode = (
    <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg w-fit" role="tablist" aria-label="Crew Members view">
      <button
        type="button"
        // 🔑 THE SAME DESTINATION AS THE MEMBER-NAME BUTTON, WHICH HAS
        // BEEN GUARDED SINCE THE STRIP SHIPPED — leaving this tab open
        // meant the name asked and the tab beside it did not, for one
        // transition. Guarded here, not by reusing the row's old
        // navigation handler (`handleGanttMemberClick`, deleted
        // 2026-08-24 with the `Full Details` button it belonged to) —
        // that one also expanded the clicked member's row; this tab
        // switches the view and expands nobody.
        onClick={() => guardGanttDayEdit(
          'switch to the table view', () => setCrewViewMode('table')
        )}
        className={`px-3 py-1.5 min-h-[36px] text-xs font-semibold rounded-md transition-colors ${
          crewViewMode === 'table'
            ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
        }`}
        role="tab"
        aria-selected={crewViewMode === 'table'}
        aria-pressed={crewViewMode === 'table'}
      >
        Table view
      </button>
      <button
        type="button"
        onClick={() => setCrewViewMode('gantt')}
        className={`hidden md:inline-block px-3 py-1.5 min-h-[36px] text-xs font-semibold rounded-md transition-colors ${
          crewViewMode === 'gantt'
            ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
        }`}
        role="tab"
        aria-selected={crewViewMode === 'gantt'}
        aria-pressed={crewViewMode === 'gantt'}
      >
        Gantt view
      </button>
    </div>
  );

  // 🎨 Header sizing (36px, no forced full-width) matches the other four
  // sections' `+ Add X` header buttons exactly — see e.g. `budget-crew-grades`'s
  // `headerActions` for the identical class string. `canEdit` already implies
  // `!isVersionLocked`, so this is never rendered beside the lock badge.
  /* 🔄 SUPPRESSED WHILE A CREW MEMBER IS EXPANDED (2026-08-23), and this is
     the ONE capability-affecting half of moving Save/Cancel/Delete up here —
     flagged for the owner rather than slipped in.

     The Labour Flow header now carries: title · view toggle · `+ Add Member` ·
     search · and, while a member is expanded, an unsaved chip + three action
     buttons. That is more than one row's worth at the width the owner works at,
     and he has complained about a wrapping bar twice. **THE RULE: `+ Add Member`
     and the per-member actions are MUTUALLY EXCLUSIVE**, because they already
     are in substance — the add form and the expanded edit panel are two
     `data-crew-edit-form` surfaces, and this page's dirty machinery
     (`isMemberDirty` dimming the other rows, the layout-switch guard, the
     collapse prompt) is built around ONE edit at a time. Cancel or Save
     collapses the panel and the button is back in one click.

     ⚠️ WHAT I HAVE NOT DONE IS MEASURE IT. jsdom applies no stylesheet and
     lays nothing out, so "the header stays on one row" is a BROWSER claim and
     this file makes none. The reduction is ~130px; whether that is sufficient
     at 1280 with the sidebar open is unverified and is the first thing to check
     if he reports a wrap. Reverting the gate is one operand. */
  const addMemberHeaderButton = canEdit && !expandedMemberId ? (
    <button
      type="button"
      onClick={() => setShowAddMember(prev => !prev)}
      className={`${BTN_XS.primary} min-h-[36px] md:min-h-0 inline-flex items-center justify-center whitespace-nowrap`}
    >
      {showAddMember ? 'Cancel' : '+ Add Member'}
    </button>
  ) : null;

  /* ═══ SAVE / CANCEL / DELETE — IN THE LABOUR FLOW HEADER (2026-08-23) ═════
     Owner, correcting his own first instinct: *"wait that doesn't work for table
     view. instead put it in the Labour Flow header … then it works for both
     gantt and table."* He is right — the Gantt's own header does not exist in
     table view, so buttons placed there would vanish; the section header is
     present in both.

     🔑 THE NODE MOVED AND NOTHING ELSE DID. Same three handlers, the same
     `canEdit && expandedMemberId && !ganttFormMemberId` gate, the same
     `hidden md:flex`, the same `crew-member-inline-delete` testid. This is a
     placement change; it grants nobody anything and takes nothing away.

     🚨 THE GATE'S THIRD OPERAND IS LOAD-BEARING AND ITS SPELLING IS ALREADY
     RECORDED AS A TRAP — do not "simplify" it while moving it.
     `!ganttFormMemberId` hides this bar at the Gantt's LEVEL 2, where the
     strip's Save is the ONE Save (contract item 14). 🚫 It is NOT
     `crewViewMode`, which is the obvious and wrong spelling: this bar is
     deliberately reachable in GANTT view for a member expanded from the TABLE,
     and `Deactivate` there is exit E8 of the day-strip guard — a view-mode gate
     deletes that exit outright and reds `ganttDayExitGuard.test.js` §1.8 alone.

     🔻 DELETE KEEPS ITS EXISTING `window.confirm` AND IS NEITHER DROPPED NOR
     UPGRADED. Moving a destructive control next to a search box and a view
     toggle puts it where the cursor rests for harmless actions, so the
     confirmation matters MORE here than it did in the body — but silently
     promoting it to a `ConfirmDialog` would be an unrequested product change to
     a flow the day-strip exit guard also hooks (`handleDeleteMember` asks about
     an unsaved day edit FIRST, deliberately). It goes through the same
     `handleDeleteMember` it always did.

     🎨 AND IT IS VISUALLY SEPARATED FROM THE CONSTRUCTIVE CONTROLS, the way
     `Full Details` was in the day strip: a `border-l` + `pl-2` divider reading
     as "and, separately, this one", not a fourth peer in a button group. The
     divider is a NEUTRAL hairline, so it adds no colour channel — the button's
     own `danger-soft` treatment is still the only thing saying "destructive". */
  const crewMemberActionsNode = canEdit && expandedMemberId && !ganttFormMemberId ? (
    <div className="hidden md:flex items-center gap-2" data-testid="crew-member-header-actions">
      {isMemberDirty && (
        <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">Unsaved changes</span>
      )}
      <button
        type="button"
        onClick={() => handleSaveEditMember(expandedMemberId, true)}
        disabled={savingMember}
        className={BTN_XS.confirm}
      >
        {savingMember ? 'Saving...' : 'Save Changes'}
      </button>
      <button
        type="button"
        onClick={() => collapseMember()}
        className={BTN_XS.secondary}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => {
          const m = members.find(mm => mm.id === expandedMemberId);
          if (m) handleDeleteMember(m);
        }}
        className={`${BTN_XS['danger-soft']} ml-1 pl-2 border-l border-gray-300 dark:border-gray-600 rounded-l-none`}
        // The page renders several buttons reading "Delete" (cost codes,
        // grades, versions), so a by-text lookup in a test picks an arbitrary
        // one and certifies nothing.
        data-testid="crew-member-inline-delete"
      >
        Delete
      </button>
    </div>
  ) : null;

  // 🚨 THE HAZARD THIS SECTION HAS AND THE OTHER FOUR DID NOT: this is a TEXT
  // INPUT, not a button, and it is the reason `CollapsibleSection`'s
  // `e.target === e.currentTarget` keydown guard exists at all — WITHOUT it a
  // typed SPACE reaches the header's `role="button"` handler, collapses the
  // section, and the space never reaches the input either (see the guard's
  // own docblock). Verified rather than trusted: see
  // `BudgetCostCodes.labourFlowHeaderToolbar.test.js` §5, and the guard was
  // reverted and the mutation run by hand to see it go red (reported).
  // Shared between the header (rendered there only while unlocked) and the
  // body (rendered there only while locked) — never duplicated.
  const crewSearchNode = members.length > 0 && (
    <div className="relative w-full md:w-64">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      <input
        type="text"
        value={crewSearch}
        onChange={(e) => setCrewSearch(e.target.value)}
        placeholder="Search crew..."
        aria-label="Search crew members"
        className="w-full pl-8 pr-16 py-1.5 min-h-[36px] text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500"
      />
      {crewSearch && (
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {filteredMembers.length} of {members.length}
          </span>
          <button
            type="button"
            onClick={() => setCrewSearch('')}
            aria-label="Clear search"
            title="Clear search"
            className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );

  // The "version is locked" badge — one node, rendered by the phone section
  // header and by the desktop toolbar's right slot (never both: each is
  // mounted for one side of `isPhoneViewport`).
  const versionLockedBadge = isVersionLocked ? (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-xs font-medium"
      role="status"
      title="This version is locked. Unlock from the version selector to edit, or clone it to make changes."
    >
      <LockIcon locked className="h-3.5 w-3.5 flex-shrink-0" />
      <span>This version is locked. Unlock from the version selector to edit, or clone it to make changes.</span>
    </span>
  ) : null;

  /* ═══ THE LABOUR FLOW TOOLBAR — DESKTOP ONLY (owner 2026-10-05: "yes build
     it into the preview for desktop please") ═════════════════════════════════
     The section's "Labour Flow" title row is hidden at `md`+ (the tab above
     already names it — `hideHeaderAtMd`), and everything that row carried
     moves HERE, onto ONE sticky line modelled on the Breakdown's: search ·
     Table|Gantt on the left; `+ Add Member` — or, while a member is expanded,
     its unsaved chip + Save/Cancel/Delete, or the lock badge — on the right,
     under the page's `Actions ▾`.
     📏 NOT ON THE TABS ROW: measured on the demo department, the free space
     between the tablist and `Actions ▾` is 206px at 1280 (366 at 1440) with
     the sidebar open, against ~570px for these controls.
     🔑 MOVED, NOT RE-DERIVED: the same state (`crewSearch`, `crewViewMode`),
     the same guarded Table switch, the same `addMemberHeaderButton` /
     `crewMemberActionsNode` / lock badge nodes with their gates untouched. Only
     the search box and the view switch became the shared `ui/SearchInput` /
     `ui/Tabs` (bare) — `debounceMs={0}` because the filter is in memory.
     📱 Mounted only when NOT a phone, so below `md` the DOM carries no second
     search box or switch and the phone keeps its section header unchanged.
     🎨 `!bg-white`: `TOOLBAR_SHELL` is the page-ground gray, and this bar sits
     INSIDE the white card directly under the active tab, which must still read
     as attached to its panel. In dark, `index.css`'s
     `html.dark .bg-gray-100 { #1e293b !important }` already equals the card.
     📌 `labourToolbarRef` → `useStickyToolbar`, so the crew table's pinned
     header (`crewTableStickyTop`) sits flush beneath this bar. The Gantt's
     date band pins inside the chart's own scroller and is unaffected. */
  const labourFlowToolbar = !isPhoneViewport ? (
    <Toolbar
      ref={labourToolbarRef}
      marginClassName="mb-0"
      className="px-3 rounded-t-lg !bg-white dark:!bg-slate-800"
      data-testid="labour-flow-toolbar"
      rightSlot={versionLockedBadge || crewMemberActionsNode || addMemberHeaderButton || undefined}
    >
      {members.length > 0 && (
        <div className={TOOLBAR_GROUP}>
          <SearchInput
            value={crewSearch}
            onChange={setCrewSearch}
            debounceMs={0}
            placeholder="Search crew..."
            ariaLabel="Search crew members"
            testId="crew-search-input"
          />
          {crewSearch && (
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-testid="crew-search-count">
              {filteredMembers.length} of {members.length}
            </span>
          )}
        </div>
      )}
      <Tabs
        bare
        tabs={[
          { id: 'table', label: 'Table view' },
          { id: 'gantt', label: 'Gantt view' },
        ]}
        active={crewViewMode}
        onChange={(id) => (id === 'table'
          // The Table switch keeps its day-strip guard (see `crewViewToggleNode`).
          ? guardGanttDayEdit('switch to the table view', () => setCrewViewMode('table'))
          : setCrewViewMode(id))}
        idPrefix="crew-view"
        ariaLabel="Crew Members view"
      />
      {crewViewMode === 'gantt' && (
        // `contents`: the portalled TOOLBAR_GROUP is laid out as the bar's own child.
        <div ref={setGanttControlsSlot} className="contents" data-testid="labour-flow-gantt-controls-slot" />
      )}
    </Toolbar>
  ) : null;

  /* ═══ THE PAGE'S ONE `Actions ▾` (owner 2026-10-05, "fab lets build the
     preview") — right end of the tabs row; phone = the print group only.
     The data handed to the PDF is what the retired band `Download / Print`
     button handed it, moved verbatim; the per-tab exports read the same
     object, so a tab's printed and exported rows cannot disagree. */
  const manageSectionData = {
    // Cost-code budget + manual spend adjustment live in the
    // version-scoped maps, not on the row — merge both in so
    // the PDF shows the SAME figures as the table (the ruled
    // "every budget-surface spent figure includes the
    // adjustment"; no marker glyph in the PDF).
    costCodes: costCodes.map(cc => ({
      ...cc,
      budget_amount: parseFloat(budgetAmounts[cc.id] || 0) || 0,
      expenditure_to_date: spentWithAdjustment(cc),
    })),
    paymentTypes,
    // Resolve the grade's default access-rights role name for display.
    grades: grades.map(g => ({
      ...g,
      default_role_name: g.default_role_id
        ? (roleDefinitions.find(r => r.id === g.default_role_id)?.display_name || 'None')
        : 'None',
    })),
    members,
    totals: { totalLabour, totalComputer, crewGrandTotal, totalBudget },
  };
  const buildManagePdf = (activeKeys) => (activeKeys.length === 0 ? null : buildBudgetManagePdf({
    activeKeys,
    data: manageSectionData,
    department: activeDepartment?.department_name,
    production: activeDepartment?.production_name,
    company: activeDepartment?.company_name,
    versionLabel: selectedVersion?.version_name,
    formatMoney,
  }));
  const downloadCurrentTabExport = () => {
    if (activeTab === 'budget-cost-codes') { downloadCostCodesExport(); return; }
    const table = buildManageSectionCsv(activeTab, manageSectionData, { formatMoney });
    if (table) downloadManageExport(table.headers, table.rows, TAB_FILENAME_SUFFIX[activeTab] || '');
  };
  const activeTabLabel = (SECTION_TABS.find(t => t.id === activeTab) || {}).label || '';
  /* THIS TAB's items. `Fit columns to screen` only where the table HAS fit
     support — Cost Codes today; Labour Flow's crew table has none, and none
     was built (brief, 2026-10-05). Never permission-gated: export/print/fit
     need no write authority. */
  /* `Fit columns to screen` is drawn LAST in the menu, after the print group
     (`trailingActions`, 2026-10-05: the hires menu's order). */
  const renderCurrentTabTrailingActions = activeTab === 'budget-cost-codes'
    ? ({ close, itemClassName }) => (
        <button
          type="button"
          role="menuitem"
          onClick={() => { close(); fitCostCodesColumnsToScreen(); }}
          disabled={!canFitCostCodesColumns || costCodesColumnsAlreadyFit}
          title={!canFitCostCodesColumns
            ? 'Nothing measured to fit to yet'
            : costCodesColumnsAlreadyFit
              ? 'The columns already fit the screen'
              : 'Fit every column to the available screen width'}
          className={itemClassName}
          data-testid="cost-codes-actions-fit-columns"
        >
          Fit columns to screen
        </button>
      )
    : null;
  const renderCurrentTabActions = ({ close, itemClassName }) => (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={() => { close(); downloadCurrentTabExport(); }}
        title={`Download the ${activeTabLabel} table as ${CSV_LABEL}`}
        className={itemClassName}
        data-testid="budget-actions-export-csv"
      >
        {CSV_MENU_LABEL}
      </button>
    </>
  );

  return (
    <div className={`p-2 md:p-4 lg:p-6 pt-0 md:pt-0 lg:pt-0 space-y-6 ${PAGE_SIDE_GUTTER}`}>
      {/* `pt-0` at every width (owner, 2026-10-05): the band starts under the app
          bar, as on Cost Reports and the Breakdown; sides and bottom unchanged. */}
      {leaveGuard.dialog}
      {/* ═══════════════════════════════════════════════════════════════════
          ONE-BAND PAGE HEADER — tabs, context and the version controls on a
          single row (owner, 2026-08-23: *"to save vertical space we could put
          [the header block] and buttons in the same row as the two tabs 'Cost
          Reports / Edit Budgets'"*).

          This was TWO stacked bands: a tabs row, then a white card carrying an
          `<h2>` version name + department + production on the left and the
          version controls on the right. Measured in Chromium at 1440x900:
          42px tabs + 24px `space-y-6` gap + 58px card = 124px, reduced to 44px.

          🔑 WHAT MADE IT FIT WAS DELETING A DUPLICATE, NOT SHRINKING ANYTHING.
          The `<h2>` printed `selectedVersionForTitle.version_name` — the name
          of the version whose id is `selectedVersionId`, which is exactly what
          the `<select>` 200px to its right already displays as its own value.
          Two controls-worth of width for one fact. The department and
          production stay, because nothing else on the page names them.
          `selectedVersionForTitle` itself is NOT dead — `usePageTitle` still
          reads it for the browser tab.

          📕 Follows the two-band chrome in `components/ui/CLAUDE.md`: the
          context is MUTED TEXT YOU READ, the controls are the things you
          CLICK, both bands right-align their actions, and there is ONE rule
          under the whole thing rather than one between the bands. It is not
          literally `ui/PageHeaderBand` + `ui/Toolbar` — this page has never
          used those, the brief's "this is that chrome" over-states it, and
          porting it wholesale is a bigger change than the owner asked for.

          🔻 BELOW `md` IT IS A COLUMN, NOT A SCROLLING ROW: tabs, then
          context, then the controls, which is what the two stacked bands
          already did on a phone. `flex-wrap` on the control cluster means that
          even between `md` and the width where everything fits, the controls
          wrap onto a second line inside the band — the row never overflows
          horizontally and nothing is ever reachable only by sideways scroll.

          🪤 `md:items-end` IS LOAD-BEARING. The active tab's underline is a
          `border-b-2` pulled onto the band's own rule with `-mb-px`; under the
          default `items-stretch`/`items-center` the taller control cluster
          makes the row taller than the nav and the underline floats in mid-air
          detached from the rule it is supposed to sit on.

          `data-version-selector` MOVED HERE FROM THE DELETED CARD and must
          stay: `e2e/online-budget-crew-ui.spec.js` uses it as the stable
          anchor proving the editable surface rendered, and
          `e2e/online-route-guards-ui.spec.js` asserts its ABSENCE for a user
          who should have been redirected. Its `html.dark` fill rule in
          `index.css` went with the card — see the note there.
          ═══════════════════════════════════════════════════════════════════ */}
      <div className={BUDGET_TABS_BAND} data-version-selector>
        <div className={BUDGET_TABS_ROW}>
        {/* The tabs (and the `items-end` rules) are shared with Cost Reports:
            `components/budget/BudgetTabsNav.js`. */}
        <BudgetTabsNav />

      {/* The hidden BudgetPrintLayout for window.print() was removed in the
          pdfmake migration. Ctrl+P now prints the on-screen UI as the browser
          would on any normal page; the formatted budget PDF is the tabs row's
          `Actions ▾` → Download PDF / Print all (`BudgetManageActionsMenu`),
          selectable vector text via pdfmake. */}

      {/* Pre-submit validation guard (money/version-selection). Async save and
          load outcomes are announced via the shared toast; this banner now
          carries only a refusal the user must fix before resubmitting. */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-bold"
          >
            &times;
          </button>
        </div>
      )}

          {/* Controls — version selector + padlock stay visible, secondary
              version-admin actions collapse into the "Manage version"
              Dropdown. (The band's `Download / Print` button is GONE,
              2026-10-05 — print lives in the tabs row's `Actions ▾`.)
              `md:ml-auto` pushes the whole cluster to the band's right edge so
              there is a vertical edge down the right, as on the other
              two-band pages; `flex-wrap` is what keeps it from ever becoming a
              horizontal scroll. */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pb-2 md:pb-1.5 md:ml-auto shrink-0">
            {/* Version selector — PRIMARY, always visible */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {/* 🔑 THE LABEL GOES `sr-only` BELOW `xl`, IT IS NOT DELETED.
                  Its ~113px is the difference between the controls fitting on
                  the tabs row and wrapping to a second line at 1280 — the most
                  common laptop width — and the `<select>` immediately to its
                  right already reads `v5 — Current`, which says "version"
                  without help. A screen reader still gets the full name
                  through `htmlFor`/`id`, which is a real improvement: this
                  `<label>` was never associated with the control at all before
                  today, so it named nothing. */}
              <label htmlFor="budget-version-select" className="sr-only xl:not-sr-only text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Budget Version:
              </label>
          {versionsLoading ? (
            <LoadingSpinner size="sm" inline />
          ) : versions.length === 0 ? (
            <span className="text-sm text-gray-500 dark:text-gray-400 italic">No versions yet</span>
          ) : (
            <select
              value={selectedVersionId || ''}
              onChange={(e) => {
                const newId = parseInt(e.target.value, 10);
                // 🚨 THE ONLY EXIT ON THIS PAGE THAT REALLY DESTROYS THE EDIT
                // RATHER THAN HIDING IT. A version change refetches `members`,
                // so `ganttDayMember` resolves to a member of a DIFFERENT
                // version or to nothing — at which point `ganttDayDirty` reads
                // FALSE and the overlay is orphaned: unreachable, unsaveable,
                // and no longer protected by the very flag that would have
                // warned about it. Every other exit leaves the state intact
                // behind a control that can bring it back.
                guardGanttDayEdit(
                  'switch budget version', () => setSelectedVersionId(newId)
                );
              }}
              id="budget-version-select"
              /* 🪤 THE VERSION NAME IS THE ONE THING ON THIS BAND THAT HAS NO
                 LENGTH LIMIT — staging carries `E2E-SpendTrack-1779991276085
                 (Copy)`, 36 characters. On an auto-width `<select>` that name
                 sets the control's width, so one clone could push `Manage
                 version` and `Download / Print` onto a second line and undo
                 the whole reclaim.
                 `sm:max-w-[18rem]` caps it and `truncate` ellipsises the
                 overflow; the full name stays reachable from the native
                 `title` below, from the open dropdown itself, and from the
                 browser tab (`usePageTitle`). 🔑 A NAME MAY TRUNCATE — it is
                 recoverable from three places. NOTHING NUMERIC IS ON THIS
                 BAND: every money figure lives in the sticky totals bar or the
                 section tables, so the "never truncate a number" rule has no
                 subject here. The digits inside a cloned version's name are
                 part of an identifier, not a quantity anyone reads off. */
              title={selectedVersion?.version_name || undefined}
              className="h-11 md:h-8 border border-gray-300 rounded-lg px-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto sm:min-w-[200px] sm:max-w-[18rem] truncate"
            >
              {versions.map(v => (
                <option key={v.id} value={v.id}>
                  {v.version_name}{v.is_current ? ' — Current' : ''}{v.is_baseline ? ' — Baseline' : ''}
                </option>
              ))}
            </select>
          )}

          {/* Padlock — doubles as the Lock/Unlock toggle for users with
              manage permission, and as a read-only state indicator for
              everyone else. Replaces the previous separate Lock/Unlock
              text buttons. Confirm modals stay in place — clicking opens
              the same dialog the old text button did. */}
          {selectedVersionId && (
            canManageVersions ? (
              <button
                type="button"
                onClick={() => isVersionLocked ? setShowUnlockConfirm(true) : setShowLockConfirm(true)}
                disabled={lockingVersion || unlockingVersion}
                className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
                  isVersionLocked
                    ? 'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                    : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                } disabled:opacity-50 disabled:cursor-wait`}
                title={isVersionLocked
                  ? 'Locked — click to unlock. Tip: cloning is the safer alternative; unlocking a historical version risks overwriting it.'
                  : 'Unlocked — click to lock. Locking prevents any further edits to this version’s crew, cost codes, and lines.'}
                aria-label={isVersionLocked ? 'Unlock this version' : 'Lock this version'}
              >
                {(lockingVersion || unlockingVersion)
                  ? <span className="text-xs">…</span>
                  : <LockIcon locked={isVersionLocked} className="h-4 w-4" />}
              </button>
            ) : (
              <span
                className={`inline-flex items-center justify-center w-8 h-8 ${isVersionLocked ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}
                title={isVersionLocked ? 'This version is locked' : 'This version is unlocked'}
                aria-label={isVersionLocked ? 'Version locked' : 'Version unlocked'}
              >
                <LockIcon locked={isVersionLocked} className="h-4 w-4" />
              </span>
            )
          )}
          {/* Manage version — SECONDARY version-admin actions consolidated
              behind a single disclosure (reuses the shared Dropdown that the
              Gag Breakdown toolbar uses). Rename + New reveal their inline input
              inside the panel; Clone/Baseline/Set Current/Delete are menu rows.
              No functionality dropped — every action is still reachable here. */}
          {canManageVersions && (
            <Dropdown
              label="Manage version"
              triggerClassName={`${BTN.secondary} inline-flex items-center gap-1.5 min-h-[44px] md:min-h-0`}
              align="right"
              panelClassName="w-60 p-2"
              title="Version management: rename, clone, make baseline, set current, new, delete"
              ariaLabel="Version management actions"
            >
              {({ close }) => (
                renamingVersion ? (
                  <div className="flex flex-col gap-2">
                    <div className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Rename version
                    </div>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="h-9 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameVersion(); if (e.key === 'Escape') setRenamingVersion(false); }}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleRenameVersion}
                        disabled={savingRename || !renameValue.trim()}
                        className={`${BTN.confirm} flex-1 inline-flex items-center justify-center`}
                      >
                        {savingRename ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setRenamingVersion(false)}
                        className={`${BTN.secondary} flex-1 inline-flex items-center justify-center`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : showNewVersionInput ? (
                  <div className="flex flex-col gap-2">
                    <div className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      New version
                    </div>
                    <input
                      type="text"
                      value={newVersionName}
                      onChange={(e) => setNewVersionName(e.target.value)}
                      placeholder="Version name..."
                      className="h-9 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateVersion(); if (e.key === 'Escape') { setShowNewVersionInput(false); setNewVersionName(''); } }}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateVersion}
                        disabled={creatingVersion || !newVersionName.trim()}
                        title="Save the new empty budget version with the name above."
                        className={`${BTN.confirm} flex-1 inline-flex items-center justify-center`}
                      >
                        {creatingVersion ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        onClick={() => { setShowNewVersionInput(false); setNewVersionName(''); }}
                        title="Discard the new-version draft and return to the version selector."
                        className={`${BTN.secondary} flex-1 inline-flex items-center justify-center`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {/* Rename — reveals the inline input in this panel (no data change) */}
                    {selectedVersionId && (
                      <button
                        type="button"
                        onClick={() => { setRenamingVersion(true); setRenameValue(selectedVersion?.version_name || ''); }}
                        title="Rename this version. Doesn’t affect any cost code, crew, or budget data — just the label shown in the version selector."
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Rename
                      </button>
                    )}
                    {selectedVersionId && (
                      <button
                        type="button"
                        onClick={() => { close(); handleCloneVersion(); }}
                        disabled={cloningVersion}
                        title="Clone this version into a new editable draft. All cost codes (job codes included), crew and week overrides are physically duplicated — edits to the clone don’t affect this version."
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-wait"
                      >
                        {cloningVersion ? 'Cloning...' : 'Clone'}
                      </button>
                    )}
                    {/* Make Baseline — flips the variance anchor; disabled when
                        this version is already the baseline (doubles as indicator). */}
                    {selectedVersionId && (
                      <button
                        type="button"
                        onClick={() => { close(); handleSetBaseline(); }}
                        disabled={settingBaseline || isSelectedBaseline}
                        title={isSelectedBaseline
                          ? 'This version is the variance baseline. The Cost Report’s Variance Analysis is anchored here — every other version is compared against it.'
                          : 'Anchor Variance Analysis on this version. The Cost Report will start comparing Current against this version. Doesn’t modify any budget data.'}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        {settingBaseline ? 'Setting...' : isSelectedBaseline ? '✓ Baseline' : 'Make Baseline'}
                      </button>
                    )}
                    {/* Set Current — only for a non-current version */}
                    {selectedVersionId && !isSelectedCurrent && (
                      <button
                        type="button"
                        onClick={() => { close(); handleSetCurrent(); }}
                        disabled={settingCurrent}
                        title="Promote this version to Current — the budget the Cost Report and reports use. The outgoing current version is auto-locked so historical data is preserved."
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-wait"
                      >
                        {settingCurrent ? 'Setting...' : 'Set Current'}
                      </button>
                    )}
                    {/* New version — reveals the inline name input in this panel */}
                    <button
                      type="button"
                      onClick={() => setShowNewVersionInput(true)}
                      title="Create a brand-new empty budget version. Starts with no cost codes, crew, or lines — you build it up from scratch. To carry data over from the current budget, use Clone instead."
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      New version
                    </button>
                    {/* Delete — destructive, separated; never for the current version */}
                    {selectedVersionId && !isSelectedCurrent && (
                      <>
                        <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                        <button
                          type="button"
                          onClick={() => { close(); handleDeleteVersion(); }}
                          disabled={deletingVersion}
                          title="Delete this version permanently along with its crew, week overrides and cost-code budgets. This cannot be undone. The current version cannot be deleted."
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded-lg transition-colors text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-wait"
                        >
                          {deletingVersion ? 'Deleting...' : 'Delete'}
                        </button>
                      </>
                    )}
                  </div>
                )
              )}
            </Dropdown>
          )}
          </div>
          </div>
        </div>
      </div>

      {/* All budget sections — hidden until at least one version exists */}
      {versions.length > 0 && (<>

      {/* Section tabs — desktop only; mobile keeps the stacked accordion layout.
          Visually attached to the active section panel below: active tab gets
          the same white/gray-800 bg as the panel + a top shadow lifting it up,
          inactive tabs are muted. Container has no margin-bottom so the active
          tab's flat bottom edge meets the section's top edge without a gap.
          🔀 2026-10-05: the strip and the page's ONE `Actions ▾` share a row,
          the menu at its RIGHT end (owner; the house tabs-row placement).
          Below `md` the strip is hidden and the menu stands alone, right-
          aligned above the stacked sections, holding the print group only. */}
      {/* `md:!mt-[15px]`, not the stack's 24 (owner, 2026-10-05: the band→tabs
          gap read bigger than Cost Reports' band→card). `items-end` against
          the taller Actions slot put the tabs 9px BELOW the row top, so 15+9
          = 24px band→tabs, measured at 1280 and 1440 — Cost Reports' 24. */}
      <div className="flex items-end gap-2 md:!mt-[15px]" data-testid="budget-tabs-row">
      <div className="hidden md:flex items-end gap-0.5 px-1 min-w-0" role="tablist" aria-label="Budget sections">
        {SECTION_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            // 🚨 THE EXIT AN IMAGINED LIST MISSES. These tabs are the DESKTOP
            // layout's section switcher, and the Gantt is desktop-only — so
            // leaving `budget-crew-members` hides the whole Labour Flow panel,
            // Save bar and all, with nothing collapsing and nothing navigating.
            // Found by enumerating what the strip needs to stay REACHABLE, not
            // by listing controls that look related to days.
            onClick={() => guardGanttDayEdit(
              `switch to the ${tab.label || tab.id} section`,
              () => setActiveTab(tab.id)
            )}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap border border-b-0 ${
              activeTab === tab.id
                ? 'bg-[white] dark:bg-slate-700/30 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 shadow-[0_-2px_4px_rgba(0,0,0,0.04)] relative -mb-px z-10'
                : 'bg-gray-100 dark:bg-slate-700/30 text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-pressed={activeTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
      </div>
        <div className="ml-auto pb-1 shrink-0" data-testid="budget-actions-slot">
          <BudgetManageActionsMenu
            tabs={PRINT_TABS}
            currentTabId={activeTab}
            currentTabLabel={activeTabLabel}
            currentActions={renderCurrentTabActions}
            trailingActions={renderCurrentTabTrailingActions}
            printOnly={isPhoneViewport}
            buildPdf={buildManagePdf}
            filename={`${activeDepartment?.department_name || 'Department'}-Budget.pdf`}
            title={`${activeDepartment?.department_name || ''} Budget`}
          />
        </div>
      </div>

      {/* Cost Codes Section — the TAB. 🔀 2026-10-29: it holds two nested
          sections (Cost codes, Job codes; `renderCodeList`'s docblock), which
          carry the headers and the `+ Add …` buttons. On desktop this outer
          title row only repeated the tab above, so it is hidden there
          (`hideHeaderAtMd`, Labour Flow's precedent); on a phone it stays —
          it is that accordion's header. 🔻 No section-header `Actions ▾`
          (2026-10-05): the page's ONE menu sits at the right end of the tabs
          row (`BudgetManageActionsMenu`). */}
      <CollapsibleSection
        id="budget-cost-codes"
        title="Cost Codes &amp; Budget"
        className={activeTab === 'budget-cost-codes' ? 'md:!mt-0' : 'md:hidden'}
        forceExpanded={activeTab === 'budget-cost-codes'}
        hideHeaderAtMd
        onToggle={(nextCollapsed) => {
          if (nextCollapsed) {
            setShowAddCostCode(false);
            costCodeEditor.reset();
            setCostCodeForm({ code: '', name: '', description: '', budget_amount: '' });
          }
        }}
      >
        <div className="p-4 space-y-4">
          <LockedVersionBanner show={isVersionLocked} />
          {costCodesLoading ? (
            <LoadingSpinner message="Loading cost codes..." />
          ) : (
            <>
          {/* 🔀 COST CODES — the first of the tab's two equal sections. */}
          <CollapsibleSection
            id="cost-codes-list"
            title={codeSectionTitle('Cost codes', standardCodes.length)}
            subtitle="The department’s standing codes for everyday spend."
            className={CODE_SECTION_CARD}
            expandWhen={showAddCostCode && canEdit}
            onToggle={(folded) => { if (folded) { setShowAddCostCode(false); foldCodeSection(standardCodes); } }}
            /* 🚨 Safe in the header only because of `CollapsibleSection`'s
               `e.target === e.currentTarget` key guard (its docblock). */
            headerActions={canEdit ? (
              <button
                type="button"
                onClick={() => setShowAddCostCode(prev => !prev)}
                className={CODE_SECTION_ADD_BUTTON}
                data-testid="add-cost-code-toggle"
              >
                {showAddCostCode ? 'Cancel' : '+ Add Cost Code'}
              </button>
            ) : null}
          >
          {/* Add Cost Code Form */}
          {showAddCostCode && canEdit && (
            <div className="p-3">
            <form onSubmit={handleAddCostCode} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600" data-testid="add-cost-code-form">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">New Cost Code</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Code *</label>
                  <input
                    type="text"
                    value={costCodeForm.code}
                    onChange={(e) => setCostCodeForm(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="e.g. 1001"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Name *</label>
                  <input
                    type="text"
                    value={costCodeForm.name}
                    onChange={(e) => setCostCodeForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Props — Build Materials"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Description</label>
                  <input
                    type="text"
                    value={costCodeForm.description}
                    onChange={(e) => setCostCodeForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm"
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Budget</label>
                  <CurrencyInput
                    value={costCodeForm.budget_amount}
                    onChange={(val) => setCostCodeForm(prev => ({ ...prev, budget_amount: val }))}
                    placeholder="0.00"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={savingCostCode || !costCodeForm.code.trim() || !costCodeForm.name.trim()}
                    className={BTN.confirm}
                  >
                    {savingCostCode ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </form>
            </div>
          )}
          {standardCodes.length === 0 ? (
            <div className="p-3">
              <EmptyState
                size="sm"
                title="No cost codes yet"
                description="Cost codes group spend across the budget — add one to start tracking."
                cta={canEdit ? { label: 'Add Cost Code', onClick: () => setShowAddCostCode(true) } : undefined}
              />
            </div>
          ) : renderCodeList(standardCodes, 'standard')}
          </CollapsibleSection>

              {/* 🔀 JOB CODES (owner ruling 2026-10-28) — the second of the two
                  equal sections (2026-10-29); its New job code form sits at the
                  top of its OWN section. */}
              <CollapsibleSection
                id="job-codes-list"
                title={codeSectionTitle('Job codes', jobCodes.length)}
                subtitle="One code per big job — its orders are coded to it automatically."
                className={CODE_SECTION_CARD}
                expandWhen={showAddJobCode && canEdit}
                onToggle={(folded) => { if (folded) { setShowAddJobCode(false); foldCodeSection(jobCodes); } }}
                headerActions={canEdit ? (
                  <button
                    type="button"
                    onClick={() => setShowAddJobCode((prev) => !prev)}
                    className={CODE_SECTION_ADD_BUTTON}
                    data-testid="add-job-code-toggle"
                  >
                    {showAddJobCode ? 'Cancel' : '+ Add job code'}
                  </button>
                ) : null}
              >
              {showAddJobCode && canEdit && (
                <div className="p-3">
                <form ref={addJobCodeFormRef} onSubmit={handleAddJobCode} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600" data-testid="add-job-code-form">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">New job code</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                      <label htmlFor="job-code-code" className={FIELD_LABEL_CLASS}>Code</label>
                      <input
                        id="job-code-code"
                        type="text"
                        value={jobCodeForm.code}
                        onChange={(e) => setJobCodeForm((prev) => ({ ...prev, code: e.target.value }))}
                        placeholder="Automatic (J01, J02 …)"
                        className={FORM_INPUT_FIELD}
                        data-testid="job-code-code"
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label htmlFor="job-code-name" className={FIELD_LABEL_CLASS}>Job *</label>
                      <input
                        id="job-code-name"
                        type="text"
                        value={jobCodeForm.name}
                        onChange={(e) => setJobCodeForm((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g. Exploding car"
                        className={FORM_INPUT_FIELD}
                        required
                        readOnly={jobCodeForm.gag_id !== ''}
                        data-testid="job-code-name"
                      />
                      {jobCodeForm.gag_id !== '' && (
                        <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400" data-testid="job-code-name-from-gag">
                          Named after its gag. Renaming it later renames the gag too.
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="job-code-gag" className={FIELD_LABEL_CLASS}>Gag (optional)</label>
                      <select
                        id="job-code-gag"
                        value={jobCodeForm.gag_id}
                        onChange={(e) => {
                          const gagId = e.target.value;
                          const gag = jobGags.find((g) => String(g.id) === gagId);
                          // Q1 (2026-10-28): a code LINKED to a gag ADOPTS its title (the server
                          // stores the gag's title whatever is typed), so a pick fills it.
                          setJobCodeForm((prev) => ({ ...prev, gag_id: gagId, name: gag ? gag.title : prev.name }));
                        }}
                        className={FORM_INPUT_FIELD}
                        data-testid="job-code-gag"
                      >
                        <option value="">No gag</option>
                        {jobGags
                          .filter((g) => !costCodes.some((cc) => cc.is_job && Number(cc.gag_id) === Number(g.id)))
                          .map((g) => (
                            <option key={g.id} value={g.id}>{g.title}{g.scenes_display ? ` — Sc ${g.scenes_display}` : ''}</option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Budget</label>
                      <CurrencyInput
                        value={jobCodeForm.budget_amount}
                        onChange={(val) => setJobCodeForm((prev) => ({ ...prev, budget_amount: val }))}
                        placeholder="0.00"
                        className={FORM_INPUT_FIELD}
                        testId="job-code-budget"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      disabled={savingJobCode || !jobCodeForm.name.trim()}
                      className={BTN.confirm}
                      data-testid="job-code-submit"
                    >
                      {savingJobCode ? 'Adding...' : 'Add job code'}
                    </button>
                  </div>
                </form>
                </div>
              )}
              {jobCodes.length === 0 ? (
                <p className="p-3 text-sm text-gray-500 dark:text-gray-400" data-testid="budget-job-codes-empty">
                  {`No job codes yet. A gag pushed to the budget from the Breakdown gets one${canEdit ? ', or add one here' : ''}.`}
                </p>
              ) : renderCodeList(jobCodes, 'job')}
              </CollapsibleSection>

              {/* The COMBINED Total — outside both sections, so it stays on
                  screen whichever is folded; the same columns as both tables. */}
              {costCodes.length > 0 && renderCodeList([], 'total')}

              {!selectedVersionId && costCodes.length > 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 italic pt-2">
                  Create or select a budget version to assign budget amounts.
                </p>
              )}
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* ═══════════════════════════════════════════
          PAYMENT TYPES SECTION
          ═══════════════════════════════════════════ */}
      <CollapsibleSection
        id="budget-payment-types"
        /* 🔺 THE SECTION'S PRIMARY CREATE LIVES ON THE HEADER ROW.
            It sat on a row of its own at the top of the section body. MEASURED
            in Chromium at 1440x900, moving it shrinks the section body from
            873px to 846px and the document from 1205px to 1178px — **27px per
            section**, and the header row does NOT grow to take it (44px before
            and after). The same complaint that moved the page header onto the
            tabs row; a smaller win, honestly measured rather than estimated.
            🚨 THIS IS ONLY SAFE BECAUSE OF THE `e.target === e.currentTarget`
            GUARD IN `CollapsibleSection`'s `onKeyDown`. The header row is a
            `role="button"` with `tabIndex={0}`, and before that guard a SPACE
            or ENTER reaching it from anything inside collapsed the whole
            section — so a keyboard user pressing Space on this button would
            have closed the very section they were adding to, and the button
            would never have fired. Click was already covered by the wrapper's
            `stopPropagation`; the keyboard half was not.
            🎨 It keeps `BTN_XS.primary`. The one-filled-control rule in
            `components/ui/CLAUDE.md` governs what rests on a PAGE's chrome,
            not a section header, and only one section is open at a time on
            desktop — so exactly one filled create is ever on screen, which is
            the outcome that rule is protecting. */
        headerActions={canEdit ? (
          <button
            type="button"
            onClick={() => ptList.add()}
            className={`${BTN_XS.primary} min-h-[36px] md:min-h-0 inline-flex items-center justify-center whitespace-nowrap`}
            data-testid="pt-list-add"
          >
            + Add Payment Type
          </button>
        ) : null}
        title="Payment Types"
        className={activeTab === 'budget-payment-types' ? 'md:!mt-0' : 'md:hidden'}
        forceExpanded={activeTab === 'budget-payment-types'}
        onToggle={(nextCollapsed) => {
          if (nextCollapsed) {
            paymentTypeEditor.reset();
          }
        }}
      >
        <div className="p-4 space-y-4">

          {/* Table */}
          {paymentTypesLoading ? (
            <LoadingSpinner message="Loading payment types..." />
          ) : canEdit ? (
            <EditableList
              list={ptList}
              testId="pt-list"
              rowName={(r) => r.name}
              saving={savingPaymentType}
              onSave={savePaymentTypeList}
              columns={[
                { key: 'name', label: 'Name', width: 'minmax(0,16rem)', placeholder: 'Name' },
                { key: 'description', label: 'Description', width: 'minmax(0,1fr)', placeholder: 'Optional description' },
              ]}
            />
          ) : paymentTypes.length === 0 ? (
            <EmptyState
              size="sm"
              title="No payment types yet"
              description="Add the labels you use for AP categorisation (P-card, BACS, etc.)."
            />
          ) : (
            <>
              {/* Mobile: card layout — 🔀 EXPAND-TO-EDIT (2026-10-24): the card
                  header taps to open ONE panel; no Edit button. */}
              <div className="md:hidden space-y-2">
                {paymentTypes.map((pt, idx) => {
                  const isExpanded = paymentTypeEditor.isExpanded(pt.id);
                  return (
                    <div
                      key={pt.id}
                      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-opacity ${paymentTypeEditor.isDimmed(pt.id) ? 'opacity-40' : ''}`}
                      data-testid={`pt-card-${pt.id}`}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onClick={() => paymentTypeEditor.toggle(pt)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); paymentTypeEditor.toggle(pt); }
                        }}
                        className={`flex items-start gap-2 p-3 min-h-[44px] cursor-pointer rounded-lg ${isExpanded ? 'bg-gray-100 dark:bg-slate-700 rounded-b-none' : ''}`}
                        data-testid={`pt-card-toggle-${pt.id}`}
                      >
                        <span className="mt-[3px] shrink-0 inline-flex"><ExpandChevron expanded={isExpanded} /></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={pt.name}>{pt.name}</div>
                          {pt.description && (
                            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate" title={pt.description}>{pt.description}</div>
                          )}
                        </div>
                        {canEdit && (
                          <RowReorderButtons index={idx} count={paymentTypes.length} onMove={handleReorderPaymentType} layout="card" />
                        )}
                      </div>
                      {paymentTypeEditor.isPanelMounted(pt.id) && (
                        <div className={`px-3 pb-3 pt-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-lg ${rowPanelFadeClass(paymentTypeEditor.isFading(pt.id))}`}>
                          {renderPaymentTypePanel(pt, 'card')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop: fixed-width table — `table-fixed` + `<colgroup>` keep the
                  columns the same width collapsed, expanded and mid-edit. */}
              <BudgetTableScroller scrollerRef={paymentTypesScrollportRef} wrapperClassName="hidden md:block max-w-[56rem]" testId="budget-payment-types-table-scroller" stickyBottom={crewTableStickyBottomPx}>
              <table className="w-full min-w-[640px] table-fixed text-sm" data-testid="budget-payment-types-table">
                <colgroup>
                  <col data-col="name" style={{ width: 280 }} />
                  <col data-col="description" />
                  {canEdit && <col data-col="reorder" style={{ width: 40 }} />}
                  {canEdit && <col data-col="row_menu" style={{ width: ROW_MENU_COL_WIDTH }} />}
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Name</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Description</th>
                    {canEdit && <th className="px-1 py-2"><span className="sr-only">Reorder</span></th>}
                    {canEdit && <th className="px-1 py-2"><span className="sr-only">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {paymentTypes.map((pt, idx) => {
                    const isExpanded = paymentTypeEditor.isExpanded(pt.id);
                    // 🔀 IN-PLACE EDITING (2026-10-25, the Breakdown's model):
                    // an editor's open row edits name + description in place.
                    const editing = canEdit && paymentTypeEditor.isEditing(pt.id);
                    const pad = editing ? ROW_CELL_PAD_EDIT : ROW_CELL_PAD;
                    const textControl = (key, testId, label) => (
                      <div onClick={stopRowToggleOnControl} className="min-w-0 flex-1">
                        <input type="text" className={ROW_EDIT_CONTROL} value={editPaymentTypeForm[key] ?? ''}
                          aria-label={`${label} for ${pt.name}`}
                          onChange={(e) => paymentTypeEditor.setField(key, e.target.value)} data-testid={testId} />
                      </div>
                    );
                    return (
                      <React.Fragment key={pt.id}>
                      <tr
                        data-testid={`pt-row-${pt.id}`}
                        className={`border-b border-gray-100 dark:border-gray-700/50 ${editableRowClass({ expanded: isExpanded, dimmed: paymentTypeEditor.isDimmed(pt.id) })}`}
                        onClick={() => paymentTypeEditor.toggle(pt)}
                      >
                        <td className={`${pad} text-gray-800 dark:text-gray-200 font-medium`}>
                          <span className="flex items-center gap-1 min-w-0">
                            <RowToggleChevron
                              expanded={isExpanded}
                              onToggle={() => paymentTypeEditor.toggle(pt)}
                              expandLabel={`Expand ${pt.name}`}
                              collapseLabel={`Collapse ${pt.name}`}
                              testId={`pt-chevron-${pt.id}`}
                            />
                            {editing
                              ? textControl('name', 'edit-pt-name-row', 'Name')
                              : <span className="truncate" title={pt.name}>{pt.name}</span>}
                          </span>
                        </td>
                        <td className={`${pad} text-gray-500 dark:text-gray-400 truncate`} title={pt.description || undefined}>
                          {editing ? textControl('description', 'edit-pt-description-row', 'Description') : (pt.description || '-')}
                        </td>
                        {canEdit && (
                          <td className="px-1 py-1 text-center" onClick={stopRowToggle}>
                            <RowReorderButtons index={idx} count={paymentTypes.length} onMove={handleReorderPaymentType} />
                          </td>
                        )}
                        {canEdit && (
                          <RowMenuCell label={`Actions for ${pt.name}`} items={[{ key: 'delete', label: 'Delete payment type', danger: true, onClick: () => handleDeletePaymentType(pt) }]} />
                        )}
                      </tr>
                      <RowExpansion
                        editor={paymentTypeEditor}
                        id={pt.id}
                        colSpan={canEdit ? 4 : 2}
                        panelWidth={paymentTypesScrollportWidth}
                        testId={`pt-panel-row-${pt.id}`}
                        bar={canEdit ? (
                          <RowEditBar
                            onSave={paymentTypeEditor.saveAndCollapse}
                            onCancel={paymentTypeEditor.collapse}
                            saving={savingPaymentType}
                            dirty={paymentTypeEditor.isDirty}
                            testIdPrefix={`pt-row-edit-${pt.id}`}
                          />
                        ) : null}
                      >
                        {renderPaymentTypePanel(pt, 'row')}
                      </RowExpansion>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </BudgetTableScroller>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* ═══════════════════════════════════════════
          CREW GRADES SECTION
          ═══════════════════════════════════════════ */}
      <CollapsibleSection
        id="budget-crew-grades"
        /* 🔺 THE SECTION'S PRIMARY CREATE LIVES ON THE HEADER ROW.
            It sat on a row of its own at the top of the section body. MEASURED
            in Chromium at 1440x900, moving it shrinks the section body from
            873px to 846px and the document from 1205px to 1178px — **27px per
            section**, and the header row does NOT grow to take it (44px before
            and after). The same complaint that moved the page header onto the
            tabs row; a smaller win, honestly measured rather than estimated.
            🚨 THIS IS ONLY SAFE BECAUSE OF THE `e.target === e.currentTarget`
            GUARD IN `CollapsibleSection`'s `onKeyDown`. The header row is a
            `role="button"` with `tabIndex={0}`, and before that guard a SPACE
            or ENTER reaching it from anything inside collapsed the whole
            section — so a keyboard user pressing Space on this button would
            have closed the very section they were adding to, and the button
            would never have fired. Click was already covered by the wrapper's
            `stopPropagation`; the keyboard half was not.
            🎨 It keeps `BTN_XS.primary`. The one-filled-control rule in
            `components/ui/CLAUDE.md` governs what rests on a PAGE's chrome,
            not a section header, and only one section is open at a time on
            desktop — so exactly one filled create is ever on screen, which is
            the outcome that rule is protecting. */
        headerActions={canEdit ? (
          <button
            type="button"
            onClick={() => setShowAddGrade(prev => !prev)}
            className={`${BTN_XS.primary} min-h-[36px] md:min-h-0 inline-flex items-center justify-center whitespace-nowrap`}
          >
            {showAddGrade ? 'Cancel' : '+ Add Grade'}
          </button>
        ) : null}
        title="Crew Grades"
        className={activeTab === 'budget-crew-grades' ? 'md:!mt-0' : 'md:hidden'}
        forceExpanded={activeTab === 'budget-crew-grades'}
        onToggle={(nextCollapsed) => {
          if (nextCollapsed) {
            setShowAddGrade(false);
            gradeEditor.reset();
            setGradeForm({ name: '', weekly_rate: '', computer_rental: '', default_role_id: '' });
          }
        }}
      >
        <div className="p-4 space-y-4">
          {/* Add Grade Form */}
          {showAddGrade && canEdit && (
            <form onSubmit={handleAddGrade} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">New Crew Grade</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Grade Name *</label>
                  <input
                    type="text"
                    value={gradeForm.name}
                    onChange={(e) => setGradeForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. HOD"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Weekly Rate (£)</label>
                  <CurrencyInput
                    value={gradeForm.weekly_rate}
                    onChange={(v) => setGradeForm(prev => ({ ...prev, weekly_rate: v }))}
                    placeholder="£0.00"
                    className={inputCls}
                  />
                </div>
                {/* ═══ THE GRADE'S DEFAULT FOR THE *COMPUTER ALLOWANCE* ═══════════════
                    🔑 It read `Computer Rental (£/wk)` until 2026-08-23. Sitting between
                    `Weekly Rate (£)` and `6th Day Rate (£)`, that heading reads as another
                    COST CATEGORY of the grade — which is exactly the presentation the owner
                    has now ruled against three times: *"remember computer is one type of
                    allowance and not different."*

                    🪤 IT IS NOT RENAMED TO `Allowances`, AND THAT IS THE CAREFUL PART. A
                    grade carries a default for THIS allowance and for no other (there are no
                    `crew_grades.allowance_*` columns — see the Half-B note in the branch
                    report), so a plural heading would claim to cover three slots that do not
                    exist here. It is named as what it is: the grade's default for the
                    computer allowance.

                    🪤 AND IT IS STILL A RATE, NOT A COST. `crew_grades.computer_rental` is
                    £/week — an INPUT — where `computer_cost` is what the engagement comes
                    to. The ruling says computer is not a special KIND of allowance; it does
                    not say a rate and a cost are the same fact, so the `(£/wk)` stays.
                    ONE label, four screen sites + the PDF: `constants/budget.js`. */}
                <div>
                  <label className={FIELD_LABEL_CLASS}>{COMPUTER_ALLOWANCE_LABEL} (£/wk)</label>
                  <CurrencyInput
                    value={gradeForm.computer_rental}
                    onChange={(v) => setGradeForm(prev => ({ ...prev, computer_rental: v }))}
                    placeholder="£0.00"
                    className={inputCls}
                  />
                </div>
                {/* 6th/7th-day premium rates.
                    🔻 CORRECTED 2026-08-23. This comment said "BLANK means
                    UNPRICED (the day stays in the standard pool at rate/5), NOT
                    free — the placeholder says so", and the placeholder read
                    "Unpriced". Both became WRONG the moment the resolve-time
                    default landed: a blank field now costs 1.5× / 2× the DAILY
                    rate, which is MORE than the standard pool, not less. So the
                    old placeholder was the rate card telling a buyer a day was
                    unpriced while the budget charged the most expensive figure
                    on the screen. The identical correction was made at the
                    MEMBER override input by the defaults commit and missed here,
                    on the screen where the policy is actually set.
                    BLANK still is not FREE — that part was always right and is
                    still what the placeholder has to convey; only the reason
                    changed. See `premiumDefaultLabel`. */}
                <div>
                  <label className={FIELD_LABEL_CLASS}>6th Day Rate (£)</label>
                  <CurrencyInput
                    value={gradeForm.sixth_day_rate}
                    onChange={(v) => setGradeForm(prev => ({ ...prev, sixth_day_rate: v }))}
                    placeholder={premiumDefaultLabel(6, departmentAgreementRates)}
                    className={inputCls}
                    testId="grade-sixth-day-rate"
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>7th Day Rate (£)</label>
                  <CurrencyInput
                    value={gradeForm.seventh_day_rate}
                    onChange={(v) => setGradeForm(prev => ({ ...prev, seventh_day_rate: v }))}
                    placeholder={premiumDefaultLabel(7, departmentAgreementRates)}
                    className={inputCls}
                    testId="grade-seventh-day-rate"
                  />
                </div>
                <div>
                  <GradeAccessRightsPicker
                    value={gradeForm.default_role_id}
                    onChange={(v) => setGradeForm(prev => ({ ...prev, default_role_id: v }))}
                    roleDefinitions={roleDefinitions}
                    loadFailed={roleDefinitionsFailed}
                    className={inputCls}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={savingGrade || !gradeForm.name.trim()}
                    className={BTN.confirm}
                  >
                    {savingGrade ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-testid="grade-overtime-start-hint">
                Overtime rates start at the house values. Edit the grade to change them.
              </p>
            </form>
          )}

          {/* Grades Table */}
          {gradesLoading ? (
            <LoadingSpinner message="Loading crew grades..." />
          ) : grades.length === 0 ? (
            <EmptyState
              size="sm"
              title="No crew grades yet"
              description="Grades drive default rates for the crew table."
              cta={canEdit ? { label: 'Add Grade', onClick: () => setShowAddGrade(true) } : undefined}
            />
          ) : (
            <>
              {/* ═══ CREW GRADES — EXPAND-TO-EDIT (2026-10-24) ═══════════════
                  Owner: *"clicking the row makes it editable without the cells
                  changing size. Also clicking it should expand the row without
                  having to click the edit button."* A click anywhere on a row
                  (or a phone card's header) toggles it; the summary stays TEXT
                  and ONE panel under it holds every field. State:
                  `gradeEditor` (`hooks/useExpandRowEditor.js`); parts:
                  `components/budget/ExpandRowParts.js`. Reorder arrows stop
                  propagation so they never toggle. */}
              {/* Mobile: card layout */}
              <div className="md:hidden space-y-2">
                {grades.map((g, idx) => {
                  const isExpanded = gradeEditor.isExpanded(g.id);
                  const roleName = g.default_role_id
                    ? (roleDefinitions.find(r => r.id === g.default_role_id)?.display_name || null)
                    : null;
                  return (
                    <div
                      key={g.id}
                      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-opacity ${gradeEditor.isDimmed(g.id) ? 'opacity-40' : ''}`}
                      data-testid={`grade-card-${g.id}`}
                    >
                      {/* Header: tap to open — chevron + name + role + reorder */}
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onClick={() => gradeEditor.toggle(g)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); gradeEditor.toggle(g); }
                        }}
                        className={`flex items-start gap-2 p-3 min-h-[44px] cursor-pointer rounded-t-lg ${isExpanded ? 'bg-gray-100 dark:bg-slate-700' : ''}`}
                        data-testid={`grade-card-toggle-${g.id}`}
                      >
                        <span className="mt-[3px] shrink-0 inline-flex"><ExpandChevron expanded={isExpanded} /></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={g.name}>{g.name}</div>
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {roleName ? `Access: ${roleName}` : <span className="italic text-gray-500 dark:text-gray-400">No default access rights</span>}
                          </div>
                        </div>
                        {canEdit && (
                          <RowReorderButtons index={idx} count={grades.length} onMove={handleReorderGrade} layout="card" />
                        )}
                      </div>

                      {/* Rates (display only) — they stay on the card while it is open */}
                      <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Weekly Rate</div>
                          <div className="text-base font-semibold text-gray-800 dark:text-gray-200 text-right pr-2">{formatMoney(g.weekly_rate)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Total allowances/wk</div>
                          <div className="text-base text-gray-600 dark:text-gray-400 text-right pr-2" data-testid={`grade-card-allowances-total-${g.id}`}>{moneyOrAbsent(g.allowances_total_weekly)}</div>
                        </div>
                      </div>
                      {!gradeEditor.isPanelMounted(g.id) && g.overtime_rates && (
                        <div className="px-3 pb-3">
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Overtime</div>
                          <OvertimeRatesSummary rates={g.overtime_rates} agreementRates={g.agreement_rates} formatMoney={formatMoney} testId={`grade-overtime-card-${g.id}`} />
                        </div>
                      )}

                      {gradeEditor.isPanelMounted(g.id) && (
                        <div className={`px-3 pb-3 pt-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-lg ${rowPanelFadeClass(gradeEditor.isFading(g.id))}`}>
                          {renderGradePanel(g, 'card')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop: fixed-width table. 🔑 `table-fixed` + the `<colgroup>`
                  below are what keep every column the same width collapsed,
                  expanded and mid-edit — a fixed layout never sizes a column
                  to its content, so the in-place inputs fill it instead. The
                  Grade Name column takes what is left (it is the only one with
                  no declared width); `min-w-[1100px]` lets the scroller own any
                  shortfall rather than squeezing the declared columns.
                  🔀 2026-10-25: an open row's cells ARE its inputs, so this
                  fixed layout is what keeps them from resizing a column.
                  🔀 Measured 2026-10-24 at 1280 with the sidebar open: the
                  table is wider than its scrollport, so the expansion is
                  sticky at the scrollport width — the bar's Save stays in view. */}
              <BudgetTableScroller scrollerRef={gradesScrollportRef} wrapperClassName="hidden md:block" testId="budget-grades-table-scroller" stickyBottom={crewTableStickyBottomPx}>
              <table
                className="w-full table-fixed text-sm"
                data-testid="budget-grades-table"
                style={{ minWidth: gradeTable.columns.reduce((s, c) => s + gradeTable.widths[c.key], 0) }}
              >
                <colgroup>
                  {/* 🔀 2026-10-25 (owner): Access Rights 2nd; the table FITS
                      the page — `GRADE_TABLE_COLUMNS` through the shared
                      `fitColumnWidths` (see `gradeTableWidths`). */}
                  {gradeTable.columns.map((c) => (
                    <col key={c.key} data-col={c.key} style={{ width: gradeTable.widths[c.key] }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Grade Name</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400"><GradeAccessRightsLabel /></th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Weekly Rate (£)</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400" data-testid="grade-allowances-total-header">{GRADE_ALLOWANCES_TOTAL_LABEL}</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">6th Day (£)</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">7th Day (£)</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400" data-testid="grade-hourly-header">Hourly (£)</th>
                    {canEdit && <th className="px-1 py-2"><span className="sr-only">Reorder</span></th>}
                    {canEdit && <th className="px-1 py-2"><span className="sr-only">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {grades.map((g, idx) => {
                    const isExpanded = gradeEditor.isExpanded(g.id);
                    // 🔀 IN-PLACE EDITING (2026-10-25, the Breakdown's model):
                    // while this row is open for an editor, its own cells ARE
                    // the controls, at the column's declared width (`table-fixed`
                    // + the `<colgroup>` above), with half the cell gap. Overtime
                    // has no room in its cell, so its six inputs open underneath.
                    const editing = canEdit && gradeEditor.isEditing(g.id);
                    const pad = editing ? ROW_CELL_PAD_EDIT : ROW_CELL_PAD;
                    const set = (key) => (v) => gradeEditor.setField(key, v);
                    return (
                      <React.Fragment key={g.id}>
                      <tr
                        id={`crew-grade-row-${g.id}`}
                        data-testid={`grade-row-${g.id}`}
                        className={`border-b border-gray-100 dark:border-gray-700/50 ${editableRowClass({ expanded: isExpanded, dimmed: gradeEditor.isDimmed(g.id) })}`}
                        onClick={() => gradeEditor.toggle(g)}
                      >
                        <td className={`${pad} text-gray-800 dark:text-gray-200`}>
                          <span className="flex items-center gap-1 min-w-0">
                            <RowToggleChevron
                              expanded={isExpanded}
                              onToggle={() => gradeEditor.toggle(g)}
                              expandLabel={`Expand ${g.name}`}
                              collapseLabel={`Collapse ${g.name}`}
                              testId={`grade-chevron-${g.id}`}
                            />
                            {editing ? (
                              <span className="min-w-0 flex-1" onClick={stopRowToggleOnControl}>
                                <input type="text" className={ROW_EDIT_CONTROL} value={editGradeForm.name ?? ''}
                                  aria-label={`Grade name for ${g.name}`}
                                  onChange={(e) => gradeEditor.setField('name', e.target.value)}
                                  data-testid="edit-grade-name-row" />
                              </span>
                            ) : (
                              <span className="truncate" title={g.name} data-testid={`grade-name-${g.id}`}>{g.name}</span>
                            )}
                          </span>
                        </td>
                        {/* ACCESS RIGHTS WHEN HIRED — 2nd column (owner,
                            2026-10-25). A failed role load keeps the TEXT here;
                            the panel underneath says why it cannot be edited.
                            The select clips a long role name in this cell, so
                            its `title` carries the chosen option in full. */}
                        <td className={`${pad} text-gray-500 dark:text-gray-400 truncate`} data-testid={`grade-access-${g.id}`}>
                          {editing && !roleDefinitionsFailed ? (
                            <div onClick={stopRowToggleOnControl}>
                              <GradeAccessRightsPicker
                                value={editGradeForm.default_role_id}
                                onChange={set('default_role_id')}
                                roleDefinitions={roleDefinitions}
                                className={ROW_EDIT_CONTROL}
                                showLabel={false}
                                ariaLabel={`Default access rights for ${g.name}`}
                                title={editGradeForm.default_role_id
                                  ? (roleDefinitions.find(r => String(r.id) === String(editGradeForm.default_role_id))?.display_name || undefined)
                                  : '-- None --'}
                              />
                            </div>
                          ) : g.default_role_id
                            ? (roleDefinitions.find(r => r.id === g.default_role_id)?.display_name || '—')
                            : <span className="italic text-gray-500 dark:text-gray-400">None</span>}
                        </td>
                        <td className={`${pad} text-right text-gray-800 dark:text-gray-200 truncate`}>
                          {editing ? (
                            <div onClick={stopRowToggleOnControl}>
                              <CurrencyInput value={editGradeForm.weekly_rate} onChange={set('weekly_rate')}
                                className={ROW_EDIT_MONEY_CONTROL} testId="edit-grade-weekly-rate-row" />
                            </div>
                          ) : formatMoney(g.weekly_rate)}
                        </td>
                        {/* TOTAL ALLOWANCES (£/wk) — 2026-10-25, owner mock. The
                            SERVER's `allowances_total_weekly` (the four resolved
                            slots' weekly amounts; caps ignored — a cap bounds an
                            engagement, not a week), READ-ONLY even while the row
                            is edited: the slots are edited in the panel below,
                            and a client-side sum of typed amounts would be a
                            second money answer. Weekly Rate's text style. */}
                        <td className={`${pad} text-right text-gray-500 dark:text-gray-400 truncate`} title="Read-only — the sum of the allowances below" data-testid={`grade-allowances-total-${g.id}`}>
                          {moneyOrAbsent(g.allowances_total_weekly)}
                        </td>
                        {/* 🚨 `== null`, never a falsy test: a grade priced at 0 must
                            read £0.00, not the default label. They are different
                            answers and they cost different amounts. 🪤 A stored 0 is
                            no longer WRITABLE (2026-08-23) but is still READABLE, so
                            this branch is not dead and must not be tidied to `!`.
                            The ITALIC MUTED treatment says "nobody typed a figure
                            here". 🔀 2026-10-24: the SHORT label
                            (`premiumDefaultShortLabel`, the long one wrapped this
                            column to three lines) and `text-gray-500` with its house
                            dark pair — `text-gray-400` measured 2.54:1 and failed. */}
                        <td className={`${pad} text-right text-gray-800 dark:text-gray-200 truncate`} data-testid={`grade-sixth-${g.id}`}>
                          {/* Editing: the SHORT default label as the blank
                              field's placeholder — the long one does not fit
                              this column (the reason the cell uses it at rest). */}
                          {editing ? (
                            <div onClick={stopRowToggleOnControl} title={premiumPlaceholder(6, { weekly: editGradeForm.weekly_rate, short: true, formatMoney, rates: g.agreement_rates }).title}>
                              <CurrencyInput value={editGradeForm.sixth_day_rate} onChange={set('sixth_day_rate')}
                                placeholder={premiumPlaceholder(6, { weekly: editGradeForm.weekly_rate, short: true, formatMoney, rates: g.agreement_rates }).placeholder}
                                className={ROW_EDIT_MONEY_CONTROL} testId="edit-grade-sixth-day-rate-row" />
                            </div>
                          ) : g.sixth_day_rate == null
                            ? <span className="italic text-gray-500 dark:text-gray-400">{premiumDefaultShortLabel(6, g.agreement_rates)}</span>
                            : formatMoney(g.sixth_day_rate)}
                        </td>
                        <td className={`${pad} text-right text-gray-800 dark:text-gray-200 truncate`} data-testid={`grade-seventh-${g.id}`}>
                          {editing ? (
                            <div onClick={stopRowToggleOnControl} title={premiumPlaceholder(7, { weekly: editGradeForm.weekly_rate, short: true, formatMoney, rates: g.agreement_rates }).title}>
                              <CurrencyInput value={editGradeForm.seventh_day_rate} onChange={set('seventh_day_rate')}
                                placeholder={premiumPlaceholder(7, { weekly: editGradeForm.weekly_rate, short: true, formatMoney, rates: g.agreement_rates }).placeholder}
                                className={ROW_EDIT_MONEY_CONTROL} testId="edit-grade-seventh-day-rate-row" />
                            </div>
                          ) : g.seventh_day_rate == null
                            ? <span className="italic text-gray-500 dark:text-gray-400">{premiumDefaultShortLabel(7, g.agreement_rates)}</span>
                            : formatMoney(g.seventh_day_rate)}
                        </td>
                        {/* HOURLY (£) — the SERVER-resolved hourly (the grade's
                            override, else weekly ÷ 5 ÷ 11 as the server derives
                            it), never re-derived here; "—" when unpriced. The
                            same text style as Weekly Rate in both themes — a
                            derived figure is NOT muted; the pen alone marks an
                            override. Editing: the grade's own `hourly_rate`,
                            whose blank placeholder is the derived figure. */}
                        <td className={`${pad} text-right text-gray-800 dark:text-gray-200 truncate`} data-testid={`grade-hourly-${g.id}`}>
                          {editing ? (
                            <div onClick={stopRowToggleOnControl}>
                              <CurrencyInput value={editGradeForm.hourly_rate ?? ''} onChange={set('hourly_rate')}
                                placeholder={overtimeRatePlaceholder(OVERTIME_HOURLY_FIELD, g.overtime_rates, 'grade', formatMoney, g.agreement_rates)}
                                className={ROW_EDIT_MONEY_CONTROL} testId="edit-grade-overtime-row-hourly_rate" />
                            </div>
                          ) : <GradeHourlyCell rate={g.overtime_rates?.hourly_rate} formatMoney={formatMoney} testId={`grade-hourly-value-${g.id}`} />}
                        </td>
                        {canEdit && (
                          <td className="px-1 py-1 text-center" onClick={stopRowToggle}>
                            <RowReorderButtons index={idx} count={grades.length} onMove={handleReorderGrade} />
                          </td>
                        )}
                        {canEdit && (
                          <RowMenuCell label={`Actions for ${g.name}`} items={[{ key: 'delete', label: 'Delete grade', danger: true, onClick: () => handleDeleteGrade(g) }]} />
                        )}
                      </tr>
                      <RowExpansion
                        editor={gradeEditor}
                        id={g.id}
                        colSpan={canEdit ? 9 : 7}
                        panelWidth={gradesScrollportWidth}
                        testId={`grade-panel-row-${g.id}`}
                        bar={canEdit ? (
                          <RowEditBar
                            onSave={gradeEditor.saveAndCollapse}
                            onCancel={gradeEditor.collapse}
                            saving={savingGrade}
                            dirty={gradeEditor.isDirty}
                            testIdPrefix={`grade-row-edit-${g.id}`}
                          />
                        ) : null}
                      >
                        {renderGradePanel(g, 'row')}
                      </RowExpansion>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </BudgetTableScroller>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* ═══════════════════════════════════════════
          CREW MEMBERS SECTION
          ═══════════════════════════════════════════ */}
      <CollapsibleSection
        id="budget-crew-members"
        title="Labour Flow"
        className={activeTab === 'budget-crew-members' ? 'md:!mt-0' : 'md:hidden'}
        forceExpanded={activeTab === 'budget-crew-members'}
        /* 🔻 Desktop: no title row — its controls live in `labourFlowToolbar`.
           The phone keeps the header, and its controls, unchanged. */
        hideHeaderAtMd
        headerActions={!isPhoneViewport ? null : isVersionLocked ? versionLockedBadge : (
          <div className="flex flex-wrap items-center gap-2">
            {crewViewToggleNode}
            {addMemberHeaderButton}
            {crewSearchNode}
            {crewMemberActionsNode}
          </div>
        )}
        onToggle={(nextCollapsed) => {
          if (nextCollapsed) {
            setShowAddMember(false);
            setExpandedMemberId(null);
            setClosingMemberId(null);
            setOpeningMemberId(null);
            setEditMemberForm({});
          }
        }}
      >
        {/* 📐 `md:p-0 md:space-y-0` — desktop flattening (2026-10-05): the
            toolbar, the crew table and the Gantt sit flush in the section card
            instead of in a padded box inside it. Phone keeps `p-4 space-y-4`. */}
        <div className="p-4 space-y-4 md:p-0 md:space-y-0">
          {labourFlowToolbar}
          {/* Toolbar: Table view / Gantt view + Add Member + Search normally
              live in this section's header (see `headerActions` above),
              reclaiming this row for everyone but a viewer of a LOCKED
              version. While locked the header shows only the "version is
              locked" sentence (the badge wins — see the reasoning at
              `crewViewToggleNode` above `return (`), so the view switcher
              and search stay HERE instead, using the SAME
              `crewViewToggleNode` / `crewSearchNode` the header uses when
              unlocked — one definition each, never a second copy drifting
              apart. `+ Add Member` needs no such fallback: it is
              `canEdit`-gated, and `canEdit` is already `false` whenever the
              version is locked, so there is nothing to show here for it. */}
          {/* 🔀 2026-10-05: the locked fallback is PHONE ONLY — on desktop the
              toolbar carries search and the switch beside the lock badge. */}
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {isVersionLocked && isPhoneViewport && crewViewToggleNode}
            {isVersionLocked && isPhoneViewport && crewSearchNode}
            {/* 🔄 THE PER-MEMBER ACTION BUTTONS MOVED INTO THE SECTION HEADER
                (2026-08-23) — see `crewMemberActionsNode`, defined beside
                `addMemberHeaderButton`. They stood HERE, at the top of the
                section body, which meant they were absent from the header the
                owner actually looks at and, in Gantt view, sat above a chart
                rather than beside its controls.
                🔑 ONE definition, in the header, so the two placements cannot
                drift — there is deliberately no fallback copy here of the kind
                `crewViewToggleNode` / `crewSearchNode` keep for the LOCKED state.
                Those exist because the locked header is taken over by the badge;
                these buttons are `canEdit`-gated and `canEdit` is already false
                whenever the version is locked, so there is nothing to fall back
                to and a copy here could only ever be dead.
                Mobile keeps the bottom-of-panel Save/Cancel/Delete row, exactly
                as before — the header node is `hidden md:flex`. */}
          </div>
          {/* Add Member Form — matches the expand-to-edit panel layout */}
          {showAddMember && canEdit && (
            <form onSubmit={handleAddMember} data-crew-edit-form className="bg-white rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-4 md:m-3">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Quick Add <span className="font-normal text-xs text-gray-400 dark:text-gray-500 ml-2">Only * fields are needed for a quick budget entry. Fill in the rest anytime or via Admin Settings.</span></h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {/* Row 1: Who + When */}
                <div>
                  <label className={FIELD_LABEL_CLASS}>Name *</label>
                  <UserAutocomplete
                    value={memberForm.name}
                    onChange={(name, userId) => setMemberForm(prev => ({ ...prev, name, user_id: userId }))}
                    placeholder="e.g. John Smith"
                    className={inputClsSmallTouch}
                    required
                    excludeUserIds={assignedUserIds}
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Grade *</label>
                  <select
                    value={memberForm.crew_grade_id}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, crew_grade_id: e.target.value }))}
                    className={inputClsSmallTouch}
                    required
                  >
                    <option value="">Select grade...</option>
                    {grades.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({formatMoney(g.weekly_rate)}/wk)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Rate Override (£/wk)</label>
                  <CurrencyInput value={memberForm.weekly_rate_override ?? ''}
                    onChange={(v) => setMemberForm(prev => ({ ...prev, weekly_rate_override: v }))}
                    placeholder={memberForm.crew_grade_id ? formatMoney(grades.find(g => String(g.id) === String(memberForm.crew_grade_id))?.weekly_rate ?? 0) : 'Grade rate'}
                    className={inputClsSmallTouch} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>6th Day Rate (£)</label>
                  <CurrencyInput value={memberForm.sixth_day_rate_override ?? ''}
                    onChange={(v) => setMemberForm(prev => ({ ...prev, sixth_day_rate_override: v }))}
                    placeholder="Grade rate"
                    className={inputCls}
                    testId="new-member-sixth-day-rate-override" />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>7th Day Rate (£)</label>
                  <CurrencyInput value={memberForm.seventh_day_rate_override ?? ''}
                    onChange={(v) => setMemberForm(prev => ({ ...prev, seventh_day_rate_override: v }))}
                    placeholder="Grade rate"
                    className={inputCls}
                    testId="new-member-seventh-day-rate-override" />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Start Date *</label>
                  <input type="date" value={memberForm.start_date}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, start_date: e.target.value }))}
                    className={inputClsSmallTouch} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>End Date *</label>
                  <input type="date" value={memberForm.end_date}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, end_date: e.target.value }))}
                    className={inputClsSmallTouch} />
                </div>
                {/* Row 2: Where + Details + Contact */}
                <div>
                  <label className={FIELD_LABEL_CLASS}>Location</label>
                  <input type="text" value={memberForm.location}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g. Pinewood"
                    className={inputClsSmallTouch} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>{DEFAULT_ALLOCATION_LABEL}</label>
                  <select
                    value={memberForm.default_allocation}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, default_allocation: e.target.value }))}
                    className={inputClsSmallTouch}
                  >
                    {DEFAULT_ALLOCATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Email</label>
                  <input type="email" value={memberForm.user_email}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, user_email: e.target.value }))}
                    placeholder="name@example.com"
                    className={inputClsSmallTouch} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Phone</label>
                  <input type="tel" value={memberForm.user_phone}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, user_phone: e.target.value }))}
                    placeholder="e.g. 07700 900000"
                    className={inputClsSmallTouch} />
                </div>
                <div>
                  <div className="flex gap-1.5">
                    <div className="flex-1">
                      <label className={FIELD_LABEL_CLASS}>{COMPUTER_ALLOWANCE_LABEL}</label>
                      <CurrencyInput value={memberForm.computer_rental ?? ''}
                        onChange={(v) => setMemberForm(prev => ({ ...prev, computer_rental: v }))}
                        placeholder="£/wk"
                        className={inputClsSmallTouch} />
                    </div>
                    <div className="flex-1">
                      <label className={FIELD_LABEL_CLASS}>Allowance Cap</label>
                      <CurrencyInput value={memberForm.computer_rental_cap ?? ''}
                        onChange={(v) => setMemberForm(prev => ({ ...prev, computer_rental_cap: v }))}
                        placeholder="No cap"
                        className={inputClsSmallTouch} />
                    </div>
                  </div>
                </div>
                {/* Row 3: Notes (full width) */}
                <div className="sm:col-span-2 lg:col-span-5">
                  <label className={FIELD_LABEL_CLASS}>Notes</label>
                  <textarea
                    value={memberForm.notes || ''}
                    onChange={(e) => setMemberForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional notes..."
                    rows={2}
                    className={`${inputClsSmallTouch} resize-y`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:flex md:items-center gap-2 pt-1">
                <button type="submit"
                  disabled={savingMember || !memberForm.name.trim() || !memberForm.crew_grade_id}
                  className={`${BTN.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}>
                  {savingMember ? 'Adding...' : 'Add Member'}
                </button>
                <button type="button"
                  onClick={() => { setShowAddMember(false); setMemberForm({ crew_grade_id: '', name: '', user_id: null, location: '', start_date: '', end_date: '', default_allocation: 1.0, computer_rental: '', computer_rental_cap: '', weekly_rate_override: '', sixth_day_rate_override: '', seventh_day_rate_override: '', notes: '', user_email: '', user_phone: '' }); }}
                  className={`${BTN.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}>
                  Cancel
                </button>
                {/* Ruling 24 — managers only (`canSendCrewInvites`). */}
                {canInvite && (
                <span className="relative group col-span-2 md:col-span-1">
                  <button type="button"
                    data-testid="crew-add-and-invite"
                    disabled={!memberForm.user_email?.trim() || !memberForm.name.trim() || !memberForm.crew_grade_id || savingMember || sendingInvite}
                    onClick={() => handleAddMemberAndInvite()}
                    className={`w-full md:w-auto min-h-[44px] md:min-h-0 inline-flex items-center justify-center text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${memberForm.user_email?.trim() ? 'bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600' : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'}`}>
                    {sendingInvite ? 'Sending...' : 'Add & Invite'}
                  </button>
                  {!memberForm.user_email?.trim() && (
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 dark:bg-gray-600 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      Enter an email address to send an invite
                    </span>
                  )}
                </span>
                )}
              </div>
            </form>
          )}

          {/* Members Table + Gantt */}
          {membersLoading ? (
            <LoadingSpinner message="Loading crew members..." />
          ) : members.length === 0 ? (
            <EmptyState
              size="sm"
              title="No crew added yet"
              description="Add a member to see them in the budget and Gantt views."
              cta={canEdit ? { label: 'Add Member', onClick: () => setShowAddMember(true) } : undefined}
            />
          ) : (
            <>
            {/* Table view — always visible on mobile (Gantt is desktop-only); on desktop, visible when 'table' is selected */}
            {/* 🔀 ONE PAGE SCROLL (2026-09-26) — owner: *"yh this worked well do
                all other similar pages"*. This was an `overflow-x-auto
                overflow-y-auto max-h-[700px]` box inside an `overflow-hidden`
                card: a scroll window in a scrolling page, with the header and
                the total row pinned INSIDE it. `CrewMembersTableFrame` pins
                the header under the app bar and the TOTAL row above the save
                bar against the PAGE, with a sticky horizontal scrollbar, and
                takes its column widths from the rows table itself. The rows
                below are unchanged; only the header `<th>`s and the total
                `<td>`s moved into the two arrays, their classes minus the
                `sticky top-0 z-10` / `sticky bottom-0 z-10` that pinned them
                inside the box (the PANES pin now). */}
            <CrewMembersTableFrame
              /* `md:border-0`: flush in the section card on desktop (2026-10-05). */
              className={crewViewMode === 'gantt' ? 'md:hidden md:border-0' : 'md:border-0'}
              bodyTableClassName="w-full text-sm table-fixed md:table-auto"
              stickyTop={crewTableStickyTop}
              stickyBottom={crewTableStickyBottomPx}
              // While a row is open its cells may be CONTROLS: hold every column.
              freezeWidths={expandedMemberId != null || closingMemberId != null}
              headerCells={[
                { key: 'idx', className: 'text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 w-8 hidden md:table-cell', label: '#' },
                { key: 'name', className: 'text-left px-1 md:px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 w-[40%] md:w-auto', label: 'Name' },
                { key: 'grade', className: `text-left px-1 md:px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 w-[26%] md:w-auto ${CREW_ROW_EDIT_MIN.grade}`, label: 'Grade' },
                { key: 'location', className: `text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden lg:table-cell ${CREW_ROW_EDIT_MIN.location}`, label: 'Location' },
                { key: 'start', className: `text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden md:table-cell ${CREW_ROW_EDIT_MIN.date}`, label: 'Start' },
                { key: 'end', className: `text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden md:table-cell ${CREW_ROW_EDIT_MIN.date}`, label: 'End' },
                { key: 'alloc', className: `text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden sm:table-cell ${CREW_ROW_EDIT_MIN.alloc}`, label: 'Alloc' },
                { key: 'weeks', className: 'text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden lg:table-cell', label: 'Weeks' },
                { key: 'rate', className: 'text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden sm:table-cell', label: 'Rate/wk' },
                { key: 'budget', className: 'text-right px-1 md:px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 w-[34%] md:w-auto', label: 'Budget' },
                { key: 'allowances', className: 'text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 hidden lg:table-cell', label: 'Allowances' },
                ...(canEdit ? [{ key: 'reorder', className: 'w-10 px-1 py-2 hidden md:table-cell', label: '' }] : []),
              ]}
              /* The TOTAL row — pinned above the save bar by the frame.
                 🔑 `data-testid` ADDED 2026-08-23 SO THE LABOUR COLUMN'S
                 TOTAL CAN BE PINNED AT LABOUR-ONLY. The footer's crew total
                 gained the three allowances on that date; this figure
                 deliberately did NOT, because it is a COLUMN total and must
                 equal the column above it. Without a stable handle the only
                 way to assert that is to guess at row order, and the
                 assertion that stops someone "helpfully" adding allowances
                 here too is worth a testid. (The same two figures, the same
                 two expressions — only the element they render in moved.) */
              footerRowTestId="crew-table-total-row"
              footerCells={[
                { key: 'idx', className: 'px-2 py-2 hidden md:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'name', className: 'px-1 md:px-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600', content: 'Total' },
                { key: 'grade', className: 'px-1 md:px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'location', className: 'px-3 py-2 hidden lg:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'start', className: 'px-3 py-2 hidden md:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'end', className: 'px-3 py-2 hidden md:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'alloc', className: 'px-3 py-2 hidden sm:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'weeks', className: 'px-3 py-2 hidden lg:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'rate', className: 'px-3 py-2 hidden sm:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' },
                { key: 'budget', className: 'px-1 md:px-3 py-2 text-right text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600', content: formatMoney(totalLabour) },
                { key: 'allowances', className: 'px-3 py-2 text-right text-gray-800 dark:text-gray-200 hidden lg:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600', content: formatMoney(totalAllowancesInclComputer), testId: 'crew-allowances-total' },
                ...(canEdit ? [{ key: 'reorder', className: 'px-1 py-2 hidden md:table-cell bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600' }] : []),
              ]}
            >
                  {filteredMembers.length === 0 && crewSearch && (
                    <tr>
                      <td colSpan={12} className="text-center py-6 text-xs text-gray-500 dark:text-gray-400 italic">
                        No crew match "{crewSearch}". <button type="button" onClick={() => setCrewSearch('')} className="text-blue-600 dark:text-blue-400 hover:underline ml-1">Clear search</button>
                      </td>
                    </tr>
                  )}
                  {filteredMembers.map((m, idx) => {
                    /* 🔑 THE ROW AND THE COLUMN TOTAL BELOW IT READ THE SAME
                       RESOLVER (2026-08-23). `totalLabour` / `totalComputer`
                       became EDIT-preview-aware in the same change; leaving
                       these cells on the raw row would have left the `Total`
                       row no longer equal to the column above it, which is the
                       property §5 of `BudgetCostCodes.footerAllowances.test.js`
                       exists to protect. A column total that stops matching its
                       own column is a second wrong number, not a fix.
                       ⚠️ `dp` is still the DRAG alone and is kept for the blue
                       "provisional" text below — a live EDIT preview is already
                       marked, on the cost-summary panel directly beneath this
                       row. Taking a new visual channel here would be a design
                       decision nobody has made. */
                    const dp = dragPreview && dragPreview.memberId === m.id ? dragPreview : null;
                    const mEff = memberWithLivePreview(m);
                    const isExpanded = expandedMemberId === mEff.id;
                    const startFormatted = mEff.start_date ? new Date(mEff.start_date.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    const endFormatted = mEff.end_date ? new Date(mEff.end_date.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    const allocLabel = ALLOCATION_OPTIONS.find(o => o.value === parseFloat(m.default_allocation))?.label || m.default_allocation;
                    // Dim other rows when dirty — DIM ONLY, never `pointer-events-none`:
                    // a click on another row must reach `handleExpandMember`, which asks
                    // Save / Discard / Keep editing (owner on staging 2026-10-07: the
                    // greyed rows swallowed the click, so the pop-up never appeared).
                    const dimmed = isMemberDirty && expandedMemberId && expandedMemberId !== m.id;
                    // 🔀 IN-PLACE (2026-10-25, the Breakdown's model): an editor's open row
                    // edits grade, location, dates and allocation IN their cells from `md`
                    // (the phone keeps a text row; its panel keeps the fields). Widths hold
                    // because the frame FREEZES them while a row is open (`freezeWidths`).
                    const editingRow = canEdit && isExpanded && closingMemberId !== m.id;
                    const rowPad = editingRow ? 'md:px-1 md:py-1' : '';
                    const inCell = (field, text, textClass = '') => (editingRow ? (
                      <>
                        <span className="hidden md:block" onClick={stopRowToggleOnControl}>{memberFieldControl(m, field, { className: ROW_EDIT_CONTROL, where: 'row' })}</span>
                        <span className={`md:hidden ${textClass}`}>{text}</span>
                      </>
                    ) : text);

                    return (
                      <React.Fragment key={m.id}>
                        <tr
                          id={`crew-member-row-${m.id}`}
                          className={`border-b border-gray-100 dark:border-gray-700/50 ${editableRowClass({ expanded: isExpanded, dimmed })}`}
                          onClick={() => handleExpandMember(m)}
                        >
                          <td className="px-2 py-2 text-center text-gray-400 dark:text-gray-500 hidden md:table-cell">{idx + 1}</td>
                          <td className="px-1 md:px-3 py-2 text-gray-800 dark:text-gray-200 font-medium">
                            {/* 📱 BELOW `md` THE NAME LINE WRAPS (2026-09-27, owner).
                                There the table is `table-fixed` and Name is a
                                declared 40% — a non-wrapping `inline-flex` line of
                                name + status + role/sub-dept chips + link icon ran
                                ~107px out of the cell at 375px and painted over
                                the Grade text. So below md the chevron keeps its
                                place beside the name (outer span: no wrap), and
                                the name + badges wrap in the inner span — badges
                                UNDER the name, and a long unbroken name may break
                                (`overflow-wrap: anywhere` lowers the flex item's
                                min-content, which `break-words` does not). md+
                                is the unchanged single `inline-flex` line: the
                                inner span is `md:contents`, so its children lay
                                out in the outer span exactly as before (the
                                table is `table-auto` there, the column grows, and
                                `CrewMembersTableFrame` measures widths from these
                                cells). Pinned by `BudgetCostCodes.crewTable
                                PageScroll.test.js` (the Name-cell block); the
                                no-overlap claim is a BROWSER measurement. */}
                            <span className="flex items-start gap-1 md:inline-flex md:items-center">
                              <span className="mt-[3px] md:mt-0 shrink-0 inline-flex"><ExpandChevron expanded={isExpanded} /></span>
                              <span data-crew-name-line="" className="flex flex-wrap items-center gap-x-1 gap-y-0.5 min-w-0 [overflow-wrap:anywhere] md:contents md:[overflow-wrap:normal]">
                              {m.name}
                              {m.user_status && (
                                <UserStatusIndicator
                                  status={m.user_status}
                                  // Additive, default-off elsewhere — see
                                  // UserStatusIndicator's own docblock. Only
                                  // this crew table passes it, and only the
                                  // field that actually answers "since when"
                                  // for the status shown: an invite's own
                                  // send time for 'invited', the linked
                                  // user's row creation for 'placeholder'.
                                  // Both fields come straight off GET
                                  // /api/budget/crew-members (server/routes/
                                  // budget.js) — no client-side re-derivation.
                                  sinceDate={
                                    m.user_status === 'invited' ? m.invite_sent_at
                                      : m.user_status === 'placeholder' ? m.user_created_at
                                      : undefined
                                  }
                                />
                              )}
                              {m.user_role_display_name && (
                                <span className="ml-1.5 px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                  {m.user_role_display_name}
                                </span>
                              )}
                              {m.user_sub_department_name && (
                                <span className="ml-1 px-1.5 py-0 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                  {m.user_sub_department_name}
                                </span>
                              )}
                              {m.user_id && (
                                <a href={`/admin/settings#users`} className="ml-1 text-gray-300 dark:text-gray-600 hover:text-blue-500 dark:hover:text-blue-400" title="View in Settings" onClick={(e) => e.stopPropagation()}>
                                  <svg className="w-3 h-3 inline" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" /><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" /></svg>
                                </a>
                              )}
                              </span>
                            </span>
                          </td>
                          <td className={`${editingRow ? 'px-1 md:py-1' : 'px-1 md:px-3 py-2'} text-gray-800 dark:text-gray-200`} data-testid={`crew-grade-cell-${m.id}`}>
                            {inCell('grade', <span className="inline-flex items-center gap-1">{m.grade_name || '—'}</span>)}
                          </td>
                          <td className={`px-3 py-2 ${rowPad} text-gray-500 dark:text-gray-400 hidden lg:table-cell`} data-testid={`crew-location-cell-${m.id}`}>{inCell('location', m.location || '—')}</td>
                          <td className={`px-3 py-2 ${rowPad} text-gray-500 dark:text-gray-400 hidden md:table-cell`} data-testid={`crew-start-cell-${m.id}`}>{inCell('start_date', startFormatted)}</td>
                          <td className={`px-3 py-2 ${rowPad} text-gray-500 dark:text-gray-400 hidden md:table-cell`} data-testid={`crew-end-cell-${m.id}`}>{inCell('end_date', endFormatted)}</td>
                          <td className={`px-3 py-2 ${rowPad} text-center text-gray-500 dark:text-gray-400 hidden sm:table-cell`} data-testid={`crew-alloc-cell-${m.id}`}>{inCell('default_allocation', allocLabel)}</td>
                          <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 hidden lg:table-cell">{mEff.total_weeks ?? '—'}</td>
                          {/* RATE/WK — READ-ONLY in the row by owner ruling (the override is "a
                              delibrate action in the expanded menu"). 🔀 2026-10-25: an override is
                              marked by the shared pen (`OverrideMarker`) — it REPLACED the amber
                              text + "*" (owner: "if a marker already exists, replace it"), so the
                              figure keeps the cell's ordinary shade and the pen is the sole carrier.
                              `rate_is_override` is the SERVER's answer; nothing is re-derived.
                              🪤 A TERNARY, never `&&`: a numeric 0 renders "0" (measured). */}
                          <td className="px-3 py-2 text-right hidden sm:table-cell" data-testid={`crew-rate-cell-${m.id}`}>
                            <span className="inline-flex items-center justify-end gap-1 text-gray-500 dark:text-gray-400">
                              {m.rate_is_override ? (
                                <OverrideMarker
                                  title={`Rate overridden: ${formatMoney(m.effective_weekly_rate)}/wk (grade rate ${formatMoney(m.grade_weekly_rate)}/wk) — edit in the expanded row`}
                                  testId={`crew-rate-override-${m.id}`}
                                />
                              ) : null}
                              {formatMoney(m.effective_weekly_rate ?? m.grade_weekly_rate)}
                            </span>
                          </td>
                          <td className={`px-1 md:px-3 py-2 text-right font-medium ${dp ? 'text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-gray-200'}`}>{formatMoney(mEff.budget_cost)}</td>
                          {/* ALLOWANCES, INCLUDING THE LAPTOP (2026-08-23). Owner:
                              *"on table view please change 'Computer' to 'Allowances'
                              and make show the values are the sum of all allowances
                              for each user and not just computer."*
                              🔑 `allowance_total` IS EXACTLY THAT FIELD and is read,
                              never re-derived: the server publishes it as
                              `computer_cost + a1 + a2 + a3`
                              (`helpers/crewMemberCostFields.js`), each part already
                              rounded, so summing the four here would be a fifth copy
                              of a cost formula in a directory that only REPORTS money.
                              Computer rental IS an allowance (owner: *"computer is one
                              type of allowance and not different"*), so this is the
                              domain's own grouping, not a display preference.
                              ⚠️ It is NOT the footer's grouping — `crewGrandTotal`
                              keeps computer and allowances as separate terms, and
                              swapping THAT for `allowance_total` charges every laptop
                              twice. One field, two groupings; the fuller sentence is
                              at `totalAllowancesInclComputer`.
                              `mEff`, not `m` — same reason as the Budget cell: the
                              `Total` row sums this column through the same resolver,
                              so reading the raw row here would let the column total
                              disagree with the column while a preview is live. A
                              no-op under a DRAG (the drag preview carries no
                              `allowance_total`, so `mEff.allowance_total ===
                              m.allowance_total`), exactly as it was for
                              `computer_cost`. */}
                          <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 hidden lg:table-cell" data-testid={`crew-allowances-${m.id}`}>{formatMoney(mEff.allowance_total)}</td>
                          {canEdit && (
                            <td className="px-1 py-1 text-center w-10 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                              {/* Reorder buttons hidden while a search filter
                                  is active. `idx` here is the position in the
                                  full members list (we map filteredMembers but
                                  filteredMembers === members when search is
                                  empty), so handleReorderMember would target
                                  the wrong neighbours under a filtered view. */}
                              {!crewSearch.trim() && (
                                <RowReorderButtons index={idx} count={members.length} onMove={handleReorderMember} />
                              )}
                            </td>
                          )}
                        </tr>

                        {/* Expanded Edit Panel */}
                        {(isExpanded || closingMemberId === m.id) && (
                          // The crew row editor: never a table pan (`useDragScroll`).
                          <tr data-pan-skip="">
                            <td colSpan={canEdit ? 12 : 11} className="px-0 py-0">
                              {renderMemberDetailsPanel(m)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

            </CrewMembersTableFrame>

            {/* Gantt view — desktop only, rendered when 'gantt' is selected */}
            {crewViewMode === 'gantt' && (
              /* 📐 No border of its own and `flush` on the chart (2026-10-05):
                 this was a framed chart inside a framed box inside the card. */
              /* 📜 `overflow-clip`, NOT `overflow-hidden` (2026-10-05): a hidden
                 ancestor is a scroll container, and the chart's page-pinned date
                 band and sticky bar would pin to IT instead of the page. */
              <div className="hidden md:block rounded-b-lg overflow-clip">
                <CrewGanttView
                  pageScroll
                  stickyTop={labourToolbarBottom}
                  stickyBottom={crewTableStickyBottomPx}
                  controlsTarget={ganttControlsSlot}
                  flush
                  members={ganttMembers}
                  grades={grades}
                  canEdit={canEdit}
                  onMemberUpdate={handleGanttUpdate}
                  onMemberDragPreview={handleGanttDragPreview}
                  totalLabour={totalLabour}
                  /* ✅ BROUGHT INTO LINE 2026-08-23. This read `totalComputer={totalComputer}`
                      under a footer labelled `Computer`, and the note here said that
                      correcting it needed a SECOND prop plus the single-member chart moved
                      with it, *"Do not quietly swap the value under the existing prop name —
                      the component's label would then be a lie."* That is exactly what was
                      done: the prop is now `totalAllowances`, the footer says `Allowances`,
                      and the figure is every allowance including the laptop.
                      🔑 `totalAllowancesInclComputer`, NOT a fourth reduce — it is
                      `totalComputer + totalAllowance`, the same two terms the crew table's
                      `Allowances` column total already uses, so the chart's footer and the
                      table's footer cannot disagree.
                      🚨 `crewGrandTotal` IS UNCHANGED and still counts `totalComputer` as its
                      own term. This is a DISPLAY figure on the chart's own bar and does not
                      enter it; folding `allowance_total` into `crewGrandTotal` double-counts
                      every laptop (£3,755 against £3,455). */
                  totalAllowances={totalAllowancesInclComputer}
                  /* 🔑 THE STRIP'S LIVE-COSTING STATE — 2026-08-23. The
                     component draws it beside the strip member's price and
                     nowhere else; `failed` shows a dash rather than the saved
                     figure, so a number the server has not agreed to is never
                     presented as current. */
                  dayEditPreviewState={ganttStripCostState}
                  dayEditMemberId={ganttDayMemberId}
                  dayEditWorkedDates={ganttDayWorkedDates}
                  /* 🔑 THE ONE SAVE BUTTON'S ONE DIRTY SIGNAL — see
                     `ganttSurfaceDirty`. The single-member chart inside the
                     table's details panel keeps `ganttDayDirty`: it has no
                     Details control, so it can never be the level-2 surface. */
                  dayEditDirty={ganttSurfaceDirty}
                  dayEditSaving={ganttDaySaving}
                  onDayEditToggle={handleGanttRowExpandToggle}
                  onDayToggle={handleGanttDayToggle}
                  onDayEditSave={handleGanttDaySave}
                  onDayEditCancel={handleGanttDayCancel}
                  /* ─── LEVEL 2 — the second expand (PROTOTYPE, option B) ───
                     🔑 THE PANEL IS RENDERED BY THE PAGE AND HANDED DOWN AS A
                     NODE. `CrewGanttView` places it and animates it and knows
                     nothing else about it; the form's JSX, its handlers and its
                     state stay where they already were, which is what stops
                     this being a second implementation of a money form.
                     🪤 IT IS MOUNTED FOR `closingMemberId` TOO, and that is not
                     tidiness: `detailsOpen` drives the HEIGHT and
                     `ganttDetailsMemberId` clears synchronously, so keeping the
                     node mounted through `collapseMember`'s existing 250ms
                     window is what makes the collapse animate instead of
                     vanishing. Same device the table's own fade already uses. */
                  detailsMemberId={ganttDetailsMemberId}
                  /* 🔑 THE ANCHOR OUTLIVES THE CONTROL. `ganttDetailsMemberId`
                     clears the moment the user collapses, but the panel is still
                     fading out for 250ms and still has to be positioned against a
                     row for every frame of it — so the chart is told which row
                     independently of whether the control still says "open". */
                  detailsAnchorId={ganttDetailsMemberId ?? ganttDetailsClosingId}
                  detailsOpen={ganttDetailsMemberId != null}
                  detailsPanel={ganttDetailsPanelMember
                    && (ganttDetailsMemberId != null || ganttDetailsClosingId != null)
                    ? renderMemberDetailsPanel(ganttDetailsPanelMember, { embedded: true })
                    : null}
                  onDetailsToggle={handleGanttDetailsToggle}
                />
              </div>
            )}
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* ═══ ROOM FOR THE FIXED TOTALS BAR — MEASURED, NOT A CONSTANT ═══
          Owner: *"the entire gantt chart seems to have a big space between the
          bottom of the chart and the bottom sticky totals bar."*

          This was `<div className="h-16" />`. The full measurement, the three
          beliefs about it that turned out to be FALSE, and why the answer
          cannot be a constant are all in `utils/saveBarSpacer.js` — read that
          before changing anything here.

          The short version: `h-16` was not the waste. Three other mechanisms
          already reserve 89px between the last content and the end of the
          document (`space-y-6`'s margin, the container's `lg:p-6` bottom
          padding, and the app's own `<footer>`), and the spacer double-counted
          every one of them. What is reserved now is only what is still
          missing, which measured 17px where the old constant was 64 + a 24px
          margin — and 0 on a tab whose content never reaches the bar.

          🚨 `null` MEANS "NOT MEASURED YET" AND FALLS BACK TO `h-16`, NEVER TO
          ZERO. The first paint happens before any geometry exists, and a
          0-height first paint would put the bottom of a long page under the
          bar for a frame. Unmeasured and not-needed are different answers. */}
      <div
        ref={saveBarSpacerRef}
        aria-hidden="true"
        data-save-bar-spacer
        className={saveBarSpacerPx == null ? 'h-16' : undefined}
        style={saveBarSpacerPx == null ? undefined : { height: saveBarSpacerPx }}
      />

      </>)}

      {/* ═══════════════════════════════════════════
          STICKY TOTALS BAR
          ═══════════════════════════════════════════ */}
      {selectedVersionId && (
        <>
        {/* Mobile override for save bar positioning */}
        <style>{`@media (max-width: 767px) {
          [data-save-bar-fade] { left: 0 !important; bottom: 65px !important; }
          [data-save-bar] { left: 0.5rem !important; right: 0.5rem !important; bottom: calc(65px + 0.5rem) !important; }
        }`}</style>
        {/* Fade gradient above save bar */}
        <div className="fixed bottom-0 right-0 z-30 pointer-events-none" data-save-bar-fade
          /* 5rem (was 6rem + 10px): the fade now ends ~16px above the save bar,
             so the sticky scrollbars / pinned Total row — which clear the
             fade's MEASURED top — sit just above the bar rather than ~40px up
             (owner 2026-10-05: the Gantt's side scroll should "live between
             the bottom of the gantt and the bottom sticky"). */
          style={{ left: 'var(--sidebar-width, 16rem)', height: '5rem', background: 'linear-gradient(to bottom, transparent 0%, var(--fade-color, #f5f5f5) 25%, var(--fade-color, #f5f5f5) 100%)' }}
        />
        <div className="fixed bottom-4 right-4 z-40 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-xl border-2 border-gray-300 dark:border-gray-500" data-save-bar
          style={{ left: 'calc(var(--sidebar-width, 16rem) + 1rem)' }}
        >
          {/* Mobile: one label/value pair per row, figure right-aligned.
              🔴 MONEY IS NEVER TRUNCATED (demo polish 2026-09-25): the two
              pairs used to share one line with `truncate` on each figure, so a
              phone read "£128,40…". Stacking costs one row of height and shows
              every figure in full. Pinned by
              BudgetCostCodes.mobileTotalsNoTruncate.test.js. The Total rule's
              `border-gray-200` is bare on purpose: index.css's
              `html.dark .border-gray-200 { … !important }` supplies its dark
              colour, so a `dark:` pair would be inert. */}
          <div className="md:hidden px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
            <div data-save-bar-total-row className="flex items-baseline justify-between gap-3">
              <span>Cost Code budget:</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap tabular-nums">{formatMoney(totalBudget)}</span>
            </div>
            <div data-save-bar-total-row className="flex items-baseline justify-between gap-3">
              <span>Labour inc allowances:</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap tabular-nums">{formatMoney(crewGrandTotal)}</span>
            </div>
            <div data-save-bar-total-row className="flex items-baseline justify-between gap-3 pt-0.5 border-t border-gray-200">
              <span className="font-bold">Total:</span>
              <span className="font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap tabular-nums">{formatMoney(totalBudget + crewGrandTotal)}</span>
            </div>
          </div>

          {/* Desktop: horizontal totals */}
          <div className="hidden md:flex max-w-full px-4 py-2.5 items-center justify-end gap-4">
            <div className="flex items-center gap-4 text-base text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1.5">
                <span>Cost Code budget:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{formatMoney(totalBudget)}</span>
              </div>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <div className="flex items-center gap-1.5">
                <span>Labour inc allowances:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{formatMoney(crewGrandTotal)}</span>
              </div>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <div className="flex items-center gap-1.5">
                <span>Total:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">{formatMoney(totalBudget + crewGrandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Phase 11 / Task 20: Lock confirmation modal — explains the snapshot
          capture before the user commits. Closes on Cancel + after a successful
          POST. */}
      {showLockConfirm && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lock-confirm-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLockConfirm(false); }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200 dark:border-gray-700 ${MODAL_PANEL_MOBILE_CAP}`}>
            <div className="flex items-start gap-3">
              <span className="text-green-600 dark:text-green-400 mt-0.5">
                <LockIcon locked className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h3 id="lock-confirm-title" className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  Lock {selectedVersion?.version_name || 'this version'}?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Prevents any further edits to this version's crew and cost codes. Locked versions can be unlocked at any time.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLockConfirm(false)}
                className={BTN.secondary}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLockVersion}
                disabled={lockingVersion}
                className={BTN.confirm}
              >
                {lockingVersion ? 'Locking...' : 'Lock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 11 / Task 20: Unlock confirmation modal — calls out cloning as
          the safer alternative since unlocking the official record allows
          direct edits. */}
      {showUnlockConfirm && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unlock-confirm-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowUnlockConfirm(false); }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200 dark:border-gray-700 ${MODAL_PANEL_MOBILE_CAP}`}>
            <div className="flex items-start gap-3">
              <span className="text-amber-600 dark:text-amber-400 mt-0.5">
                <LockIcon locked={false} className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h3 id="unlock-confirm-title" className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  Unlock this budget version?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Unlocking the current budget allows direct edits. Recommended: clone this version first and edit the clone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowUnlockConfirm(false)}
                className={BTN.secondary}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUnlockVersion}
                disabled={unlockingVersion}
                className={BTN.warning}
              >
                {unlockingVersion ? 'Unlocking...' : 'Unlock anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The ruled manual-spend-adjustment warning (owner's wording basis),
          raised from the row SAVE when the adjustment draft changed — BEFORE
          any request. Continue runs the whole row save incl. the audited
          PATCH; Cancel aborts the save entirely (nothing sent, row stays in
          edit mode). The optional one-line reason lives in the prompt slot. */}
      <ConfirmDialog
        open={pendingAdjustmentSave != null}
        title="Manual spend adjustment"
        message={SPEND_ADJUSTMENT_WARNING}
        confirmLabel="Continue"
        promptLabel="Reason (optional, one line)"
        promptValue={adjustmentNoteDraft}
        onPromptChange={setAdjustmentNoteDraft}
        busy={savingCostCode}
        onConfirm={handleAdjustmentConfirmContinue}
        onCancel={handleAdjustmentConfirmCancel}
      />

      {/* JOB CODES — "N orders found for this job — link them to J01?" Shows
          the orders; nothing moves until the buyer confirms (owner ruling 3). */}
      <ConfirmDialog
        open={!!relinkTarget}
        title={relinkTarget ? `Link these orders to ${relinkTarget.code}?` : ''}
        message={relinkTarget ? `These orders are for ${relinkTarget.name} but have lines on another cost code (or none). Confirm to put every line on ${relinkTarget.code} · ${relinkTarget.name}.` : ''}
        variant="save"
        confirmLabel={relinkTarget ? `Link to ${relinkTarget.code}` : 'Link'}
        busy={relinking}
        onConfirm={handleConfirmRelink}
        onCancel={() => setRelinkTarget(null)}
      >
        {relinkTarget && (
          <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 text-sm" data-testid="job-relink-list">
            {(relinkCandidates[relinkTarget.id] || []).map((o) => (
              <li key={o.id} className="py-1.5 flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-gray-800 dark:text-gray-200">
                  <span className="font-mono">{o.order_number || `#${o.id}`}</span>
                  {o.supplier_name ? <span className="text-gray-500 dark:text-gray-400"> · {o.supplier_name}</span> : null}
                </span>
                <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{o.lines_to_link} line{o.lines_to_link === 1 ? '' : 's'}</span>
              </li>
            ))}
          </ul>
        )}
      </ConfirmDialog>

      {/* The expand-to-edit tables' three-way prompts for a held row click. */}
      <RowEditPrompt
        editor={paymentTypeEditor}
        title="Unsaved payment type changes"
        noun={paymentTypes.find((x) => x.id === paymentTypeEditor.expandedId)?.name || 'This payment type'}
        busy={savingPaymentType}
      />
      <RowEditPrompt
        editor={costCodeEditor}
        title="Unsaved cost code changes"
        noun={costCodes.find((x) => x.id === costCodeEditor.expandedId)?.code || 'This cost code'}
        busy={savingCostCode}
      />
      <RowEditPrompt
        editor={gradeEditor}
        title="Unsaved grade changes"
        noun={grades.find((x) => x.id === gradeEditor.expandedId)?.name || 'This grade'}
        busy={savingGrade}
      />

      {/* ═══ UNSAVED WORKED DAYS — the Gantt day strip's exit prompt ═══
          Three outcomes, cancel · alt · confirm, exactly the shape and order
          `components/OrderInlineEditor.js` uses for its collapse prompt.
          `initialFocus="cancel"` so Enter can neither discard nor save
          unreviewed. NOT `variant="danger"`: nothing here is destructive until
          the user picks the middle button, and a red heading on a routine tab
          click is the kind of alarm people learn to click through. */}
      <ConfirmDialog
        open={ganttDayExit != null}
        /* 🔀 TITLE WIDENED 2026-08-22 — it said "Unsaved worked days", and once
           a batched bar drag became part of the same edit that title could name
           the wrong thing entirely (a pure date drag has no unsaved days at
           all). A dialog asking someone to discard must describe what it is
           about to throw away. */
        title="Unsaved crew changes"
        message={(
          <>
            {ganttDayExit?.kind === 'table' && (
              <span className="block">
                {ganttDayExit.memberName} has unsaved changes. Discarding puts
                them back as they were saved.
              </span>
            )}
            {ganttDayExit?.kind === 'both' && (
              <span className="block" data-testid="crew-unsaved-both">
                {ganttDayExit.memberName} has unsaved changes in the crew table AND
                unsaved worked days on the chart. Save each with its own Save
                button first, or discard both.
              </span>
            )}
            {/* Each half is named only when it is actually pending, so the
                prompt never invents work the user did not do. */}
            {ganttDayExit?.kind !== 'table' && ganttDayDatesDirty && (
              <span className="block">
                {ganttDayMember?.name || 'This crew member'} has an unsaved date
                change: {ganttDayEffectiveDates.start_date} to{' '}
                {ganttDayEffectiveDates.end_date}.
              </span>
            )}
            {ganttDayExit?.kind !== 'table' && ganttDayDaysDirty && (
              <span className={ganttDayDatesDirty ? 'block mt-2' : 'block'}>
                {ganttDayMember?.name || 'This crew member'} has unsaved worked-day
                changes to {ganttDayChangedWeeks.length}{' '}
                {ganttDayChangedWeeks.length === 1 ? 'week' : 'weeks'}
                {ganttDayChangedWeeks.length > 0 && (
                  <> (week commencing {ganttDayChangedWeeks.join(', ')})</>
                )}.
              </span>
            )}
            {/* 🔑 THE SECOND SENTENCE IS NOT PADDING. A day click writes the day
                set AND that week's allocation, and the allocation is what pays
                for computer rental and all three allowances. Someone deciding
                whether to discard is deciding about both, so the dialog says
                so — a prompt that under-describes what it is about to throw
                away is a prettier silent refusal. */}
            {ganttDayExit?.kind !== 'table' && (
              <span className="block mt-2">
                Worked days set the labour cost, and they also set each week&rsquo;s
                allocation, which pays for computer rental and allowances. Dates
                set how many weeks are charged at all. Discarding puts them back as
                they were saved.
              </span>
            )}
            <span className="block mt-2 font-medium">
              What would you like to do before you {ganttDayExit?.what}?
            </span>
          </>
        )}
        {...UNSAVED_PROMPT_BUTTONS}
        {...(ganttDayExit?.kind === 'both' ? { altLabel: 'Discard both' } : {})}
        confirmLabel="Save and continue"
        confirmDisabled={ganttDayExit?.kind === 'both'}
        variant="save"
        busy={ganttDaySaving || (ganttDayExit?.kind === 'table' && savingMember)}
        onCancel={handleGanttDayExitCancel}
        onAlt={handleGanttDayExitDiscard}
        onConfirm={handleGanttDayExitSave}
      />
    </div>
  );
}

export default BudgetCostCodes;
